import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import { SourceDeal } from './data/sourceDeals';
import { useSourceDeals } from './hooks/useSourceDeals';
import type { EstimateEntry } from './data/estimateDb';
import {
  DAY_TIME, NETWORK_MAP, PROGRAM_DISPLAY, ESTIMATE_NAME_MAP,
  NETWORK_ORDER, PROGRAM_ORDER, ALL_PROGRAMS, PLAN_LENGTHS,
  SYSTEM_DEMOS, ALL_ESTIMATE_DEMOS,
  PERIOD_MAP as INITIAL_PERIOD_MAP,
  UNIVERSE_DATA as INITIAL_UNIVERSE_DATA,
  QUARTER_TABS as INITIAL_QUARTER_TABS,
  SUMMARY_TAB_KEY,
} from './data/constants';
import {
  getLatestVersionByYearKindAndCard, getLatestGeneralVersionByYear,
  hasVersionForCard, cardOf,
  listVersionsByYear,
  getVersionById, getVersionLabel,
  lateQuarterOf,
  getLatestVersionWithLateQuarter, getLatestGeneralVersionWithLateQuarter,
  hydrateEstimateVersions, isHydrated,
  getHydrateError, retryHydrate, onHydrateChange,
} from '../estimate-uploads/storage';
import {
  hydrateSfPlanBindings, isSfBindingsHydrated, getSfPlanBinding,
  setSfPlanBinding, onSfBindingsChange,
} from '../estimate-uploads/sf-binding-storage';
import {
  EstimateVersion, EstimateKind, formatEstimateStream,
  RateCard, RATE_CARDS, formatRateCard,
  normalizeProgramName,
} from '../estimate-uploads/types';
import {
  hydratePlans, isPlansHydrated, getSavedPlanByName, listSavedPlans, savePlan,
  pushPlanToSf,
  deletePlan as deleteSavedPlan,
  updatePlanStatus, PLAN_STATUSES, PlanStatus,
  listPlanVersions, getPlanVersion, namePlanVersion, snapshotVersionForExport,
  refreshPlansCache, overlayHistoricalVersion, getSavedPlanById,
  SavePlanRequest, SavedPlan, PlanDeal, PlanVersionMeta,
} from './plan-storage';
import { getEmailFromSessionStorage } from '../utils/session_utils';
import {
  searchAdvertisers, searchBrands, searchContacts,
  resolveSalesPlan, listPrograms, listPrimaryDemos, getSfInstanceUrl,
  ContactLookupItem, BrandLookupItem,
} from '../utils/sf-lookups';
import { LookupField } from './LookupField';
import { downloadProposalPdf } from './pdf-export';
import { perSpotRate, rowDollars } from './money';

// ─── Layout constants ──────────────────────────────────────────────────────
//
// The MPB stacks under the parent app's fixed Nosey toolbar. Several
// sticky offsets and viewport calculations depend on the heights of
// that toolbar + the MPB's own header, which used to be inlined as
// magic numbers across the file. Centralising them here means a
// header-height tweak is a one-line change.
//
//   FIXED_HEADER_HEIGHT   — App.tsx StyledHeader (antd Layout default,
//                           position: fixed; top: 0).
//   MPB_HEADER_HEIGHT     — the white MVM / Media Plan Builder bar
//                           that sticks at top:FIXED_HEADER_HEIGHT.
//   STICKY_BAR_OFFSET     — the toolbar + tabs + stats row's sticky
//                           position; sum of the two above.
const FIXED_HEADER_HEIGHT = 64;
const MPB_HEADER_HEIGHT = 54;
const STICKY_BAR_OFFSET = FIXED_HEADER_HEIGHT + MPB_HEADER_HEIGHT;

// ─── Color palette ─────────────────────────────────────────────────────────
//
// Named handles for the hex codes that recur most across this file's
// JSX styles. Only the high-frequency ones live here; one-offs stay
// inline where they appear. The mapping is intentionally light on
// abstraction (a generic "primary" instead of a brand name) so any
// future re-theming is a one-line update.
//
// CSS inside `<style>{`…`}</style>` template literals still uses raw
// hex because the in-DOM stylesheet doesn't share scope with this object.
const colors = {
  text:        '#111827', // headings + strong body text
  textSecondary: '#374151',
  textDark:    '#0f172a', // table-cell numeric text
  textMuted:   '#6b7280', // captions, secondary labels
  textDisabled: '#9ca3af',
  border:      '#dde1e9', // light separator on white bg
  borderInk:   '#e2e8f0', // standard panel border
  primary:     '#2563eb', // brand accent blue
  primaryLight: '#eff4ff', // accent tint
  bgPanel:     '#f8fafc', // table header / panel bg
  success:     '#059669',
  danger:      '#dc2626',
  warn:        '#d97706',
  warnBg:      '#fffbeb',
} as const;

// ─── Form field styles ─────────────────────────────────────────────────────
//
// The New Plan modal used to repeat these exact style recipes on
// every label + input pair. Hoisted to module scope so the modal JSX
// reads as content rather than as style declarations.
const formLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: colors.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 4,
};
const formInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px',
  border: `1px solid ${colors.border}`, borderRadius: 6,
  fontSize: 13,
};
const formSelectStyle: React.CSSProperties = {
  ...formInputStyle,
  // Strip native OS chrome — chevron, inner shadow, height quirks —
  // so <select> aligns visually with the text inputs above it. Custom
  // chevron is rendered as a background SVG on the right (a caret-down
  // in slate-500). Extra right padding leaves room for it. Also set
  // an explicit lineHeight matching the inputs so height matches
  // pixel-for-pixel across Chrome / Firefox / Safari.
  background:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\") no-repeat right 10px center, #fff",
  paddingRight: 32,
  lineHeight: 1.4,
  color: '#111827',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
};

// ─── Estimate version injection ────────────────────────────────────────────
//
// The MediaPlanBuilder originally read three hardcoded constants
// (ESTIMATE_DB, UNIVERSE_DATA, QUARTER_TABS) directly from disk. With the
// estimate-versioning system, each plan binds to a specific version and
// those constants must come from the bound version at runtime.
//
// We keep the original identifiers but switch them from imported constants
// to module-level `let` bindings seeded with the legacy data. The
// component re-points them via `setActiveEstimates(version)` whenever the
// active plan's bound version changes. Every existing reference in this
// file continues to compile and just picks up the current value at call
// time — no further refactor required.
// Estimate values (rating, imps, vpvh) come *exclusively* from the active
// uploaded version. With no version active, this stays empty and every
// estimate-derived cell (RTG, HH, CPM, Total HH, etc.) renders blank.
// This is intentional — the legacy hardcoded constant used to act as a
// fallback, but that masked the "no version uploaded" state and could
// silently show stale numbers. Snowflake is the only source of truth now.
let ESTIMATE_DB: Record<string, EstimateEntry> = {};
let UNIVERSE_DATA = INITIAL_UNIVERSE_DATA;
let QUARTER_TABS = INITIAL_QUARTER_TABS;
// PERIOD_MAP translates a Deal's (quarter, year) pair to the period label
// used in estimateDb keys (e.g. ("Q1", 2027) → "1Q'27"). Keyed by
// `${quarter}-${year}` because Scatter plans can include the same
// Q-letter twice (4Q'26 AND 4Q'27) and a flat quarter-only mapping
// would collide. Must be regenerated per active version because the
// season's calendar shifts every year.
let PERIOD_MAP = INITIAL_PERIOD_MAP;
// "25/26"-style annual-rollup label shown in the Summary view. Derived
// from the active version's quarter year span (4Q'26 → 3Q'27 → "26/27").
// Defaults to the legacy fixed string when no version is active.
let SEASON_LABEL = '25/26';
// "4Q25-3Q26"-style quarter-range label used in network total rows.
// Derived from the first and last quarter labels in QUARTER_TABS,
// apostrophes stripped. Defaults to the legacy fixed string when no
// version is active.
let SEASON_RANGE_LABEL = '4Q25-3Q26';
// Program-name index from the active version. Used by getEstimate to
// resolve case-/whitespace-insensitive program lookups (e.g. SF carries
// "Modern Family WKND" but the workbook has "Modern Family Wknd").
// Empty object = no version active, fall back to legacy exact-match.
let PROGRAM_NAME_INDEX: Record<string, string> = {};

/** Compute the "YY/YY" season label from a version's quarter list. */
function computeSeasonLabel(quarters: { year: number }[]): string {
  if (!quarters.length) return '';
  const startYear = quarters[0].year;
  const endYear = quarters[quarters.length - 1].year;
  // "26/27" style. Last 2 digits of each year.
  const yy = (y: number) => String(y % 100).padStart(2, '0');
  return `${yy(startYear)}/${yy(endYear)}`;
}

/** Strip the apostrophe out of a quarter label: "4Q'26" → "4Q26". */
function stripApostrophe(label: string): string {
  return label.replace(/'/g, '');
}

/** "4Q26-3Q27"-style range from the first and last quarter labels. */
function computeSeasonRangeLabel(quarters: { label: string }[]): string {
  if (!quarters.length) return '';
  return `${stripApostrophe(quarters[0].label)}-${stripApostrophe(quarters[quarters.length - 1].label)}`;
}

/** "ABC 4Q26 Quarter Totals" — single-quarter network total label. */
function quarterTotalsLabel(network: string, quarterLabel: string): string {
  return `${network} ${stripApostrophe(quarterLabel)} Quarter Totals`;
}

/**
 * `calcEstimateFields` cache.
 *
 * The function is called hundreds of times per render (every renderDealRow,
 * renderSubtotalRow, renderNetworkTotalRow, aggregateSummaryMetrics, etc.)
 * but is pure for a given (inputs, active estimate version) — recomputing
 * is pointless. We memoize by a string key built from the inputs.
 *
 * Invalidation: the function reads PERIOD_MAP / ESTIMATE_DB / PROGRAM_NAME_INDEX
 * which all swap when `setActiveEstimates` runs with a different version.
 * Clearing the cache on every version change (tracked via the version's
 * stable id) keeps results aligned with the active version's data.
 *
 * Memory profile: each entry is a small ~80-byte object. Even at 10k
 * unique input combinations across a session the cache stays under 1MB.
 */
const estimateCache = new Map<string, EstimateResult>();
let estimateCacheVersionToken: number | null = null;

/** Internal: clear the cache when the active version changes. */
function syncEstimateCacheVersion(version: EstimateVersion | null): void {
  const newToken = version?.id ?? null;
  if (newToken !== estimateCacheVersionToken) {
    estimateCache.clear();
    estimateCacheVersionToken = newToken;
  }
}

function setActiveEstimates(version: EstimateVersion | null): void {
  // Invalidate the calcEstimateFields cache first — every cell value
  // depends on the module-level state we're about to overwrite below.
  syncEstimateCacheVersion(version);
  if (version) {
    ESTIMATE_DB = version.estimateDb;
    UNIVERSE_DATA = version.universeData;
    QUARTER_TABS = version.quarters.map(q => ({
      key: q.key,
      label: q.label,
      quarter: q.quarter,
      year: q.year,
    }));
    // Rebuild PERIOD_MAP so fmtPeriod returns the version's labels.
    // Keyed by `${quarter}-${year}` because Scatter plans span the
    // same Q-letter across two calendar years (4Q'26 + 4Q'27) and the
    // older Q-letter-only keying would collide on Q4.
    const pm: Record<string, string> = {};
    version.quarters.forEach(q => { pm[`${q.quarter}-${q.year}`] = q.label; });
    PERIOD_MAP = pm;
    // ⚠ Season labels come from the version's OWN quarters, excluding a
    // borrowed late quarter.
    //
    // A merged version appends exactly one quarter (Q4 of primaryYear + 1) to
    // its base, so dropping the last entry recovers the base's list. Without
    // this, every 26/27 plan would relabel the moment the extended file was
    // uploaded — including BY Upfront plans, which still display only four
    // tabs (4Q'26–3Q'27). They would read "4Q26-4Q27", a range they don't
    // cover, in the on-screen network totals, the Summary "Plan Total" row and
    // the Excel export.
    //
    // SEASON_LABEL matters more than cosmetics: it is persisted as
    // PLAN_VERSION.YEAR on every save. Both labels are therefore computed to
    // be exactly what they were before the late-quarter merge existed.
    const labelQuarters = version.mergedFrom
      ? version.quarters.slice(0, -1)
      : version.quarters;
    SEASON_LABEL = computeSeasonLabel(labelQuarters);
    SEASON_RANGE_LABEL = computeSeasonRangeLabel(labelQuarters);
    PROGRAM_NAME_INDEX = version.programNameIndex || {};
  } else {
    // No active version → estimate values go blank, but keep structural
    // fallbacks (quarter tabs, period labels, universe boxes, season
    // label) so the UI shell still renders sensibly. Only RTG/HH/CPM/
    // VPVH/etc cells go empty.
    ESTIMATE_DB = {};
    UNIVERSE_DATA = INITIAL_UNIVERSE_DATA;
    QUARTER_TABS = INITIAL_QUARTER_TABS;
    PERIOD_MAP = INITIAL_PERIOD_MAP;
    SEASON_LABEL = '25/26';
    SEASON_RANGE_LABEL = '4Q25-3Q26';
    PROGRAM_NAME_INDEX = {};
  }
}

/**
 * Compute the quarter tabs to display for a single plan, based on its
 * Marketplace value (which mirrors the SF SALES_PLAN__C.Upfront_Scatter__c
 * picklist).
 *
 *   - BY Upfront / unset / legacy "Upfront" → 4 quarters covering the
 *     broadcast year: 4Q(py-1) + 1Q–3Q(py). Matches the legacy default
 *     before per-plan layouts existed.
 *   - CY Upfront → 4 quarters covering the calendar year: 1Q–4Q(py).
 *   - Scatter → 5 quarters covering both: 4Q(py-1) + 1Q–4Q(py).
 *
 * For each (quarter, year) tuple in the pattern, we look up the matching
 * tab in `versionQuarters` (the estimate file's own quarters). When
 * present, we reuse that tab so its `label` matches the ESTIMATE_DB key
 * byte-for-byte (lookups stay correct). When absent — e.g., 4Q'27 under
 * CY/Scatter when the current estimate file still only covers
 * 4Q'26–3Q'27 — we synthesize a tab with a deterministic label/key.
 * Estimate-derived cells (RTG, (000), CPM HH, …) will render as "—" for
 * synthesized quarters until MVMS uploads an estimate file that includes
 * them; rate/unit/cost cells stay editable.
 *
 * primaryYear comes from the active estimate version's PRIMARY_YEAR
 * column (e.g., 2027 for the 26/27 season). When no version is active
 * (cold-start), we fall through to versionQuarters as-is — there's
 * nothing meaningful to layout yet.
 */
type QuarterTab = { key: string; label: string; quarter: string; year: number };
function buildVisibleQuarters(
  marketplace: string | undefined | null,
  versionQuarters: QuarterTab[],
  primaryYear: number | null,
): QuarterTab[] {
  if (!primaryYear) return versionQuarters;

  // (quarter, year) pattern per marketplace. Order matters — drives tab
  // strip ordering and Summary aggregation.
  //
  // Year convention (matches the estimate parser): `primaryYear` is the
  // calendar year where Q4 of the broadcast year sits. For a 25/26 BY
  // file, primaryYear=2025 with quarters [4Q'25 (year 2025), 1Q'26-3Q'26
  // (year 2026)]. So Q4 = py and Q1-Q3 = py + 1.
  //
  // CY Upfront (e.g. CY 2026) covers Q1-Q4 of the calendar year that
  // follows the broadcast year's Q4 — i.e. py + 1.
  //
  // Scatter spans both: Q4 of py + Q1-Q4 of py + 1 → 5 quarters total.
  const py = primaryYear;
  let pattern: Array<{ quarter: string; year: number }>;
  switch (marketplace) {
    case 'CY Upfront':
      pattern = [
        { quarter: 'Q1', year: py + 1 },
        { quarter: 'Q2', year: py + 1 },
        { quarter: 'Q3', year: py + 1 },
        { quarter: 'Q4', year: py + 1 },
      ];
      break;
    case 'Scatter':
      pattern = [
        { quarter: 'Q4', year: py },
        { quarter: 'Q1', year: py + 1 },
        { quarter: 'Q2', year: py + 1 },
        { quarter: 'Q3', year: py + 1 },
        { quarter: 'Q4', year: py + 1 },
      ];
      break;
    case 'BY Upfront':
    default:
      // Default covers blank marketplace + legacy "Upfront" + any other
      // unrecognized value — all behave like the pre-feature layout.
      pattern = [
        { quarter: 'Q4', year: py },
        { quarter: 'Q1', year: py + 1 },
        { quarter: 'Q2', year: py + 1 },
        { quarter: 'Q3', year: py + 1 },
      ];
      break;
  }

  return pattern.map(t => {
    const found = versionQuarters.find(q => q.quarter === t.quarter && q.year === t.year);
    if (found) return found;
    // Synthesize. Key format mirrors the estimate parser's:
    //   ("Q4", 2027) → label "4Q'27", key "4Q27"
    const yy = (t.year % 100).toString().padStart(2, '0');
    const qDigit = t.quarter.startsWith('Q') ? t.quarter.slice(1) : t.quarter;
    return {
      key: `${qDigit}Q${yy}`,
      label: labelForQuarterYear(t.quarter, t.year),
      quarter: t.quarter,
      year: t.year,
    };
  });
}

/**
 * Build a synthetic EstimateVersion for the "All Plans" landing view.
 *
 * The All Plans view needs to display BOTH BY (Broadcast Year) and
 * CY (Calendar Year) plans in a single tab strip. Those two marketplace
 * types span overlapping-but-different quarter sets:
 *
 *   BY 25/26  = 4Q'25, 1Q'26, 2Q'26, 3Q'26
 *   CY  26    = 1Q'26, 2Q'26, 3Q'26, 4Q'26
 *
 * Their union is 5 quarters: 4Q'25, 1Q'26, 2Q'26, 3Q'26, 4Q'26 — Scatter's
 * pattern, so `buildVisibleQuarters('Scatter', …)` gives us the layout.
 *
 * But estimates for those quarters live in TWO different upfront versions:
 *   • 25/26 upfront covers 4Q'25–3Q'26
 *   • 26/27 upfront covers 4Q'26 (the trailing CY quarter)
 *
 * This helper merges them — `baseV`'s estimateDb / projectedEstimateDb /
 * programNameIndex are taken as the base, and any entries from `overlapV`
 * whose quarter label is the missing-quarter (4Q'26 for the 25/26 view)
 * are layered in on top.
 *
 * For the 26/27 view, `overlapV` is null — the trailing 4Q'27 quarter
 * would need a 27/28 upfront which doesn't exist yet, so those cells
 * render blank with the Phase C ⚠ missing-estimate flag.
 *
 * Returns null if no base version exists (cold start with no uploads).
 */
/**
 * Cache of merged (base + late-quarter) versions, and the source of their
 * synthetic ids.
 *
 * ⚠ Both halves exist for `estimateCache`, not for tidiness. That cache is
 * keyed on calcEstimateFields' INPUTS only and is invalidated by a single
 * global token — `syncEstimateCacheVersion(version.id)`. Two requirements
 * follow, and they pull in opposite directions:
 *
 *   • STABLE while the inputs are unchanged. Re-merging on every render would
 *     hand back a new id each time, clearing the cache constantly and making
 *     the memoisation worthless. It would also produce a new object identity,
 *     which re-fires every `[activeVersion]` effect — and the resolve effect
 *     that calls this depends on `allDeals`, so it runs on every cell edit.
 *
 *   • DIFFERENT when the inputs change. If a rebuild reused the old id, the
 *     cache would keep serving numbers computed from the previous data.
 *
 * Caching on the component OBJECTS (not their ids) satisfies both. Ids are
 * stable because a cache hit returns the very same object; a re-hydration
 * replaces the registry's version objects, which misses the cache and
 * allocates a fresh id — exactly the invalidation we want.
 *
 * Ids count down from -1_000_000: clear of real ids (positive) and of
 * buildAllPlansVersion's `-(primaryYear * 10 + flag)` range (~-20260).
 */
const mergedVersionCache = new Map<string, {
  base: EstimateVersion;
  overlap: EstimateVersion;
  merged: EstimateVersion;
}>();
let nextMergedVersionId = -1000000;

/**
 * Which estimate version supplies a plan's LATE QUARTER, if it needs one.
 *
 * A 26/27 estimate file covers 4Q'26–3Q'27. CY Upfront and Scatter plans both
 * display a 4Q'27 tab, and a BY Upfront plan can add one via "+ Add Quarter",
 * but MVMS doesn't rate that quarter until later in the season and then ships
 * an extended 26/27 file carrying it as a fifth quarter.
 *
 * A plan can't simply re-bind to that file — its 1Q–3Q'27 numbers differ from
 * whatever the plan is pinned to, so re-binding would move tabs the user has
 * already worked in. Instead the bound version keeps every quarter it has and
 * ONLY the missing trailing quarter is sourced elsewhere.
 *
 * Returns null when no merge is needed or possible:
 *   • the bound version already carries the quarter (a plan created after the
 *     extended file — the common case going forward), or
 *   • nothing has it yet (before the extended file is uploaded), in which case
 *     the tab keeps rendering blank exactly as it does today.
 *
 * @param pinnedId  PLAN_VERSION.OVERLAP_ESTIMATE_VERSION_ID. When set it WINS,
 *                  so a later 4Q revision can't move a plan that has already
 *                  entered units against the numbers it first resolved.
 */
/**
 * The REAL EST_VERSION.ID behind a version — the one safe to persist.
 *
 * ⚠ Use this, never `version.id`, anywhere a version id is written to the
 * database. A merged version's `id` is a synthetic negative that matches no
 * row; saving it would leave the plan bound to nothing, and on the next load
 * `getVersionById` would miss and silently drop the plan onto the season's
 * latest general estimate — changing every number on a plan someone had
 * already worked in.
 */
function baseVersionIdOf(v: EstimateVersion | null | undefined): number | null {
  if (!v) return null;
  const id = v.mergedFrom ? v.mergedFrom.baseId : v.id;
  // Backstop, and it catches a real case: `buildAllPlansVersion` also produces
  // a synthetic version with a negative id, and it carries NO `mergedFrom`
  // (it has no single base — it spans a whole season). When the user creates a
  // plan straight from the All Plans landing view, that object is the
  // activeVersion the fallback would reach for. Every real EST_VERSION.ID is a
  // positive sequence value, so rejecting non-positives makes the invariant —
  // a synthetic id never reaches the database — hold by construction rather
  // than by each caller remembering.
  return id != null && id > 0 ? id : null;
}

/** The version that supplied a merged version's late quarter, if any. */
function overlapVersionIdOf(v: EstimateVersion | null | undefined): number | null {
  return v?.mergedFrom ? v.mergedFrom.overlapId : null;
}

function resolveLateQuarterSource(
  baseV: EstimateVersion | null,
  pinnedId: number | null,
): EstimateVersion | null {
  if (!baseV || typeof baseV.primaryYear !== 'number') return null;
  // Already has it — nothing to merge.
  if (lateQuarterOf(baseV)) return null;

  // A pin is a commitment: honour it even if a newer version now has the
  // quarter. Only ignore it if it points at a version that no longer exists
  // or that (somehow) lacks the quarter — in which case fall through rather
  // than leave the tab blank.
  if (pinnedId != null) {
    const pinned = getVersionById(pinnedId);
    if (pinned && pinned.primaryYear === baseV.primaryYear && lateQuarterOf(pinned)) {
      return pinned;
    }
  }

  // Prefer the plan's own rate card, then fall back to general. A custom card
  // (WPP) never gets its own 5-quarter file by decision, so its plans take the
  // general card's 4Q — silently, no marker or tooltip.
  const card = cardOf(baseV);
  return getLatestVersionWithLateQuarter(baseV.primaryYear, baseV.kind, card)
      ?? getLatestGeneralVersionWithLateQuarter(baseV.primaryYear);
}

/**
 * Merge a late quarter from `overlapV` into `baseV`, producing a synthetic
 * version that behaves like a real one everywhere downstream.
 *
 * Only the trailing quarter's rows are layered in. Every quarter the base
 * version already has is untouched — that is the whole point: the plan's
 * existing numbers must not move.
 *
 * Because the result IS the plan's active version, the entire grid works on
 * the late-quarter tab — HH (000s), RTG HH, VPVH, CPM HH, "+ Add Show"'s
 * program list and the exports — not merely demo impressions.
 *
 * Mirrors `buildAllPlansVersion` above, which has done exactly this (base plus
 * an overlap version's trailing quarter) in production since the 25/26 season.
 */
function buildLateQuarterMergedVersion(
  baseV: EstimateVersion,
  overlapV: EstimateVersion,
): EstimateVersion {
  const lateQ = lateQuarterOf(overlapV);
  // Caller guarantees this; guard rather than assert so a future caller can't
  // silently produce a version that claims a quarter it has no rows for.
  if (!lateQ) return baseV;

  // Identity check, not just an id match — see mergedVersionCache. If either
  // component object has been replaced (a re-hydrate), fall through and
  // rebuild against the fresh data under a new id.
  const cacheKey = `${baseV.id}:${overlapV.id}`;
  const hit = mergedVersionCache.get(cacheKey);
  if (hit && hit.base === baseV && hit.overlap === overlapV) return hit.merged;

  const estimateDb = { ...baseV.estimateDb };
  const projectedEstimateDb = { ...baseV.projectedEstimateDb };
  const programNameIndex = { ...baseV.programNameIndex };

  // estimateDb keys are `Program|Day|Period|Demo` — period at index 2.
  for (const [k, v] of Object.entries(overlapV.estimateDb)) {
    if (k.split('|')[2] === lateQ.label) estimateDb[k] = v;
  }
  for (const [k, v] of Object.entries(overlapV.projectedEstimateDb)) {
    if (k.split('|')[2] === lateQ.label) projectedEstimateDb[k] = v;
  }
  // Union the program indices, preferring base. The overlap file may know a
  // program the base doesn't (or, per MVMS, may know fewer).
  for (const [k, v] of Object.entries(overlapV.programNameIndex)) {
    if (!(k in programNameIndex)) programNameIndex[k] = v;
  }

  const merged: EstimateVersion = {
    ...baseV,
    id: nextMergedVersionId--,
    // Provenance travels WITH the version rather than in component state, so
    // "this id is synthetic, persist baseId instead" can never go stale. See
    // baseVersionIdOf — the save payload depends on this.
    mergedFrom: { baseId: baseV.id, overlapId: overlapV.id },
    // Appended, not sorted: the late quarter is chronologically last by
    // definition (Q4 of primaryYear + 1), and quarter ORDER drives the tab
    // strip and Summary aggregation.
    quarters: [...baseV.quarters, lateQ],
    estimateDb,
    projectedEstimateDb,
    programNameIndex,
    // ⚠ universeData is deliberately NOT merged — the base version's universes
    // are kept for every quarter including the late one. Universes are stable
    // within a season, so this is almost certainly right, and taking them from
    // the overlap file would silently change the RTG columns on quarters the
    // plan already had. Deliberate choice, not an oversight.
  };

  mergedVersionCache.set(cacheKey, { base: baseV, overlap: overlapV, merged });
  return merged;
}

function buildAllPlansVersion(
  primaryYear: number,
  baseV: EstimateVersion | null,
  overlapV: EstimateVersion | null,
): EstimateVersion | null {
  if (!baseV) return null;

  // Use the Scatter pattern (Q4 of py + Q1-Q4 of py+1) since that's
  // exactly the union of BY and CY windows for a single season.
  const quarters = buildVisibleQuarters('Scatter', baseV.quarters, primaryYear);

  // Trailing CY-only quarter (Q4 of py+1) — the one that lives in the
  // OTHER season's upfront. Used to filter overlapV's estimateDb entries
  // so we only merge in the rows we actually need.
  const trailingLabel = labelForQuarterYear('Q4', primaryYear + 1);

  const estimateDb = { ...baseV.estimateDb };
  const projectedEstimateDb = { ...baseV.projectedEstimateDb };
  const programNameIndex = { ...baseV.programNameIndex };

  if (overlapV) {
    // estimateDb keys: "Program|Day|Period|Demo" e.g. "911|Wknd|4Q'26|HHRTG"
    // Period is at index 2.
    for (const [k, v] of Object.entries(overlapV.estimateDb)) {
      if (k.split('|')[2] === trailingLabel) estimateDb[k] = v;
    }
    for (const [k, v] of Object.entries(overlapV.projectedEstimateDb)) {
      if (k.split('|')[2] === trailingLabel) projectedEstimateDb[k] = v;
    }
    // Union the program-name indices — overlap may know programs the
    // base version doesn't (or vice versa); prefer base when both have
    // the same normalized key.
    for (const [k, v] of Object.entries(overlapV.programNameIndex)) {
      if (!(k in programNameIndex)) programNameIndex[k] = v;
    }
  }

  return {
    ...baseV,
    // Negative synthetic id so this never collides with real version
    // ids (which are positive auto-increment) — helps any downstream
    // code that compares by id detect "this isn't a real version".
    id: -(primaryYear * 10 + (overlapV ? 1 : 0)),
    // Season label derived locally from primaryYear so this helper has
    // no dependency on the LAST_SEASON_PRIMARY_YEAR constant declared
    // later in the file. 2025 = 25/26 season, 2026 = 26/27, etc.
    fileName: (() => {
      const yy = String(primaryYear % 100).padStart(2, '0');
      const yyNext = String((primaryYear + 1) % 100).padStart(2, '0');
      const seasonStr = `${yy}/${yyNext}`;
      return overlapV ? `All Plans · ${seasonStr} (merged)` : `All Plans · ${seasonStr}`;
    })(),
    primaryYear,
    quarters,
    estimateDb,
    projectedEstimateDb,
    programNameIndex,
    // Derived from baseV, never asserted. Callers resolve general-only, so
    // this is 'general' in practice — but hard-coding the literal would turn
    // a resolution bug into an active mislabel: a merged version stamped
    // 'general' while holding another card's numbers would give the pill the
    // wrong label and getPriorYearDemo000 the wrong YoY baseline.
    //
    // ⚠ `universeData` IS still inherited from baseV, and it is per-version:
    // two rate cards can carry different universe numbers. It drives
    // avgRtgHH / avgRtgDemo in aggregateSummaryMetrics, so the All Plans RTG
    // columns are a SECOND card leak, independent of estimateDb. Any fix that
    // only threads estimateDb through will leave both RTG columns showing the
    // general card's ratings for a WPP plan. See the aggregate-HH follow-up.
    rateCard: cardOf(baseV),
  };
}

/**
 * "Next quarter" walker — Q4 rolls over to Q1 of the next year, others
 * just increment within the same year. Returns a QuarterTab shaped like
 * the ones buildVisibleQuarters produces so the result drops into the
 * tab strip without further adaptation.
 *
 *   nextQuarterAfter({ q:'Q3', y:2027 }) → 4Q'27
 *   nextQuarterAfter({ q:'Q4', y:2027 }) → 1Q'28
 */
function nextQuarterAfter(last: QuarterTab): QuarterTab {
  const order: Record<string, string> = { Q1: 'Q2', Q2: 'Q3', Q3: 'Q4', Q4: 'Q1' };
  const newQ = order[last.quarter] ?? 'Q1';
  const newYear = last.quarter === 'Q4' ? last.year + 1 : last.year;
  const yy = (newYear % 100).toString().padStart(2, '0');
  const qDigit = newQ.slice(1);
  return {
    key: `${qDigit}Q${yy}`,
    label: labelForQuarterYear(newQ, newYear),
    quarter: newQ,
    year: newYear,
  };
}

/**
 * How many `nextQuarterAfter` steps from `from` to `to`, walking
 * forward in time. Returns null if `to` is not reachable within
 * maxSteps (i.e., `to` is at or before `from` — would require
 * walking backward) — caller treats that as "zero extras needed".
 *
 * Used by the marketplace-change handler to recompute the extra-
 * quarters count so the user's chosen final quarter is preserved
 * across a marketplace switch:
 *
 *   BY Upfront + 2 extras → ends at 1Q'28
 *   switch to CY Upfront (base ends at 4Q'27)
 *     → quartersBetween(4Q'27, 1Q'28) = 1
 *     → new extras count = 1 (one step past 4Q'27 lands on 1Q'28)
 */
function quartersBetween(
  from: QuarterTab,
  to: QuarterTab,
  maxSteps = 40,
): number | null {
  if (from.quarter === to.quarter && from.year === to.year) return 0;
  let cursor = from;
  for (let i = 1; i <= maxSteps; i++) {
    cursor = nextQuarterAfter(cursor);
    if (cursor.quarter === to.quarter && cursor.year === to.year) return i;
  }
  return null;
}

// ─── Extra-quarters per-plan storage ──────────────────────────────────────────
//
// 26/27 plans can extend their tab strip past the marketplace's default
// quarter set via "+ Add Quarter" (and trim with a hover × on each extra
// tab). The count of extras lives in localStorage keyed by plan name so
// it survives a reload. NOTE: this is an interim storage — the proper
// home is a new column on PLAN_VERSION so the value rides with the plan
// to Snowflake / other devices. Until that column exists, anyone opening
// the same plan on a different browser won't see the extras. Migration
// will read this map once and persist into Snowflake, then we'll drop it.

const EXTRA_QUARTERS_STORAGE_KEY = 'mpb_plan_extras_v1';

function readExtraQuartersMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(EXTRA_QUARTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function getExtraQuartersCount(planName: string): number {
  const map = readExtraQuartersMap();
  const v = map[planName];
  return typeof v === 'number' && v > 0 ? v : 0;
}

function setExtraQuartersCount(planName: string, count: number): void {
  try {
    const map = readExtraQuartersMap();
    if (count <= 0) delete map[planName];
    else map[planName] = count;
    localStorage.setItem(EXTRA_QUARTERS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* localStorage quota / private mode — swallow silently. */
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Naming convention used by `Deal` (and to a lesser extent `AddedRow`):
 *
 *   • Plain fields (`cpm`, `program`, `salesPlan`, …)
 *       Persisted to Snowflake and round-trip cleanly through the save
 *       endpoint. These are the values you see in PLAN_DEAL rows.
 *
 *   • `base*` (`baseCpm`, `baseRate`, `baseDollars`, `baseCpmHH`)
 *       Cached "last clean" snapshots used by the UI to decide whether
 *       a cell renders as edited (orange) or pristine. Not persisted.
 *
 *   • `_*` (`_manual`, `_meta`, `_cpmManuallyEdited`, `_baselineCpm`)
 *       Transient working state — UI-only flags and per-row scratch
 *       used by edit handlers. Never sent to the backend.
 *
 *   • `override*` (`overrideDemo000`)
 *       User-typed value that wins over the estimate-derived default
 *       at render time. Persisted (saved as PLAN_DEAL.impressions_000).
 *
 * The `_` prefix is the marker for "do not persist" — when adding
 * fields here, follow that convention so the save path's column
 * mapping stays obviously safe.
 */
interface Deal extends SourceDeal {
  newRate?: number | null;
  newHhCpm?: number | null;
  /**
   * Per-field baselines captured at initial load and re-set on every save.
   * The UI compares current value to baseline to decide whether to render
   * the "edited" (orange) style. Editing then reverting → flag clears.
   */
  baseCpm?: number;
  baseRate?: number | null;
  baseDollars?: number;
  baseCpmHH?: number | null;
  _manual?: boolean;
  _meta?: ManualPlanMeta;
  /**
   * Bulk-edit lock. Set when the user edits CPM, Rate, or Demo Imp
   * directly via the per-row inputs. Bulk CPM grid updates skip rows
   * with this flag set. Cleared automatically when the row reverts to
   * baseline on all three fields (so bulk edits can reach it again).
   */
  _cpmManuallyEdited?: boolean;
  /**
   * Per-row override for primary-demo (000) impressions. When set,
   * `recalcDeal` uses this instead of the value derived from
   * `calcEstimateFields`. Null/undefined means "use the estimate".
   */
  overrideDemo000?: number | null;
  /**
   * Per-row secondary demo impressions (000s) the user has typed, keyed by
   * demo name. Wins over the estimate at render time, exactly like
   * `overrideDemo000` does for the primary demo.
   *
   * On hydrate this is populated from PLAN_DEAL.SECONDARY_IMPRESSIONS_000
   * (which stores RESOLVED values), so a reloaded plan shows what was
   * saved rather than what today's estimate would compute — same
   * behaviour as impressions_000 -> overrideDemo000 for primary.
   */
  overrideSecondary000?: Record<string, number> | null;
  /**
   * Set on deals copied via roll-forward (25/26 → 26/27). Holds the
   * original SF dealKey so `saveToSnowflake` can persist it as
   * `sourceSfDealKey` on the new PLAN_DEAL row, preserving the link
   * back to the 25/26 SF deal even though the running `dealKey` was
   * regenerated to avoid collisions in `updateDeal` lookups.
   */
  _rolledFromSfDealKey?: string;
  /**
   * Runtime baseline for the per-row `± %` adjustment input — equals
   * `originalCpm` on first load, jumps to whatever the user types in
   * the `$` field, and never persists to Snowflake. The `% ` field
   * renders the most recent typed adjustment as the deviation from
   * this baseline.
   *
   * (Note: `originalCpm` itself is inherited from SourceDeal — it's
   * the frozen v1 CPM, displayed in the Original CPM column and the
   * starting value of `_baselineCpm`.)
   */
  _baselineCpm?: number | null;
}

interface AddedRow {
  key: string;
  program: string;
  salesPlan: string;
  quarter: string;     // e.g. "Q4"
  year: number;        // e.g. 2025 — matches the tab the row was added under
  unitLength: string;
  cpm: number;
  totalUnits: number;
  guaranteed: number;
  actual000: number;
  hh_000: number | null;
  demo?: string;
  newRate?: number | null;
  totalDollars?: number;
  overrideDemo000?: number | null;
  /**
   * Per-row secondary demo impressions (000s) the user has typed, keyed by
   * demo name. Wins over the estimate at render time, exactly like
   * `overrideDemo000` does for the primary demo.
   *
   * On hydrate this is populated from PLAN_DEAL.SECONDARY_IMPRESSIONS_000
   * (which stores RESOLVED values), so a reloaded plan shows what was
   * saved rather than what today's estimate would compute — same
   * behaviour as impressions_000 -> overrideDemo000 for primary.
   */
  overrideSecondary000?: Record<string, number> | null;
  /**
   * Per-field baselines for AddedRow. Captured when row is added (or
   * when length is set, since the matched program's CPM is auto-loaded
   * then). UI compares current values to these to decide orange styling.
   */
  baseCpm?: number;
  baseRate?: number | null;
  /**
   * Frozen v1 CPM for this added row. Mirrors `Deal.originalCpm`.
   * Stays null until the row is saved to Snowflake for the first time
   * — at that point the backend stamps `original_cpm = cpm`. On
   * subsequent loads via `savedPlanToDeals` the row becomes a Deal,
   * not an AddedRow, so this field is only meaningful in the unsaved
   * + Add Row lifecycle.
   */
  originalCpm?: number | null;
  /**
   * Runtime baseline for the per-row `± %` adjustment input on this
   * added row. Equals `cpm` when first set; jumps to whatever the
   * user types in the `$` field afterward.
   */
  _baselineCpm?: number | null;
  /**
   * Salesforce Programs__c.ID for this row's program. Populated when
   * the row is created by + Add Row / + Add Show (inherits from an
   * existing PLAN_DEAL row for the same program in the current plan,
   * or from the SF lookup when the picker gains that support). Used by
   * the push-to-SF flow. Null means "user has to re-pick this program
   * before we can push the plan to SF".
   */
  programId?: string | null;
}

interface ManualPlanMeta {
  planName: string;
  advertiser: string;
  dealName: string;
  agency: string;
  status: string;
  brand: string;
  ae: string;
  buyer: string;
  demo: string;
  marketplace: string;
  equiv: string;
  year: string;
  /**
   * Set when the plan was created by rolling forward a 25/26 SF plan.
   * Carries the source plan's display name + SF ID so the first save
   * to Snowflake can stamp PLAN.source_plan_name and
   * PLAN.source_plan_sf_id for downstream traceability.
   */
  sourcePlanName?: string;
  sourcePlanSfId?: string;
  /**
   * Salesforce lookup IDs picked in the Create Plan modal's typeaheads
   * (Advertiser__c, Brands__c, Contact, and the Contact's parent
   * Account for Agency). Carried in localStorage alongside the display
   * strings above so the first Snowflake save can stamp PLAN_VERSION's
   * matching ID columns. All four nullable — legacy drafts saved
   * before the lookup migration won't have them.
   */
  advertiserId?: string | null;
  brandId?: string | null;
  contactId?: string | null;
  accountId?: string | null;
  /**
   * Computed-formula plan name —
   *   `{agency}-{brand}-{advertiser}-{demo}-{year}`
   * Mirrors `SALES_PLAN__C.SALES_PLAN_NAME__C`. Computed in the
   * Create Plan modal from the picked lookup display names and
   * carried through localStorage so the first save stamps it on
   * PLAN_VERSION. Optional for legacy drafts.
   */
  salesPlanName?: string | null;
  /**
   * Plan-level default unit length in seconds ("15" / "30" / "45" /
   * "60"), set on the Create Plan modal's "Unit Length for Plan"
   * picker. New rows added via + Add Show / + Add Row seed their
   * unitLength from this value instead of leaving it blank. Null =
   * no default; new rows start blank (legacy behavior for pre-column
   * drafts).
   */
  defaultUnitLength?: string | null;
}

interface EstimateResult {
  rtgHH: number | null;
  hh000: number | null;
  totalHH: number | null;
  vpvh: number | null;
  rtgDemo: number | null;
  demo000: number | null;
}

// ─── Pure helper functions ─────────────────────────────────────────────────────

export function fmt(n: number | null | undefined): string {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function fmtBig(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toFixed(0);
}

/**
 * Format a (quarter, year) tuple as a period label, e.g.
 *   ("Q1", 2027) → "1Q'27"
 *   ("Q4", 2026) → "4Q'26"
 *
 * Resolution order:
 *   1. PERIOD_MAP[`${q}-${year}`] — the active estimate version's own
 *      label, byte-identical to what's used as the ESTIMATE_DB key. This
 *      is the source of truth: if MVMS ever ships a label with non-
 *      standard formatting (e.g. extra whitespace), we want to round-
 *      trip it exactly, not derive a divergent string.
 *   2. labelForQuarterYear(q, year) — deterministic fallback for
 *      quarters NOT covered by the active estimate version (e.g. 4Q'27
 *      under a Scatter/CY plan when the estimate file still only covers
 *      4Q'26-3Q'27). The label is synthesized in the same format MVMS
 *      uses; estimate lookups for these quarters will miss (intentional
 *      — the UI flags missing estimates separately).
 *   3. Raw quarter string fallback — only hit if both inputs are
 *      malformed; preserves the old single-arg behavior for any straggler
 *      caller that hasn't been updated yet.
 */
export function fmtPeriod(q: string, year?: number): string {
  if (year != null) {
    const key = `${q}-${year}`;
    if (PERIOD_MAP[key]) return PERIOD_MAP[key];
    return labelForQuarterYear(q, year);
  }
  return PERIOD_MAP[q] || q;
}

/**
 * Deterministic period label from a (quarter, year) tuple. Used as a
 * fallback when the active estimate version doesn't cover the quarter
 * (so PERIOD_MAP has no entry for it). Format mirrors MVMS's estimate
 * file convention exactly: `"4Q'27"` (digit + 'Q' + apostrophe + last
 * two digits of year, zero-padded).
 */
export function labelForQuarterYear(quarter: string, year: number): string {
  // `quarter` is "Q1"/"Q2"/"Q3"/"Q4" — strip the leading "Q" to get
  // the digit. Fall through to the raw string if it's malformed
  // (defensive — shouldn't happen since the source is always typed).
  const qDigit = quarter.startsWith('Q') ? quarter.slice(1) : quarter;
  const yy = (year % 100).toString().padStart(2, '0');
  return `${qDigit}Q'${yy}`;
}

/**
 * Dollar-format the frozen v1 CPM for the Original CPM column.
 * Renders "—" when null / 0 — the latter covers + Add Row deals that
 * haven't been first-saved yet (their `originalCpm` is null until the
 * backend stamps it).
 */
export function fmtOrigCpm(v: number | null | undefined): string {
  if (v == null || v === 0) return '—';
  return '$' + Number(v).toFixed(2);
}

/**
 * Build the SF-mirror plan-name string used as the uniqueness key for
 * new 26/27 plans. Mirrors the Salesforce `Sales_Plan_Name__c` workflow
 * formula:
 *
 *   {Agency}-{Brand}-{Advertiser}-{Demo}-{Year}
 *
 * Empty parts render as "…" so the user sees a live preview while they
 * pick lookups one at a time. Pass `placeholder = ''` instead if you
 * want the uniqueness-comparable form (no ellipses, just empty between
 * dashes — which still produces a stable comparable string).
 *
 * Year is hardcoded "26/27" upstream because that's the only season we
 * save today; if/when we add 25/26 saving, plumb the year through.
 */
export function computeSalesPlanName(args: {
  agency: string | null | undefined;
  brand: string | null | undefined;
  advertiser: string | null | undefined;
  demo: string | null | undefined;
  year: string | null | undefined;
}, placeholder = '…'): string {
  const part = (v: string | null | undefined) => (v && v.trim()) || placeholder;
  return [
    part(args.agency),
    part(args.brand),
    part(args.advertiser),
    part(args.demo),
    part(args.year),
  ].join('-');
}

/** Pretty-print an ISO "as of" date for the plan UI pill. "Mar 2, 2026". */
function formatAsOf(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function demoToRTG(demo: string): string {
  return demo ? demo.trim() + 'RTG' : '';
}

function getEstimate(program: string, day: string, period: string, demoRTG: string) {
  // Resolution order — same fallback chain regardless of whether we're on
  // legacy data or a versioned estimateDb:
  //   1. Exact-match canonical via the version's case-insensitive index
  //      (catches "Modern Family WKND" vs "Modern Family Wknd" drift)
  //   2. Manual alias via ESTIMATE_NAME_MAP (legacy aliases)
  //   3. Verbatim program name (last-ditch — works if SF and workbook
  //      already agree)
  const indexed = PROGRAM_NAME_INDEX[normalizeProgramName(program)];
  const p = indexed || ESTIMATE_NAME_MAP[program] || program;
  const key = `${p}|${day}|${period}|${demoRTG}`;
  return ESTIMATE_DB[key] || null;
}

/**
 * Required plan fields that are still empty, in the modal's top-to-bottom
 * order. Same rule the Create modal applies: a lookup is filled when it has
 * a Salesforce id OR is the deliberate "TBD" placeholder (id null, name
 * "TBD"). Agency isn't checked — it rides on the Buyer pick.
 *
 * `allowTbd: false` (pushed plans) requires a real Salesforce id.
 */
export function missingPlanFields(f: {
  advertiserId: string | null; advertiser: string;
  nickname: string;
  brandId: string | null; brand: string;
  contactId: string | null; buyer: string;
  demo: string; marketplace: string;
}, opts: { allowTbd: boolean } = { allowTbd: true }): string[] {
  const tbdOk = (name: string) => opts.allowTbd && name === 'TBD';
  const missing: string[] = [];
  if (!f.advertiserId && !tbdOk(f.advertiser)) missing.push('Advertiser');
  if (!f.nickname.trim()) missing.push('Plan Nickname');
  if (!f.brandId && !tbdOk(f.brand)) missing.push('Brand');
  if (!f.contactId && !tbdOk(f.buyer)) missing.push('Agency & Buyer');
  if (!f.demo) missing.push('Primary Demo');
  if (!f.marketplace) missing.push('Marketplace');
  return missing;
}

/** "A", "A and B", "A, B, and C". */
export function joinFieldList(fields: string[]): string {
  if (fields.length <= 1) return fields[0] ?? '';
  if (fields.length === 2) return `${fields[0]} and ${fields[1]}`;
  return `${fields.slice(0, -1).join(', ')}, and ${fields[fields.length - 1]}`;
}

export function calcEstimateFields(d: {
  program: string; quarter: string; year: number; demo: string;
  unitLength: string | number; totalUnits: number;
  guaranteed?: number;
}): EstimateResult {
  // Memoization. Inputs that influence the result, in canonical order.
  // `guaranteed` is normalized to '' when absent so the key for "no
  // guaranteed" doesn't collide with guaranteed=undefined vs 0.
  // `year` participates in the cache key because Scatter plans can
  // include the same (program, quarter) in two different years (4Q'26
  // AND 4Q'27) — without year in the key, the second lookup would
  // hit the first one's cached result and read the wrong estimate.
  // The cache is also keyed implicitly on the active estimate version
  // via syncEstimateCacheVersion in setActiveEstimates.
  const cacheKey = `${d.program}|${d.quarter}|${d.year}|${d.demo}|${d.unitLength}|${d.totalUnits ?? 0}|${d.guaranteed ?? ''}`;
  const cached = estimateCache.get(cacheKey);
  if (cached) return cached;

  const day = DAY_TIME[d.program] || '';
  const period = fmtPeriod(d.quarter, d.year);
  const demoRTG = demoToRTG(d.demo);
  const length = parseFloat(String(d.unitLength)) || 30;
  const units = d.totalUnits || 0;
  const est = getEstimate(d.program, day, period, demoRTG);

  // Per-spot primary-demo impressions for the row.
  //
  // Salesforce stores `Guaranteed (000) Primary Demo` at limited precision
  // (typically 2 decimals). The ESTIMATE_DB (regenerated from the upstream
  // Marathon Panel data) holds the same value at full precision. When the
  // two are within a tiny rounding tolerance, the SF value is just the
  // estimate rounded — so we use the ESTIMATE_DB value to recover precision.
  // When they differ meaningfully, the schedule has a custom guaranteed
  // (e.g., a discounted/premium buy) and we trust the SF value.
  const demoFromGuaranteed = (d.guaranteed != null && d.guaranteed > 0 && units > 0)
    ? d.guaranteed / units
    : null;
  const demoFromEstimate = est && est.t != null ? est.t * (length / 30) : null;

  /**
   * Threshold for "this is just rounding noise, not a real difference."
   * Salesforce stores per-spot guaranteed at 2 decimals → max rounding error
   * of 0.005. Threshold of 0.01 gives 2x margin to catch true rounding
   * artifacts while rejecting meaningful demo differences (which can be on
   * the order of 0.5+ when programs/spots have genuinely different ratings).
   */
  const PRECISION_TOLERANCE = 0.01;

  let demo000: number | null;
  if (demoFromGuaranteed != null && demoFromEstimate != null
      && Math.abs(demoFromGuaranteed - demoFromEstimate) < PRECISION_TOLERANCE) {
    demo000 = demoFromEstimate;          // snap to high-precision estimate
  } else {
    demo000 = demoFromGuaranteed ?? demoFromEstimate;  // use SF, or fall back
  }

  if (!est) {
    const noEstResult: EstimateResult = {
      rtgHH: null, hh000: null, totalHH: null,
      vpvh: null, rtgDemo: null,
      demo000,
    };
    estimateCache.set(cacheKey, noEstResult);
    return noEstResult;
  }
  const hh000 = est.m != null ? est.m * (length / 30) : null;
  const totalHH = hh000 != null ? hh000 * units : null;
  const result: EstimateResult = {
    rtgHH: est.j ?? null, hh000, totalHH,
    vpvh: est.p ?? null, rtgDemo: est.x ?? null, demo000,
  };
  estimateCache.set(cacheKey, result);
  return result;
}

/**
 * Computes the CPM HH for an entire program block.
 * Rule: take the highest-rate :15 row in the program (per-spot rate / HH per :15 spot).
 * If no :15 is booked, fall back to the highest-rate row of any length —
 * mathematically equivalent to a synthetic :15 calc since the length scaling cancels.
 * Returns a single value applied uniformly to all rows + the subtotal in the program.
 *
 * Uses the UNROUNDED per-spot rate ((guaranteed/units) × cpm) to match Excel
 * precision. The rounded `newRate` is for display only.
 */
function computeProgramCpmHh(deals: Deal[]): number | null {
  if (!deals.length) return null;
  const fifteens = deals.filter(d => (parseFloat(String(d.unitLength)) || 0) === 15);
  const candidates = fifteens.length > 0 ? fifteens : deals;
  const ref = candidates.reduce(
    (max, d) => (d.newRate || 0) > (max.newRate || 0) ? d : max,
    candidates[0]
  );
  const est = calcEstimateFields(ref);
  if (!est.hh000) return null;

  const units = ref.totalUnits || 0;
  const unroundedRate = (ref.guaranteed != null && ref.guaranteed > 0 && units > 0)
    ? (ref.guaranteed / units) * ref.cpm
    : (ref.newRate || 0);
  if (!unroundedRate) return null;

  return unroundedRate / est.hh000;
}

/**
 * Compact, deterministic signature of the editable state used to drive
 * the "Unsaved" indicator. Only user-mutable fields are included; purely
 * derived values (newRate, totalDollars, newHhCpm) are intentionally
 * omitted because they re-derive from the inputs on every recalc.
 * Field order is fixed → equivalent states always produce equal strings.
 */
/**
 * Stable string for a row's secondary demo overrides.
 *
 * Object key order isn't guaranteed, so the keys are sorted — otherwise
 * two identical override sets could hash differently and the plan would
 * look permanently dirty.
 */
function secondarySignature(v: Record<string, number> | null | undefined): string {
  if (!v) return '';
  return Object.keys(v).sort().map(k => `${k}:${v[k]}`).join(',');
}

function dealSignature(d: Deal): string {
  return [
    d.dealKey,
    d.salesPlan,
    d.cpm,
    d.overrideDemo000 ?? '',
    // Without this, typing a secondary demo override never marks the plan
    // dirty — Save stays disabled and the edit can't be persisted at all.
    secondarySignature(d.overrideSecondary000),
    d.totalUnits,
    d.guaranteed,
    d._cpmManuallyEdited ? 1 : 0,
  ].join('|');
}

function addedRowSignature(r: AddedRow): string {
  return [
    r.key,
    r.salesPlan,
    r.program,
    r.unitLength,
    r.cpm,
    r.overrideDemo000 ?? '',
    secondarySignature(r.overrideSecondary000),
    r.totalUnits,
  ].join('|');
}

function planSignature(deals: Deal[], rows: AddedRow[]): string {
  return deals.map(dealSignature).join(';') + '||' + rows.map(addedRowSignature).join(';');
}

/**
 * The same signature, narrowed to ONE plan.
 *
 * Dirty state has to be per-plan because `allDeals` holds every plan at once.
 * A single cross-plan signature meant that saving plan B recomputed the
 * baseline over plan A's unsaved rows as well, silently adopting them as
 * "saved": the UNSAVED pill cleared, Save and Revert went dead, and the
 * beforeunload warning stopped firing — so closing the tab dropped A's work
 * with no warning anywhere.
 *
 * Narrowing by `salesPlan` keeps each plan's baseline independent, so writing
 * one can no longer speak for another. Order within a plan still matters (the
 * signature is a join), but filtering preserves relative order, so a plan the
 * caller did not touch produces a byte-identical string.
 */
export function planSignatureFor(planName: string, deals: Deal[], rows: AddedRow[]): string {
  return planSignature(
    deals.filter(d => d.salesPlan === planName),
    rows.filter(r => r.salesPlan === planName),
  );
}

/** Distinct plan names present in either array — the keys worth snapshotting. */
export function planNamesIn(deals: Deal[], rows: AddedRow[]): string[] {
  const names = new Set<string>();
  deals.forEach(d => { if (d.salesPlan) names.add(d.salesPlan); });
  rows.forEach(r => { if (r.salesPlan) names.add(r.salesPlan); });
  return Array.from(names);
}

/**
 * Exported for recalc-deal.test.ts. Nothing else imports it — the component
 * calls it directly. It is the only money site the tests can reach without
 * rendering the whole grid, so it is where the formula gets pinned.
 */
export function recalcDeal(d: Deal): Deal {
  const est = calcEstimateFields(d);
  // Honor per-row Demo Imp override if the user has set one via the
  // schedule-row input. Falls back to the estimate's demo000.
  const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
  if (effDemo != null && !isNaN(d.cpm)) {
    d.newRate = perSpotRate(effDemo, d.cpm);
    d.totalDollars = rowDollars(d.newRate, d.totalUnits);
  } else {
    d.newRate = null;
    d.totalDollars = 0;
  }
  d.newHhCpm = est.hh000 && d.newRate ? d.newRate / est.hh000 : null;
  // Auto-release the bulk-edit lock when all three editable fields are
  // back at their baseline values — gives the user an escape hatch
  // without a dedicated reset button.
  if (d._cpmManuallyEdited
      && d.baseCpm != null && Math.abs(d.cpm - d.baseCpm) < 1e-9
      && (d.newRate ?? null) === (d.baseRate ?? null)
      && d.overrideDemo000 == null) {
    d._cpmManuallyEdited = false;
  }
  return d;
}

/**
 * Aggregates a list of deals into the metrics shown in the Summary table:
 *   units, equiv, totalHH, totalDemo, totalCost, plus averages and CPMs.
 * Used for per-program-per-quarter cells, 25/26 annual rollups, and network totals.
 *
 * Module-level (not inside the component) so summary-metrics.test.ts can
 * reach it. It only touches module-level state — calcEstimateFields and
 * UNIVERSE_DATA — so hoisting it is a pure move.
 */
export function aggregateSummaryMetrics(deals: Deal[]) {
  let units = 0, equiv = 0, totalCost = 0, totalHH = 0, totalDemo = 0;
  let primaryDemo = '';
  let vpvh: number | null = null;

  for (const d of deals) {
    const length = parseFloat(String(d.unitLength)) || 30;
    const u = d.totalUnits || 0;
    units += u;
    equiv += (length / 30) * u;
    totalCost += d.totalDollars || 0;
    const est = calcEstimateFields(d);
    if (est.hh000 != null) totalHH += est.hh000 * u;
    // The EFFECTIVE demo — a typed Demo (000) override wins over the
    // estimate, exactly as the row cells, the quarter-tab rollups and the
    // PDF already do. This was the one rollup still reading the raw
    // estimate, so a unit re-priced to 114 contributed 95 here: the Summary
    // tab showed 665 (000) and $23.50 CPM for a block whose quarter tab and
    // PDF both said 684 and $22.84. Dollars were right, impressions weren't,
    // so the CPM — totalCost ÷ totalDemo — drifted with them.
    const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
    if (effDemo != null) totalDemo += effDemo * u;
    if (!primaryDemo) primaryDemo = d.demo || '';
    if (vpvh == null && est.vpvh != null) vpvh = est.vpvh;
  }

  const hhUniverse = UNIVERSE_DATA["HH"] || 128400;
  // `demoUniverseKnown` distinguishes "the demo isn't in UNIVERSE_DATA"
  // (data missing — render '—') from "the demo IS in UNIVERSE_DATA
  // with a zero universe" (real zero). The visible output is the same
  // today because fmtRtg renders both 0 and null as '—', but keeping
  // the data shape honest prevents future consumers (e.g. CSV export,
  // a debug panel) from mistakenly treating "no universe data" as
  // "zero rating".
  const demoUniverseKnown = primaryDemo && primaryDemo in UNIVERSE_DATA;
  const demoUniverse = demoUniverseKnown ? UNIVERSE_DATA[primaryDemo] : 0;

  const avgHH = equiv > 0 ? totalHH / equiv : 0;
  const avgDemo = equiv > 0 ? totalDemo / equiv : 0;

  return {
    units,
    equiv,
    totalCost,
    totalHH,
    totalDemo,
    avgHH,
    avgDemo,
    avgRtgHH: hhUniverse > 0 ? (avgHH * 100) / hhUniverse : 0,
    avgRtgDemo: (demoUniverseKnown && demoUniverse > 0)
      ? (avgDemo * 100) / demoUniverse
      : null,
    vpvh: vpvh ?? 0,
    avgEquivUnitRate: equiv > 0 ? totalCost / equiv : 0,
    cpmHH: totalHH > 0 ? totalCost / totalHH : 0,
    cpmDemo: totalDemo > 0 ? totalCost / totalDemo : 0,
  };
}

function getSecondaryDemoEst(d: Deal | AddedRow, demo: string): { demo000: number | null } {
  // Mirror getEstimate()'s resolution chain so secondary demos hit
  // the same canonical program-name index that primary uses.
  // Without PROGRAM_NAME_INDEX, programs whose SF name differs from
  // the estimate file's canonical name (e.g. "911" in SF vs the
  // version-specific canonical form) silently miss the lookup and
  // every secondary-demo cell renders "—" — even though the primary
  // demo (going through getEstimate) finds the row just fine.
  const indexed = PROGRAM_NAME_INDEX[normalizeProgramName(d.program)];
  const prog = indexed || ESTIMATE_NAME_MAP[d.program] || d.program;
  const day = DAY_TIME[d.program] || '';
  const period = fmtPeriod(d.quarter, d.year);
  const length = parseFloat(String(d.unitLength)) || 30;
  const key = `${prog}|${day}|${period}|${demo}RTG`;
  const est = ESTIMATE_DB[key];
  if (!est) return { demo000: null };
  return { demo000: est.t != null ? est.t * (length / 30) : null };
}

/**
 * Effective secondary demo impressions (000s) for a row: the user's typed
 * value if there is one, otherwise the estimate's.
 *
 * Deliberately mirrors how `recalcDeal` resolves the primary demo
 * (`overrideDemo000 ?? est.demo000`) so the two demo families can't drift
 * apart in behaviour. Every consumer — the grid cell, the subtotals, the
 * Excel and PDF exports, and the save payload — must go through here
 * rather than calling getSecondaryDemoEst directly, or a typed override
 * will show in one place and not another.
 */
/**
 * True when a Demo (000) cell is committed showing exactly what it was
 * rendered with — i.e. the user clicked or tabbed through without editing.
 *
 * The cell DISPLAYS the impressions rounded (47.5 shows as 48) but the row is
 * priced on the exact value. Without this check, blurring an untouched cell
 * committed the displayed 48 as a typed override and re-priced the row at
 * 48 × CPM — Rate and Total $ moved with no edit. Most :15 and :45 rows sit on
 * a .5, so tabbing across a row was enough.
 *
 * Reliable because both demo inputs are keyed on their displayed value, so
 * they remount — and reset `defaultValue` — whenever that value changes.
 */
export function demoCellUnchanged(val: string, inputEl?: Pick<HTMLInputElement, 'defaultValue'> | null): boolean {
  return inputEl != null && val === inputEl.defaultValue;
}

function effectiveSecondary000(d: Deal | AddedRow, demo: string): number | null {
  const typed = d.overrideSecondary000?.[demo];
  if (typed != null && !isNaN(typed)) return typed;
  return getSecondaryDemoEst(d, demo).demo000;
}

/**
 * CPM for a TOTAL row: that row's total dollars ÷ that row's total impressions.
 *
 * The one rule for every aggregate CPM in the grid — HH, primary demo, each
 * secondary demo. Never an average of the per-row CPMs above it: with equal
 * impressions per unit, ten units at $14.34 and one at $12.00 is $14.13
 * weighted, not the $13.17 a plain mean of the two rows gives, and the
 * weighted figure is what the dollars actually bought. `null` (rendered as a dash) when there are no impressions.
 */
export function cpmOf(dollars: number, impressions: number | null | undefined): number | null {
  return impressions != null && impressions > 0 ? dollars / impressions : null;
}

/**
 * Per-secondary-demo totals for a total row: Σ(effective per-spot (000) × units)
 * and the CPM against the row's dollars.
 *
 * Uses effectiveSecondary000, so a typed secondary override wins over the
 * estimate — the same resolution the row cells and the PDF use. Rows with no
 * value for a demo (no estimate, no override) are skipped rather than counted
 * as zero; if NONE have a value the demo reports null so it renders as a dash
 * instead of a misleading 0.
 */
export function secondaryTotalsFor(
  rows: ReadonlyArray<Deal | AddedRow>,
  demos: readonly string[],
  dollars: number,
): Array<{ total000: number | null; cpm: number | null }> {
  return demos.map(demo => {
    let total = 0;
    let saw = false;
    for (const r of rows) {
      const v = effectiveSecondary000(r, demo);
      if (v == null || isNaN(v)) continue;
      saw = true;
      total += v * (r.totalUnits || 0);
    }
    if (!saw) return { total000: null, cpm: null };
    return { total000: total, cpm: cpmOf(dollars, total) };
  });
}

function colLetter(n: number): string {
  let s = '';
  let num = n + 1;
  while (num > 0) {
    const r = (num - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    num = Math.floor((num - 1) / 26);
  }
  return s;
}

// ─── Season constants ─────────────────────────────────────────────────────────
//
// 25/26 is last-season inventory (view-only, sourced from Salesforce).
// 26/27 is the current/active season — all new and rolled-forward plans
// land here and persist to Snowflake. Both numbers represent the
// primary_year column on EST_VERSION (the broadcast year a quarter
// labeled "4Q'25 → 3Q'26" belongs to is 2025; "4Q'26 → 3Q'27" → 2026).
//
// Hardcoded for now. When 27/28 rolls in, replace these with a
// dynamic "max primary_year" lookup (and shift the view-only year
// accordingly). The whole app needs to flip together — we keep them
// as named constants to make that future bump a single-line change.
const LAST_SEASON_PRIMARY_YEAR = 2025;
const CURRENT_SEASON_PRIMARY_YEAR = 2026;

// Quarter-key sets ({quarter}|{year}) for each season. Used anywhere
// we need to narrow deals by "does this belong to season X?" without
// having a specific plan (and its visibleQuarters) in hand — chiefly
// the All Plans season toggle. The last-season set is also used in
// sourcePlanInAllDeals to detect the 25/26 SF source of a rolled plan
// when nicknames collide.
//
// Coverage rationale (both seasons include a trailing Q4 to catch
// Scatter, which spans 5 quarters):
//   Last season 25/26:    Q4|2025, Q1-Q3|2026 (Q4|2026 excluded on
//                         purpose — that quarter belongs to the next
//                         season's start, and Scatter overlap here is
//                         extremely rare in practice)
//   Current season 26/27: Q4|2026, Q1-Q4|2027
const LAST_SEASON_QUARTER_KEYS = new Set<string>([
  `Q4|${LAST_SEASON_PRIMARY_YEAR}`,
  `Q1|${CURRENT_SEASON_PRIMARY_YEAR}`,
  `Q2|${CURRENT_SEASON_PRIMARY_YEAR}`,
  `Q3|${CURRENT_SEASON_PRIMARY_YEAR}`,
]);
const CURRENT_SEASON_QUARTER_KEYS = new Set<string>([
  `Q4|${CURRENT_SEASON_PRIMARY_YEAR}`,
  `Q1|${CURRENT_SEASON_PRIMARY_YEAR + 1}`,
  `Q2|${CURRENT_SEASON_PRIMARY_YEAR + 1}`,
  `Q3|${CURRENT_SEASON_PRIMARY_YEAR + 1}`,
  `Q4|${CURRENT_SEASON_PRIMARY_YEAR + 1}`,
]);

/**
 * Renders one column-header label for the main grid.
 *
 * Plain text can't express two things the design needs:
 *
 *   1. "(000s)" keeps a lowercase s while every other letter is
 *      upper-cased. The uppercasing is done in CSS (text-transform on
 *      `th`, which other tables in this component also rely on, so it
 *      can't simply be removed), so the fragment is wrapped in `.lc`
 *      — a text-transform:none escape hatch.
 *
 *   2. Demo tokens must not break at their hyphen. The header wraps to
 *      two lines, and "RTG A18-49" has to break as "RTG" / "A18-49",
 *      never "RTG A18-" / "49". Swapping the hyphen for a non-breaking
 *      hyphen (U+2011) removes that break opportunity; in Inter it is
 *      visually identical to a plain hyphen.
 *
 * Applies to dynamic labels too — the primary/secondary demo names are
 * interpolated by the caller, so "CPM M21-54" behaves the same way.
 */
const GridHdr: React.FC<{ label: string }> = ({ label }) => {
  const nb = label.replace(/([A-Za-z0-9])-(\d)/g, '$1‑$2');
  // A newline in the label is a HARD break. Relying on natural wrapping
  // doesn't work for these: the column is sized by its body cells (a demo
  // input plus a YoY line, say), so it can end up wide enough that the
  // header never needs to wrap even though two lines is what's wanted.
  const lines = nb.split('\n');
  return (
    <>
      {lines.map((line, li) => (
        <React.Fragment key={li}>
          {li > 0 && <br />}
          {line.split(/(\(000s\))/gi).map((part, i) =>
            /^\(000s\)$/i.test(part)
              ? <span key={i} className="lc">(000s)</span>
              : <React.Fragment key={i}>{part}</React.Fragment>,
          )}
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * Circled plus, used by the in-grid "Add Show" / "Add Row" links.
 *
 * strokeWidth 1.6 rather than 2: at 15px a 2px stroke fills the circle in
 * and the glyph reads as a solid dot. currentColor so the icon follows the
 * link's colour, including on hover.
 */
const PlusCircleIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" />
    <line x1="12" y1="7.5" x2="12" y2="16.5" />
    <line x1="7.5" y1="12" x2="16.5" y2="12" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const MediaPlanBuilder: React.FC = () => {
  // Hydrate estimate versions from Snowflake on first visit to this tab.
  // Cache is shared across all consumers, so navigating away and back
  // re-uses the already-loaded data with no spinner the second time.
  const [estimatesHydrated, setEstimatesHydrated] = useState<boolean>(isHydrated());
  // Surface hydrate failure as a yellow ribbon. Component-level state so
  // re-render fires on retry success/failure. `onHydrateChange` syncs us
  // when EstimateUploads triggers a retry from another tab.
  const [hydrateErr, setHydrateErr] = useState<string | null>(getHydrateError());
  const [retryingHydrate, setRetryingHydrate] = useState(false);
  // Saved-plans cache hydrate — runs in parallel with estimate-versions.
  // No separate spinner; if plans fail to hydrate the user just sees no
  // saved plans (degraded but functional, like the estimate ribbon flow).
  const [plansHydrated, setPlansHydrated] = useState<boolean>(isPlansHydrated());
  // SF plan estimate bindings cache hydrate — also runs in parallel.
  // Used by 25/26 SF source plans to look up their bound estimate
  // version (or fall through to default if no binding). Bindings are
  // small (one row per explicitly-bound SF plan), so degraded mode here
  // is acceptable: missing bindings = "use default upfront."
  //
  // The `bindingsTick` counter forces a re-render whenever the binding
  // cache changes (post Save / Clear) so dependent UI (e.g. the pill
  // dropdown, the Save button visibility) stays in sync without each
  // consumer re-reading the cache itself.
  const [bindingsTick, setBindingsTick] = useState(0);
  useEffect(() => {
    if (estimatesHydrated) return;
    hydrateEstimateVersions().finally(() => {
      setEstimatesHydrated(true);
      setHydrateErr(getHydrateError());
    });
  }, [estimatesHydrated]);
  useEffect(() => {
    if (plansHydrated) return;
    hydratePlans().finally(() => setPlansHydrated(true));
  }, [plansHydrated]);
  // Fire-and-forget hydrate for SF bindings — soft-dependency, app boots
  // fine without it. Subscribe to mutations so post-save flips re-render.
  useEffect(() => {
    if (!isSfBindingsHydrated()) {
      void hydrateSfPlanBindings();
    }
    return onSfBindingsChange(() => setBindingsTick(t => t + 1));
  }, []);
  useEffect(() => onHydrateChange(() => setHydrateErr(getHydrateError())), []);
  async function handleRetryHydrate() {
    setRetryingHydrate(true);
    await retryHydrate();
    setHydrateErr(getHydrateError());
    setRetryingHydrate(false);
  }

  const { data: sourceDeals, loading: sourceDealsLoading, error: sourceDealsError } = useSourceDeals();
  const [allDeals, setAllDeals] = useState<Deal[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  /**
   * Estimate version bound to the currently-selected plan. Resolved via
   * `resolvePlanVersion(planFilter)` which auto-stamps the plan to the
   * latest version on first view (the "backfill" behaviour from the spec).
   * Falls back to `null` when no plan is selected or no version has been
   * uploaded yet — in which case the legacy hardcoded ESTIMATE_DB takes
   * over via `setActiveEstimates(null)`.
   */
  const [activeVersion, setActiveVersion] = useState<EstimateVersion | null>(null);
  // "Estimates as of" pill dropdown — open/close state for the version
  // picker shown on directly-selected 25/26 SF source plans. The pill
  // is non-clickable everywhere else (per the season-toggle spec: the
  // 25/26 toggle view of a rolled-forward 26/27 plan can READ the
  // source's binding but can't change it).
  const [pillDropdownOpen, setPillDropdownOpen] = useState(false);
  const pillDropdownRef = useRef<HTMLDivElement>(null);
  // Save-binding button spinner. True while the PUT to
  // /sf-plan-estimate-bindings is in flight.
  const [savingBinding, setSavingBinding] = useState(false);
  // Custom plan-dropdown (replaces the native <select>) — open/close +
  // anchor ref for click-outside detection.
  // Unified "Load Plan" picker — replaces the old dropdown pill +
  // separate "Search plans…" input. One state controls the popover;
  // one wrapper ref covers click-outside; one input ref lets us blur
  // on pick and .select() on focus for the type-over-to-replace UX.
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const planPickerRef = useRef<HTMLDivElement>(null);
  const planPickerInputRef = useRef<HTMLInputElement>(null);
  // Plan-search picker — search term + popup visibility. Popup auto-
  // shows whenever the input has focus AND search term is non-empty
  // (or empty + focused, which we treat as "no results yet, just keep
  // typing"). Picking a result clears search + closes popup.
  // Search query typed into the unified plan picker input. Empty →
  // dropdown shows the full plan list; non-empty → filters by
  // substring match against plan NAME.
  const [planSearchTerm, setPlanSearchTerm] = useState('');
  // Last-saved signature PER PLAN, keyed by plan name. `isDirty` compares the
  // active plan's current signature to its own entry, so reverting an edit
  // clears the "Unsaved" indicator with no per-handler bookkeeping.
  //
  // Per-plan, not one string across everything: `allDeals` holds every plan
  // simultaneously, so a single shared baseline meant saving one plan
  // re-baselined the others too — adopting their unsaved rows as saved and
  // taking the pill, Save, Revert and the beforeunload warning down with it.
  // See `planSignatureFor`.
  //
  // A missing entry means "no baseline recorded" (a plan that has never been
  // loaded or saved this session) and reads as clean, matching the previous
  // behaviour for an unset snapshot.
  const [savedSnapshots, setSavedSnapshots] = useState<Record<string, string>>({});

  /**
   * Re-baseline specific plans from the arrays the caller is about to commit.
   *
   * Takes the arrays explicitly rather than reading state because every caller
   * runs before React has applied its own setState — reading `allDeals` here
   * would sign the PREVIOUS render's data and leave the plan instantly dirty.
   */
  const snapshotPlans = useCallback(
    (names: string[], deals: Deal[], rows: AddedRow[]) => {
      if (!names.length) return;
      setSavedSnapshots(prev => {
        const next = { ...prev };
        names.forEach(n => { next[n] = planSignatureFor(n, deals, rows); });
        return next;
      });
    },
    [],
  );
  // Active tab key. Either SUMMARY_TAB_KEY or one of the QUARTER_TABS keys.
  // Defaults to Summary (the first tab). Edits persist across tab switches
  // because allDeals holds every quarter's data; filteredDeals just narrows
  // the rendered slice.
  const [activeQuarter, setActiveQuarter] = useState<string>(SUMMARY_TAB_KEY);
  const [planFilter, setPlanFilter] = useState('');
  // Chunked rendering — first paint draws only TABLE_CHUNK_SIZE rows so
  // big datasets (e.g., All Plans · 25/26 with 800+ deals) appear
  // instantly. The remaining rows stream in via setTimeout, growing
  // visibleRowCount one chunk per frame-ish, so the browser stays
  // responsive between increments. Reset by the useEffect below when
  // the source data changes (different season, plan, quarter, sort)
  // so the user always sees the head of the new view immediately.
  const TABLE_CHUNK_SIZE = 100;
  const [visibleRowCount, setVisibleRowCount] = useState<number>(TABLE_CHUNK_SIZE);
  // Per-plan extra quarter count — how many quarters the user has
  // appended past the marketplace's natural pattern via "+ Add Quarter"
  // (and trimmed back via ×). Synced to localStorage on every change
  // through setExtraQuarters() below so the value survives reloads.
  // Loaded from localStorage on planFilter change in the effect below.
  const [extraQuarters, setExtraQuartersState] = useState<number>(0);
  // Tick counter incremented every time the saved-plans cache changes
  // (savePlan succeeds, deletePlan succeeds). Read by memos that
  // consume `listSavedPlans()` inline — planOptions, filteredDeals,
  // filteredAddedRows — so they re-evaluate after a save without
  // needing planCache to be tracked as a React state. Same pattern as
  // the existing `bindingsTick` for SF plan binding changes.
  const [planCacheVersion, setPlanCacheVersion] = useState<number>(0);
  // Season toggle on 26/27 plans rolled forward from a 25/26 source.
  // '26/27' (default) = the plan as the user is building it — editable,
  // current-year estimates, full quarter set.
  // '25/26'           = the original 25/26 SF source plan, view-only,
  //                     resolved against the 25/26 estimate version.
  // Reset to '26/27' on every planFilter change so the toggle never
  // bleeds across plans; never persisted to localStorage (decision: a
  // page reload should always land in 26/27 view).
  const [seasonView, setSeasonView] = useState<'25/26' | '26/27'>('26/27');
  // All Plans landing view: which season's plans + estimates to show.
  // Only meaningful when planFilter === '' (no specific plan selected).
  // Picked by the user from the dropdown — "All Plans · 25/26" sets this
  // to '25/26', "All Plans · 26/27" sets it to '26/27' (the default).
  //
  // Drives:
  //   1. activeVersion is built as a synthetic 5-quarter merge of the
  //      two adjacent upfronts (so BY + CY plans both fit in one view).
  //   2. filteredDeals / filteredAddedRows filter the plan-row list to
  //      only those plans whose deals fall in this season's quarter set.
  const [allPlansSeason, setAllPlansSeason] = useState<'25/26' | '26/27'>('26/27');
  // (searchInput state removed — search no longer filters table rows;
  // the plan-name picker uses `planSearchTerm` declared with the other
  // picker state below.)
  const [sortCol, setSortCol] = useState('program');
  const [sortDir, setSortDir] = useState(1);
  const [addedRows, setAddedRows] = useState<AddedRow[]>([]);
  const addedRowCounter = useRef(0);

  // Ref mirrors of allDeals + addedRows so saveToSnowflake can read
  // the freshest state AFTER a blur-triggered onBlur commit fires.
  // React batches setState until the next render — the save function's
  // closure would otherwise capture stale values. Refs are assigned
  // inline on every render (see below), and saveToSnowflake yields
  // one tick after the blur so React has a chance to re-render and
  // re-run these assignments before we read.
  const allDealsRef = useRef<Deal[]>([]);
  const addedRowsRef = useRef<AddedRow[]>([]);

  /**
   * planId → the PLAN_VERSION.version_no that this plan's rows in `allDeals`
   * were built from. Sent as `baseVersionNo` so the backend can refuse a save
   * that would overwrite work saved by someone else in the meantime.
   *
   * WHY A MAP AND NOT ONE VALUE: every saved plan is merged into `allDeals`
   * in a single effect at mount — switching plans only changes `planFilter`,
   * it does not load anything. So there is no "current plan's loaded version",
   * only a per-plan answer to "where did these rows come from".
   *
   * WHY NOT JUST READ THE CACHE AT SAVE TIME: the cache moves underneath the
   * grid in two ways the grid does not follow. It is refreshed on tab focus
   * (so it can be NEWER than the rows on screen — the check would pass in
   * exactly the case it exists to catch), and `overlayHistoricalVersion`
   * rewrites `versionNo` to a historical number while the picker is showing
   * an old version (so it can be OLDER, and the check would fire for no
   * reason). Neither matches what the user is actually editing from.
   *
   * A `null` entry means "do not assert" — set when the grid holds a version
   * pulled from the history picker, which is a deliberate revert rather than
   * a collision. Missing entry means the same thing.
   */
  const loadedVersionByPlanRef = useRef<Map<number, number | null>>(new Map());

  /**
   * Set when this plan is known to have moved on without us — either a save
   * came back 409 PLAN_VERSION_CONFLICT, or a background refresh saw a newer
   * version while the grid was dirty.
   *
   * Holds the plan name rather than a boolean so a stale flag can't bleed
   * across a plan switch. Cleared by reloading the plan, or by switching away.
   *
   * Deliberately does NOT discard the user's edits. Their work stays on screen
   * and they choose what to do — reloading is offered, never imposed.
   */
  const [conflictedPlan, setConflictedPlan] = useState<string | null>(null);
  // Inline sync — runs on every render, keeps refs in lockstep with
  // the latest state values React just committed.
  allDealsRef.current = allDeals;
  addedRowsRef.current = addedRows;

  // Salesforce Programs__c catalog. Fetched once on mount so the
  // + Add Row / + Add Show flows can stamp `Deal__c.Program__c` on
  // newly-added rows. Without this, AddedRows saved with PROGRAM_ID =
  // NULL and the push-to-SF flow errored with MISSING_PROGRAM_IDS on
  // brand-new plans that had no rolled-forward source deals to
  // inherit an ID from. Keyed by lowercased name so lookup is
  // case-insensitive (SF names use title case, in-app names sometimes
  // vary — e.g. "911" vs "9-1-1").
  const [sfProgramMap, setSfProgramMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const items = await listPrograms();
      if (cancelled) return;
      const m = new Map<string, string>();
      for (const p of items) {
        if (p.name && p.id) m.set(p.name.trim().toLowerCase(), p.id);
      }
      setSfProgramMap(m);
    })();
    return () => { cancelled = true; };
  }, []);

  // Salesforce picklist for Sales_Plan__c.Primary_Demographic__c —
  // fetched once on mount. Powers the Primary Demo picker in New Plan
  // + Edit Plan Details, replacing the hardcoded SYSTEM_DEMOS shortlist.
  // Falls back to SYSTEM_DEMOS when the fetch hasn't landed yet or
  // failed (SF outage) so the picker is never empty.
  const [sfPrimaryDemos, setSfPrimaryDemos] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const demos = await listPrimaryDemos();
      if (cancelled) return;
      if (demos.length > 0) setSfPrimaryDemos(demos);
    })();
    return () => { cancelled = true; };
  }, []);
  // Effective demo list — SF picklist when available, hardcoded
  // shortlist otherwise. Both pickers below read from this.
  const primaryDemoOptions = sfPrimaryDemos ?? SYSTEM_DEMOS;

  // Salesforce org this environment pushes to, for "View in Salesforce"
  // links. Fetched from the backend rather than hardcoded: the frontend is
  // one build shared by staging and production, so it cannot know the target
  // org at compile time. The base used to be a literal Training sandbox URL,
  // which meant that once production pushes went live, every pushed plan
  // rendered a link with a real PRODUCTION record id on the TRAINING host —
  // a 404 at best, and at worst a silent match against an unrelated sandbox
  // record with the same id.
  //
  // '' means unknown (fetch failed / not configured) → the link is hidden
  // rather than pointed somewhere plausible-looking but wrong.
  // Export All → Excel-or-PDF chooser. Opened only after the post-order
  // export gate has passed, so cancelling here can't strand a version.
  const [exportFormatPrompt, setExportFormatPrompt] = useState(false);

  const [sfInstanceUrl, setSfInstanceUrl] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = await getSfInstanceUrl();
      if (cancelled) return;
      setSfInstanceUrl(url);
    })();
    return () => { cancelled = true; };
  }, []);
  /**
   * Resolve an SF Programs__c.ID for a program name. Order of precedence:
   *   1. Existing PLAN_DEAL row for this name in the current plan (covers
   *      rolled-forward provenance where SF already assigned an ID).
   *   2. Existing AddedRow for this name in the current plan (chains
   *      the ID across sibling adds in the same session).
   *   3. SF program catalog (fetched at mount).
   * Returns null when unresolved — pre-push validator surfaces this
   * to the user with a repair prompt.
   */
  const resolveSfProgramId = (program: string, salesPlan: string): string | null => {
    if (!program) return null;
    const inheritedFromDeal = allDeals.find(
      d => d.salesPlan === salesPlan && d.program === program && d.programId,
    )?.programId ?? null;
    if (inheritedFromDeal) return inheritedFromDeal;
    const inheritedFromAdded = addedRows.find(
      r => r.salesPlan === salesPlan && r.program === program && r.programId,
    )?.programId ?? null;
    if (inheritedFromAdded) return inheritedFromAdded;
    return sfProgramMap.get(program.trim().toLowerCase()) ?? null;
  };
  const [secondaryDemos, setSecondaryDemos] = useState<string[]>([]);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Save-button loading state — true while a savePlan POST is in flight.
  // Disables the button + flips its label to "Saving…" so the user can't
  // double-click during the round-trip.
  const [isSaving, setIsSaving] = useState(false);
  // Revert-confirmation modal. Simple boolean since the modal has no
  // configurable content — always confirms the same destructive action
  // and always targets the current planFilter's latest saved version.
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  /**
   * Plan the user is trying to switch TO while the current one has unsaved
   * edits. Non-null means the confirm dialog is up; the string is the pending
   * destination so confirming can complete the switch.
   *
   * `''` is a legitimate destination (the All Plans view), so this is
   * `string | null` and every check is against `null` — not truthiness.
   */
  const [pendingPlanSwitch, setPendingPlanSwitch] = useState<string | null>(null);
  // Remount token for uncontrolled (`defaultValue` + onBlur) inputs.
  // Bumped by Revert so typed-but-uncommitted text in the bulk CPM
  // strip / row inputs is discarded instead of surviving the state
  // rollback. Embedded in those inputs' `key` props.
  const [inputRemountKey, setInputRemountKey] = useState(0);
  // Create Plan modal — in-flight POST /snowflake/plans state. Disables
  // both Create and Cancel and flips the Create button to "Creating…"
  // so the user can't double-click or bail out mid-save.
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  // Create Plan modal — in-flight resolveSalesPlan lookup fetch triggered
  // by picking a 25/26 source plan. Renders a full-modal overlay so the
  // user sees that the lookup fields are about to auto-populate instead
  // of thinking the modal is broken while the round-trip is pending.
  const [isResolvingSalesPlan, setIsResolvingSalesPlan] = useState(false);
  // Push-to-Salesforce modal state. Opened when the user picks "Order"
  // from the status dropdown — the actual status flip happens on the
  // backend after SF push succeeds, so we don't touch PLAN.status locally
  // until we get the fresh plan back.
  const [pushToSfModalOpen, setPushToSfModalOpen] = useState(false);
  const [pushToSfInFlight, setPushToSfInFlight] = useState(false);
  // Error surface for the modal. Discriminated by `code` so we can render
  // specific guidance per error type instead of a generic "push failed".
  const [pushToSfError, setPushToSfError] = useState<{
    message: string;
    code?: string;
    affectedPrograms?: string[];
    sfDetail?: unknown;
    /**
     * Full JSON body the backend sent to Salesforce, populated on
     * SF_REJECTED. Rendered in a collapsible section so we can inspect
     * what the payload looked like when SF pushed back.
     */
    requestBody?: unknown;
  } | null>(null);
  // ── Plan version history (Snowflake-backed) ──
  // Only meaningful for Snowflake-saved plans (activePlan?.kind === 'snowflake').
  // The chip in the MPB header opens a popup that lazy-fetches GET
  // /snowflake/plans/:id/versions; clicking a row in that popup loads
  // that historical version into the live grid (overlaying planCache
  // in memory) so the user can view + edit it, with a yellow banner
  // warning them they're not on the latest.
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState<PlanVersionMeta[] | null>(null);
  const [versionHistoryErr, setVersionHistoryErr] = useState<string | null>(null);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  // Tracks whether the currently-viewed plan has a historical version
  // overlaid on the cache (i.e., the grid is showing a past version,
  // not the actual latest). Powers the warning banners and the
  // "Return to latest" affordance. Cleared on successful save or when
  // the user explicitly returns to latest.
  //
  // Lifecycle-aware locking (see planIsViewOnly below):
  //   • viewingMilestoneIndex === currentLifecycleIndex → CURRENT
  //     lifecycle historical revision. Editable, save creates a
  //     new revision on top of latest. Shows a light info banner.
  //   • viewingMilestoneIndex <  currentLifecycleIndex → PREVIOUS
  //     lifecycle. Read-only (all inputs disabled, save disabled).
  //     Shows the yellow warning banner.
  const [viewingHistoricalRef, setViewingHistoricalRef] = useState<{
    planId: number;
    displayLabel: string;   // "v1.2", "Upfront V1", "v2 · Live Order" — same as picker label
    latestVersionNo: number; // the ACTUAL latest we'll restore to
    viewingMilestoneIndex: number;    // which lifecycle is being viewed
    currentLifecycleIndex: number;    // the actual latest's lifecycle
  } | null>(null);
  // Loading + error for the historical-load fetch. Rendered as a
  // small toast/overlay while the deals payload comes back.
  const [historicalVersionLoading, setHistoricalVersionLoading] = useState(false);
  const [historicalVersionErr, setHistoricalVersionErr] = useState<string | null>(null);
  // Snapshot of the ACTUAL latest deals for this plan, captured BEFORE
  // overlayHistoricalVersion mutates the cache. Powers the diff-highlight
  // feature — grid cells that differ from these values get amber styling
  // + hover tooltip so the user can see at a glance what changed.
  //
  // Only populated when the picked version isn't itself the latest;
  // cleared on returnToLatest / plan switch / save success. Also carries
  // the latest version's raw versionNo for the tooltip copy.
  const [historicalDiffBaseline, setHistoricalDiffBaseline] = useState<{
    latestDeals: PlanDeal[];
    latestVersionNo: number;
  } | null>(null);
  // Post-order revision mode. When the plan is pushed-to-SF, everything
  // is normally locked. Clicking "Revise Order" flips this on, which
  // unlocks editing so the user can create a new post-order lifecycle
  // (client options / order revisions). Finalizing via Name & Export
  // creates an 'order_update' milestone that supersedes the prior
  // Live Order. Client-side only — auto-detected on load when the
  // plan has unfinalized post-order working revisions.
  const [revisingOrderMode, setRevisingOrderMode] = useState(false);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [showSFModal, setShowSFModal] = useState(false);
  const [showAddShowModal, setShowAddShowModal] = useState(false);
  const [addShowSearch, setAddShowSearch] = useState('');
  /**
   * Quarter keys selected as the Add Show modal's target(s). Initialised
   * by `openAddShowModal` based on which tab the user is on (current
   * quarter pre-checked, or all four if they were on Summary). Picking
   * a program adds the same row across every checked quarter.
   */
  const [addShowQuarterKeys, setAddShowQuarterKeys] = useState<Set<string>>(new Set());
  /**
   * Programs the user has picked in the Add Show modal but hasn't yet
   * committed via the modal's "Add" button. Multi-select — user can
   * check several shows at once and add them all in a single click.
   * Each picked program lands in every quarter checked in
   * addShowQuarterKeys. Cleared on modal open/close.
   */
  const [addShowSelected, setAddShowSelected] = useState<Set<string>>(new Set());
  // (showDemoDropdown state removed — never read or set anywhere.)
  const [demoPillDropdownOpen, setDemoPillDropdownOpen] = useState(false);
  // 20% guard modal state — pending confirmation prompt for large CPM /
  // demo (000) changes. `resolve` is the captured Promise resolver; on
  // user click we call it with true (apply) or false (cancel) and clear
  // the modal. Null while idle.
  const [guardModal, setGuardModal] = useState<{
    fieldLabel: string;
    oldValue: number;
    newValue: number;
    formatValue: (v: number) => string;
    resolve: (confirmed: boolean) => void;
  } | null>(null);
  // Marketplace-change warning modal. Fires from Edit Plan Details
  // when the new marketplace would hide quarters that contain saved
  // deals or unsaved AddedRows for this plan. `hidden` is a per-tab
  // breakdown so the modal can show row counts. Promise-based same as
  // guardModal: submitEditPlan awaits the resolve and continues vs.
  // aborts. Null while idle.
  const [marketplaceWarn, setMarketplaceWarn] = useState<{
    oldMarketplace: string;
    newMarketplace: string;
    hidden: Array<{ key: string; label: string; dealCount: number; addedRowCount: number }>;
    resolve: (proceed: boolean) => void;
  } | null>(null);
  // Delete-plan confirmation — replaces the native window.confirm() so
  // the confirmation lives inside the app's modal chrome (consistent
  // with the marketplace warn + push confirm modals) and is testable
  // via UI automation (native confirm freezes the automation runtime).
  const [deleteConfirm, setDeleteConfirm] = useState<{
    planName: string;
    dealCount: number;
    resolve: (proceed: boolean) => void;
  } | null>(null);
  // Delete-single-row confirmation — mirrors the plan-delete modal but
  // for a single PLAN_DEAL row inside the current plan. Fires from the
  // ✕ button on saved rows (AddedRows use `removeAddedRow` directly
  // since they've never been persisted). Removes the row from allDeals
  // immediately; the row's absence from the next Save payload drops it
  // from the new plan_version snapshot. If the user reloads without
  // saving, the row returns from Snowflake — same volatility as Units
  // edits.
  // Post-order export modal — prompts the user to (optionally) save a
  // named snapshot version alongside the export. Pre-order exports skip
  // this and download directly. See handleExportGate for the flow.
  //
  // resolve: user's choice.
  //   - { save: true, name }  → hit /versions/export first, then export
  //   - { save: false }       → export without saving a version
  //   - null                  → cancel entirely (no export, no save)
  const [postOrderExportModal, setPostOrderExportModal] = useState<{
    resolve: (choice: { save: true; name: string } | { save: false } | null) => void;
  } | null>(null);

  const [deleteRowConfirm, setDeleteRowConfirm] = useState<{
    program: string;
    quarter: string;
    year: number;
    totalUnits: number;
    resolve: (proceed: boolean) => void;
  } | null>(null);
  // Status picklist state.
  // `statusDropdownOpen` toggles the Working/Hold/Order dropdown.
  // `statusBump` is incremented after a successful API status update —
  // planCache lives outside React, so we need a state change to force
  // the status pill to re-render with the new value.
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [statusBump, setStatusBump] = useState(0);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [demoSearchFilter, setDemoSearchFilter] = useState('');
  // Holds the "next" plan nickname value during a rename flow. The
  // current sole producer is the Edit Plan Details modal (Stage B);
  // saveToSnowflake reads it to detect renames and rewrite the
  // PLAN.name on the next save. Initialised to planFilter on every
  // plan switch (see the matching useEffect below) so a no-op save
  // never triggers an unintended rename.
  const [planNameDraft, setPlanNameDraft] = useState('');
  // NOTE: program expand/collapse was removed deliberately. The grid is very
  // wide, so users clicking what looks like empty space to blur the field
  // they were editing were landing on the program header row and collapsing
  // that program's lines — leaving them unsure what they were looking at.
  // The program header rows are now inert: no click handler, no pointer
  // cursor, no chevron. Nothing else depended on the collapsed state.
  // Sticky-bar height — drives the calc() that sizes the table scroll
  // container. Measured via a callback ref (see stickyBarRefCallback)
  // so the observer re-attaches when React swaps the element. The
  // sticky bar in practice doesn't unmount (it lives outside any
  // ternary), but the callback-ref pattern is harmless + consistent
  // with how we observe the group-header row below.
  const [stickyBarH, setStickyBarH] = useState(0);
  const stickyBarROHolder = useRef<ResizeObserver | null>(null);

  // Group-header height — drives the --group-header-h CSS variable
  // that anchors the second thead row's sticky offset. Measured via
  // a callback ref because the <table> (and therefore its thead and
  // group-header row) unmounts + remounts every time the user
  // switches between the Summary tab and a Quarter tab. A plain
  // useRef + useEffect ([]) would observe the first-mounted row
  // forever and never re-bind after tab switches, leaving the CSS
  // variable stuck at whatever the previous table's height was.
  //
  // Default of 32 overestimates the real height by 2–6px so the
  // first paint (before the callback fires) errs on the side of
  // slight overlap rather than a visible gap. Browsers won't show
  // anything wrong — the detail row stays hidden behind the group
  // row for at most one frame.
  const [groupHeaderH, setGroupHeaderH] = useState(32);
  const groupHeaderROHolder = useRef<ResizeObserver | null>(null);
  const demoDropdownRef = useRef<HTMLDivElement>(null);
  // Wrapper around the version chip + popup — used by the click-outside
  // handler to close the popup when the user clicks anywhere else. Set
  // only on Snowflake-saved plans (the chip is hidden / inert otherwise).
  const versionChipWrapperRef = useRef<HTMLDivElement>(null);

  // New plan modal state.
  // `npRollForwardSource` is the picked 25/26 SF plan to template from
  // (or null for a blank new plan). When set, the modal's metadata
  // fields auto-fill from the source plan's deals. Phase 94 uses this
  // alongside `npRollForwardSfId` at submit time to copy + year-shift
  // the source deals into the new 26/27 plan.
  const [npRollForwardSource, setNpRollForwardSource] = useState<string | null>(null);
  const [npRollForwardSfId, setNpRollForwardSfId] = useState<string | null>(null);
  const [npRollForwardSearch, setNpRollForwardSearch] = useState('');
  const [npRollForwardOpen, setNpRollForwardOpen] = useState(false);
  const npRollForwardRef = useRef<HTMLDivElement>(null);
  /**
   * Track which SEASON the picked source belongs to. Two distinct flows
   * downstream:
   *   • '25/26' — traditional roll-forward: year+1 shift, drop programs
   *               not in the target 26/27 estimate, run the Unit Length
   *               transform if the user picked a target length.
   *   • '26/27' — verbatim clone of a same-season plan: no year shift,
   *               no length transform, all deals copied as-is with new
   *               dealKeys.
   *   • null    — nothing picked (from-scratch flow).
   *
   * Also drives the helper text above the picker ("Rolling forward from"
   * vs "Cloning from") and the chip color inside the confirmation box.
   */
  const [npRollForwardYear, setNpRollForwardYear] = useState<'25/26' | '26/27' | null>(null);
  /**
   * Snowflake plan ID of a 26/27 source (null for 25/26 sources). Written
   * to PLAN.source_plan_id on save so the clone traces back to its parent
   * plan the same way rolled-forward plans trace back via source_plan_sf_id.
   */
  const [npRollForwardPlanId, setNpRollForwardPlanId] = useState<number | null>(null);
  /**
   * Unit-length transform target for the roll-forward. Values:
   *   '15' | '30' | '45' | '60' — convert every source row to that
   *                                length using new_units = round(
   *                                old_units × src_len / target_len),
   *                                and merge into the target-length
   *                                row (units>1, rate-matched first)
   *                                for the same show/quarter/year/demo.
   *   'as-is'                     — pass source rows through verbatim.
   *   ''                          — nothing picked yet (before source
   *                                is selected, or between picks).
   * Auto-populates to the most-common length on the picked source
   * plan; user can override.
   */
  const [npUnitLength, setNpUnitLength] = useState<string>('');

  // SF-lookup-backed fields: each tracked as (id, name) pair so the
  // form can both display the picked name AND ship the SF id to the
  // save endpoint. Names live in the legacy `npAdvertiser`/`npBrand`/
  // `npBuyer`/`npAgency` strings so existing submit/validation code
  // keeps reading them as before; the *Id state is the new addition.
  const [npAdvertiser,   setNpAdvertiser]   = useState('');
  const [npAdvertiserId, setNpAdvertiserId] = useState<string | null>(null);
  const [npBrand,        setNpBrand]        = useState('');
  const [npBrandId,      setNpBrandId]      = useState<string | null>(null);
  const [npBuyer,        setNpBuyer]        = useState(''); // = contact display name
  const [npContactId,    setNpContactId]    = useState<string | null>(null);
  const [npAgency,       setNpAgency]       = useState(''); // auto-fills from contact
  const [npAccountId,    setNpAccountId]    = useState<string | null>(null);
  const [npDealname, setNpDealname] = useState('');
  // Account Exec stays free text. Default to Tiffany DeDeo on every
  // fresh modal open (matches the spec — most plans are hers).
  const [npAe, setNpAe] = useState('Tiffany DeDeo');
  const [npDemoInput, setNpDemoInput] = useState('');
  const [npDemo, setNpDemo] = useState('');
  const [npMarketplace, setNpMarketplace] = useState('');
  // Which agency rate card this plan pulls its estimates from. 'general' is
  // the master estimate every plan used before rate cards existed; 'wpp' is
  // WPP's negotiated file.
  //
  // Locked at creation — a plan is pinned to one estimate version and 26/27
  // plans have no rebinding UI, so this is the only moment it can be chosen.
  // The picker below is deliberately independent of the Agency field (no
  // Salesforce coupling), with a soft warning if the two disagree.
  const [npRateCard, setNpRateCard] = useState<RateCard>('general');
  const [npError, setNpError] = useState('');

  // ── Rate card picker: applicability + available options ──
  //
  // A rate card is a variant of an UPFRONT. Marketplace 'Scatter' has no
  // custom cards, so the field doesn't apply there at all.
  const npRateCardApplies = npMarketplace !== 'Scatter';

  // Only offer cards that actually have a 26/27 upfront estimate behind
  // them. 'general' is always listed so the control never renders empty
  // even before hydration finishes; a custom card has to earn its place.
  // With one option the caller hides the field entirely — nothing to pick.
  const npRateCardOptions = useMemo<RateCard[]>(() => {
    return RATE_CARDS.filter(c =>
      c === 'general'
      || hasVersionForCard(CURRENT_SEASON_PRIMARY_YEAR, 'upfront', c),
    );
  }, [estimatesHydrated]);

  // Keep the pick valid. Switching Marketplace to Scatter, or an offered
  // card disappearing between renders, must not leave a plan about to be
  // created against a card it can't reach.
  useEffect(() => {
    if (npRateCard === 'general') return;
    if (!npRateCardApplies || !npRateCardOptions.includes(npRateCard)) {
      setNpRateCard('general');
    }
  }, [npRateCard, npRateCardApplies, npRateCardOptions]);

  // NOTE: the rate card is deliberately independent of Agency. It is not
  // derived from, validated against, or cross-checked with the agency in any
  // way — the user picks it, and General is the default. Resist adding a
  // "did you mean WPP?" hint here: agency names come from Salesforce and
  // there is no reliable mapping from a record to a rate card.
  const [npShowDemoDropdown, setNpShowDemoDropdown] = useState(false);
  // Ref to the Primary Demo input so the picker's mousedown handlers
  // can blur it on pick / focus it on clear — dropdown clicks fire
  // before natural blur so we call it explicitly.
  const npDemoInputRef = useRef<HTMLInputElement>(null);

  // ── Edit Plan Details modal state ──
  //
  // Opened from the Plan Details panel's "✎ Edit Details" button. Pre-
  // filled from `getPlanMeta(planFilter)` on open, reset on close. Save
  // builds a fresh ManualPlanMeta and immediately calls saveToSnowflake
  // with it as `metaOverride`, committing a new PLAN_VERSION row. The
  // user-facing rename flow is reused: if the nickname changes, we push
  // the new value into planNameDraft and saveToSnowflake's existing
  // rename block handles the rest.
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editAdvertiser, setEditAdvertiser]     = useState('');
  const [editAdvertiserId, setEditAdvertiserId] = useState<string | null>(null);
  const [editBrand, setEditBrand]               = useState('');
  const [editBrandId, setEditBrandId]           = useState<string | null>(null);
  const [editBuyer, setEditBuyer]               = useState('');
  const [editContactId, setEditContactId]       = useState<string | null>(null);
  const [editAgency, setEditAgency]             = useState('');
  const [editAccountId, setEditAccountId]       = useState<string | null>(null);
  const [editNickname, setEditNickname]         = useState('');
  const [editAe, setEditAe]                     = useState('');
  const [editDemo, setEditDemo]                 = useState('');
  const [editDemoInput, setEditDemoInput]       = useState('');
  const [editShowDemoDropdown, setEditShowDemoDropdown] = useState(false);
  const [editMarketplace, setEditMarketplace]   = useState('');
  const [editEquiv, setEditEquiv]               = useState('');
  // Plan-level default unit length (":15" / ":30" / ":45" / ":60" or
  // empty for no default). Edited via the same picker style as Create
  // Plan and consumed by + Add Show / + Add Row to seed new rows.
  const [editDefaultUnitLength, setEditDefaultUnitLength] = useState('');
  const [editError, setEditError]               = useState('');

  // ─── Active estimate version resolution ─────────────────────────────────────
  //
  // Year-aware resolution. The estimate version we anchor to depends on
  // which "kind" of plan is selected:
  //
  //   1. Snowflake-saved plan (26/27, editable) — use the
  //      estimate_version_id stamped on its latest PLAN_VERSION row.
  //      This is durable + cross-browser and beats the per-browser
  //      localStorage binding the legacy resolver uses. Fallback to
  //      the latest 26/27 if the stamped id is missing (deleted
  //      estimate version — rare but possible).
  //
  //   2. Manual draft plan (26/27, unsaved + localStorage-only) — go
  //      through `resolvePlanVersion` so the draft gets auto-stamped
  //      to the latest 26/27 on first view and stays sticky after
  //      newer 26/27 uploads.
  //
  //   3. SF source plan (25/26, view-only) — always resolve to the
  //      25/26 estimate (`primary_year=2025`). The 25/26 estimate is
  //      a single backfilled v1 today; if more 25/26 versions ever
  //      get uploaded, `getLatestVersionByYear` returns the highest
  //      id within that season.
  //
  //   4. No plan selected — show the latest 26/27 (current season).
  //
  // Side effect: `setActiveEstimates(activeVersion)` below this
  // useEffect syncs the module-level ESTIMATE_DB / UNIVERSE_DATA /
  // QUARTER_TABS refs so every helper in this file picks up the
  // version's data — including the season-appropriate quarter tabs
  // (4Q25/1Q26/... for 25/26 plans, 4Q26/1Q27/... for 26/27).
  //
  // Cache of synthetic All Plans versions keyed by
  //   `${primaryYear}|${baseV.id}|${overlapV?.id ?? 'none'}`
  // so flipping between 25/26 and 26/27 tabs reuses a previously-
  // built version instead of re-iterating thousands of estimateDb keys
  // each click. Invalidated implicitly: a new estimate upload changes
  // baseV.id (or overlapV.id), so the cache key changes and we rebuild.
  const allPlansVersionCacheRef = useRef<Map<string, EstimateVersion>>(new Map());
  useEffect(() => {
    if (!planFilter) {
      // "All Plans" view — build a synthetic 5-quarter version that
      // spans BOTH the BY and CY windows for the active season tab.
      //
      //   • All Plans · 25/26 → quarters 4Q'25, 1Q'26, 2Q'26, 3Q'26, 4Q'26
      //     - 4Q'25–3Q'26 estimates from 25/26 upfront
      //     - 4Q'26 estimates merged in from 26/27 upfront
      //   • All Plans · 26/27 → quarters 4Q'26, 1Q'27, 2Q'27, 3Q'27, 4Q'27
      //     - 4Q'26–3Q'27 estimates from 26/27 upfront
      //     - 4Q'27 has no estimate yet (27/28 upfront not uploaded) →
      //       cells render blank with the Phase C ⚠ flag.
      //
      // This lets BY plans (4 quarters) and CY plans (4 quarters offset
      // by one) both display correctly in a single landing view.
      const isCurrent = allPlansSeason === '26/27';
      const basePY = isCurrent ? CURRENT_SEASON_PRIMARY_YEAR : LAST_SEASON_PRIMARY_YEAR;
      const overlapPY = isCurrent ? null : CURRENT_SEASON_PRIMARY_YEAR;
      // GENERAL card only. This is the cross-plan landing view — it must not
      // render against one agency's negotiated estimate.
      //
      // The old kind-only helpers order by as_of_date DESC then id DESC, and a
      // custom card's row is by construction the higher id, so it wins outright
      // on a date tie and wins on date whenever its file is the later vintage.
      // WPP's first upload is as-of 2026-07-20 against the general master's
      // 2026-05-20 — so without this scoping the entire All Plans view, for
      // every agency, would resolve to WPP's numbers.
      const baseV = getLatestGeneralVersionByYear(basePY);
      const overlapV = overlapPY != null
        ? getLatestGeneralVersionByYear(overlapPY)
        : null;
      // Cache hit short-circuits the expensive estimateDb merge.
      if (!baseV) {
        setActiveVersion(null);
        return;
      }
      const cacheKey = `${basePY}|${baseV.id}|${overlapV?.id ?? 'none'}`;
      const cached = allPlansVersionCacheRef.current.get(cacheKey);
      if (cached) {
        setActiveVersion(cached);
        return;
      }
      const built = buildAllPlansVersion(basePY, baseV, overlapV);
      if (built) allPlansVersionCacheRef.current.set(cacheKey, built);
      setActiveVersion(built);
      return;
    }

    /**
     * Wrap a resolved base version with its late quarter.
     *
     * Returns `baseV` unchanged when no merge is needed — the version already
     * has the quarter, or nothing has it yet. In the latter case the tab keeps
     * rendering blank with its ⚠, exactly as it does today.
     *
     * The merged result carries `mergedFrom`, so which version supplied the
     * quarter is readable straight off `activeVersion` and needs no parallel
     * component state to drift out of sync with it.
     */
    const applyLateQuarter = (
      baseV: EstimateVersion | null,
      pinnedId: number | null,
    ): EstimateVersion | null => {
      const source = resolveLateQuarterSource(baseV, pinnedId);
      if (!baseV || !source) return baseV;
      return buildLateQuarterMergedVersion(baseV, source);
    };

    /**
     * Resolve a 25/26 SF source plan to its active estimate version.
     * If the user has explicitly bound it (via the pill picker), that
     * binding wins. Otherwise default to the latest 25/26 *UPFRONT* —
     * the spec is that scatter only applies when the user opts in by
     * picking + saving.
     *
     * The final `?? getLatestVersionByYear(...)` fallback is intentional
     * defensive code, not a pre-DDL-backfill leftover: if every upfront
     * version for the year is ever deleted (admin slip-up, restore from
     * an old snapshot, …) we'd still surface *some* estimate rather
     * than rendering every cell as blank.
     */
    const resolveSfSourceVersion = (sfPlanSfId: string | null): EstimateVersion | null => {
      if (sfPlanSfId) {
        const binding = getSfPlanBinding(sfPlanSfId);
        if (binding) {
          const bound = getVersionById(binding.estimateVersionId);
          if (bound) return bound;
          // Binding points at a deleted version — fall through to default.
        }
      }
      // GENERAL card only — an SF source plan with no explicit binding must
      // never default onto a custom agency card. Latent today (no 25/26 WPP
      // file exists) but this is the path every unbound 25/26 plan takes, so
      // a single WPP upload for last season would move all of them at once.
      return getLatestGeneralVersionByYear(LAST_SEASON_PRIMARY_YEAR);
    };

    // (0) Season toggle: the 25/26 view of a rolled-forward 26/27 plan
    // resolves against the SOURCE SF plan's binding (the user is, in that
    // moment, looking at the 25/26 source). Source SF id comes from the
    // saved-plan record — same place sourcePlanSfId is derived later in
    // the file.
    if (seasonView === '25/26') {
      const saved = getSavedPlanByName(planFilter);
      const sourceSfId = saved?.sourcePlanSfId ?? null;
      setActiveVersion(resolveSfSourceVersion(sourceSfId));
      return;
    }

    // (1) Snowflake-saved 26/27 plan — bound estimate wins.
    const saved = getSavedPlanByName(planFilter);
    if (saved) {
      // Late-quarter fallback applies to THIS season's plans only. The 25/26
      // paths above and below are view-only historical reference; merging a
      // quarter into them would change what a past season renders, which no
      // one asked for and which the All Plans merge already covers.
      const pinnedOverlapId = saved.latestVersion?.overlapEstimateVersionId ?? null;
      const stampedId = saved.latestVersion?.estimateVersionId ?? null;
      if (stampedId != null) {
        const bound = getVersionById(stampedId);
        if (bound) {
          setActiveVersion(applyLateQuarter(bound, pinnedOverlapId));
          return;
        }
      }
      // Saved plan with no/missing stamp → fall back to latest 26/27
      // GENERAL UPFRONT (same default semantics as the SF source plan path).
      //
      // A plan's rate card is a property of the estimate version it's bound
      // to, so a plan with no binding has no card to recover — general is
      // the only defensible answer, and it's what every plan predating rate
      // cards actually used.
      //
      // This is not a path a WPP plan can reach: the New Plan picker only
      // offers a card that has an estimate behind it, and submitNewPlan
      // refuses the activeVersion fallback for a custom card, so a WPP plan
      // is always created with a real estimateVersionId.
      setActiveVersion(
        applyLateQuarter(
          getLatestGeneralVersionByYear(CURRENT_SEASON_PRIMARY_YEAR),
          pinnedOverlapId,
        ),
      );
      return;
    }

    // (2) SF source plan (25/26 view-only). Look up the plan's own SF
    // id via its deals' salesPlanSfId, then resolve through the binding
    // path. allDeals is in the dep array so this re-runs as soon as
    // source-deals hydrate; bindingsTick re-runs it after Save / Clear.
    const sfDeal = allDeals.find(d => d.salesPlan === planFilter);
    const ownSfId = sfDeal?.salesPlanSfId || null;
    setActiveVersion(resolveSfSourceVersion(ownSfId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, dataLoaded, plansHydrated, estimatesHydrated, seasonView, allPlansSeason, allDeals, bindingsTick]);
  // ^^^ estimatesHydrated is required because getLatestVersionByYearAndKind
  // reads the ESTIMATE_VERSION_REGISTRY which is populated by
  // hydrateEstimateVersions() as an async side-effect. Without this
  // dep, cold-load can race: dataLoaded / plansHydrated / allDeals all
  // settle first → effect fires with an empty registry → baseV is null
  // → setActiveVersion(null) → module-level SEASON_RANGE_LABEL and
  // ESTIMATE_DB fall back to their initial '4Q25-3Q26' / {} defaults
  // → All Plans landing view renders HH cols as "—", TOTAL (000S) as 0,
  // and section labels as "ABC 4Q25-3Q26 TOTAL" regardless of season.
  // The effect never re-fires (because estimatesHydrated wasn't a dep)
  // so the busted state persists until the user clicks into a specific
  // plan, which flips planFilter and forces a re-run. Adding this dep
  // makes the effect re-run the moment estimates finish loading.

  // Synchronously inject the version's data into the module-level refs.
  // Has to happen during render (before any helper is called) so the
  // FIRST render after a version change uses the right tables, not the
  // previous one. Idempotent and side-effect-only — no state writes.
  setActiveEstimates(activeVersion);

  // ─── Season toggle: reset on plan change + cancel rename on season swap ─────
  //
  // No persistence (decision: a reload always lands on 26/27 view).
  // When the user changes plans, force back to 26/27 so the toggle
  // never carries state across plans.
  useEffect(() => {
    setSeasonView('26/27');
  }, [planFilter]);
  // Seed this plan's extra-quarter count whenever the user navigates
  // to a different plan. Source-of-truth precedence:
  //   1. The saved plan's latest version on Snowflake (true persisted
  //      value — visible to teammates / across devices).
  //   2. localStorage fallback (covers unsaved manual drafts the user
  //      has been editing pre-first-save, plus a defensive fallback
  //      for Snowflake-saved plans that pre-date the column).
  //   3. Zero (no extras) when nothing's been stored anywhere.
  // Writes happen inline in the add/remove handlers; this is the
  // read path only.
  useEffect(() => {
    if (!planFilter) {
      setExtraQuartersState(0);
      return;
    }
    const saved = getSavedPlanByName(planFilter);
    const fromSaved = saved?.latestVersion?.extraQuartersCount;
    if (typeof fromSaved === 'number') {
      setExtraQuartersState(Math.max(0, fromSaved));
      return;
    }
    setExtraQuartersState(getExtraQuartersCount(planFilter));
  }, [planFilter, plansHydrated, bindingsTick]);

  // Secondary demos follow the selected plan.
  //
  // These used to live in component state with no hydrate at all, so
  // adding a demo and refreshing silently lost it — and switching plans
  // carried one plan's demo columns onto another. Now read from the saved
  // version, and cleared when no plan is selected (All Plans has no single
  // set of secondary demos to show).
  useEffect(() => {
    if (!planFilter) {
      setSecondaryDemos([]);
      return;
    }
    const saved = getSavedPlanByName(planFilter);
    const fromSaved = saved?.latestVersion?.secondaryDemos;
    setSecondaryDemos(Array.isArray(fromSaved) ? fromSaved : []);
  }, [planFilter, plansHydrated, bindingsTick]);
  // Cancel any in-progress plan-name edit on season swap — the rename
  // input belongs to the 26/27 view; if the user flipped to 25/26 mid-
  // edit we'd otherwise leave the editor mounted with stale state.
  // (Quarter-tab remapping is handled separately by the activeVersion

  // ─── Tab-position preservation across seasons ───────────────────────────────
  //
  // QUARTER_TABS keys differ between seasons ("4Q'25" vs "4Q'26", etc),
  // but each tab carries a stable `.quarter` field ("Q1".."Q4"). To
  // keep the user on the same broadcast quarter when they flip seasons
  // (or switch to a plan bound to a different estimate version), we
  // track the "kind" of the active tab (Summary or Q1-Q4) in a ref,
  // then remap to the equivalent tab key in the new QUARTER_TABS
  // whenever the active estimate version changes.
  const activeQuarterKindRef = useRef<string>('Summary');
  /**
   * Calendar year of the active tab, tracked alongside the kind above.
   *
   * ⚠ The kind alone ("Q1".."Q4") stopped being a unique identifier when a
   * version gained the ability to carry a LATE QUARTER: a merged 26/27 version
   * holds both 4Q'26 and 4Q'27, so `QUARTER_TABS.find(t => t.quarter === 'Q4')`
   * matches 4Q'26 first and silently drags a user sitting on 4Q'27 back to the
   * wrong tab. Matching on (quarter, year) disambiguates; the remap effect
   * falls back to kind-only so the cross-SEASON behaviour this ref exists for
   * (4Q'25 → 4Q'26 when flipping seasons) still works, since the years
   * deliberately differ there.
   */
  const activeQuarterYearRef = useRef<number | null>(null);

  /**
   * Per-plan quarter tabs. Driven by the active plan's Marketplace
   * (BY Upfront / CY Upfront / Scatter — mirrors SF
   * SALES_PLAN__C.Upfront_Scatter__c). See buildVisibleQuarters() for
   * the layout per marketplace. On the All Plans landing (no
   * planFilter), surfaces the synthetic 5-quarter window stamped by
   * the version-resolve useEffect via buildAllPlansVersion — that
   * window unions BY + CY quarters for the active season tab so both
   * marketplace types fit in one view.
   *
   * Declared here (above the useEffects that consume it) rather than
   * next to filteredDeals because those useEffects need to read
   * `visibleQuarters` in their dependency arrays — block-scoped order
   * matters for `let`/`const` references inside the same scope.
   *
   * Deps:
   *   - planFilter → drives which plan's meta we read
   *   - activeVersion → supplies versionQuarters + primaryYear; without
   *     it we can't compute a meaningful pattern
   *   - plansHydrated → Snowflake-saved plans appear only after
   *     hydrate; before that getPlanMeta falls back to null and we'd
   *     mis-resolve to the default BY layout
   */
  const visibleQuarters = useMemo<QuarterTab[]>(() => {
    if (!planFilter) {
      // All Plans landing — show the synthetic 5-quarter window from
      // the merged All Plans version (4Q'25–4Q'26 for the 25/26 tab,
      // 4Q'26–4Q'27 for the 26/27 tab). The version-resolve useEffect
      // above stamps these onto activeVersion.quarters. Falls back to
      // the legacy hardcoded QUARTER_TABS only during the initial paint
      // before activeVersion is set.
      return activeVersion?.quarters ?? QUARTER_TABS;
    }
    const meta = getPlanMeta(planFilter);
    const base = buildVisibleQuarters(
      meta?.marketplace,
      activeVersion?.quarters ?? QUARTER_TABS,
      activeVersion?.primaryYear ?? null,
    );
    // Append extra user-added quarters past the marketplace pattern.
    // Each successive call to nextQuarterAfter walks Q1→Q2→Q3→Q4→
    // Q1-of-next-year so the appended tabs always continue the
    // chronological sequence from the current last tab.
    if (extraQuarters > 0 && base.length > 0) {
      const out = [...base];
      for (let i = 0; i < extraQuarters; i++) {
        out.push(nextQuarterAfter(out[out.length - 1]));
      }
      return out;
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, activeVersion, plansHydrated, extraQuarters]);

  /**
   * Set of tab keys that are part of the plan's visibleQuarters but
   * NOT covered by the active estimate version. These tabs render
   * normally (the user can still enter Units / CPM / Rate), but every
   * estimate-derived cell — RTG, (000), CPM HH, VPVH — renders blank
   * because there's no row in ESTIMATE_DB to look up.
   *
   * Drives two UI affordances:
   *   1. ⚠ icon next to the tab label in the tab strip
   *   2. Full-width banner above the data table when the active tab is
   *      missing estimates
   *
   * Common case: a CY or Scatter plan when the current estimate file
   * still only covers the BY window (4Q'26–3Q'27). 4Q'27 lives in
   * visibleQuarters but won't have ESTIMATE_DB entries until MVMS
   * uploads the next file.
   */
  const missingEstimateKeys = useMemo<Set<string>>(() => {
    const versionKeys = new Set((activeVersion?.quarters ?? []).map(q => q.key));
    const missing = new Set<string>();
    for (const t of visibleQuarters) {
      if (!versionKeys.has(t.key)) missing.add(t.key);
    }
    return missing;
  }, [visibleQuarters, activeVersion]);

  // Keep the kind ref in sync whenever the user clicks a tab. Reads
  // against the CURRENT QUARTER_TABS so the kind is always recorded
  // relative to the season that was active at click time.
  useEffect(() => {
    if (activeQuarter === SUMMARY_TAB_KEY) {
      activeQuarterKindRef.current = 'Summary';
      return;
    }
    // Look in visibleQuarters first so synthesized tabs (e.g., 4Q'27 on
    // a CY/Scatter plan when the estimate file doesn't cover that
    // quarter yet) still record their kind correctly. Fall back to the
    // full QUARTER_TABS as a defensive last resort.
    const tab = visibleQuarters.find(t => t.key === activeQuarter)
             || QUARTER_TABS.find(t => t.key === activeQuarter);
    if (tab) {
      activeQuarterKindRef.current = tab.quarter;
      // Year too — see the remap effect below. "Q4" alone stopped being a
      // unique identifier once a version could carry 4Q'26 AND 4Q'27.
      activeQuarterYearRef.current = tab.year;
    }
  }, [activeQuarter, visibleQuarters]);

  // Remap activeQuarter to the same kind in the new QUARTER_TABS
  // whenever activeVersion changes. activeVersion is what drives the
  // QUARTER_TABS rebuild via setActiveEstimates during render, so
  // depending on it (not seasonView) ensures we run AFTER the new
  // season's tab list is in place — the kind→key lookup hits the
  // right set. Same-season activeVersion changes (e.g., a new estimate
  // upload) are no-ops because the .quarter values stay stable.
  useEffect(() => {
    const kind = activeQuarterKindRef.current;
    if (kind === 'Summary') {
      if (activeQuarter !== SUMMARY_TAB_KEY) setActiveQuarter(SUMMARY_TAB_KEY);
      return;
    }
    // Exact (quarter, year) match first. A merged version carries TWO Q4
    // entries — 4Q'26 and 4Q'27 — and a kind-only find returns 4Q'26, which
    // would yank the user off the 4Q'27 tab every time this effect ran.
    //
    // Kind-only is still the fallback, and is what does the real work here:
    // when the SEASON changes the years deliberately don't match (4Q'25 →
    // 4Q'26), and keeping the user on the same broadcast quarter across that
    // switch is the whole reason this ref exists.
    const year = activeQuarterYearRef.current;
    const newTab =
      (year != null
        ? QUARTER_TABS.find(t => t.quarter === kind && t.year === year)
        : undefined)
      ?? QUARTER_TABS.find(t => t.quarter === kind);
    const newKey = newTab?.key ?? SUMMARY_TAB_KEY;
    if (activeQuarter !== newKey) setActiveQuarter(newKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersion]);

  // Keep activeQuarter in sync with visibleQuarters. When the user
  // switches plans (or a plan's marketplace changes), the previously-
  // selected tab key may no longer correspond to a visible quarter —
  // e.g., switching from a BY plan (active 4Q'26) to a CY plan
  // (1Q'27-4Q'27, no 4Q'26). Without this effect, the tab strip would
  // render with nothing highlighted and filters keyed off activeQuarter
  // would silently return empty. Falling back to Summary preserves the
  // safest user-visible state.
  useEffect(() => {
    if (activeQuarter === SUMMARY_TAB_KEY) return;
    const stillVisible = visibleQuarters.some(t => t.key === activeQuarter);
    if (!stillVisible) setActiveQuarter(SUMMARY_TAB_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleQuarters]);

  // ─── Derived state ──────────────────────────────────────────────────────────

  /**
   * True when the currently-selected plan is an SF source plan (25/26
   * inventory) — every editing affordance is gated on this flag.
   *
   * Classification matches `planOptions`: anything that lives in the
   * Snowflake save cache is a 26/27 plan (editable). The remainder is
   * SF-only, which we render view-only.
   *
   * Returns `false` for "All Plans" (no plan selected) — that view has
   * its own read-mostly affordances and doesn't need the lockout.
   */
  const isViewOnly = useMemo(() => {
    if (!planFilter) return false;
    if (getSavedPlanByName(planFilter)) return false;
    return true;
    // planCacheVersion in deps: without it, this memo goes stale when a
    // new 26/27 plan is saved with the same nickname as its 25/26 source.
    // planFilter doesn't change (same string), but the plan cache now
    // has a saved-plan entry that would flip this memo to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, plansHydrated, planCacheVersion]);

  // ─── Season toggle derived state ────────────────────────────────────────────
  //
  // The SF ID of the 25/26 source plan this 26/27 plan was rolled
  // forward from. Read from the saved-plan cache (Snowflake record's
  // source_plan_sf_id). Null for plans without a source (created from
  // scratch, or pre-roll-forward).
  const sourcePlanSfId = useMemo<string | null>(() => {
    if (!planFilter) return null;
    const saved = getSavedPlanByName(planFilter);
    return saved?.sourcePlanSfId ?? null;
    // planCacheVersion in deps: same stale-memo fix as isViewOnly above.
    // When a new 26/27 plan with same nickname as its source is saved,
    // planFilter doesn't change but the cache now holds the plan with
    // its sourcePlanSfId — this memo needs to pick that up so the
    // season toggle appears and the 25/26 view resolves correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, plansHydrated, planCacheVersion]);

  // Look up the 25/26 SF source plan in allDeals by its SF id. We must
  // filter OUT Snowflake-saved plans because rolled-forward 26/27
  // deals carry the source's SF id on their salesPlanSfId
  // (savedPlanToDeals stamps it for traceability). The "source plan" we
  // want is the SF-only plan — i.e. the one whose name isn't in the
  // saved-plan registry. Returns the SF plan's name (used to filter
  // allDeals by salesPlan when rendering the 25/26 view), or null when
  // the source has been removed from SF since the 26/27 plan was
  // rolled forward.
  const sourcePlanInAllDeals = useMemo<string | null>(() => {
    if (!sourcePlanSfId) return null;
    // The 25/26 season quarter set — deals in these (quarter, year)
    // tuples are considered source-side SF inventory. This is how we
    // distinguish source SF deals from rolled 26/27 Snowflake deals
    // when both share `salesPlan` AND `salesPlanSfId` (which happens
    // when a rolled 26/27 plan keeps its source SF plan's nickname).
    //
    // Previous version skipped by `getSavedPlanByName(d.salesPlan)` —
    // that mis-fired when nicknames collided: a rolled plan whose
    // nickname matched its source would cause the loop to skip the
    // ACTUAL source SF deals (because their name matched the saved
    // plan), and the 25/26 tab would render "Source plan not found."
    for (const d of allDeals) {
      if (d.salesPlanSfId !== sourcePlanSfId) continue;
      if (!LAST_SEASON_QUARTER_KEYS.has(`${d.quarter}|${d.year}`)) continue;
      return d.salesPlan;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcePlanSfId, allDeals, plansHydrated]);

  // The season toggle is only visible for editable 26/27 plans that
  // have a sourcePlanSfId. Hides for: pure 25/26 SF plans (isViewOnly),
  // 26/27 plans created from scratch (no source), and the "All Plans"
  // landing view (no planFilter). When sourcePlanInAllDeals is null the
  // toggle still renders — clicking 25/26 lands on the "Source plan
  // not found" empty state inside the table area.
  const canShowSeasonToggle = !!planFilter && !isViewOnly && !!sourcePlanSfId;

  // Plan name used for filtering allDeals + driving the header display.
  // On the 25/26 toggle view this swaps to the source SF plan's name;
  // everywhere else it's the underlying planFilter. Falls back to a
  // sentinel that matches no deal when the source has been removed
  // from SF — the rendering layer detects this and shows the empty
  // state instead of a blank table.
  const effectivePlanFilter = (seasonView === '25/26' && canShowSeasonToggle)
    ? (sourcePlanInAllDeals ?? '__SOURCE_NOT_FOUND__')
    : planFilter;

  // "Is editing locked right now?" Used everywhere readOnly / button
  // disable / + Add gates were previously gated on isViewOnly. The
  // original isViewOnly stays around for SF-only messaging (the amber
  // banner, the "Order" status tooltip — neither applies to the
  // toggled-25/26 case where the message is implied by the toggle).
  //
  // The "All Plans" landing view (no plan selected) is also read-only:
  // there's no specific plan to attribute edits to, the Save button
  // would be inert anyway, and any state changes would be orphaned.
  // Treat it as view-only so the per-row inputs don't pretend they're
  // editable.
  // A plan that's been pushed to SF is terminal one-way and fully locked
  // (Save hidden, Add Row/Show hidden, cells read-only). Check the fresh
  // saved plan every render — cheaper than tracking a separate memo since
  // getSavedPlanByName is a hash lookup on the cache Map.
  const currentSavedPlanForLock = planFilter ? getSavedPlanByName(planFilter) : null;
  const isPushed = !!currentSavedPlanForLock?.pushedToSfAt;

  /**
   * Push-time SPN collision. When multiple 26/27 drafts share a Sales
   * Plan Name (the scenario-variant workflow — "250K / 500K / 800K" of
   * the same client), only ONE can end up as the SF Order at a time.
   * If any OTHER 26/27 plan with the same SPN as this one has already
   * been pushed, block THIS plan from being pushed too — the SF-side
   * uniqueness on `Sales_Plan__c.Name` would reject it, and even if
   * SF didn't, we'd end up with ambiguous inventory.
   *
   * Returns the nickname of the blocking pushed sibling (or null when
   * clear). Consumed by the Order-status option's disabled state so
   * the user sees the block before they can even attempt the push.
   *
   * Excludes the current plan itself so a pushed plan doesn't
   * "conflict with" its own record (defensive; the status pill
   * short-circuits to the read-only pushed badge earlier anyway).
   */
  const pushSpnConflictNickname = useMemo<string | null>(() => {
    const mySpn = currentSavedPlanForLock?.latestVersion?.salesPlanName;
    if (!mySpn) return null;
    for (const p of listSavedPlans()) {
      if (!p.pushedToSfAt) continue;
      if (currentSavedPlanForLock && p.id === currentSavedPlanForLock.id) continue;
      if (p.latestVersion?.salesPlanName === mySpn) return p.name;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSavedPlanForLock, plansHydrated, planCacheVersion]);

  /**
   * TBD placeholder gate. Plans that carry the literal "TBD" display
   * name on Advertiser / Brand / Agency / Buyer are intentional drafts
   * — the client hasn't committed to a real value yet — and cannot be
   * pushed to SF (SF's ID fields need real records, not placeholders).
   *
   * Runtime coercion: check display name === "TBD" (case-insensitive)
   * regardless of whether an SF id is still stored underneath. This
   * catches both the new-model plans (id=null, name="TBD") and any
   * legacy plans still linked to real SF "TBD" records that the
   * backfill script hasn't caught yet.
   *
   * Returns an array of the offending field labels so the tooltip can
   * name them ("Cannot push — Advertiser, Brand are TBD."); empty
   * array means clear to push.
   */
  const pushTbdFields = useMemo<string[]>(() => {
    const v = currentSavedPlanForLock?.latestVersion;
    if (!v) return [];
    const fields: string[] = [];
    const isTbd = (name: string | null | undefined) => (name ?? '').trim().toUpperCase() === 'TBD';
    if (isTbd(v.advertiser)) fields.push('Advertiser');
    if (isTbd(v.brand))      fields.push('Brand');
    if (isTbd(v.agency))     fields.push('Agency');
    if (isTbd(v.buyer))      fields.push('Buyer');
    return fields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSavedPlanForLock, plansHydrated, planCacheVersion]);

  // Auto-detect an in-flight post-order revision from the cache. If
  // the plan is pushed AND the latest version's key_type is NOT the
  // order itself (i.e., there's a plain save or a named bookmark
  // AFTER the order), someone was revising and didn't finalize. Enter
  // revising mode automatically so their session picks up where they
  // left off. A finalized order_update lands as the new latest —
  // key_type='order_update' — and this heuristic keeps the plan locked
  // (correct behavior post-finalize until the user starts revising again).
  const latestKeyType = currentSavedPlanForLock?.latestVersion?.keyType ?? null;
  const hasInflightPostOrderRevision =
    isPushed
    && latestKeyType !== 'order'
    && latestKeyType !== 'order_update'
    // ⚠ 'late_quarter' must be excluded or the whole feature inverts.
    //
    // A late-quarter save lands AFTER the order, which is exactly the shape
    // this heuristic reads as "someone started revising". Without this line,
    // adding a single 4Q'27 row to a pushed plan would silently unlock every
    // other tab on the next load — the opposite of the rule it is meant to
    // implement. This is why those saves carry their own key_type rather than
    // being plain untagged saves.
    && latestKeyType !== 'late_quarter';
  // Effective revising = user clicked Revise Order OR there's an
  // in-flight revision on record. Either way, we treat the plan as
  // editable (post-order lifecycle in progress).
  const isRevisingOrder = revisingOrderMode || hasInflightPostOrderRevision;

  // Historical-view lifecycle lock:
  //   • Viewing a PREVIOUS lifecycle (v1.x while latest is on v3) →
  //     everything read-only. User has to Return to latest to edit.
  //   • Viewing a CURRENT lifecycle revision (v3.1 while latest is
  //     v3.3) → editable; save creates a new revision on the
  //     current lifecycle. No lock.
  //   • Not viewing historical → no lock from this rule.
  const isViewingPreviousLifecycle = !!(
    viewingHistoricalRef
    && viewingHistoricalRef.planId === currentSavedPlanForLock?.id
    && viewingHistoricalRef.viewingMilestoneIndex < viewingHistoricalRef.currentLifecycleIndex
  );
  const isViewingCurrentLifecycleHistorical = !!(
    viewingHistoricalRef
    && viewingHistoricalRef.planId === currentSavedPlanForLock?.id
    && viewingHistoricalRef.viewingMilestoneIndex === viewingHistoricalRef.currentLifecycleIndex
  );

  /**
   * The plan's late quarter (4Q'27 on a 26/27 plan), when its active estimate
   * has one. Drives the pushed-plan lock exception below and the save-time pin.
   */
  const planLateQuarter = useMemo(
    () => lateQuarterOf(activeVersion),
    [activeVersion],
  );

  /**
   * Is the user currently ON the late-quarter tab?
   *
   * This is what makes the pushed-plan lock exception a ONE-LINE change rather
   * than a refactor of all ~40 `planIsViewOnly` call sites. The grid is already
   * per-tab: `filteredDeals` narrows rows with
   * `d.quarter === tab.quarter && d.year === tab.year`, and the only
   * cross-quarter editor, `renderCpmInputGrid('summary')`, renders solely on
   * the Summary tab. So the lock flag doesn't need to become a per-quarter
   * predicate — it just needs to know which tab is active, and it is
   * recomputed every render anyway.
   */
  const isOnLateQuarterTab = !!planLateQuarter
    && activeQuarter === planLateQuarter.key;

  const planIsViewOnly = !planFilter
    || isViewOnly
    // Pushed plans lock EXCEPT when the user is actively revising the
    // order (an editable post-order lifecycle is in progress). The
    // revised state finalizes via Name & Export → order_update, at
    // which point isRevisingOrder becomes false and the plan re-locks.
    //
    // …and EXCEPT on the late-quarter tab. 4Q'27 was never part of what was
    // sent to Salesforce — its estimates didn't exist when the order was
    // pushed — so freezing it would block the exact workflow the late-quarter
    // feature exists to enable. Every other tab stays locked.
    //
    // Note what this does NOT except: `isViewingPreviousLifecycle` and the
    // 25/26 season toggle below. Those are historical-VIEWING locks, not order
    // locks, and they keep everything read-only including the late quarter.
    || (isPushed && !isRevisingOrder && !isOnLateQuarterTab)
    || isViewingPreviousLifecycle
    || (seasonView === '25/26' && canShowSeasonToggle);

  /**
   * True when edits are allowed ONLY in the late quarter — a pushed plan that
   * isn't mid-revision, sitting on its 4Q tab.
   *
   * Distinct from `!planIsViewOnly`: it identifies the constrained state, so
   * the cross-quarter affordances (+ Add Show's all-quarters default) can
   * narrow themselves, and so the save can tag itself for the backend.
   */
  const isLateQuarterOnlyEditing = isPushed && !isRevisingOrder && isOnLateQuarterTab;

  /**
   * May the user click "+ Add Quarter" right now?
   *
   * Normally this is just "the plan is editable". The exception exists because
   * of a gap the tab-scoped lock creates on its own:
   *
   *   A BY Upfront plan's pattern ends at 3Q'27, so its 4Q'27 tab doesn't
   *   exist until someone adds it. On a PUSHED BY plan, `isOnLateQuarterTab`
   *   can therefore never be true — there is no such tab to stand on — so
   *   `planIsViewOnly` stays true, "+ Add Quarter" stays hidden, and the
   *   plan can never reach the quarter this whole feature is about.
   *
   * So a pushed plan may add a quarter, but ONLY when the quarter it would
   * append is the late quarter. `nextQuarterAfter` is mechanical — press it
   * again and you get 1Q'28 — and appending that to a sent order is exactly
   * the structural change the lock is there to prevent.
   */
  const canAddQuarter = (() => {
    if (!planIsViewOnly) return true;
    if (!isPushed || isRevisingOrder || !planLateQuarter) return false;
    const last = visibleQuarters[visibleQuarters.length - 1];
    if (!last) return false;
    return nextQuarterAfter(last).key === planLateQuarter.key;
  })();

  // ─── Historical-view diff highlighting ─────────────────────────────
  //
  // When the user is viewing a past version (viewingHistoricalRef set)
  // and we have the pre-overlay latest snapshot (historicalDiffBaseline),
  // compute a per-cell diff so the grid can highlight cells that
  // differ from the actual latest.
  //
  // Row identity across versions uses the natural key
  //   (program, quarter, year, unitLength, demo)
  // because PLAN_DEAL row IDs are fresh per version — a deal in v1.2
  // and the "same" deal in latest share these five attributes even
  // though their dealKey differs.
  //
  // Field-level diff covers ONLY source-of-truth user-editable fields:
  // cpm, rate (via newRate|rate), totalUnits, overrideDemo000,
  // totalDollars. Derived cells (RTG, HH, VPVH, etc.) recompute from
  // the estimate and highlighting them would be redundant noise.
  type DiffField = 'cpm' | 'rate' | 'totalUnits' | 'overrideDemo000' | 'totalDollars';
  interface RowDiff {
    fields: Map<DiffField, { historical: number | null; latest: number | null }>;
  }
  const historicalDiff = useMemo(() => {
    if (!viewingHistoricalRef || !historicalDiffBaseline || !planFilter) {
      return null;
    }
    // Natural-key builder — mirrors the same tuple both sides use.
    const nk = (r: { program: string; quarter: string; year: number; unitLength: string | number; demo?: string | null }) =>
      `${r.program}|${r.quarter}|${r.year}|${String(r.unitLength ?? '')}|${r.demo ?? ''}`;
    // Latest side — from the pre-overlay snapshot.
    const latestByKey = new Map<string, PlanDeal>();
    for (const pd of historicalDiffBaseline.latestDeals) {
      latestByKey.set(nk({
        program: pd.program,
        quarter: pd.quarter,
        year: pd.year,
        unitLength: pd.unitLength ?? '',
        demo: pd.demo,
      }), pd);
    }
    // Historical side — from allDeals (currently overlaid with the
    // picked version's rows for this plan).
    const historicalDeals = allDeals.filter(d => d.salesPlan === planFilter);
    const historicalKeys = new Set<string>();
    const diffedByKey = new Map<string, RowDiff>();
    let totalChangedCells = 0;
    // Small helper — treat null/undefined as "empty" and use an
    // epsilon compare for floats so a saved 5.00 vs 5 doesn't flag.
    const differs = (a: number | null | undefined, b: number | null | undefined): boolean => {
      const aa = a == null ? null : Number(a);
      const bb = b == null ? null : Number(b);
      if (aa === null && bb === null) return false;
      if (aa === null || bb === null) return true;
      return Math.abs(aa - bb) > 0.005;
    };
    for (const d of historicalDeals) {
      const key = nk({
        program: d.program,
        quarter: d.quarter,
        year: d.year,
        unitLength: d.unitLength ?? '',
        demo: d.demo,
      });
      historicalKeys.add(key);
      const latest = latestByKey.get(key);
      if (!latest) {
        // Row exists in historical but not in latest — full-row
        // "removed later" highlight. Encoded by marking every source
        // field as changed (with latest=null). Rare in practice.
        const fields = new Map<DiffField, { historical: number | null; latest: number | null }>();
        fields.set('cpm', { historical: d.cpm ?? null, latest: null });
        fields.set('rate', { historical: (d.newRate ?? d.rate) ?? null, latest: null });
        fields.set('totalUnits', { historical: d.totalUnits ?? null, latest: null });
        fields.set('overrideDemo000', { historical: d.overrideDemo000 ?? null, latest: null });
        fields.set('totalDollars', { historical: d.totalDollars ?? null, latest: null });
        diffedByKey.set(key, { fields });
        totalChangedCells += fields.size;
        continue;
      }
      // Row exists in both — compare each source-of-truth field.
      const fields = new Map<DiffField, { historical: number | null; latest: number | null }>();
      const histRate = (d.newRate ?? d.rate) ?? null;
      const latestRate = latest.rate ?? null;
      if (differs(d.cpm, latest.cpm)) {
        fields.set('cpm', { historical: d.cpm ?? null, latest: latest.cpm ?? null });
      }
      if (differs(histRate, latestRate)) {
        fields.set('rate', { historical: histRate, latest: latestRate });
      }
      if (differs(d.totalUnits, latest.totalUnits)) {
        fields.set('totalUnits', { historical: d.totalUnits ?? null, latest: latest.totalUnits ?? null });
      }
      if (differs(d.overrideDemo000, latest.impressions_000)) {
        fields.set('overrideDemo000', { historical: d.overrideDemo000 ?? null, latest: latest.impressions_000 ?? null });
      }
      if (differs(d.totalDollars, latest.totalDollars)) {
        fields.set('totalDollars', { historical: d.totalDollars ?? null, latest: latest.totalDollars ?? null });
      }
      if (fields.size > 0) {
        diffedByKey.set(key, { fields });
        totalChangedCells += fields.size;
      }
    }
    // Rows that exist in latest but NOT in historical — added later.
    // Grid doesn't render them (historical view is "what existed at
    // that time"), but the footer surfaces the list for context.
    const addedInLatest: PlanDeal[] = [];
    for (const pd of historicalDiffBaseline.latestDeals) {
      const key = nk({
        program: pd.program,
        quarter: pd.quarter,
        year: pd.year,
        unitLength: pd.unitLength ?? '',
        demo: pd.demo,
      });
      if (!historicalKeys.has(key)) addedInLatest.push(pd);
    }
    return {
      diffedByKey,
      totalChangedCells,
      addedInLatest,
      naturalKeyFor: nk,
    };
  }, [viewingHistoricalRef, historicalDiffBaseline, allDeals, planFilter]);

  /**
   * Amber highlight style for a single diffed `<td>`. Applied to the
   * cell background so the input's own styling (green units, yellow
   * demo, orange CPM) shows through with an amber tint behind. Also
   * sets `title` via a helper for the hover tooltip.
   */
  const diffCellBg: React.CSSProperties = {
    background: '#fef3c7',
    boxShadow: 'inset 0 0 0 1.5px #f59e0b',
  };
  const formatDiffValue = (v: number | null | undefined): string => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return String(v);
  };

  const filteredDeals = useMemo<Deal[]>(() => {
    // Tabs only narrow data when (a) a plan is selected, and
    // (b) the active tab is one of the quarter tabs (not Summary).
    // In the all-plans view OR when on the Summary tab, no quarter filter.
    // Uses `effectivePlanFilter` so the 25/26 toggle view shows the
    // source SF plan's deals (resolved against the 25/26 estimate via
    // the resolution useEffect). On '__SOURCE_NOT_FOUND__' no deal
    // matches, the table renders empty, and the rendering layer
    // surfaces the "Source plan not found" empty state.
    const isSummary = activeQuarter === SUMMARY_TAB_KEY;
    const isAllPlans = !effectivePlanFilter;
    // Look in visibleQuarters so synthesized tabs (e.g., 4Q'27 on a
    // CY/Scatter plan when the estimate file doesn't cover it) still
    // resolve their (quarter, year) for filtering. Apply tab narrowing
    // for both in-plan and All Plans views.
    const tab = !isSummary
      ? (visibleQuarters.find(t => t.key === activeQuarter) || QUARTER_TABS.find(t => t.key === activeQuarter))
      : null;
    // All Plans view: narrow rows to the active season's plan set.
    // Same plan-metadata classification as the dropdown badges —
    // saved plans are 26/27, everything else (raw SF source) is 25/26.
    // Built inline (rather than reading planOptions) because
    // planOptions is declared LATER in this function body and would
    // be in TDZ at the time this hook's deps array is constructed.
    let allPlansSet: Set<string> | null = null;
    if (isAllPlans) {
      const isCurrent = allPlansSeason === '26/27';
      const savedNames = new Set(listSavedPlans().map(p => p.name));
      const currentNames = new Set<string>();
      savedNames.forEach(n => currentNames.add(n));
      if (isCurrent) {
        allPlansSet = currentNames;
      } else {
        // 25/26 view: every plan present in allDeals that is NOT a
        // current-season plan. Mirrors planOptions' "else 25/26" arm.
        const sfNames = new Set<string>();
        for (const d of allDeals) {
          if (!currentNames.has(d.salesPlan)) sfNames.add(d.salesPlan);
        }
        allPlansSet = sfNames;
      }
    }
    // Search no longer filters table rows — it drives the plan-name
    // picker popup instead (`planSearchTerm` + the search picker below
    // the input). Removing it from the row filter keeps the in-plan
    // view stable while the user types to navigate to another plan.
    //
    // Season narrowing: when a specific plan is selected and no
    // specific quarter tab is active (Summary tab), restrict to
    // deals that fall in the plan's currently-viewed visibleQuarters.
    // Without this, if a rolled 26/27 plan and its 25/26 SF source
    // share a nickname, `allDeals` contains both — and totals/counts
    // would double. visibleQuarters is per-plan, per-season, so the
    // check discriminates cleanly:
    //   26/27 view → 26/27 quarters only → rolled deals only
    //   25/26 view → 25/26 quarters only → source SF deals only
    // Quarter-tab-active views already narrow correctly via `tab` above.
    const visibleQuarterKeys = effectivePlanFilter && !tab
      ? new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`))
      : null;
    // Season-scope filter for All Plans view: nickname-only allPlansSet
    // is not enough when a rolled 26/27 plan shares its nickname with
    // its 25/26 SF source — deals from both seasons match the name and
    // leak into a single section. Narrow to the currently-viewed
    // season's quarter set so the two lines of deals separate cleanly.
    const allPlansSeasonKeys = isAllPlans
      ? (allPlansSeason === '26/27'
          ? CURRENT_SEASON_QUARTER_KEYS
          : LAST_SEASON_QUARTER_KEYS)
      : null;
    let deals = allDeals.filter(d =>
      (!tab || (d.quarter === tab.quarter && d.year === tab.year)) &&
      (!allPlansSet || allPlansSet.has(d.salesPlan)) &&
      (!effectivePlanFilter || d.salesPlan === effectivePlanFilter) &&
      (!visibleQuarterKeys || visibleQuarterKeys.has(`${d.quarter}|${d.year}`)) &&
      (!allPlansSeasonKeys || allPlansSeasonKeys.has(`${d.quarter}|${d.year}`))
    );
    deals = [...deals].sort((a, b) => {
      const key = sortCol as keyof Deal;
      if (sortCol === 'program' || sortCol === 'salesPlan') {
        return String(a[key]).localeCompare(String(b[key])) * sortDir;
      }
      return (Number(a[key]) - Number(b[key])) * sortDir;
    });
    return deals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeals, activeQuarter, effectivePlanFilter, sortCol, sortDir, visibleQuarters, allPlansSeason, plansHydrated, planCacheVersion]);

  /**
   * Same scope filter as `filteredDeals` but applied to `addedRows`.
   * Used by the top-of-page stat cards and the bottom totals row so
   * brand-new manual rows (the "+ Add Show" / "+ Add Row" path)
   * actually move the headline numbers. Without this, the user adds
   * a row, sees the per-row + subtotal numbers update, but the top
   * stats + bottom totals stay at zero — confusing because the data
   * is clearly there in the table.
   */
  const filteredAddedRows = useMemo<AddedRow[]>(() => {
    const isSummary = activeQuarter === SUMMARY_TAB_KEY;
    const isAllPlans = !effectivePlanFilter;
    const tab = !isSummary
      ? (visibleQuarters.find(t => t.key === activeQuarter) || QUARTER_TABS.find(t => t.key === activeQuarter))
      : null;
    // Same plan-metadata classification as filteredDeals — keeps the
    // bottom totals + stat cards in sync with the row list above.
    // Inlined for the same TDZ reason filteredDeals does.
    let allPlansSet: Set<string> | null = null;
    if (isAllPlans) {
      const isCurrent = allPlansSeason === '26/27';
      const currentNames = new Set<string>();
      listSavedPlans().forEach(p => currentNames.add(p.name));
      if (isCurrent) {
        allPlansSet = currentNames;
      } else {
        const sfNames = new Set<string>();
        for (const d of allDeals) {
          if (!currentNames.has(d.salesPlan)) sfNames.add(d.salesPlan);
        }
        allPlansSet = sfNames;
      }
    }
    // Same season-scope filter as filteredDeals — keeps AddedRows
    // (in-progress new rows the user hasn't saved yet) out of the wrong
    // season's All Plans view when a nickname collides.
    const allPlansSeasonKeys = isAllPlans
      ? (allPlansSeason === '26/27'
          ? CURRENT_SEASON_QUARTER_KEYS
          : LAST_SEASON_QUARTER_KEYS)
      : null;
    return addedRows.filter(r =>
      (!tab || (r.quarter === tab.quarter && r.year === tab.year)) &&
      (!allPlansSet || allPlansSet.has(r.salesPlan)) &&
      (!effectivePlanFilter || r.salesPlan === effectivePlanFilter) &&
      (!allPlansSeasonKeys || allPlansSeasonKeys.has(`${r.quarter}|${r.year}`))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedRows, activeQuarter, effectivePlanFilter, visibleQuarters, allPlansSeason, plansHydrated, allDeals, planCacheVersion]);

  /**
   * SF-grain Deal count for the currently-viewed plan.
   *
   * MPB tracks each schedule row as its own PLAN_DEAL, but on push
   * Salesforce collapses rows sharing (Program, Length, Quarter, Year)
   * into a single Deal__c parent with one Schedules__c child per row.
   * Users need to see the SF Deal count in the toolbar / stat card so
   * "N deals" matches what they'll see in SF's Deals related list after
   * push (14 schedules can land as 8 Deals if some P/L/Q/Y tuples
   * repeat). Same grouping key as the backend push (push-to-sf.ts).
   */
  const filteredDealGroupCount = useMemo<number>(() => {
    const groups = new Set<string>();
    for (const d of filteredDeals) {
      groups.add(`${d.program}|${d.unitLength}|${d.quarter}|${d.year}`);
    }
    for (const r of filteredAddedRows) {
      if (r.program && r.unitLength) {
        groups.add(`${r.program}|${r.unitLength}|${r.quarter}|${r.year}`);
      }
    }
    return groups.size;
  }, [filteredDeals, filteredAddedRows]);

  // Duplicate-row detection for the currently-viewed plan.
  //
  // Salesforce's Schedules__c.Duplicate_Check__c formula rejects two
  // records sharing the same (Program, Length, Quarter, Year, Primary
  // Demo (000), CPM, Rate) tuple. When the user hits Push to SF with
  // duplicates in the plan, SF returns a DUPLICATE_VALUE error and the
  // push aborts (Composite Tree is atomic). We surface those dupes
  // BEFORE push so the user can fix them locally — red row highlight,
  // warning banner, and Push button disable.
  //
  // Scope: current plan's deals + AddedRows, restricted to visibleQuarters
  // so a rolled 26/27 plan's 25/26 source deals (via nickname
  // consolidation) don't accidentally count as intra-plan dupes.
  const duplicateRowKeys = useMemo<Set<string>>(() => {
    if (!planFilter || planIsViewOnly) return new Set();
    // Scope to the late quarter on a pushed Order. Two reasons:
    //   • `planIsViewOnly` is now tab-dependent, so without this the dupe
    //     banner and red highlights would blink on and off as the user moves
    //     between tabs on a pushed plan.
    //   • It would flag duplicates sitting in LOCKED quarters, which the user
    //     has no editable cell anywhere to resolve.
    const scopeQuarters = (isPushed && !isRevisingOrder && planLateQuarter)
      ? [planLateQuarter]
      : visibleQuarters;
    const visibleKeys = new Set(scopeQuarters.map(t => `${t.quarter}|${t.year}`));
    // Composite key mirroring SF's Duplicate_Check__c inputs. Round
    // demo000 to whole thousands to match SF's stamped value (avoids
    // float-precision false-negatives for effectively-equal impressions).
    const dupeKey = (
      program: string,
      unitLength: string | number,
      quarter: string,
      year: number,
      demo000: number | null,
      cpm: number | null,
      rate: number | null,
    ) => [
      program,
      String(unitLength),
      quarter,
      year,
      demo000 != null ? Math.round(demo000) : '',
      cpm != null ? cpm.toFixed(2) : '',
      rate != null ? Math.round(rate) : '',
    ].join('|');
    const counts = new Map<string, number>();
    const bump = (k: string) => counts.set(k, (counts.get(k) ?? 0) + 1);
    for (const d of allDeals) {
      if (d.salesPlan !== planFilter) continue;
      if (!visibleKeys.has(`${d.quarter}|${d.year}`)) continue;
      if (!(d.totalUnits > 0)) continue;
      const est = calcEstimateFields(d);
      const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
      bump(dupeKey(d.program, d.unitLength, d.quarter, d.year, effDemo, d.cpm, d.newRate ?? d.baseRate ?? null));
    }
    for (const r of addedRows) {
      if (r.salesPlan !== planFilter) continue;
      if (!visibleKeys.has(`${r.quarter}|${r.year}`)) continue;
      if (!r.program || !r.unitLength || !(r.totalUnits > 0)) continue;
      const est = calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      });
      const effDemo = getEffectiveDemo000(r, est);
      bump(dupeKey(r.program, r.unitLength, r.quarter, r.year, effDemo, r.cpm ?? null, r.newRate ?? null));
    }
    const dupes = new Set<string>();
    counts.forEach((n, k) => { if (n > 1) dupes.add(k); });
    return dupes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, planIsViewOnly, visibleQuarters, allDeals, addedRows, planCacheVersion]);

  // Helper the row renderers use to answer "is THIS row a dupe?".
  // Kept as a plain function (closes over duplicateRowKeys) so it can be
  // called inline from renderDealRow without an extra memo per row.
  function isDealDuplicate(d: Deal): boolean {
    if (duplicateRowKeys.size === 0) return false;
    if (!(d.totalUnits > 0)) return false;
    const est = calcEstimateFields(d);
    const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
    const k = [
      d.program,
      String(d.unitLength),
      d.quarter,
      d.year,
      effDemo != null ? Math.round(effDemo) : '',
      d.cpm != null ? d.cpm.toFixed(2) : '',
      (d.newRate ?? d.baseRate ?? null) != null ? Math.round(d.newRate ?? d.baseRate ?? 0) : '',
    ].join('|');
    return duplicateRowKeys.has(k);
  }
  function isAddedRowDuplicate(r: AddedRow): boolean {
    if (duplicateRowKeys.size === 0) return false;
    if (!r.program || !r.unitLength || !(r.totalUnits > 0)) return false;
    const est = calcEstimateFields({
      program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '',
      unitLength: r.unitLength, totalUnits: r.totalUnits,
    });
    const effDemo = getEffectiveDemo000(r, est);
    const k = [
      r.program,
      String(r.unitLength),
      r.quarter,
      r.year,
      effDemo != null ? Math.round(effDemo) : '',
      r.cpm != null ? r.cpm.toFixed(2) : '',
      r.newRate != null ? Math.round(r.newRate) : '',
    ].join('|');
    return duplicateRowKeys.has(k);
  }

  // Plan dropdown shows ALL plans across both seasons (no tab filter).
  // Each entry carries the season it belongs to so the dropdown can
  // surface a [25/26] / [26/27] prefix and the rest of the app can
  // route to the right view (view-only vs editable).
  //
  // Classification:
  //   - Snowflake-saved plans         → 26/27 (current season)
  //   - Everything else (SF source)   → 25/26 (last season, view-only)
  //
  // Sorted: current season first, then alphabetical within each season.
  // This puts the editable plans the user is more likely to want at
  // the top of the list, with the 25/26 reference inventory below.
  //
  // `plansHydrated` is in the deps so the dropdown updates the moment
  // the plan cache finishes loading on initial mount.
  const planOptions = useMemo<Array<{ name: string; year: number }>>(() => {
    const savedNames = new Set<string>();
    // Track which 25/26 SF sources have already been rolled forward
    // into a saved 26/27 plan (regardless of whether their nicknames
    // match). Any source in this set gets hidden from the picker — the
    // user reaches its 25/26 view via the 26/27 plan's season toggle,
    // not via a separate dropdown entry that duplicates the same source.
    // Prior behavior only consolidated when nicknames matched, leaving
    // stray 25/26 entries in the picker when the user renamed after
    // roll-forward.
    const rolledForwardSourceIds = new Set<string>();
    listSavedPlans().forEach(p => {
      savedNames.add(p.name);
      if (p.sourcePlanSfId) rolledForwardSourceIds.add(p.sourcePlanSfId);
    });
    // Map source-name → its SF id (first deal wins; all deals of one
    // source share the same salesPlanSfId).
    const sourceSfIdByName = new Map<string, string | null>();
    for (const d of allDeals) {
      if (!sourceSfIdByName.has(d.salesPlan)) {
        sourceSfIdByName.set(d.salesPlan, d.salesPlanSfId ?? null);
      }
    }
    const all = new Set<string>();
    allDeals.forEach(d => {
      // Skip 25/26 SF sources whose SF id was rolled forward — the
      // rolled 26/27 plan is the canonical entry.
      const sfId = d.salesPlanSfId;
      if (sfId && rolledForwardSourceIds.has(sfId) && !savedNames.has(d.salesPlan)) {
        return;
      }
      all.add(d.salesPlan);
    });
    savedNames.forEach(n => all.add(n));

    const classify = (name: string): number =>
      savedNames.has(name)
        ? CURRENT_SEASON_PRIMARY_YEAR
        : LAST_SEASON_PRIMARY_YEAR;

    const list: Array<{ name: string; year: number }> = [];
    all.forEach(name => list.push({ name, year: classify(name) }));
    list.sort((a, b) => {
      // Current season (higher year) first.
      if (a.year !== b.year) return b.year - a.year;
      return a.name.localeCompare(b.name);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeals, plansHydrated, planCacheVersion]);

  /**
   * Human-readable season label for the year a plan belongs to.
   * Hardcoded today (only two seasons in the system); future-friendly
   * because the dropdown calls this per-row and never depends on the
   * relative ordering of seasons elsewhere.
   */
  function seasonBadge(year: number): string {
    if (year === CURRENT_SEASON_PRIMARY_YEAR) return '26/27';
    if (year === LAST_SEASON_PRIMARY_YEAR) return '25/26';
    return String(year);
  }

  /**
   * Small color-coded pill that surfaces a plan's season at a glance.
   * Blue tint for the current season (26/27), amber for the prior season
   * (25/26). Same visual language as the inline 25/26 badge next to plan
   * names and the kind badges in the Estimates pill — keeps the season
   * signaling consistent across the app.
   */
  function PlanYearPill({ year }: { year: number }): React.ReactElement {
    const isCurrent = year === CURRENT_SEASON_PRIMARY_YEAR;
    return (
      <span style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 4,
        background: isCurrent ? '#dbeafe' : '#fef3c7',
        color: isCurrent ? '#1e3a8a' : '#78350f',
        fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}>
        {seasonBadge(year)}
      </span>
    );
  }

  /**
   * Single row inside the plan dropdown / search popup. Renders the
   * year pill + plan name with a hover background + "selected"
   * highlight when the row's plan matches the active planFilter.
   * Shared between the two pickers so they look + feel identical.
   */
  function PlanRow({
    selected, onClick, badge, label, muted,
  }: {
    selected: boolean;
    onClick: () => void;
    badge: React.ReactNode | null;
    label: string;
    muted?: boolean;
  }): React.ReactElement {
    return (
      <button
        type="button"
        onClick={onClick}
        title={label}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', textAlign: 'left',
          padding: '8px 10px',
          background: selected ? colors.primaryLight : 'transparent',
          border: 'none', borderRadius: 5,
          cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 12.5,
          color: muted ? colors.textMuted : colors.textDark,
          fontWeight: selected ? 600 : (muted ? 500 : 500),
        }}
        onMouseEnter={e => {
          if (!selected) e.currentTarget.style.background = '#f3f4f6';
        }}
        onMouseLeave={e => {
          if (!selected) e.currentTarget.style.background = 'transparent';
        }}
      >
        {badge}
        <span style={{
          flex: 1, minWidth: 0,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {label}
        </span>
        {selected && (
          <span style={{ fontSize: 11, color: colors.primary, flexShrink: 0 }}>✓</span>
        )}
      </button>
    );
  }

  // renderPlanSearchResults removed — the unified Load Plan picker
  // renders results inline (no 5-item cap; the whole list scrolls).

  /**
   * 25/26 SF source plans available as roll-forward templates for the
   * New Plan modal. One entry per unique SF plan, carrying the bits
   * of metadata we can prefill from the deal grain (advertiser/brand/
   * demo via account / brand / primaryDemo). Sorted alphabetically so
   * the search box and the open-dropdown list match visually.
   *
   * Excludes anything already in the Snowflake save cache or the
   * manual-draft list — those are 26/27 plans and aren't valid
   * roll-forward sources.
   */
  const rollForwardSources = useMemo<Array<{
    name: string;
    year: '25/26';
    sfId: string | null;
    planId: null;
    advertiser: string | null;
    brand: string | null;
    demo: string | null;
  }>>(() => {
    // Also skip 25/26 sources that have already been rolled forward into
    // a saved 26/27 plan (SF id match, regardless of nickname). Prevents
    // the user from creating a SECOND 26/27 plan from the same source,
    // which would produce duplicate deals under the same source SF id.
    const rolledForwardSourceIds = new Set<string>();
    listSavedPlans().forEach(p => {
      if (p.sourcePlanSfId) rolledForwardSourceIds.add(p.sourcePlanSfId);
    });
    const seen = new Set<string>();
    const result: Array<{ name: string; year: '25/26'; sfId: string | null; planId: null; advertiser: string | null; brand: string | null; demo: string | null }> = [];
    for (const d of allDeals) {
      if (seen.has(d.salesPlan)) continue;
      // Skip 26/27 plans — only 25/26 inventory is a valid template.
      if (getSavedPlanByName(d.salesPlan)) continue;
      // Skip already-rolled-forward sources.
      if (d.salesPlanSfId && rolledForwardSourceIds.has(d.salesPlanSfId)) continue;
      seen.add(d.salesPlan);
      result.push({
        name: d.salesPlan,
        year: '25/26',
        sfId: d.salesPlanSfId ?? null,
        planId: null,
        advertiser: d.account ?? null,  // SF schedule's "DealAccount" = advertiser display name
        brand: d.brand ?? null,
        demo: d.demo ?? null,
      });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeals, plansHydrated, planCacheVersion]);

  /**
   * 26/27 saved plans available as clone sources for the New Plan modal.
   * Only surfaced in the picker when the user opened the modal while
   * viewing a 26/27 plan (see `showCloneSources` at picker render).
   * Every entry carries the full meta needed to pre-populate the form
   * from a same-season sibling — nothing to re-fetch, unlike the SF
   * resolve step for 25/26 sources.
   */
  const cloneSources = useMemo<Array<{
    name: string;
    year: '26/27';
    sfId: null;
    planId: number;
    advertiser: string | null;
    brand: string | null;
    demo: string | null;
    agency: string | null;
    buyer: string | null;
    ae: string | null;
    marketplace: string | null;
    advertiserId: string | null;
    brandId: string | null;
    contactId: string | null;
    accountId: string | null;
  }>>(() => {
    const result: Array<{
      name: string; year: '26/27'; sfId: null; planId: number;
      advertiser: string | null; brand: string | null; demo: string | null;
      agency: string | null; buyer: string | null; ae: string | null;
      marketplace: string | null;
      advertiserId: string | null; brandId: string | null;
      contactId: string | null; accountId: string | null;
    }> = [];
    for (const p of listSavedPlans()) {
      const v = p.latestVersion;
      if (!v) continue;
      result.push({
        name: p.name,
        year: '26/27',
        sfId: null,
        planId: p.id,
        advertiser: v.advertiser ?? null,
        brand: v.brand ?? null,
        demo: v.demo ?? null,
        agency: v.agency ?? null,
        buyer: v.buyer ?? null,
        ae: v.ae ?? null,
        marketplace: v.marketplace ?? null,
        advertiserId: v.advertiserId ?? null,
        brandId: v.brandId ?? null,
        contactId: v.contactId ?? null,
        accountId: v.accountId ?? null,
      });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansHydrated, planCacheVersion]);

  /**
   * Whether the picker's search list should include 26/27 clone sources
   * alongside the 25/26 roll-forward sources. Only true when the user
   * opened the modal while viewing a saved 26/27 plan — the "context
   * unlocks the pool" rule from the product discussion.
   */
  const showCloneSources = !!planFilter && !isViewOnly && !!getSavedPlanByName(planFilter);

  /**
   * Token-based AND substring filter on the New Plan source picker.
   * Splitting on whitespace lets the user narrow with multiple keywords
   * in any order ("panera bohm" matches "BOHM ... PANERA ..."). Empty
   * search shows everything.
   *
   * When the modal was opened from a 26/27 plan, `cloneSources` is
   * merged into the search pool alphabetically — both years live in
   * the same list with a year chip distinguishing them.
   */
  const filteredRollForwardSources = useMemo(() => {
    const combined = showCloneSources
      ? [...rollForwardSources, ...cloneSources].sort((a, b) => a.name.localeCompare(b.name))
      : rollForwardSources;
    const q = npRollForwardSearch.trim().toLowerCase();
    if (!q) return combined;
    const tokens = q.split(/\s+/).filter(Boolean);
    return combined.filter(opt => {
      const hay = opt.name.toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
  }, [rollForwardSources, cloneSources, showCloneSources, npRollForwardSearch]);

  /**
   * Live preview of the SF-mirror `Sales_Plan_Name__c` formula for the
   * Create Plan modal:
   *
   *   {Agency}-{Brand}-{Advertiser}-{Demo}-{Year}
   *
   * Renders with "…" placeholders for ingredients the user hasn't
   * picked yet — gives a live readout that fills in as they pick.
   */
  const npComputedSalesPlanName = useMemo(() => computeSalesPlanName({
    agency: npAgency,
    brand: npBrand,
    advertiser: npAdvertiser,
    demo: npDemo,
    year: '26/27',
  }), [npAgency, npBrand, npAdvertiser, npDemo]);

  /**
   * All four required ingredients picked? Used to gate the collision
   * error — partial preview is fine, we just don't yell about
   * "duplicate" until the user has finished filling in the parts.
   */
  const npComputedNameComplete = !!(npAgency && npBrand && npAdvertiser && npDemo);

  /**
   * Map of existing 26/27 plans keyed by their `sales_plan_name`,
   * valued with the plan's nickname (PLAN.name) so the collision
   * error can render a clickable "open existing plan" link.
   *
   * Pulls from Snowflake-saved plans (every saved plan is 26/27
   * today, the only writable season). Case-sensitive — SF preserves
   * casing on the formula output. Map keeps the FIRST occurrence of
   * a given sales_plan_name; theoretical duplicates (shouldn't exist
   * post-migration) would just show whichever loaded first.
   *
   * NOTE: legacy rows backfilled to `sales_plan_name` use the same
   * formula as the new code path, so the comparison is apples-to-
   * apples even for pre-migration saves.
   */
  const npExisting26_27SalesPlanNames = useMemo(() => {
    const names = new Map<string, string>();
    listSavedPlans().forEach(p => {
      // Only PUSHED plans count as blocking. Draft plans that share a
      // Sales Plan Name are an intentional workflow — sales reps build
      // scenario variants like "GENENTECH LINEAR 250K / 500K / 800K"
      // at the same client signature and only push ONE once the client
      // picks a budget. Blocking drafts against each other would break
      // that pattern. The push-to-SF gate downstream (`pushSpnConflict`)
      // catches the actual "someone already pushed this SPN" problem
      // at the moment it matters.
      if (!p.pushedToSfAt) return;
      const spn = p.latestVersion?.salesPlanName;
      if (spn && !names.has(spn)) names.set(spn, p.name);
    });
    return names;
    // Deps intentionally include `planCacheVersion` — delete/save flows
    // bump it so cache-reading memos re-run against the fresh Map.
    // Without it, a delete-then-recreate cycle showed a spurious
    // "already exists" collision against the deleted plan.
  }, [plansHydrated, planCacheVersion]);

  /**
   * The nickname of the existing plan that collides with what the
   * user is building (or null if no collision). Drives both the
   * collision flag below and the click-to-open link in the error
   * message.
   */
  const npCollidingPlanNickname = npComputedNameComplete
    ? (npExisting26_27SalesPlanNames.get(npComputedSalesPlanName) ?? null)
    : null;

  /**
   * The new uniqueness gate — replaces the old nickname-collision
   * check. True only when all ingredients are filled (so partial-
   * preview state never errors) AND the computed name already exists
   * on a 26/27 plan. Powers the inline error + Create button disable.
   */
  const npNameCollision = npCollidingPlanNickname !== null;

  /**
   * Required-field completeness gate for the Create button. Mirrors the
   * check inside `submitNewPlan` so the button reflects the exact same
   * validation the server-bound submit path enforces. Powers both the
   * disabled state and the "which fields are missing" tooltip so the
   * user sees the block BEFORE clicking, not just after.
   */
  // TBD placeholder counts as "filled" for the required-field gate —
  // display name = "TBD" + null SF id is an intentional draft state
  // (see task #93). The push-to-SF gate downstream refuses to send
  // these plans to Salesforce until they're resolved to real values,
  // so allowing Create at this point doesn't risk a bad SF push.
  const npMissingRequired: string[] = [];
  if (!npAdvertiserId && npAdvertiser !== 'TBD') npMissingRequired.push('Advertiser');
  if (!npDealname) npMissingRequired.push('Plan Nickname');
  if (!npBrandId && npBrand !== 'TBD') npMissingRequired.push('Brand');
  if (!npContactId && npBuyer !== 'TBD') npMissingRequired.push('Buyer');
  if (!npDemo) npMissingRequired.push('Primary Demo');
  if (!npMarketplace) npMissingRequired.push('Marketplace');
  const npHasMissingRequired = npMissingRequired.length > 0;

  // 26/27 verbatim clone nickname collision. Backend PLAN uniqueness is
  // (name, season, source_plan_sf_id). Since 26/27 clones carry
  // sourceSfId=null, an unchanged nickname would resolve to the SAME
  // (name, season, null) triple as the source plan — the backend's
  // race-safe "adopt existing" branch would then treat this new plan
  // as another version of the source, silently polluting the source's
  // history instead of creating a new PLAN row.
  //
  // Both the frontend guard here AND the backend clone carve-out
  // shipped together — this is the first line of defense (user sees
  // the block before submitting), backend is the safety net for any
  // request that slips past.
  //
  // Scoped to 26/27 clones because:
  //   • 25/26 roll-forwards disambiguate via sourceSfId (safe to share
  //     nicknames across seasons).
  //   • From-scratch first-saves don't have a source to collide with.
  const npCloneNicknameCollision =
    npRollForwardYear === '26/27'
    && !!npRollForwardSource
    && !!npDealname
    && npDealname.trim() === npRollForwardSource.trim();

  // ── Edit Plan Details modal — live formula + uniqueness check ──
  //
  // Mirrors the Create modal's `npComputedSalesPlanName` /
  // `npCollidingPlanNickname` / `npNameCollision` trio, but for the
  // Edit modal's edit* state. The uniqueness check EXCLUDES the
  // current plan from the collision set — editing a plan and
  // re-saving it with the same formula should not collide with
  // itself.

  const editComputedSalesPlanName = useMemo(() => computeSalesPlanName({
    agency: editAgency,
    brand: editBrand,
    advertiser: editAdvertiser,
    demo: editDemo,
    year: '26/27',
  }), [editAgency, editBrand, editAdvertiser, editDemo]);

  const editComputedNameComplete = !!(editAgency && editBrand && editAdvertiser && editDemo);

  const editCollidingPlanNickname = useMemo(() => {
    if (!editComputedNameComplete) return null;
    const collisions = new Map<string, string>();
    listSavedPlans().forEach(p => {
      // Only PUSHED plans block edits (matches the same rule the New
      // Plan modal uses — see npExisting26_27SalesPlanNames). Editing
      // one draft to match another draft's SPN is a valid scenario-
      // variant flow; blocking it would break the workflow.
      if (!p.pushedToSfAt) return;
      const spn = p.latestVersion?.salesPlanName;
      // Exclude the plan being edited so its current formula doesn't
      // false-positive against itself.
      if (spn && p.name !== planFilter && !collisions.has(spn)) {
        collisions.set(spn, p.name);
      }
    });
    return collisions.get(editComputedSalesPlanName) ?? null;
    // planCacheVersion is included so a delete elsewhere invalidates
    // this memo's snapshot — same reasoning as the New Plan collision
    // memo above.
  }, [editComputedNameComplete, editComputedSalesPlanName, plansHydrated, planFilter, planCacheVersion]);

  const editNameCollision = editCollidingPlanNickname !== null;

  // Derived "unsaved changes" flag. Builds a compact, deterministic
  // signature of the editable state (skipping derived/cosmetic fields)
  // and compares to the signature captured at the last save / load.
  //
  // Why not JSON.stringify(allDeals)?
  //   1. ~10× smaller payload at 2k deals (one ~80-byte line per deal)
  //   2. Field order is fixed, so two semantically-equal states always
  //      produce the same string (JSON.stringify follows insertion order
  //      and recalcDeal mutates fields in different orders on different
  //      code paths → false-positive dirty otherwise).
  //   3. Excludes purely-derived fields (newHhCpm, totalDollars) so a
  //      recalc with no user input doesn't churn the signature.
  const isDirty = useMemo(() => {
    if (!planFilter) return false;
    const base = savedSnapshots[planFilter];
    // No baseline recorded for this plan — nothing to compare against, so it
    // cannot be dirty. Same semantics the single-snapshot version had when it
    // was still unset.
    if (base === undefined) return false;
    if (planSignatureFor(planFilter, allDeals, addedRows) !== base) return true;
    // Secondary demos live in their own state, not on the deal rows, so a
    // change to the LIST doesn't move planSignature at all. Without this,
    // adding or removing a demo left Save disabled — the selection couldn't
    // be persisted and was lost on the next reload.
    //
    // Compared against the saved version rather than a snapshot string so it
    // stays correct across plan switches: the hydrate effect resets
    // secondaryDemos from whichever plan is now selected.
    if (planFilter) {
      const savedDemos = getSavedPlanByName(planFilter)?.latestVersion?.secondaryDemos ?? [];
      if (savedDemos.length !== secondaryDemos.length
        || savedDemos.some((d, i) => d !== secondaryDemos[i])) {
        return true;
      }
    }
    return false;
  }, [allDeals, addedRows, savedSnapshots, secondaryDemos, planFilter, plansHydrated, bindingsTick]);

  /**
   * Is ANY plan unsaved — not just the one on screen.
   *
   * Only the beforeunload warning wants this. Everything else (the pill, Save,
   * Revert) is about the plan in front of the user. But closing the tab
   * discards every plan's edits at once, so that prompt has to ask the broader
   * question — and it is exactly the prompt that went quiet when one shared
   * baseline let a save on plan B speak for plan A.
   */
  const anyPlanDirty = useMemo(() => {
    return Object.keys(savedSnapshots).some(
      name => planSignatureFor(name, allDeals, addedRows) !== savedSnapshots[name],
    );
  }, [allDeals, addedRows, savedSnapshots]);

  // beforeunload warning — prompts the user when they try to close the
  // tab, refresh, or navigate to a different URL while `isDirty`
  // (pending edits) is true.
  //
  // Modern browsers ignore custom messages and show their own generic
  // "Changes you made may not be saved" dialog; setting `returnValue`
  // is what triggers it. Only attached when at-risk so the prompt
  // doesn't fire on every navigation.
  //
  // Covers tab close / page refresh / typing a new URL. Intra-app route
  // changes (clicking a different menu item) aren't intercepted — those
  // would require a React Router blocker, which is a bigger lift. The
  // visible "Unsaved" pill near the deal count is the soft signal for
  // intra-app navigation.
  useEffect(() => {
    // `anyPlanDirty`, not `isDirty`: closing the tab drops unsaved work on
    // EVERY plan, including ones the user edited and then navigated away from.
    if (!anyPlanDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for legacy browsers (Chrome <51, older Firefox).
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyPlanDirty]);

  // ─── Keeping up with other people's saves ───────────────────────────────────
  //
  // MPB hydrates every plan once at mount and then reads from that cache for
  // the rest of the session, so a second user's save was invisible until you
  // reloaded the page. These two effects are the moments where picking up
  // their work is both cheap and expected.
  //
  // `syncActivePlanFromServer` owns every safety decision (in flight, dirty,
  // viewing history, unsaved drafts) — the triggers just say WHEN.

  // On plan switch. You clicked into this plan; you expect what it says now.
  useEffect(() => {
    if (!plansHydrated || !dataLoaded) return;
    void syncActivePlanFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, plansHydrated, dataLoaded]);

  // On window focus. Covers the case no switch can: sitting on one plan while
  // a teammate saves it. `focus` rather than an interval — polling a plan
  // nobody is looking at is load for nothing, and coming back to the tab is
  // exactly when a stale screen gets noticed.
  useEffect(() => {
    const onFocus = () => { void syncActivePlanFromServer(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // Every piece of state the sync reads is listed, so the listener is
    // re-registered with a fresh closure whenever any of them moves. The
    // one thing deliberately NOT here is allDeals/addedRows — the rebuild
    // reads those through refs precisely so a stale closure can't wipe
    // other plans' rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, isDirty, isSaving, plansHydrated, dataLoaded, viewingHistoricalRef]);

  // Validation: any deal or added row with CPM <= 0 / NaN, or null rate,
  // blocks Save. We scan only the currently-rendered slice so users see
  // an immediate cause-and-effect; a different tab's invalid row would
  // be caught the moment they navigate to it.
  /**
   * On a pushed Order, only the late quarter is editable — so a save gate must
   * only consider rows the user can actually fix.
   *
   * ⚠ Without this, a pushed plan with one bad row in a LOCKED quarter is
   * unsaveable and unfixable: `filteredDeals` is tab-scoped on a quarter tab
   * but plan-wide on Summary, and Save is deliberately tab-independent, so the
   * user could enter 4Q'27 rows, click Summary, and find Save blocked by a red
   * cell in a quarter that has no editable input anywhere. Legacy rows predate
   * both the validation and the order, so this is not hypothetical.
   */
  const rowIsGateable = useCallback((quarter: string, year: number): boolean => {
    // Deliberately NOT keyed on the active tab. Save is tab-independent, so
    // the set of rows a gate considers must be too — otherwise the gate's
    // answer changes as the user clicks around, which is how you get a Save
    // button that works on one tab and not another for no visible reason.
    const restricted = isPushed && !isRevisingOrder && planLateQuarter != null;
    if (!restricted || !planLateQuarter) return true;
    return quarter === planLateQuarter.quarter && year === planLateQuarter.year;
  }, [isPushed, isRevisingOrder, planLateQuarter]);

  const hasInvalid = useMemo(() => {
    for (const d of filteredDeals) {
      if (!rowIsGateable(d.quarter, d.year)) continue;
      if (!(d.cpm > 0)) return true;
      if (d.newRate == null) return true;
    }
    for (const r of addedRows) {
      if (r.salesPlan !== planFilter) continue;
      if (!rowIsGateable(r.quarter, r.year)) continue;
      if (!(r.cpm > 0)) return true;
      if (r.newRate == null) return true;
    }
    return false;
  }, [filteredDeals, addedRows, planFilter, rowIsGateable]);

  // Any row (saved deal or added row) with a blank unit length blocks
  // save AND blocks estimate lookup — the (program, quarter, year,
  // unitLength, demo) natural key can't match without a length, so
  // RTG / (000) / CPM cells all come back empty and derived totals
  // (dollars, CPM HH) can't compute. Surface this as a distinct save
  // gate so the tooltip explains the exact fix ("set unit length"),
  // and drive the per-cell red border below.
  const hasMissingUnitLength = useMemo(() => {
    // Same reasoning as hasInvalid — see rowIsGateable.
    for (const d of filteredDeals) {
      if (!rowIsGateable(d.quarter, d.year)) continue;
      if (!String(d.unitLength ?? '').trim()) return true;
    }
    for (const r of addedRows) {
      if (r.salesPlan !== planFilter) continue;
      if (!rowIsGateable(r.quarter, r.year)) continue;
      if (!String(r.unitLength ?? '').trim()) return true;
    }
    return false;
  }, [filteredDeals, addedRows, planFilter, rowIsGateable]);

  // Per-quarter attention counts. Drives the red ⚠ dot on the tab
  // strip AND the "Can't save — fix rows in …" banner above the grid.
  // Scans ALL rows for the current plan (both saved deals and
  // AddedRows) — filteredDeals is already scoped to the active tab,
  // so it wouldn't surface issues on OTHER quarters, which is exactly
  // the class of blocker we want to expose (user typed CPM on 4Q'26
  // and doesn't realize 1Q'27-3Q'27 have blank LEN + CPM=0).
  //
  // Returns a Map keyed by `${quarter}|${year}` → count of "unfinished"
  // rows in that quarter (rows with blank LEN OR cpm <= 0 OR null
  // rate). Count of zero = tab is clean.
  const quarterAttentionCounts = useMemo(() => {
    if (!planFilter) return new Map<string, number>();
    const out = new Map<string, number>();
    const bump = (q: string, y: number) => {
      const k = `${q}|${y}`;
      out.set(k, (out.get(k) ?? 0) + 1);
    };
    const isUnfinished = (row: {
      unitLength: string | number | null | undefined;
      cpm: number | null | undefined;
      newRate?: number | null | undefined;
      rate?: number | null | undefined;
    }): boolean => {
      if (!String(row.unitLength ?? '').trim()) return true;
      if (!(Number(row.cpm) > 0)) return true;
      const eff = row.newRate ?? row.rate;
      if (eff == null) return true;
      return false;
    };
    for (const d of allDeals) {
      if (d.salesPlan !== planFilter) continue;
      if (isUnfinished(d)) bump(d.quarter, d.year);
    }
    for (const r of addedRows) {
      if (r.salesPlan !== planFilter) continue;
      if (isUnfinished(r)) bump(r.quarter, r.year);
    }
    return out;
  }, [planFilter, allDeals, addedRows]);

  // Per-quarter historical-diff counts. Drives the amber • dot on
  // the tab strip when the user is viewing a past version. Sums the
  // per-cell field changes on rows keyed to that quarter, so the
  // count matches the "5 cells differ from latest" chip in the top
  // banner but split by quarter. Empty map when not viewing history.
  const quarterDiffCounts = useMemo(() => {
    const out = new Map<string, number>();
    if (!historicalDiff) return out;
    // Reverse-map from natural key back to (quarter, year). Iterate
    // the current allDeals slice for this plan — historicalDiff's
    // diffedByKey is keyed the same way.
    if (!planFilter) return out;
    for (const d of allDeals) {
      if (d.salesPlan !== planFilter) continue;
      const key = historicalDiff.naturalKeyFor({
        program: d.program,
        quarter: d.quarter,
        year: d.year,
        unitLength: d.unitLength ?? '',
        demo: d.demo,
      });
      const rd = historicalDiff.diffedByKey.get(key);
      if (rd && rd.fields.size > 0) {
        const k = `${d.quarter}|${d.year}`;
        out.set(k, (out.get(k) ?? 0) + rd.fields.size);
      }
    }
    return out;
  }, [historicalDiff, allDeals, planFilter]);

  // ─── Init ───────────────────────────────────────────────────────────────────

  // Keep planNameDraft in sync whenever the active plan changes
  useEffect(() => { setPlanNameDraft(planFilter); }, [planFilter]);


  // Callback ref for the sticky bar. Fires every time React assigns a
  // new element (mount, remount, unmount). On mount: tears down any
  // prior observer, measures synchronously, then attaches a fresh
  // ResizeObserver. On unmount: tears down the observer. This pattern
  // beats useEffect([]) because it survives element re-creation —
  // useEffect would observe the first-mounted node forever.
  const stickyBarRefCallback = useCallback((el: HTMLDivElement | null) => {
    if (stickyBarROHolder.current) {
      stickyBarROHolder.current.disconnect();
      stickyBarROHolder.current = null;
    }
    if (el) {
      const update = () => setStickyBarH(el.offsetHeight);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      stickyBarROHolder.current = ro;
    }
  }, []);

  // Same pattern for the group-header row. Critical here because the
  // <table> remounts on every Summary ↔ Quarter tab switch — without
  // callback refs the ResizeObserver gets stranded on the detached
  // old <tr>, --group-header-h goes stale, and the second thead row
  // sticks at the wrong offset → visible gap or overlap.
  const groupHeaderRowRefCallback = useCallback((el: HTMLTableRowElement | null) => {
    if (groupHeaderROHolder.current) {
      groupHeaderROHolder.current.disconnect();
      groupHeaderROHolder.current = null;
    }
    if (el) {
      const update = () => setGroupHeaderH(el.offsetHeight);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      groupHeaderROHolder.current = ro;
    }
  }, []);

  useEffect(() => {
    if (sourceDealsLoading || sourceDeals.length === 0) return;
    loadLastYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDeals, sourceDealsLoading]);

  useEffect(() => {
    // One-shot localStorage cleanup on mount.
    //
    //   `manualPlanDeals` — legacy key from the "+ New Plan auto-populates
    //   78 rows" flow; safe to sweep unconditionally.
    //
    //   `manualPlans` — legacy draft-plan cache. As of the "save immediately
    //   on Create Plan" refactor, `+ New Plan` writes directly to Snowflake
    //   instead of parking a draft in localStorage. Any leftover entries
    //   from before this change are stale metadata that never touched the
    //   backend — dropping them is a one-time migration on next mount.
    //
    // Removing on every boot is safe and idempotent — no-op after the first
    // call on any given browser.
    try {
      localStorage.removeItem('manualPlanDeals');
      localStorage.removeItem('manualPlans');
    } catch { /* ignore */ }
  }, []);

  /**
   * Merge Snowflake-saved plans into allDeals after both data sources
   * have loaded.
   *
   * Name-collision avoidance: 25/26 SF plans and 26/27 Snowflake plans
   * coexist in the dropdown (server-side de-dup filter was removed in
   * the year-aware refactor). They never share names in practice because
   * the New Plan modal's live `npNameCollision` check (phase 95) blocks
   * users from creating a 26/27 plan with a name that already exists
   * — including any 25/26 SF name. So the two sources stay disjoint
   * by *name* even though the server no longer hides either set.
   *
   * Runs ONCE per mount, gated on `[dataLoaded, plansHydrated]`. After
   * that, savePlan() / deletePlan() update the cache directly and the
   * already-merged React state stays in sync without re-running this.
   * Re-running on every cache change would create duplicates: the
   * current React state and the post-save cache are the SAME rows.
   */
  useEffect(() => {
    if (!dataLoaded || !plansHydrated) return;
    const saved = listSavedPlans();
    if (!saved.length) return;
    const converted = saved.flatMap(savedPlanToDeals);
    if (!converted.length) return;

    setAllDeals(prev => {
      // Defensive dedup — Strict Mode runs effects twice in dev, and a
      // future refactor could re-trigger this. Dedup by `(dealKey,
      // salesPlan, year)` — NOT `(dealKey, salesPlan)` alone.
      //
      // Rolled-forward 26/27 saved deals inherit `dealKey =
      // pd.sourceSfDealKey` (line 2487) so they can trace back to
      // their SF source. When the rolled plan's nickname ALSO matches
      // the source SF plan's name (common in roll-forward — the modal
      // auto-fills the source name), the rolled deal ends up sharing
      // BOTH `dealKey` and `salesPlan` with the still-present source
      // SF deal from `sourceDeals`. Without year in the key, dedup
      // treats the rolled deal as a duplicate and drops it — the
      // 26/27 view then shows 0 deals because they never entered
      // `allDeals`.
      //
      // Adding year distinguishes them: source stays at its 25/26
      // years (2025/2026), rolled deals shift to 26/27 (2026/2027).
      // Strict-Mode doubles still get caught (identical year on both).
      const keyOf = (d: Deal) => `${d.dealKey}|${d.salesPlan}|${d.year}`;
      const existingKeys = new Set(prev.map(keyOf));
      const toAdd = converted.filter(d => !existingKeys.has(keyOf(d)));
      if (!toAdd.length) return prev;
      const merged = [...prev, ...toAdd];
      // Baseline every plan that just arrived, so the "Unsaved" pill doesn't
      // trip merely because saved deals were merged in.
      snapshotPlans(planNamesIn(merged, []), merged, []);
      return merged;
    });
    // Record which version each plan's rows came from — see
    // `loadedVersionByPlanRef`. This effect is the only place saved plans
    // enter `allDeals` wholesale, so it is where the baseline is set.
    saved.forEach(p => {
      const vn = p.latestVersion?.versionNo;
      if (typeof vn === 'number') loadedVersionByPlanRef.current.set(p.id, vn);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, plansHydrated]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Convert a Snowflake SavedPlan into Deal[] rows for React state.
   *
   * `impressions_000` (the effective demo (000) value at save time) is
   * always stored back into `overrideDemo000`. That guarantees the cell
   * displays the saved value regardless of what the active estimate
   * version would compute today — saved plans are locked to their
   * bound version's data. Side effect: the (000) cell renders in
   * "edited" (orange) styling for every reloaded row even when the
   * saved value equals the estimate's value. Visually loud but data-
   * accurate. TODO: add a `baseImpressions_000` field on Deal so the
   * orange styling can be reserved for true post-save edits.
   *
   * `baseCpm` / `baseRate` / `baseDollars` are set to the saved values
   * so the orange styling on CPM/Rate/$ correctly shows "clean" until
   * the user makes a new edit.
   */
  /**
   * Convert PlanDeal rows into Deal[] using a provided plan name +
   * source-SF-id pair. Used both for the live "latest version" path
   * (see `savedPlanToDeals`) and the historical snapshot viewer modal
   * (where the version may not be `plan.latestVersion`).
   *
   * Kept private to this file so both call sites stay in lockstep —
   * any future field added here flows through both consumers.
   */
  function planDealsToDeals(
    planName: string,
    sourcePlanSfId: string | null,
    deals: PlanDeal[],
  ): Deal[] {
    return deals.map(pd => ({
      // dealKey identity: prefer the original SF deal key when this row
      // was templated from SF (lets the table match per-row state to
      // history). Otherwise synthesize from the immutable backend id.
      dealKey: pd.sourceSfDealKey ?? `pd-${pd.id}`,
      salesPlan: planName,
      salesPlanSfId: sourcePlanSfId ?? '',
      program: pd.program,
      unitLength: String(pd.unitLength ?? ''),
      quarter: pd.quarter,
      year: pd.year,
      cpm: pd.cpm ?? 0,
      // Round-trip the frozen v1 CPM. Fall back to current cpm for
      // legacy rows where Snowflake's original_cpm is null (shouldn't
      // happen after the backfill migration, but defensive).
      originalCpm: pd.originalCpm ?? pd.cpm ?? 0,
      // Runtime baseline starts at originalCpm — the `± %` input
      // renders relative to that until the user types in the `$` field.
      _baselineCpm: pd.originalCpm ?? pd.cpm ?? 0,
      totalUnits: pd.totalUnits ?? 0,
      sourceDollars: pd.totalDollars ?? 0,
      totalDollars: pd.totalDollars ?? 0,
      actual000: 0,             // unused for Snowflake-loaded deals
      guaranteed: 0,
      rate: pd.rate ?? 0,
      demo: pd.demo ?? '',
      account: pd.account ?? '',
      brand: pd.brand ?? '',
      hh_000: 0,                // computed at render time from estimate
      hh_cpm: null,
      newRate: pd.rate ?? null,
      newHhCpm: null,
      // Saved values become the new baseline — CPM/Rate/$ render clean
      // until the user edits past these.
      baseCpm: pd.cpm ?? undefined,
      baseRate: pd.rate ?? null,
      baseDollars: pd.totalDollars ?? undefined,
      baseCpmHH: null,
      _cpmManuallyEdited: pd.cpmManuallyEdited,
      overrideDemo000: pd.impressions_000,
      // Same contract as impressions_000 above: the saved (resolved)
      // secondary values become overrides so a reloaded plan shows what
      // was saved, not what today's estimate would compute. Undefined for
      // rows saved before the column existed — treated as "no overrides".
      overrideSecondary000: pd.secondaryImpressions000 ?? null,
      // Round-trip Salesforce Programs__c.ID so subsequent saves and
      // (eventually) push-to-SF don't lose the FK. Null for legacy rows
      // that pre-date the PROGRAM_ID column — those plans can't be
      // pushed to SF until the user repairs the program pick.
      programId: pd.programId ?? null,
    }));
  }

  function savedPlanToDeals(plan: SavedPlan): Deal[] {
    const v = plan.latestVersion;
    if (!v) return [];
    return planDealsToDeals(plan.name, plan.sourcePlanSfId, v.deals);
  }

  /**
   * Show the 20% guard confirmation modal and wait for the user's choice.
   *
   * The Promise resolves with `true` when the user clicks "Yes, apply"
   * and `false` on Cancel / overlay-click. Edit handlers wrap their
   * commit step in this — see `maybeConfirmChange` below.
   *
   * `formatValue` controls how oldValue / newValue render in the
   * modal copy (default: `$X.XX` for CPM-style fields).
   */
  /**
   * Show the marketplace-change warning modal and wait for the user's
   * choice. Promise resolves true on Proceed, false on Cancel / overlay.
   * `hidden` is the per-tab breakdown of quarters that would become
   * invisible under the new marketplace AND contain at least one
   * deal or AddedRow for the current plan. The modal renders the
   * row counts so the user sees the impact concretely.
   *
   * Data is NEVER deleted — switching back to the old marketplace
   * (or a marketplace whose visibleQuarters re-includes the hidden
   * tabs) makes the rows reappear. The warning's purpose is just to
   * prevent the user from being surprised when their on-screen data
   * shrinks.
   */
  function showMarketplaceWarn(args: {
    oldMarketplace: string;
    newMarketplace: string;
    hidden: Array<{ key: string; label: string; dealCount: number; addedRowCount: number }>;
  }): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      // Same pattern as showGuardModal — if a prior modal is still
      // pending (unlikely but possible in fast-click scenarios), cancel
      // its promise before replacing it.
      setMarketplaceWarn(prev => {
        if (prev) prev.resolve(false);
        return { ...args, resolve };
      });
    });
  }

  function showGuardModal(args: {
    fieldLabel: string;
    oldValue: number;
    newValue: number;
    formatValue?: (v: number) => string;
  }): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      // If a prior modal is somehow still pending (rapid blur events,
      // keyboard navigation past the overlay, etc.), cancel its promise
      // before replacing it. Without this, the older handler awaits
      // forever — its resolve callback would be lost when setGuardModal
      // overwrites the state, leaving its input reset path orphaned.
      setGuardModal(prev => {
        if (prev) prev.resolve(false);
        return {
          fieldLabel: args.fieldLabel,
          oldValue: args.oldValue,
          newValue: args.newValue,
          formatValue: args.formatValue ?? ((v: number) => '$' + v.toFixed(2)),
          resolve,
        };
      });
    });
  }

  /**
   * Run the 20% guardrail against a single-edit delta. Returns true
   * when the change is approved (either below the threshold, or the
   * user confirmed via the modal). Returns false on cancel.
   *
   * Skips the modal entirely when `oldValue` is 0 or null — initial
   * entries (e.g. first CPM on a + Add Row) aren't "changes", they're
   * starting values. Same goes for going from anything to 0/null —
   * blank input is its own validation flow (red border via the
   * `hasInvalid` path).
   */
  async function maybeConfirmChange(args: {
    fieldLabel: string;
    oldValue: number | null | undefined;
    newValue: number;
    formatValue?: (v: number) => string;
  }): Promise<boolean> {
    const { oldValue, newValue } = args;
    if (oldValue == null || oldValue === 0) return true;
    if (newValue === 0) return true;
    const delta = Math.abs(newValue - oldValue) / Math.abs(oldValue);
    if (delta <= 0.20) return true;
    return showGuardModal({
      fieldLabel: args.fieldLabel,
      oldValue,
      newValue,
      formatValue: args.formatValue,
    });
  }

  function showToast(msg: string) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 3000);
  }

  /**
   * "Where this row started" for CPM math — used as the anchor for the
   * per-row `± %` adjustment and for the 20% guard comparisons. The
   * field is optional on both `Deal` and `AddedRow` (legacy rows didn't
   * have it); when null/undefined, current cpm is the de-facto baseline.
   */
  function getBaseline(d: { _baselineCpm?: number | null; cpm: number }): number {
    return d._baselineCpm ?? d.cpm;
  }

  /**
   * Resolve the effective per-spot demo (000) value for a row:
   *   1. user-typed override (if any)
   *   2. estimate-derived demo000 (when an estimate matches)
   *   3. null  →  cell renders as "—"
   *
   * Centralises the fallback chain to keep edit handlers + render
   * paths reading the same value (the override semantics changed once
   * already and the inline ternaries went out of sync).
   */
  function getEffectiveDemo000(
    d: { overrideDemo000?: number | null },
    est: { demo000?: number | null } | null | undefined,
  ): number | null {
    return d.overrideDemo000 ?? est?.demo000 ?? null;
  }

  /**
   * Resolve the working status of a sales plan.
   *
   * Status set is the three-value picklist Working / Hold / Order.
   *   - Snowflake-saved 26/27 plans  →  status from the PLAN row
   *                                     (the picklist UI in phase 92
   *                                      writes Working/Hold/Order)
   *   - 25/26 SF source plans        →  Order (synthetic, locked —
   *                                     last-season inventory is by
   *                                     definition fully booked)
   *
   * The tone string drives pill color in the UI: 'working' → green,
   * 'hold' → amber, 'order' → blue. Defaults to working if the saved
   * status string is unrecognised so the UI never crashes on legacy
   * data.
   */
  function getPlanStatus(planName: string): {
    label: 'Working' | 'Hold' | 'Order';
    tone: 'working' | 'hold' | 'order';
  } {
    const toTone = (label: string): 'working' | 'hold' | 'order' => {
      if (label === 'Hold') return 'hold';
      if (label === 'Order') return 'order';
      return 'working';
    };
    const saved = getSavedPlanByName(planName);
    if (saved) {
      const raw = (saved.status || 'Working') as string;
      // Coerce unknown values to Working so we don't leak DB junk to UI.
      const label: 'Working' | 'Hold' | 'Order' =
        raw === 'Hold' || raw === 'Order' ? raw : 'Working';
      return { label, tone: toTone(label) };
    }
    // SF source plan — synthetic Order.
    return { label: 'Order', tone: 'order' };
  }

  /**
   * Apply a new status to the currently-selected plan.
   *
   * Routing:
   *   - Snowflake-saved 26/27 plan → PATCH /snowflake/plans/:id/status.
   *     The storage helper updates the in-memory cache on success;
   *     we bump `statusBump` to force the pill to re-read.
   *   - 25/26 SF source plan → unreachable in practice (picklist is
   *     not rendered for view-only plans), defensive no-op.
   */
  async function changeStatus(newStatus: PlanStatus): Promise<void> {
    setStatusDropdownOpen(false);
    if (!planFilter) return;

    const saved = getSavedPlanByName(planFilter);
    if (!saved) return;

    // Order status is only reachable via the push-to-SF flow. Intercept
    // here so the pill click opens the confirmation modal instead of
    // directly PATCHing PLAN.status. On successful push the backend
    // flips status to 'Order' + stamps pushed_to_sf_at atomically.
    if (newStatus === 'Order') {
      if (saved.pushedToSfAt) {
        // Already pushed — no-op. The pill shouldn't be clickable
        // in this state, but defensive.
        return;
      }
      // Block the push flow if the plan has duplicate rows — SF's
      // Duplicate_Check__c would reject the request and roll back the
      // entire Composite Tree anyway. Better to catch it here with a
      // toast that points at the red-highlighted rows than to burn a
      // round-trip on a guaranteed rejection.
      if (duplicateRowKeys.size > 0) {
        showToast(
          `Fix ${duplicateRowKeys.size} duplicate row group${duplicateRowKeys.size === 1 ? '' : 's'} before pushing — Salesforce will reject them.`,
        );
        return;
      }
      // Defensive belt on top of the disabled-option gate in the pill's
      // dropdown. If somehow the change comes through (keyboard nav,
      // race, external caller), still block here so SF never receives
      // a duplicate-SPN push OR a TBD-placeholder push.
      if (pushSpnConflictNickname) {
        showToast(
          `Cannot push — "${pushSpnConflictNickname}" is already pushed with the same Sales Plan Name. Edit inputs to differentiate first.`,
        );
        return;
      }
      if (pushTbdFields.length > 0) {
        showToast(
          `Cannot push — ${pushTbdFields.join(', ')} ${pushTbdFields.length === 1 ? 'is' : 'are'} still TBD. Edit the plan and pick real Salesforce values first.`,
        );
        return;
      }
      setPushToSfError(null);
      setPushToSfModalOpen(true);
      return;
    }

    // Non-Order changes go through the normal status update.
    const result = await updatePlanStatus(
      saved.id,
      newStatus,
      getEmailFromSessionStorage() || undefined,
    );
    if (result.ok) {
      setStatusBump(b => b + 1);
      showToast(`Status set to ${newStatus}`);
    } else {
      showToast(`Failed to update status: ${result.error}`);
    }
  }

  /**
   * Confirm handler for the Push-to-Salesforce modal. POSTs to
   * /snowflake/plans/:id/push-to-sf; on success bumps the plan cache
   * so memos re-read the fresh plan (with pushed_to_sf_at,
   * sf_sales_plan_id, and status='Order'). On failure the error is
   * captured for the modal's discriminated error surface — the modal
   * stays open so the user can act on the guidance.
   */
  async function handleConfirmPushToSf(): Promise<void> {
    if (!planFilter) return;
    const saved = getSavedPlanByName(planFilter);
    if (!saved) return;

    setPushToSfInFlight(true);
    setPushToSfError(null);
    try {
      // Auto-save unsaved edits before pushing. The push endpoint reads
      // PLAN_DEAL from Snowflake — it doesn't see in-memory edits — so
      // pushing without saving first would silently send the LAST saved
      // version and drop the user's pending changes on the floor.
      // isDirty is the same signature check that drives the UNSAVED pill
      // and the Save button's active state.
      if (isDirty) {
        await saveToSnowflake();
        // Saved-plans cache bump is already handled inside saveToSnowflake
        // on success; re-lookup the plan below in case its version_no
        // moved forward.
      }
      const savedAfterMaybeSave = getSavedPlanByName(planFilter) ?? saved;
      const result = await pushPlanToSf(savedAfterMaybeSave.id);
      if (result.ok) {
        // Bump cache counter so memos re-read the plan with its new
        // pushedToSfAt / sfSalesPlanId / status='Order'.
        setPlanCacheVersion(v => v + 1);
        setStatusBump(b => b + 1);
        setPushToSfModalOpen(false);
        // Compute the Deal/Schedule split same way as the push modal +
        // SF Structure preview: rows sharing Program+Length+Quarter+Year
        // collapse into one Deal__c parent. The toast reports both
        // counts so the user immediately sees the SF-side shape after
        // push (they'll see 7 rows in SF Deals list, not 11).
        //
        // Use plan-wide count (allDeals, season-narrowed) instead of
        // `filteredDeals` because filteredDeals is scoped to the active
        // quarter tab — if the user was on 2Q'27 when pushing, the
        // toast would report "4 schedules across 2 Deals" even though
        // SF received the full 14/8 across all quarters.
        const toastVisKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
        const planWideRows = allDeals.filter(d =>
          d.salesPlan === planFilter
          && d.totalUnits > 0
          && toastVisKeys.has(`${d.quarter}|${d.year}`)
        );
        const scheduleCount = planWideRows.length;
        const dealGroupSet = new Set(
          planWideRows.map(d => `${d.program}|${d.unitLength}|${d.quarter}|${d.year}`),
        );
        const dealCount = dealGroupSet.size;
        showToast(
          `✓ Pushed ${scheduleCount} schedule${scheduleCount === 1 ? '' : 's'} across ${dealCount} Deal${dealCount === 1 ? '' : 's'} to Salesforce`,
        );
      } else {
        setPushToSfError({
          message: result.error,
          code: result.code,
          affectedPrograms: result.affectedPrograms,
          sfDetail: result.sfDetail,
          requestBody: result.requestBody,
        });
      }
    } finally {
      setPushToSfInFlight(false);
    }
  }

  // ─── Load / Init ────────────────────────────────────────────────────────────

  function loadLastYear() {
    // Initial load: preserve Salesforce stored values for rate/totalDollars.
    // We only recompute when the user edits CPM, units, or adds a new row —
    // recalcDeal is called from those edit paths (updateDeal handlers).
    //
    // 25/26 inventory note: SF deals now display with their NATIVE quarters
    // (e.g. Q4 2025, Q1 2026) — no year shift on load. The 25/26 plans
    // are view-only against the 25/26 estimate; the year-shift only
    // happens during roll-forward into a new 26/27 plan (phase 94).
    const deals: Deal[] = sourceDeals.map(d => ({
      ...d,
      newRate: d.rate ?? null,        // SF stored per-spot rate (rounded)
      totalDollars: d.sourceDollars,  // SF stored total dollars (sum of TOTAL_DOLLARS__C)
      // Seed the runtime baseline for the per-row `± %` adjustment
      // input. For SF source rows this matches `originalCpm` (= the SF
      // CPM, per the source-deals query). View-only mode hides the
      // input anyway, but we set it for consistency so a future
      // roll-forward / template path inherits a sane value.
      _baselineCpm: d.originalCpm ?? d.cpm,
    }));
    deals.forEach(d => {
      // Compute HH-related fields and baseline values for diff coloring,
      // but don't overwrite newRate/totalDollars — those came from SF.
      const est = calcEstimateFields(d);
      d.newHhCpm = est.hh000 && d.newRate ? d.newRate / est.hh000 : null;
      d.baseCpm = d.cpm;
      d.baseRate = d.newRate ?? null;
      d.baseDollars = d.totalDollars;
      d.baseCpmHH = d.newHhCpm ?? null;
    });
    setAllDeals(deals);
    setDataLoaded(true);
    // Baseline every SF source plan just loaded — initial load is "clean".
    snapshotPlans(planNamesIn(deals, []), deals, []);
    showToast(`✓ Loaded ${deals.length} deals from last year`);
  }

  // ─── Sort ───────────────────────────────────────────────────────────────────

  function sortBy(col: string) {
    if (sortCol === col) setSortDir(d => d * -1);
    else { setSortCol(col); setSortDir(1); }
  }

  // ─── Deal mutation helpers ───────────────────────────────────────────────────

  /**
   * Update a Deal in allDeals, matching on (dealKey, salesPlan).
   *
   * Why both keys: SF source dealKeys are index-synthesized ("D-NEW-
   * NNNN"), so the SAME key can recur across multiple saved plans
   * (every plan that ever rolled-forward from a given source has a
   * PLAN_DEAL row with `source_sf_deal_key` matching the SF index).
   * `savedPlanToDeals` reconstructs the running Deal with `dealKey =
   * sourceSfDealKey`, so any cross-plan match-on-dealKey-only would
   * let a per-row edit leak into a different plan's row.
   *
   * salesPlan is passed in by every edit handler — the row that
   * fired the handler knows which plan it belongs to via its own
   * `d.salesPlan`, so the pair is always available.
   */
  /**
   * Return the currently-viewed plan's version of a deal identified by
   * (dealKey, salesPlan). Nickname-consolidated plans (a rolled 26/27
   * plan sharing a nickname with its 25/26 SF source) end up with TWO
   * entries in allDeals matching that tuple — one per season — because
   * rolled-forward deals reload with `dealKey = sourceSfDealKey` and
   * both entries carry the same salesPlan string. Every edit handler
   * needs to disambiguate to the season the user is actually looking
   * at; without this narrow, `find()` returns the FIRST match (usually
   * the 25/26 source) and the 20% guard modal compares against the
   * wrong deal's rate/CPM/demo.
   *
   * When `planFilter` isn't set or the plan is a pure 25/26 SF source,
   * visibleQuarters is either the All-Plans synthetic window or the
   * 25/26 window; the narrow still resolves correctly.
   */
  function findDealInView(dealKey: string, salesPlan: string): Deal | undefined {
    return allDeals.find(d =>
      d.dealKey === dealKey
      && d.salesPlan === salesPlan
      && visibleQuarters.some(t => t.quarter === d.quarter && t.year === d.year),
    );
  }

  function updateDeal(dealKey: string, salesPlan: string, updater: (d: Deal) => Deal) {
    // Same season narrow as findDealInView — without this, editing a
    // rolled 26/27 row would ALSO mutate the identically-keyed 25/26
    // source row (both share (dealKey, salesPlan) via nickname
    // consolidation). Even though the source is read-only on save,
    // the in-session mutation ghosts the source's original values
    // until reload.
    const visKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    setAllDeals(prev => prev.map(d =>
      d.dealKey === dealKey
      && d.salesPlan === salesPlan
      && visKeys.has(`${d.quarter}|${d.year}`)
        ? updater({ ...d })
        : d,
    ));
  }

  async function onCpmChange(dealKey: string, salesPlan: string, val: string, inputEl?: HTMLInputElement) {
    // Empty input is recorded as NaN so the row reads as "invalid" — the
    // UI surfaces this via a red border and Save is disabled. Blank → invalid
    // is not subject to the 20% guard (it's a "needs attention" state, not a
    // committed value change).
    if (val === '') {
      updateDeal(dealKey, salesPlan, d => { d.cpm = NaN; d._cpmManuallyEdited = true; return recalcDeal(d); });
      return;
    }
    const cpm = parseFloat(val);
    if (isNaN(cpm) || cpm < 0) {
      // Invalid parse — revert the input's visible value to its mount-time
      // defaultValue. Uncontrolled inputs don't auto-rewind on a no-op,
      // so without this the user's bad keystrokes would stick around.
      if (inputEl) inputEl.value = inputEl.defaultValue;
      return;
    }
    // 20% guard. Compare to the row's CURRENT cpm (not baseline) — that's
    // the actual delta the user is committing. maybeConfirmChange auto-
    // bypasses when oldValue is 0/null (initial entry).
    const cur = findDealInView(dealKey, salesPlan);
    if (cur) {
      const ok = await maybeConfirmChange({
        fieldLabel: `CPM (${cur.program})`,
        oldValue: cur.cpm,
        newValue: cpm,
      });
      if (!ok) {
        // Guard canceled — restore the input's displayed value to what
        // it read on mount. Without this, the user sees the rejected
        // value lingering in the input even though state didn't change.
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    // Lock the row from bulk grid edits. Whether the input renders orange
    // is derived from `d.cpm !== d.baseCpm` at render time, so editing
    // back to baseline auto-clears the visual without bookkeeping.
    //
    // Direct `$` edit also jumps `_baselineCpm` to the typed value so
    // the `± %` field next to it reads as 0% (= no deviation from the
    // new baseline). Subsequent `± %` entries compound from this new
    // baseline, not the original v1 CPM.
    updateDeal(dealKey, salesPlan, d => {
      d.cpm = cpm;
      d._baselineCpm = cpm;
      d._cpmManuallyEdited = true;
      return recalcDeal(d);
    });
  }

  /**
   * Per-row CPM `± %` adjustment input. Treats the typed value as a
   * percent relative to `_baselineCpm` (not compounding on current
   * cpm), so consecutive entries replace rather than stack: typing
   * 10% then 5% leaves CPM at baseline × 1.05, not baseline × 1.10
   * × 1.05.
   *
   * Parses leading +/- and trailing %, so any of `5`, `+5`, `5%`,
   * `-3%` work. Empty input is a no-op.
   *
   * Skips entirely when baseline is 0/null — those inputs render as
   * disabled in the UI (caller renders the field accordingly).
   */
  async function onCpmPercentChange(dealKey: string, salesPlan: string, val: string, inputEl?: HTMLInputElement) {
    const trimmed = val.trim().replace(/%$/g, '').trim();
    if (trimmed === '') return;
    const pct = parseFloat(trimmed);
    if (!Number.isFinite(pct)) {
      if (inputEl) inputEl.value = inputEl.defaultValue;
      return;
    }
    // 20% guard. Compute newCpm here so we can confirm against current.
    // Delta is measured from current cpm (not baseline) — same as the
    // dollar path — so a user who already edited up 10% and types 15%
    // is committing a 15% jump from where the row sits now, not 5%.
    const cur = findDealInView(dealKey, salesPlan);
    if (cur) {
      const baseline = getBaseline(cur);
      if (baseline && baseline > 0) {
        const newCpm = baseline * (1 + pct / 100);
        if (newCpm > 0) {
          const ok = await maybeConfirmChange({
            fieldLabel: `CPM (${cur.program})`,
            oldValue: cur.cpm,
            newValue: newCpm,
          });
          if (!ok) {
            if (inputEl) inputEl.value = inputEl.defaultValue;
            return;
          }
        }
      }
    }
    updateDeal(dealKey, salesPlan, d => {
      const baseline = getBaseline(d);
      if (!baseline || baseline === 0) return d;
      const newCpm = baseline * (1 + pct / 100);
      if (!(newCpm > 0)) return d;
      d.cpm = newCpm;
      d._cpmManuallyEdited = true;
      return recalcDeal(d);
    });
  }

  /**
   * Bulk-set CPM for every deal of a program in the plan, ignoring quarter.
   *
   * Used by the bulk-CPM grid: when the user edits one quarter's cell for
   * a program, we want the change to apply to every quarter — CPM is a
   * program-level number for a given broadcast year, not a per-quarter
   * one. Editing a cell that has no schedules booked (inherited cells)
   * still propagates to the quarters that DO have schedules.
   *
   * Per-row CPM locks (`_cpmManuallyEdited`) are still respected — a row
   * the user explicitly edited via the per-row input remains untouched.
   */
  function setCpmForProgramAllQuarters(
    plan: string,
    program: string,
    cpm: number,
  ) {
    if (!plan || !program || isNaN(cpm) || cpm < 0) return;
    setAllDeals(prev => prev.map(d => {
      if (d.salesPlan !== plan || d.program !== program) return d;
      if (d._cpmManuallyEdited) return d;
      if (Math.abs(d.cpm - cpm) < 1e-9) return d;
      return recalcDeal({ ...d, cpm });
    }));
    setAddedRows(prev => prev.map(r => {
      if (r.salesPlan !== plan || r.program !== program) return r;
      const est = r.unitLength
        ? calcEstimateFields({ program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '', unitLength: r.unitLength, totalUnits: r.totalUnits })
        : null;
      const eff = (r.overrideDemo000 != null) ? r.overrideDemo000 : (est?.demo000 ?? null);
      const newRate = eff && cpm ? perSpotRate(eff, cpm) : null;
      return { ...r, cpm, newRate, totalDollars: rowDollars(newRate, r.totalUnits) };
    }));
  }

  /**
   * Bulk-grid percent adjustment for a program. Multiplies the CPM on
   * every Deal AND AddedRow for `plan`+`program` by `(1 + pct/100)`,
   * preserving the SHAPE of CPMs across quarters when they differ.
   *
   * Per-row CPM locks (`_cpmManuallyEdited`) are respected — same
   * policy as the dollar-value bulk-set path. The user can unlock by
   * editing the row's CPM back to baseline (the recalcDeal escape
   * hatch auto-clears the flag when CPM/Rate/Demo match baselines).
   */
  function applyCpmPercentToProgramAllQuarters(
    plan: string,
    program: string,
    pct: number,
  ) {
    if (!plan || !program || !Number.isFinite(pct) || pct === 0) return;
    const factor = 1 + pct / 100;
    if (!(factor > 0)) return;
    setAllDeals(prev => prev.map(d => {
      if (d.salesPlan !== plan || d.program !== program) return d;
      if (d._cpmManuallyEdited) return d;
      const newCpm = d.cpm * factor;
      if (!(newCpm > 0)) return d;
      return recalcDeal({ ...d, cpm: newCpm });
    }));
    setAddedRows(prev => prev.map(r => {
      if (r.salesPlan !== plan || r.program !== program) return r;
      const newCpm = (r.cpm || 0) * factor;
      if (!(newCpm > 0)) return r;
      const est = r.unitLength
        ? calcEstimateFields({ program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '', unitLength: r.unitLength, totalUnits: r.totalUnits })
        : null;
      const eff = (r.overrideDemo000 != null) ? r.overrideDemo000 : (est?.demo000 ?? null);
      const newRate = eff && newCpm ? perSpotRate(eff, newCpm) : null;
      return { ...r, cpm: newCpm, newRate, totalDollars: rowDollars(newRate, r.totalUnits) };
    }));
  }

  /**
   * Live-recalculate when the user steps a numeric input with its spin
   * arrows (or ArrowUp/ArrowDown), while leaving typed input alone.
   *
   * Why it has to be conditional: every one of these inputs is uncontrolled
   * and carries its own value inside its React `key`, so committing while
   * the field is focused changes the key and React destroys and rebuilds the
   * input. For a discrete arrow step that's tolerable — we put focus back
   * afterwards. For typing it is fatal: the caret would be kicked out after
   * every single character, which is the bug that made it impossible to
   * clear and retype a units value.
   *
   * The discriminator is InputEvent.inputType. Typing reports a reason
   * ('insertText', 'deleteContentBackward', 'insertFromPaste', …); a spinner
   * click or an arrow keypress reports nothing at all. So an empty
   * inputType means "stepped, not typed".
   *
   * Returns undefined when the field is read-only so we don't attach a
   * handler that would fight the locked state.
   */
  const stepFocusRef = useRef<string | null>(null);

  function onArrowStep(
    cellId: string,
    commit: (value: string, el: HTMLInputElement) => void,
  ): ((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined {
    if (planIsViewOnly) return undefined;
    return e => {
      // Typed — let onBlur handle it, as before.
      if ((e.nativeEvent as InputEvent).inputType) return;
      stepFocusRef.current = cellId;
      commit(e.target.value, e.target);
    };
  }

  // Put focus back on the input that was just stepped. It is a NEW DOM node
  // (the key changed, so React remounted it), which is why this can't simply
  // hold onto the old element — it's looked up by its stable data-cell id.
  // No dep array: this needs to run after whichever render the commit
  // triggered, and it early-returns on every other render.
  useEffect(() => {
    const id = stepFocusRef.current;
    if (!id) return;
    stepFocusRef.current = null;
    const el = document.querySelector<HTMLInputElement>(`[data-cell="${id}"]`);
    if (el && document.activeElement !== el) el.focus();
  });

  function onUnitsChange(dealKey: string, salesPlan: string, val: string) {
    const units = parseFloat(val);
    if (isNaN(units) || units < 0) return;
    updateDeal(dealKey, salesPlan, d => { d.totalUnits = units; return recalcDeal(d); });
  }

  /**
   * Per-row Demo Imp (000) override. Updates `overrideDemo000` so
   * recalcDeal uses this value instead of the estimate. Flags the row
   * so the bulk grid won't clobber it.
   *
   * Empty string clears the override (returns to the estimate).
   */
  async function onDealDemo000Change(dealKey: string, salesPlan: string, val: string, inputEl?: HTMLInputElement) {
    if (demoCellUnchanged(val, inputEl)) return;
    if (val !== '') {
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    const override = val === '' ? null : parseFloat(val);
    // 20% guard. "Old" is the row's currently-displayed effective demo
    // (override-if-set, else the estimate). Clearing the override (val
    // === '') skips the guard — reverting to estimate is a non-committed
    // change and maybeConfirmChange would compute newValue=null/0 anyway.
    if (override != null) {
      const cur = findDealInView(dealKey, salesPlan);
      if (cur) {
        const est = calcEstimateFields(cur);
        const oldDemo = cur.overrideDemo000 != null ? cur.overrideDemo000 : est.demo000;
        const ok = await maybeConfirmChange({
          fieldLabel: `Demo (000) (${cur.program})`,
          oldValue: oldDemo ?? 0,
          newValue: override,
          formatValue: (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 }),
        });
        if (!ok) {
          if (inputEl) inputEl.value = inputEl.defaultValue;
          return;
        }
      }
    }
    updateDeal(dealKey, salesPlan, d => {
      d.overrideDemo000 = override;
      d._cpmManuallyEdited = true;
      return recalcDeal(d);
    });
  }

  /**
   * Per-row SECONDARY demo impressions (000) override.
   *
   * Deliberately simpler than the primary handler above: a secondary demo
   * is reporting-only. It has no rate or CPM of its own driving the plan's
   * money — the CPM column beside it is derived as rate ÷ impressions — so
   * there's no recalcDeal, no _cpmManuallyEdited flag, and no 20% change
   * guard (that guard exists to catch fat-fingered edits to values that
   * move dollars).
   *
   * Empty string clears the override and the cell falls back to the
   * estimate. The demo key is removed entirely rather than set to null, so
   * "no override" is one representation and the saved JSON stays tidy.
   */
  function onSecondaryDemo000Change(
    dealKey: string,
    salesPlan: string,
    demo: string,
    val: string,
    inputEl?: HTMLInputElement,
  ): void {
    if (val !== '') {
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) {
        // Same reject-and-restore as the other numeric inputs — the field
        // is uncontrolled, so putting defaultValue back is what undoes it.
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    updateDeal(dealKey, salesPlan, d => {
      const next = { ...(d.overrideSecondary000 ?? {}) };
      if (val === '') delete next[demo];
      else next[demo] = parseFloat(val);
      d.overrideSecondary000 = Object.keys(next).length > 0 ? next : null;
      return d;
    });
  }

  /** AddedRow equivalent of onSecondaryDemo000Change. */
  function onAddedSecondaryDemo000Change(
    key: string,
    demo: string,
    val: string,
    inputEl?: HTMLInputElement,
  ): void {
    if (val !== '') {
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    updateAddedRow(key, r => {
      const next = { ...(r.overrideSecondary000 ?? {}) };
      if (val === '') delete next[demo];
      else next[demo] = parseFloat(val);
      return { ...r, overrideSecondary000: Object.keys(next).length > 0 ? next : null };
    });
  }

  /**
   * Per-row Rate override. Setting the rate directly back-solves the CPM
   * (rate / effective demo000) so the row stays internally consistent.
   * Flags the row so the bulk grid won't clobber it.
   */
  async function onDealRateChange(dealKey: string, salesPlan: string, val: string, inputEl?: HTMLInputElement) {
    if (val !== '') {
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    const newRate = val === '' ? null : parseFloat(val);
    // 20% guard. Clearing the rate (val === '') skips the guard — same
    // pattern as the CPM/demo blank-input cases. Rate ultimately drives
    // CPM via the back-solve below, but it's the typed value the user
    // sees, so we compare against the row's current displayed rate.
    if (newRate != null) {
      const cur = findDealInView(dealKey, salesPlan);
      if (cur) {
        const oldRate = cur.newRate != null ? cur.newRate : (cur.baseRate ?? cur.rate);
        const ok = await maybeConfirmChange({
          fieldLabel: `Rate (${cur.program})`,
          oldValue: oldRate ?? 0,
          newValue: newRate,
          formatValue: (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 }),
        });
        if (!ok) {
          if (inputEl) inputEl.value = inputEl.defaultValue;
          return;
        }
      }
    }
    updateDeal(dealKey, salesPlan, d => {
      const est = calcEstimateFields(d);
      const effDem = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
      if (newRate != null && effDem) {
        d.cpm = newRate / effDem;
        d.newRate = Math.round(newRate);
        // Off the ROUNDED rate, not the typed value — the rate column is
        // what Salesforce receives, so the total has to follow it.
        d.totalDollars = rowDollars(d.newRate, d.totalUnits);
        d.newHhCpm = est.hh000 && d.newRate ? d.newRate / est.hh000 : null;
      } else {
        d.newRate = null;
        d.totalDollars = 0;
        d.newHhCpm = null;
      }
      d._cpmManuallyEdited = true;
      return d;
    });
  }


  // ─── Added rows ─────────────────────────────────────────────────────────────

  // Derive quarter+year for newly-added rows from the active tab,
  // so manual rows are scoped to the same tab the user is viewing.
  function getActiveQuarterYear(): { quarter: string; year: number } {
    // Active quarter tab (normal case — user is on 4Q'26 / 1Q'27 / etc).
    // Look up in visibleQuarters first so synthesized tabs (4Q'27 etc,
    // not in the estimate version) still resolve.
    const tab = visibleQuarters.find(t => t.key === activeQuarter)
             || QUARTER_TABS.find(t => t.key === activeQuarter);
    if (tab) return { quarter: tab.quarter, year: tab.year };
    // Summary tab fallback — use the first visible quarter so new shows
    // land in the right cycle for the current plan's marketplace. Previous
    // fallback hardcoded Q4/2025 which silently put added shows in the
    // wrong season on empty plans.
    const firstTab = visibleQuarters[0] ?? QUARTER_TABS[0];
    if (firstTab) return { quarter: firstTab.quarter, year: firstTab.year };
    // Last-resort fallback (no estimate version active at all). Pull
    // from a visible deal; only relevant when QUARTER_TABS is empty.
    const fallback = filteredDeals[0];
    return { quarter: fallback?.quarter || 'Q4', year: fallback?.year || 2025 };
  }

  /**
   * Unified metadata lookup for a plan name. Reads from the Snowflake
   * saved-plan cache (PLAN_VERSION carries the metadata).
   *
   * Returns null if the plan name doesn't match a saved plan (e.g.
   * pure SF template that's never been saved).
   */
  function getPlanMeta(planName: string): ManualPlanMeta | null {
    const saved = getSavedPlanByName(planName);
    const v = saved?.latestVersion;
    if (!saved || !v) return null;
    return {
      planName,
      advertiser: v.advertiser ?? '',
      dealName:   v.dealName   ?? '',
      agency:     v.agency     ?? '',
      status:     saved.status ?? '',
      brand:      v.brand      ?? '',
      ae:         v.ae         ?? '',
      buyer:      v.buyer      ?? '',
      demo:       v.demo       ?? '',
      marketplace: v.marketplace ?? '',
      equiv:      v.equiv      ?? '',
      year:       v.year       ?? '',
      // SF lookup IDs + computed sales plan name — needed by the
      // Edit Details modal to pre-fill the lookups (without IDs the
      // typeaheads can't render the chosen items in "picked" state)
      // and by the Plan Details panel to show the formula string.
      advertiserId: v.advertiserId,
      brandId:      v.brandId,
      contactId:    v.contactId,
      accountId:    v.accountId,
      salesPlanName: v.salesPlanName,
      // Plan-level default unit length. Surfaced here so the Plan
      // Details "Default :30" chip AND the Edit Details dirty-check
      // can read it without a second lookup path. Nullable — legacy
      // plans that predate the column carry undefined.
      defaultUnitLength: v.defaultUnitLength ?? null,
    };
  }

  /**
   * Plan-level default unit length lookup. Checks the plan meta (which
   * reflects both the Create Plan modal's pick AND the Edit Details
   * modal's edits, whichever is freshest) first, then falls back to
   * the saved-plan latest version's persisted value. Returns empty
   * string when the plan has no default configured (legacy behavior:
   * new rows start blank).
   */
  function resolveDefaultUnitLength(salesPlan: string): string {
    const meta = getPlanMeta(salesPlan);
    if (meta?.defaultUnitLength) return String(meta.defaultUnitLength);
    const saved = getSavedPlanByName(salesPlan);
    if (saved?.latestVersion?.defaultUnitLength) {
      return String(saved.latestVersion.defaultUnitLength);
    }
    return '';
  }

  function addRow(program: string, salesPlan: string) {
    addedRowCounter.current++;
    const key = 'D-ADD-' + addedRowCounter.current;
    // Demo resolution: prefer the program's existing demo in the plan,
    // then any deal in the plan, then the manual plan's stored demo
    // (covers empty plans with no deals yet).
    const manualMeta = getPlanMeta(salesPlan);
    const demo = filteredDeals.find(d => d.program === program)?.demo
      || filteredDeals[0]?.demo
      || manualMeta?.demo
      || '';
    // CPM default — pick up the program's existing CPM in THIS plan so
    // every new row of the same show starts at the established rate.
    // We look at deals first, then prior addedRows in the same plan;
    // never reach into other plans' data.
    const existingCpm = allDeals.find(d => d.salesPlan === salesPlan && d.program === program && d.cpm > 0)?.cpm
      ?? addedRows.find(r => r.salesPlan === salesPlan && r.program === program && r.cpm > 0)?.cpm
      ?? 0;
    const { quarter, year } = getActiveQuarterYear();
    // Resolve SF Programs__c.ID at row-creation time so the push-to-SF
    // flow doesn't error with MISSING_PROGRAM_IDS. Prefers inheritance
    // from an existing PLAN_DEAL/AddedRow in the same plan, then falls
    // back to the SF catalog fetched on mount. Null when the program
    // isn't in SF — user hits the pre-push repair prompt.
    const programId = resolveSfProgramId(program, salesPlan);
    // Seed the new row's length with the plan's default (settable via
    // Create Plan modal / Edit Details). Empty when the plan has no
    // default — same blank-cell behavior as before.
    const defaultLen = resolveDefaultUnitLength(salesPlan);
    setAddedRows(prev => [...prev, {
      key, program, salesPlan, quarter, year,
      unitLength: defaultLen, cpm: existingCpm, totalUnits: 1,
      guaranteed: 0, actual000: 0, hh_000: null, demo, overrideDemo000: null,
      baseCpm: existingCpm, baseRate: null,
      // `originalCpm` stays null until first save — backend stamps
      // current cpm as the frozen v1 value at that point. Baseline
      // mirrors the inherited CPM so the per-row `± %` input has a
      // sensible starting point; falls back to null (disabling the
      // `% ` field) when the row has no inherited CPM yet.
      originalCpm: null,
      _baselineCpm: existingCpm > 0 ? existingCpm : null,
      programId,
    }]);
  }

  /**
   * Open the Add Show modal. Seeds the quarter picker based on which
   * tab the user is on:
   *   • Quarter tab → that quarter is the only one pre-checked
   *     (single-quarter add is one click, same as before)
   *   • Summary tab → all four quarters pre-checked (the "I want this
   *     show across the whole year" flow)
   * The user can toggle chips to refine before picking a program.
   */
  function openAddShowModal(): void {
    setAddShowSearch('');
    // Default: EVERY visible quarter pre-checked, regardless of which
    // tab the user was on. Users almost always want a new show added
    // across the plan's full quarter set — the old "only the current
    // tab" default forced them to click the other chips explicitly
    // every time. `visibleQuarters` is marketplace-driven, so a CY
    // plan still gets its correct 4-quarter set (no 4Q'26 leak into
    // a CY view).
    const initial = new Set<string>();
    if (isLateQuarterOnlyEditing && planLateQuarter) {
      // ⚠ The one place the tab-scoped lock isn't enough.
      //
      // On a pushed plan only the late-quarter tab is editable, but this modal
      // writes rows into every quarter it has checked — so the default above
      // would seed rows straight into the locked quarters of an order that has
      // already gone to Salesforce. Restrict the seed to the one quarter the
      // user is allowed to touch; the `locked` check on each quarter chip in
      // the modal's JSX stops them re-checking the others.
      initial.add(planLateQuarter.key);
    } else {
      visibleQuarters.forEach(t => initial.add(t.key));
    }
    setAddShowQuarterKeys(initial);
    // Reset the multi-select program set — each modal open is a fresh
    // batch, not a resumed one.
    setAddShowSelected(new Set());
    setShowAddShowModal(true);
  }

  /**
   * Add a program to every quarter currently checked in the modal.
   * One row per checked quarter, all seeded with the same defaults
   * (length empty, CPM inherited from any prior row of that program,
   * demo from plan meta). User refines each row after the modal
   * closes. Toast confirms count when >1 quarter was hit.
   */
  function commitAddShow() {
    // Resolve the checked quarter keys against the plan's visible
    // quarters — handles synthesized tabs (4Q'27 etc) that aren't in
    // the estimate version's QUARTER_TABS but ARE part of the plan.
    const quarters = visibleQuarters.filter(t => addShowQuarterKeys.has(t.key));
    const programs = Array.from(addShowSelected);
    if (quarters.length === 0 || programs.length === 0) return;

    // Demo resolution: prefer any deal in the plan, then the manual plan's
    // stored demo (covers empty plans with no deals yet).
    const manualMeta = getPlanMeta(planFilter);
    const demo = filteredDeals[0]?.demo || manualMeta?.demo || '';
    // Plan-level default unit length shared across all new rows this
    // batch. Empty when no default is set — legacy blank behavior.
    const defaultLen = resolveDefaultUnitLength(planFilter);

    // Build every new row up front then flush once — avoids N React
    // state updates when adding a bunch of shows × a bunch of quarters
    // simultaneously.
    const newRows: AddedRow[] = [];
    for (const program of programs) {
      // CPM default — inherit the program's CPM if any deal or addedRow
      // for this program already exists in the plan. Scoped to the
      // current plan.
      const existingCpm = allDeals.find(d => d.salesPlan === planFilter && d.program === program && d.cpm > 0)?.cpm
        ?? addedRows.find(r => r.salesPlan === planFilter && r.program === program && r.cpm > 0)?.cpm
        ?? 0;
      // Resolve SF Programs__c.ID once per show.
      const programId = resolveSfProgramId(program, planFilter);
      for (const t of quarters) {
        addedRowCounter.current++;
        const key = 'D-ADD-' + addedRowCounter.current;
        newRows.push({
          key, program, salesPlan: planFilter,
          quarter: t.quarter, year: t.year,
          unitLength: defaultLen, cpm: existingCpm, totalUnits: 1,
          guaranteed: 0, actual000: 0, hh_000: null,
          demo, overrideDemo000: null,
          baseCpm: existingCpm, baseRate: null,
          originalCpm: null,
          _baselineCpm: existingCpm > 0 ? existingCpm : null,
          programId,
        });
      }
    }
    setAddedRows(prev => [...prev, ...newRows]);
    setShowAddShowModal(false);
    setAddShowSearch('');
    setAddShowSelected(new Set());

    // Toast reads clean for each combinatorial: single show / single
    // quarter → silent (the new row is right there), otherwise summarize.
    if (programs.length === 1 && quarters.length > 1) {
      const programLabel = PROGRAM_DISPLAY[programs[0]] || programs[0];
      showToast(`✓ Added ${programLabel} to ${quarters.length} quarters`);
    } else if (programs.length > 1) {
      showToast(`✓ Added ${programs.length} shows across ${quarters.length} quarter${quarters.length === 1 ? '' : 's'}`);
    }
  }

  /**
   * Delete a saved PLAN_DEAL row from the plan. Opens a confirm modal,
   * then removes it from allDeals in-memory. The row is not deleted from
   * Snowflake directly — save creates a new PLAN_VERSION with only the
   * remaining rows, so the deleted row drops out of the new snapshot.
   *
   * Blocked on view-only plans (pushed, or 25/26 SF sources).
   *
   * Doesn't fire the auto-save-on-delete pattern because the user may
   * want to make several deletions/edits and Save once. Marks the plan
   * dirty; existing dirty check compares planSignature against the
   * saved snapshot.
   */
  function requestDeleteSavedRow(deal: Deal): void {
    if (planIsViewOnly) return;
    new Promise<boolean>(resolve => {
      setDeleteRowConfirm({
        program: deal.program,
        quarter: deal.quarter,
        year: deal.year,
        totalUnits: deal.totalUnits,
        resolve,
      });
    }).then(proceed => {
      if (!proceed) return;
      setAllDeals(prev => prev.filter(d =>
        !(d.dealKey === deal.dealKey && d.salesPlan === deal.salesPlan)
      ));
    });
  }

  function removeAddedRow(key: string) {
    setAddedRows(prev => prev.filter(r => r.key !== key));
  }

  function updateAddedRow(key: string, updater: (r: AddedRow) => AddedRow) {
    setAddedRows(prev => prev.map(r => r.key === key ? updater({ ...r }) : r));
  }

  function onAddedLengthChange(key: string, length: string) {
    updateAddedRow(key, r => {
      // Demo: prefer the row's value, then any deal-of-this-program in
      // the CURRENT plan. Do NOT reach into `allDeals` — that pulls
      // values from unrelated plans and cross-contaminates a new plan
      // with someone else's SF data.
      const demo = r.demo
        || filteredDeals.find(d => d.program === r.program)?.demo
        || '';
      // Impressions / ratings come from the ESTIMATE for the new
      // (program, quarter, demo, length). CPM is the user's own
      // negotiated value — preserved across length changes, blank
      // until they type it. The pricing pipeline is:
      //   rate         = cpm × demo_imps   (auto)
      //   totalDollars = rate × totalUnits (auto)
      //   cpmHH        = rate / hh_imps    (auto, in renderAddedRow)
      const cpm = r.cpm;
      const est = length
        ? calcEstimateFields({ program: r.program, quarter: r.quarter, year: r.year, demo, unitLength: length, totalUnits: r.totalUnits })
        : null;
      const effectiveDem = est?.demo000 ?? null;
      const newRate = effectiveDem && cpm ? perSpotRate(effectiveDem, cpm) : null;
      // Re-baseline edit flags so a length flip reads "clean" — it's
      // a rescaling of impressions, not an edit of CPM/Rate.
      return {
        ...r,
        unitLength: length,
        demo,
        cpm,
        overrideDemo000: null,
        newRate,
        totalDollars: rowDollars(newRate, r.totalUnits),
        baseCpm: cpm,
        baseRate: newRate,
      };
    });
  }

  async function onAddedCpmChange(key: string, val: string, inputEl?: HTMLInputElement) {
    const cpm = parseFloat(val) || 0;
    // 20% guard against the row's current cpm. Same skip-when-zero rule
    // as the Deals path — first CPM entry on a fresh + Add Row isn't a
    // "change", it's a starting value.
    const curRow = addedRows.find(r => r.key === key);
    if (curRow) {
      const ok = await maybeConfirmChange({
        fieldLabel: `CPM (${curRow.program})`,
        oldValue: curRow.cpm,
        newValue: cpm,
      });
      if (!ok) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
    }
    updateAddedRow(key, r => {
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year,
        demo: r.demo || filteredDeals.find(x => x.program === r.program)?.demo || allDeals.find(x => x.program === r.program)?.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      const effectiveDem = getEffectiveDemo000(r, est);
      const newRate = effectiveDem && cpm ? perSpotRate(effectiveDem, cpm) : null;
      // Direct `$` edit also jumps `_baselineCpm` to the new value so
      // the `± %` field reads as 0% afterward (same semantics as
      // `onCpmChange` for Deals).
      return {
        ...r,
        cpm,
        _baselineCpm: cpm,
        newRate,
        totalDollars: rowDollars(newRate, r.totalUnits),
      };
    });
  }

  /** AddedRow analog of `onCpmPercentChange`. Same baseline-replacing semantics. */
  async function onAddedCpmPercentChange(key: string, val: string, inputEl?: HTMLInputElement) {
    const trimmed = val.trim().replace(/%$/g, '').trim();
    if (trimmed === '') return;
    const pct = parseFloat(trimmed);
    if (!Number.isFinite(pct)) {
      if (inputEl) inputEl.value = inputEl.defaultValue;
      return;
    }
    // 20% guard — measured from current cpm to newCpm (baseline-relative).
    const curRow = addedRows.find(r => r.key === key);
    if (curRow) {
      const baseline = getBaseline(curRow);
      if (baseline && baseline > 0) {
        const newCpm = baseline * (1 + pct / 100);
        if (newCpm > 0) {
          const ok = await maybeConfirmChange({
            fieldLabel: `CPM (${curRow.program})`,
            oldValue: curRow.cpm,
            newValue: newCpm,
          });
          if (!ok) {
            if (inputEl) inputEl.value = inputEl.defaultValue;
            return;
          }
        }
      }
    }
    updateAddedRow(key, r => {
      const baseline = getBaseline(r);
      if (!baseline || baseline === 0) return r;
      const newCpm = baseline * (1 + pct / 100);
      if (!(newCpm > 0)) return r;
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year,
        demo: r.demo || filteredDeals.find(x => x.program === r.program)?.demo || allDeals.find(x => x.program === r.program)?.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      const effectiveDem = getEffectiveDemo000(r, est);
      const newRate = effectiveDem ? perSpotRate(effectiveDem, newCpm) : null;
      return { ...r, cpm: newCpm, newRate, totalDollars: rowDollars(newRate, r.totalUnits) };
    });
  }

  async function onAddedDemo000Change(key: string, val: string, inputEl?: HTMLInputElement) {
    if (demoCellUnchanged(val, inputEl)) return;
    // Empty string = clear override (revert to estimate). Same skip-guard
    // logic as the Deals demo (000) path.
    const override = val === '' ? null : (() => {
      const n = Number(val);
      return isNaN(n) || n < 0 ? null : n;
    })();
    if (override != null) {
      const curRow = addedRows.find(r => r.key === key);
      if (curRow) {
        const est = curRow.unitLength ? calcEstimateFields({
          program: curRow.program, quarter: curRow.quarter, year: curRow.year,
          demo: curRow.demo || filteredDeals.find(x => x.program === curRow.program)?.demo || allDeals.find(x => x.program === curRow.program)?.demo || '',
          unitLength: curRow.unitLength, totalUnits: curRow.totalUnits,
        }) : null;
        const oldDemo = curRow.overrideDemo000 != null ? curRow.overrideDemo000 : (est?.demo000 ?? null);
        const ok = await maybeConfirmChange({
          fieldLabel: `Demo (000) (${curRow.program})`,
          oldValue: oldDemo ?? 0,
          newValue: override,
          formatValue: (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 }),
        });
        if (!ok) {
          if (inputEl) inputEl.value = inputEl.defaultValue;
          return;
        }
      }
    }
    updateAddedRow(key, r => {
      // Same parse-with-fallback semantics for the actual state update —
      // an invalid string (non-numeric, negative) preserves the prior
      // override instead of clobbering it.
      const finalOverride = val === '' ? null : (() => {
        const n = Number(val);
        return isNaN(n) || n < 0 ? r.overrideDemo000 ?? null : n;
      })();
      const effectiveDem = finalOverride ?? null;
      const newRate = effectiveDem && r.cpm ? perSpotRate(effectiveDem, r.cpm) : r.newRate ?? null;
      return { ...r, overrideDemo000: finalOverride, newRate, totalDollars: rowDollars(newRate, r.totalUnits) };
    });
  }

  async function onAddedRateChange(key: string, val: string, inputEl?: HTMLInputElement) {
    const newRate = val === '' ? null : (() => {
      const n = Number(val);
      return isNaN(n) || n < 0 ? null : n;
    })();
    if (newRate != null) {
      const curRow = addedRows.find(r => r.key === key);
      if (curRow) {
        const oldRate = curRow.newRate ?? null;
        const ok = await maybeConfirmChange({
          fieldLabel: `Rate (${curRow.program})`,
          oldValue: oldRate ?? 0,
          newValue: newRate,
          formatValue: (v: number) => '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 }),
        });
        if (!ok) {
          if (inputEl) inputEl.value = inputEl.defaultValue;
          return;
        }
      }
    }
    updateAddedRow(key, r => {
      const finalRate = val === '' ? null : (() => {
        const n = Number(val);
        return isNaN(n) || n < 0 ? r.newRate ?? null : n;
      })();
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year,
        demo: r.demo || filteredDeals.find(x => x.program === r.program)?.demo || allDeals.find(x => x.program === r.program)?.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      const effectiveDem = getEffectiveDemo000(r, est);
      const newCpm = finalRate && effectiveDem ? finalRate / effectiveDem : r.cpm;
      return { ...r, newRate: finalRate, cpm: newCpm, totalDollars: rowDollars(finalRate, r.totalUnits) };
    });
  }

  function onAddedUnitsChange(key: string, val: string) {
    // No-op on empty / invalid / negative — same safety valve the
    // saved-Deal onUnitsChange uses. This lets the uncontrolled input
    // hold a temporarily-empty DOM state (mid-clear-and-retype)
    // without the state snapping back and locking the field.
    const totalUnits = parseFloat(val);
    if (isNaN(totalUnits) || totalUnits < 0) return;
    updateAddedRow(key, r => {
      const newRate = r.newRate ?? null;
      return { ...r, totalUnits, totalDollars: rowDollars(newRate, totalUnits) };
    });
  }

  /**
   * Persist the active plan to Snowflake. Versioned + append-only: every
   * successful save creates a new PLAN_VERSION + PLAN_DEAL set. The
   * frontend cache is updated targeted (no full re-hydrate) and the
   * isDirty pill clears.
   *
   * Flow:
   *   1. Validate (plan selected, no red cells, not already in flight).
   *   2. Apply pending plan rename (mirrors saveVersion's behavior).
   *   3. Build the SavePlanRequest from current React state + active
   *      estimate version + plan metadata.
   *   4. POST. On success, re-baseline this plan's snapshot + show toast.
   *      On error, surface the message via toast and leave state untouched.
   *
   * Per-deal `impressions_000` is the EFFECTIVE value at save time —
   * user override if set, otherwise the estimate's computed demo (000).
   * This locks the plan's impressions to the version that was active
   * when saved (matches the "versioned snapshot" model).
   */
  /**
   * Commit the active plan (deals + metadata) to Snowflake as a new
   * PLAN_VERSION row.
   *
   * `opts.metaOverride` lets a caller provide a fresh ManualPlanMeta
   * that supersedes what `getPlanMeta` would return at the moment of
   * the call. Edit Plan Details → Save immediately calls saveToSnowflake
   * with the just-built meta; passing the override threads it through
   * without needing a re-render.
   *
   * `opts.nicknameOverride` plays the same role for `planNameDraft`
   * (Edit Save renames flow): we set planNameDraft then call save in
   * the same handler — the override is what the rename block sees.
   *
   * When neither is provided, behavior matches the legacy main-Save
   * button path: both come from React state.
   */
  async function saveToSnowflake(opts?: {
    metaOverride?: ManualPlanMeta;
    nicknameOverride?: string;
  }) {
    if (!planFilter) {
      showToast('Pick a plan before saving.');
      return;
    }
    // Flush any in-flight typed value in a CPM/Rate/Demo input to state
    // before we read from state. Those inputs are uncontrolled with
    // defaultValue + onBlur commit — if the user typed a value but Save
    // was triggered without their input losing focus (paste + Cmd+Enter,
    // programmatic click, autofill), state would still hold the pre-edit
    // value and Save would ship stale data.
    //
    // Two-step flush:
    //   (a) blur() fires the input's onBlur handler synchronously,
    //       which calls setAllDeals / setAddedRows (React's setState is
    //       async — it queues the update but doesn't apply it yet).
    //   (b) yield to the event loop with a zero-delay Promise. React
    //       processes the queued state updates + re-renders + runs
    //       inline ref assignments (allDealsRef.current = allDeals).
    //       When we resume, `allDealsRef.current` holds the fresh
    //       value; the CLOSURE's `allDeals` variable stays stale, so
    //       downstream reads use the ref (see currentDeals below).
    const active = document.activeElement as HTMLElement | null;
    if (active && active.tagName === 'INPUT') {
      active.blur();
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
    // Fresh state reads via refs — see comment above.
    const currentDeals: Deal[] = allDealsRef.current;
    const currentAddedRows: AddedRow[] = addedRowsRef.current;
    if (hasInvalid) {
      showToast('Fix the red CPM/Rate cells before saving.');
      return;
    }
    if (isSaving) return;
    setIsSaving(true);

    try {
      // Plan-rename support — same shape as saveVersion. Compute the
      // post-rename data so the save payload reflects the new name,
      // BUT defer the actual React-state commits (setAllDeals,
      // setAddedRows, setPlanFilter) until AFTER savePlan succeeds —
      // otherwise a save failure would leave the UI showing the new
      // name while Snowflake still has the old, and on reload the
      // rename would silently revert.
      // `opts.nicknameOverride` (passed by submitEditPlan) wins over
      // planNameDraft when present, because the Edit Save flow doesn't
      // wait for React to re-render after setPlanNameDraft.
      // Use currentDeals / currentAddedRows (ref-backed, freshest
      // values including any onBlur flush that just happened) rather
      // than the closure's `allDeals` / `addedRows` (captured when
      // the outer function was called, before the blur).
      let dealsToSave = currentDeals;
      let rowsAfterSave = currentAddedRows;
      let effectivePlanName = planFilter;
      const newName = (opts?.nicknameOverride ?? planNameDraft).trim();
      const isRename = !!(newName && newName !== planFilter);
      if (isRename) {
        dealsToSave = currentDeals.map(d =>
          d.salesPlan === planFilter
            ? { ...d, salesPlan: newName,
                _meta: d._meta ? { ...d._meta, planName: newName } : undefined }
            : d
        );
        rowsAfterSave = currentAddedRows.map(r =>
          r.salesPlan === planFilter ? { ...r, salesPlan: newName } : r
        );
        effectivePlanName = newName;
      }

      // Season-narrow the save payload. Without this, when a rolled
      // 26/27 plan shares a nickname with its 25/26 SF source (nickname
      // consolidation), `dealsToSave.filter(d.salesPlan === planName)`
      // matches BOTH the 26/27 rolled deals AND the source's 25/26
      // deals — the source's year=2025 rows then get inserted into the
      // 26/27 PLAN_VERSION, and any subsequent Push to SF fails on
      // `broadcastQuarterFirstWeek(?, 2025)` (the calendar table only
      // covers 2026-2027). Same rationale as the SF Structure preview
      // + push confirmation totals fixes upstream: narrow to the plan's
      // visibleQuarters so only the current-season deals land in the
      // saved version.
      const saveVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
      const planDeals = dealsToSave.filter(d =>
        d.salesPlan === effectivePlanName
        && saveVisibleKeys.has(`${d.quarter}|${d.year}`)
      );
      const planAddedRows = rowsAfterSave.filter(r =>
        r.salesPlan === effectivePlanName
        && saveVisibleKeys.has(`${r.quarter}|${r.year}`)
      );

      // Existing Snowflake record (if previously saved) — carries planId
      // (so backend appends a new version), sourcePlanSfId, and the
      // original sourcePlanName from template time.
      //
      // Look up by planFilter (the ORIGINAL nickname), not
      // effectivePlanName (the post-rename nickname). In the rename
      // path, effectivePlanName is the new name the plan doesn't have
      // yet in the saved-plans cache — the lookup would miss, existing
      // resolves to null, and the payload's planId ends up null. The
      // backend then interprets that as "create a brand-new plan" and
      // silently DUPLICATES the record, leaving the original as a
      // 0-deal ghost bearing the old nickname. planFilter still points
      // at the pre-rename entry in the cache, so this lookup resolves
      // the actual plan_id and the backend does an in-place rename.
      const existing = getSavedPlanByName(planFilter);
      const firstSfDeal = planDeals.find(d => d.salesPlanSfId);
      // Source plan provenance resolution. Three sources, in priority:
      //   1. Existing Snowflake record (carries the value from a prior save)
      //   2. Edit Save's opts.metaOverride (freshly-edited meta from the
      //      Edit Details modal, before the Snowflake round-trip lands)
      //   3. Inferred from the first SF-bound deal (legacy "templated
      //      from SF" flow where dealKey IS the SF deal key)
      // All downstream reads of meta in this function (sourcePlanName,
      // advertiserId, ae, buyer, marketplace, …) flow through
      // `draftMeta` and the `meta` derived from it below, so a single
      // substitution here covers the whole save.
      const draftMeta = opts?.metaOverride;
      const sourcePlanName =
        existing?.sourcePlanName ?? draftMeta?.sourcePlanName ?? planFilter;
      const sourcePlanSfId =
        existing?.sourcePlanSfId
        ?? draftMeta?.sourcePlanSfId
        ?? firstSfDeal?.salesPlanSfId
        ?? null;
      const seasonPrimaryYear = activeVersion?.primaryYear
        ?? existing?.seasonPrimaryYear
        ?? new Date().getFullYear();
      // Prefer the override (from Edit Save) over the React-state
      // closure so the freshly-edited meta lands in the saved version.
      const meta = opts?.metaOverride ?? getPlanMeta(effectivePlanName);

      /**
       * Resolve this row's secondary demo impressions into the flat
       * { demo: value } map the backend stores.
       *
       * Driven by the plan's CURRENT secondaryDemos list, not by whatever
       * keys happen to sit on the row — so removing a demo from the plan
       * stops writing it, instead of leaving orphaned keys accumulating in
       * the JSON across saves.
       *
       * Values are resolved (typed override if present, else the estimate)
       * for the same reason impressions_000 is: a saved plan must not move
       * when a new estimate version is uploaded. Demos with no estimate
       * data and no typed value are omitted rather than written as null.
       */
      const buildSecondaryPayload = (row: Deal | AddedRow): Record<string, number> => {
        const out: Record<string, number> = {};
        for (const sd of secondaryDemos) {
          const v = effectiveSecondary000(row, sd);
          if (v != null && !isNaN(v)) out[sd] = v;
        }
        return out;
      };

      // Shape each Deal / AddedRow → backend PlanDeal. `impressions_000`
      // = override (if set) ?? estimate's demo000 ?? null. Always
      // resolved at save time so the saved value won't drift on a
      // future estimate upload.
      const dealsPayload: SavePlanRequest['deals'] = [
        ...planDeals.map(d => {
          const est = calcEstimateFields(d);
          const eff = d.overrideDemo000 != null
            ? d.overrideDemo000
            : (est.demo000 ?? null);
          return {
            program: d.program,
            day: DAY_TIME[d.program] ?? null,
            quarter: d.quarter,
            year: d.year,
            unitLength: parseFloat(String(d.unitLength)) || null,
            demo: d.demo ?? null,
            totalUnits: d.totalUnits ?? null,
            cpm: d.cpm ?? null,
            // Frozen v1 CPM — passed through unchanged for already-stamped
            // deals. The backend stamps `cpm` as `original_cpm` for any
            // deal where this is null (covers + Add Row deals being
            // first-saved).
            originalCpm: d.originalCpm ?? null,
            rate: d.newRate ?? null,
            totalDollars: d.totalDollars ?? null,
            impressions_000: eff,
            // Resolved secondary impressions, same contract as
            // impressions_000 above: typed value if there is one, else the
            // estimate's. Built from the plan's current secondaryDemos so
            // a demo removed from the plan stops being written.
            secondaryImpressions000: buildSecondaryPayload(d),
            cpmManuallyEdited: d._cpmManuallyEdited === true,
            account: d.account ?? null,
            brand: d.brand ?? null,
            // Roll-forward provenance — when a deal was templated from a
            // 25/26 SF row, `_rolledFromSfDealKey` carries the original
            // SF key. Falls back to dealKey for SF-templated deals saved
            // directly (where dealKey *is* the SF key by convention).
            sourceSfDealKey: d._rolledFromSfDealKey ?? d.dealKey ?? null,
            // Salesforce Programs__c.ID — round-tripped so pushes-to-SF
            // can populate Deal__c.Program__c without a lookup.
            programId: d.programId ?? null,
          };
        }),
        ...planAddedRows.map(r => {
          // For added rows we don't have a stored Deal — synthesize the
          // input to calcEstimateFields from the row's fields.
          const lenNum = parseFloat(String(r.unitLength)) || 0;
          const est = lenNum > 0
            ? calcEstimateFields({
                program: r.program,
                quarter: r.quarter,
                year: r.year,
                demo: r.demo ?? planDeals[0]?.demo ?? '',
                unitLength: r.unitLength,
                totalUnits: r.totalUnits,
              })
            : null;
          const eff = r.overrideDemo000 != null
            ? r.overrideDemo000
            : (est?.demo000 ?? null);
          return {
            program: r.program,
            day: DAY_TIME[r.program] ?? null,
            quarter: r.quarter,
            year: r.year,
            unitLength: lenNum || null,
            demo: r.demo ?? planDeals[0]?.demo ?? null,
            totalUnits: r.totalUnits ?? null,
            cpm: r.cpm ?? null,
            // + Add Row deals have no pre-existing original — backend
            // stamps current cpm as `original_cpm` when the field is
            // null. From the second save onward, `r.originalCpm` will
            // already be set (round-tripped via savedPlanToDeals).
            originalCpm: null,
            rate: r.newRate ?? null,
            totalDollars: r.totalDollars ?? null,
            impressions_000: eff,
            // Resolved secondary impressions, same contract as
            // impressions_000 above: typed value if there is one, else the
            // estimate's. Built from the plan's current secondaryDemos so
            // a demo removed from the plan stops being written.
            secondaryImpressions000: buildSecondaryPayload(r),
            cpmManuallyEdited: false, // added rows always start as estimate-driven
            account: null,
            brand: null,
            sourceSfDealKey: null,
            // Programs__c.ID — if the AddedRow inherited it from + Add Row
            // (same program as an existing PLAN_DEAL row in the plan),
            // r.programId is set. + Add Show that introduces a fresh
            // program to the plan currently leaves this null and the
            // push-to-SF flow prompts the user to repair.
            programId: r.programId ?? null,
          };
        }),
      ];

      // Coerce the local meta.status into the picklist enum so the
      // backend whitelist never rejects junk left over from older
      // free-text drafts (defaults to Working — same as the DB default).
      const metaStatus =
        meta?.status === 'Hold' || meta?.status === 'Order' ? meta.status : 'Working';

      // Prefer the saved plan's existing IDs (re-save case) over the
      // manual-draft IDs, so re-saving a plan that was first saved
      // before the lookup migration doesn't accidentally clobber any
      // backfilled IDs. New plans hit the meta path; existing plans
      // round-trip whatever's already on PLAN_VERSION.
      const advertiserId = existing?.latestVersion?.advertiserId ?? meta?.advertiserId ?? null;
      const brandId      = existing?.latestVersion?.brandId      ?? meta?.brandId      ?? null;
      const contactId    = existing?.latestVersion?.contactId    ?? meta?.contactId    ?? null;
      const accountId    = existing?.latestVersion?.accountId    ?? meta?.accountId    ?? null;
      // Sales Plan Name (formula). Same precedence — prefer the
      // saved value (back-filled or written by an earlier save) over
      // the manual-draft snapshot. Re-computing from the current
      // request's `agency`/`brand`/etc. would diverge from the
      // backfilled string for legacy rows, so prefer what's on disk.
      // TBD carve-out: a stored SPN containing "TBD" is a placeholder
      // artifact, not a real SF-workflow value — the formula baked in
      // the literal string when the user picked TBD lookups. Once they
      // swap in real Advertiser / Brand / Agency, the stored SPN would
      // otherwise stay frozen at "TBD-TBD-TBD-…" forever (prefer-disk
      // wins over the fresh compute), leaving the Plan Details panel
      // showing a stale name and the SPN-collision gate keying off a
      // string that no longer describes the plan.
      //
      // So: recompute whenever the stored value still mentions TBD.
      // Non-TBD stored names keep the prefer-disk behavior (backfilled
      // rows + real SF workflow values must round-trip verbatim).
      // Fallback only — used when the recompute below can't produce a
      // complete name. meta first so an Edit Details save, which supplies
      // a freshly computed name, isn't overruled by the stored one.
      const storedSpn =
        meta?.salesPlanName ?? existing?.latestVersion?.salesPlanName ?? null;
      const spnSrc = existing?.latestVersion;
      const spnArgs = {
        agency: meta?.agency ?? spnSrc?.agency ?? null,
        brand: meta?.brand ?? spnSrc?.brand ?? planDeals[0]?.brand ?? null,
        advertiser: meta?.advertiser ?? spnSrc?.advertiser ?? null,
        // meta FIRST. Reading planDeals[0] first meant that on any plan
        // with at least one saved schedule row, an edited Primary Demo was
        // silently discarded and the old demo re-written — the sales plan
        // name kept the stale demo even though the Edit modal previewed
        // the new one. Every sibling field here reads meta first; demo was
        // the only one inverted.
        demo: meta?.demo ?? planDeals[0]?.demo ?? spnSrc?.demo ?? null,
        year: SEASON_LABEL,
      };
      const freshSpn = computeSalesPlanName(spnArgs, '');
      // The sales plan name is a pure formula over those five components
      // and is read-only in the UI, so it should always track them. It
      // used to prefer whatever was stored unless that stored value said
      // "TBD" — which meant changing a plan's Primary Demo updated the
      // demo everywhere EXCEPT the name, leaving "…-W18-49-26/27" sitting
      // above a plan that is now M18+.
      //
      // Only adopt the recompute when every component actually resolved.
      // computeSalesPlanName is called with an empty placeholder, so a
      // missing piece yields an empty segment and a malformed name — and
      // completeness can't be checked by splitting the result, because
      // demo values contain hyphens themselves ("W18-49"). Check the
      // inputs instead. Incomplete, or still carrying a TBD placeholder,
      // and the stored value stands.
      const freshSpnComplete =
        Object.values(spnArgs).every(v => !!(v && String(v).trim()))
        && !freshSpn.toUpperCase().includes('TBD');
      const salesPlanName =
        freshSpnComplete
          ? freshSpn
          : (storedSpn ?? freshSpn);

      // Metadata fallback chain — prefer the meta override (from Edit
      // Details) when present, otherwise fall back to what's already
      // on the latest saved version. The existing-fallback is the
      // important half: a Snowflake-saved plan with no override (the
      // normal main-Save path) used to round-trip nulls and clobber
      // its metadata on re-save. Now those plans round-trip whatever
      // was there.
      const ev = existing?.latestVersion;

      // Guard: marketplace is required. Empty/null marketplace gets
      // persisted as null, then on reload buildVisibleQuarters silently
      // defaults to BY Upfront pattern — which can flip a CY or Scatter
      // plan's tab strip without warning. Block save and surface a
      // toast so the user explicitly picks one in Edit Details.
      const resolvedMarketplace = (meta?.marketplace || ev?.marketplace || '').trim();
      if (!resolvedMarketplace) {
        showToast('Pick a Marketplace before saving (Edit Details).');
        return;
      }

      /**
       * Value for PLAN_VERSION.OVERLAP_ESTIMATE_VERSION_ID — which estimate
       * version supplied this plan's late quarter (4Q'27 on a 26/27 plan).
       *
       * Three-way, in priority order:
       *
       *  1. Already pinned → carry it forward verbatim. The pin is a
       *     commitment; a save that merely re-writes the row must not silently
       *     re-point it at whatever is newest now.
       *  2. Not pinned, and this save contains rows in the late quarter →
       *     pin the version currently supplying it. This is the moment the
       *     plan commits: from here its 4Q numbers stop moving.
       *  3. Otherwise null — the plan has looked at the tab but put nothing
       *     in it, so it keeps re-resolving "newest that has the quarter" on
       *     every load and a corrected 4Q file reaches it for free.
       *
       * Deliberately keyed on rows existing rather than on a separate flag:
       * "untouched" IS the absence of rows, so there is no extra state to
       * keep in sync and nothing to get wrong on a reload.
       */
      const lateQuarterPinToPersist: number | null = (() => {
        const alreadyPinned = ev?.overlapEstimateVersionId ?? null;
        if (alreadyPinned != null) return alreadyPinned;

        const overlapId = overlapVersionIdOf(activeVersion);
        if (overlapId == null) return null;  // nothing merged — nothing to pin

        const lateQ = lateQuarterOf(activeVersion);
        if (!lateQ) return null;

        const touchesLateQuarter = dealsPayload.some(
          d => d.quarter === lateQ.quarter && d.year === lateQ.year,
        );
        return touchesLateQuarter ? overlapId : null;
      })();

      const request: SavePlanRequest = {
        planId: existing?.id ?? null,
        name: effectivePlanName,
        seasonPrimaryYear,
        sourcePlanName,
        sourcePlanSfId,
        status: metaStatus,
        // baseVersionIdOf, NOT activeVersion.id. When a late quarter has been
        // merged in, activeVersion is a synthetic version whose id is a
        // negative placeholder matching no EST_VERSION row. Persisting that
        // would leave the plan bound to nothing: on the next load
        // getVersionById would miss and the plan would silently fall through
        // to the season's latest general estimate, changing every number in
        // it. The plan stays bound to the version its own quarters came from.
        estimateVersionId: baseVersionIdOf(activeVersion),
        // Pin the source of the late quarter — but only once the plan has
        // actually saved rows in that quarter. See lateQuarterPinToPersist.
        overlapEstimateVersionId: lateQuarterPinToPersist,
        // Assert "this save touches only the late quarter" in the one state
        // where that is the rule: a pushed Order with no revision open. The
        // backend verifies it against the stored deals and 409s if it's false.
        //
        // ⚠ It must NOT be sent while revising. Revise Order is client state
        // the server can't see, and on the FIRST save of a revision the latest
        // version is still key_type 'order' / 'order_update' — so the server
        // cannot distinguish a legitimate revision from a sneaked edit. Sending
        // the flag then would make the guard reject every revision's opening
        // save. Omitting it means "no assertion", i.e. exactly the behaviour
        // that shipped before this feature.
        //
        // Deliberately not keyed on the active tab: a save can be triggered
        // from Summary after editing 4Q, and saveToSnowflake awaits a tick
        // before reading this, so a tab-dependent value would be stale.
        lateQuarterOnly: (isPushed && !isRevisingOrder) ? true : undefined,
        // Assert which version this grid was built from, so the backend can
        // refuse a save that would bury a teammate's. Read from the ref, never
        // from the cache — see `loadedVersionByPlanRef` for why the cache is
        // wrong in both directions.
        //
        // undefined when: the plan is new (nothing to collide with), or the
        // grid holds a version from the history picker (a deliberate revert).
        // Either way, no assertion = pre-feature behaviour.
        baseVersionNo: existing?.id != null
          ? (loadedVersionByPlanRef.current.get(existing.id) ?? undefined)
          : undefined,
        agency:      meta?.agency      ?? ev?.agency      ?? null,
        advertiser:  meta?.advertiser  ?? ev?.advertiser  ?? null,
        brand:       meta?.brand       ?? ev?.brand       ?? planDeals[0]?.brand ?? null,
        // meta FIRST — see the matching note on the salesPlanName formula
        // above. This is the field that actually persists to PLAN_VERSION,
        // so reading the deal row first is what made an edited Primary Demo
        // survive neither the save nor a refresh.
        demo:        meta?.demo       ?? planDeals[0]?.demo ?? ev?.demo ?? null,
        ae:          meta?.ae          ?? ev?.ae          ?? null,
        buyer:       meta?.buyer       ?? ev?.buyer       ?? null,
        // Guard above already rejected the save if this would be
        // empty, so resolvedMarketplace is guaranteed truthy here.
        marketplace: resolvedMarketplace,
        equiv:       meta?.equiv       ?? ev?.equiv       ?? null,
        dealName:    meta?.dealName    ?? ev?.dealName    ?? null,
        year: SEASON_LABEL,
        advertiserId,
        brandId,
        contactId,
        accountId,
        salesPlanName,
        // Per-plan extra quarter count. Source-of-truth precedence:
        //   1. Current in-component state (the user may have just
        //      clicked + Add Quarter / × this session).
        //   2. The just-saved version's extras (zero-net-change save).
        //   3. Zero (no extras — marketplace default).
        // Including this on every save means a teammate's edit also
        // round-trips the count cleanly, even if they never touched
        // the quarter strip.
        extraQuartersCount: extraQuarters > 0
          ? extraQuarters
          : (ev?.extraQuartersCount ?? 0),
        // Plan-level default unit length. Same precedence pattern as
        // extraQuartersCount — carry the value forward on every save
        // so it round-trips without the user having to re-set it.
        // meta (from localStorage / Edit Details) wins because it
        // reflects the freshest user intent; falls back to the just-
        // saved version's persisted value.
        defaultUnitLength: meta?.defaultUnitLength ?? ev?.defaultUnitLength ?? null,
        // Persist the selected secondary demos with the version. Sent from
        // live component state rather than the previous version, so adding
        // or removing a demo takes effect on the next save. Empty array is
        // meaningful — it's how a demo gets removed — so it is NOT coalesced
        // to the previous version's value the way nullable metadata is.
        secondaryDemos,
        deals: dealsPayload,
      };

      const userEmail = getEmailFromSessionStorage() || undefined;
      const result = await savePlan(request, userEmail);

      if (!result.ok) {
        // A version conflict is not a failed save in the usual sense — nothing
        // is wrong with the request and retrying it unchanged would be wrong
        // too, because it would bury whatever the other person wrote. Raise the
        // banner instead of a toast: it persists, explains, and offers the
        // reload. The edits stay exactly where they are.
        if (result.code === 'PLAN_VERSION_CONFLICT') {
          setConflictedPlan(planFilter);
          return;
        }
        showToast('Save failed: ' + result.error);
        return;
      }
      // Any prior conflict is resolved — this save was accepted, so the grid
      // and the database agree again.
      setConflictedPlan(null);

      // Save succeeded — NOW commit the deferred rename state changes
      // (if this was a rename) so the UI tracks Snowflake. If the save
      // had failed above, none of these would run and the UI would
      // stay on the pre-rename state, matching what's on the backend.
      if (isRename) {
        switchPlan(newName, { force: true });   // rename already committed
      }

      // Migrate server-returned deals into allDeals + clear this plan's
      // addedRows.
      //
      // Why: Summary tab (buildSummaryTableRows), All Plans view, SF
      // Structure preview, and other aggregate surfaces all read from
      // `allDeals` only. `addedRows` (populated by + Add Show / + Add
      // Row) doesn't get merged into `allDeals` after save unless a
      // page refresh triggers the merge effect. Result: user builds a
      // plan from scratch, saves, switches to Summary → blank. Same
      // class of bug we already fixed for version-switching (task #58).
      //
      // Fix: pull the saved deals from the server response, convert
      // via planDealsToDeals, and replace-in-place. Clear addedRows
      // for this plan (both the old and new name in the rename case)
      // since they're now persisted as Deals.
      const savedPlanDeals = result.plan.latestVersion?.deals ?? [];
      const savedDeals = planDealsToDeals(effectivePlanName, sourcePlanSfId, savedPlanDeals);

      // The grid now holds exactly what we just wrote, so that version becomes
      // the new base. Also re-arms a plan whose assertion was cleared by the
      // history picker — once you save on top of an old version, the result IS
      // the latest and subsequent saves should be checked normally again.
      const justSavedVersionNo = result.plan.latestVersion?.versionNo;
      if (typeof justSavedVersionNo === 'number') {
        loadedVersionByPlanRef.current.set(result.plan.id, justSavedVersionNo);
      }

      // Compute the NEXT full arrays up front and set them directly,
      // rather than using functional updaters.
      //
      // This is load-bearing, not style. The baseline has to be built from
      // the arrays that are about to become state, because `isDirty` will
      // compare against exactly those on the next render. Building it from
      // anything else — an earlier copy, or a narrower slice — leaves the two
      // sides unable to match, which is how the UNSAVED pill once stayed lit
      // after a successful save, with Save and Revert still enabled.
      //
      // `snapshotPlans` narrows to this plan when it signs, so passing the
      // full arrays here is correct and does not re-baseline anyone else.
      //
      // Functional updaters can't be used here because React does not
      // run them synchronously — we'd have no reliable handle on the
      // resulting arrays to build the baseline from. Setting the values
      // we computed guarantees baseline === state. Same approach the
      // create-plan path already uses.
      //
      // Filter by BOTH names to cover the rename case where the old
      // name's rows might still live in allDeals from a prior session.
      const otherPlanDeals = allDeals.filter(d =>
        d.salesPlan !== planFilter && d.salesPlan !== effectivePlanName,
      );
      const nextAllDeals = [...otherPlanDeals, ...savedDeals];
      const nextAddedRows = addedRows.filter(r =>
        r.salesPlan !== planFilter && r.salesPlan !== effectivePlanName,
      );
      setAllDeals(nextAllDeals);
      setAddedRows(nextAddedRows);

      // Reset the dirty baseline to the just-saved state, so the
      // "Unsaved" pill clears until the next edit. Built from the exact
      // arrays set above so it is byte-comparable with what `isDirty`
      // will compute on the next render.
      // Baseline ONLY the plan we just saved. Other plans keep whatever
      // baseline they had, so a teammate-style workflow — edit plan A, wander
      // to plan B, save B — no longer quietly adopts A's unsaved rows as
      // saved. Both names on a rename: the old key is stale but harmless, and
      // the new one is what `isDirty` will look up from here on.
      snapshotPlans(
        Array.from(new Set([planFilter, effectivePlanName].filter(Boolean) as string[])),
        nextAllDeals,
        nextAddedRows,
      );

      // Signal to memos that read listSavedPlans() inline (planOptions,
      // filteredDeals, filteredAddedRows) that the plan cache changed,
      // so they re-evaluate with the just-saved data. Without this, the
      // dropdown count and All Plans row list stayed stale until some
      // other state change incidentally triggered a re-render.
      setPlanCacheVersion(v => v + 1);

      // If the user had been viewing a historical version and just
      // saved, that save became the new latest — clear the banner
      // ref so the yellow warning stops showing. savePlan already
      // updated the cache entry with the new latestVersion, so the
      // header chip + picker reflect it correctly on next render.
      if (viewingHistoricalRef) setViewingHistoricalRef(null);
      // Diff baseline is meaningless post-save (baseline was the
      // pre-save latest, which no longer exists) — clear it too.
      if (historicalDiffBaseline) setHistoricalDiffBaseline(null);

      const v = result.plan.latestVersion?.versionNo;
      showToast('✓ Saved' + (v ? ' (v' + v + ')' : ''));
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Discard every unsaved edit on the current plan and reload from the
   * cached latest saved version. Called by the Revert button after the
   * user confirms the destructive action.
   *
   * Mirrors the post-save cleanup path (see saveToSnowflake): re-derive
   * Deals from `latestVersion.deals` via planDealsToDeals, splice into
   * allDeals in place of the dirty rows, and drop any addedRows for
   * this plan (they've never been saved). Reset the dirty baseline so
   * isDirty flips false immediately.
   *
   * No-op guards for the cases where a revert doesn't make sense — no
   * plan, no cached save, or nothing dirty. All defensive; the button
   * itself is already disabled in those states.
   */
  function revertToSaved(opts?: { silent?: boolean }): void {
    if (!planFilter) return;
    const saved = getSavedPlanByName(planFilter);
    if (!saved || !saved.latestVersion) return;
    const savedPlanDeals = saved.latestVersion.deals ?? [];
    // sourcePlanSfId is stored on the plan row, not the version — pass
    // it through so rolled-forward deals keep their SF traceback intact.
    const savedDeals = planDealsToDeals(planFilter, saved.sourcePlanSfId ?? null, savedPlanDeals);
    // Compute the next full arrays and set them directly — see the long
    // note in saveToSnowflake: the baseline must be built from the arrays
    // about to become state, since that is what `isDirty` compares against
    // next render. This previously signed only `savedDeals` and left the
    // UNSAVED pill lit (and Revert enabled) after a revert.
    //
    // Read the REFS, not the closure. This is also called from the
    // focus/switch sync, whose effect closure can predate the mount-time
    // merge — in which case closure `allDeals` is [] and this line would
    // silently drop every other plan's rows from the grid. The refs are
    // re-pointed on every render (same reason saveToSnowflake uses them).
    const currentDeals = allDealsRef.current;
    const currentRows = addedRowsRef.current;
    const otherPlanDeals = currentDeals.filter(d => d.salesPlan !== planFilter);
    const nextAllDeals = [...otherPlanDeals, ...savedDeals];
    const nextAddedRows = currentRows.filter(r => r.salesPlan !== planFilter);
    allDealsRef.current = nextAllDeals;
    addedRowsRef.current = nextAddedRows;
    setAllDeals(nextAllDeals);
    setAddedRows(nextAddedRows);
    // Re-baseline this plan only, so a revert here cannot mark some other
    // plan's unsaved edits as saved. Same signature calc as the save path so
    // the two stay byte-comparable.
    snapshotPlans([planFilter], nextAllDeals, nextAddedRows);
    // Any historical-view / diff-baseline state is orthogonal to
    // "revert to latest", so leave it alone — user might still be
    // viewing a previous version and just wants to drop their working
    // edits. If they were viewing the actual latest (typical case)
    // this is a full reset to that saved state.
    //
    // Force-remount every uncontrolled input (bulk CPM strip, per-row
    // CPM / rate / units / demo). Those inputs are `defaultValue` +
    // onBlur-commit, so their DOM value survives a state rollback: a
    // typed-but-uncommitted number would keep showing after the revert
    // even though the underlying deal is back to its saved value.
    // Their keys embed this counter, so bumping it discards the DOM
    // text and re-seeds from the reverted state.
    setInputRemountKey(k => k + 1);
    // Silent when this is a background sync rather than a user pressing
    // Revert — picking up a teammate's save shouldn't announce itself as
    // though the user discarded something.
    if (!opts?.silent) showToast('✓ Reverted to last saved version');
  }

  /**
   * Pull this plan fresh from Snowflake and rebuild the grid from it.
   *
   * The action behind the conflict banner. `revertToSaved` alone is not enough
   * there: it re-derives from the plan CACHE, and in a conflict the cache is
   * stale by definition — the other person's version is the thing we have not
   * got. So refresh first, then revert onto the fresh data.
   *
   * DISCARDS the user's unsaved edits, which is why it is only ever offered
   * behind a button that says so, never run automatically.
   */
  /**
   * Pull the active plan fresh from Snowflake and, if it moved, either adopt
   * the new version or warn — never both, never silently over unsaved work.
   *
   * Runs on plan switch and on window focus. Those are the two moments a user
   * plausibly expects to be looking at current data, and between them they
   * cover the reported bugs: seeing a teammate's edit after they save, and a
   * revision banner that outlived the revision.
   *
   * Three outcomes:
   *   • version unchanged     → nothing happens, no render, no flicker
   *   • changed, grid clean   → rebuild silently; the user just sees current data
   *   • changed, grid dirty   → leave every edit alone and raise the banner
   *
   * WHY `isDirty` GATES ONLY THE REBUILD: refreshing the cache is always safe
   * because the cache is not what the grid renders from. Replacing grid rows
   * is what could destroy typing, so that is what the guard protects.
   *
   * `isDirty` is the ACTIVE plan's dirty state, so unsaved work on some other
   * plan no longer blocks this one from syncing — and rebuilding here cannot
   * disturb that other plan's baseline either, because snapshots are per-plan.
   */
  const syncingRef = useRef(false);
  async function syncActivePlanFromServer(): Promise<void> {
    if (!planFilter) return;
    // A save in flight is about to rewrite this plan anyway, and racing it
    // would rebuild the grid from data the save is superseding.
    if (isSaving) return;
    // Never overlap two syncs — focus can fire repeatedly.
    if (syncingRef.current) return;
    // A plan being viewed at a historical version is deliberately not showing
    // latest. Re-syncing would yank the user out of what they opened.
    if (viewingHistoricalRef) return;

    const before = getSavedPlanByName(planFilter);
    // Only Snowflake-saved plans have versions to compare. SF source plans and
    // unsaved drafts have nothing to sync.
    if (!before?.id) return;

    syncingRef.current = true;
    try {
      await refreshPlansCache();
      const after = getSavedPlanByName(planFilter);
      const freshVersionNo = after?.latestVersion?.versionNo;
      if (after?.id == null || typeof freshVersionNo !== 'number') return;

      const known = loadedVersionByPlanRef.current.get(after.id);
      // `null` = history-picker overlay, already excluded above. `undefined` =
      // never recorded, so there is nothing to compare and nothing to adopt.
      if (typeof known !== 'number' || known === freshVersionNo) return;

      if (isDirty) {
        // Their work stays. They choose when to take the update.
        setConflictedPlan(planFilter);
        return;
      }
      loadedVersionByPlanRef.current.set(after.id, freshVersionNo);
      revertToSaved({ silent: true });
      setPlanCacheVersion(v => v + 1);
    } finally {
      syncingRef.current = false;
    }
  }

  /**
   * Change the active plan, asking first if the current one has unsaved edits.
   *
   * EVERY user-initiated plan change goes through here rather than calling
   * `setPlanFilter` directly. There are about a dozen entry points — the
   * dropdown, the search picker, All Plans rows, Plan Details links — and a
   * guard replicated at each of them is a guard that will be missing from the
   * thirteenth.
   *
   * Leaving a plan dirty used to be silent, and the edits stayed in memory
   * with nothing on screen to say so. Forcing the decision at the moment of
   * leaving means unsaved work is always either committed or knowingly
   * dropped, never quietly carried around.
   *
   * `opts.force` is for switches that are part of completing something the
   * user already confirmed — landing on a plan just created, or following a
   * rename. State is consistent by then and a prompt would be nonsense.
   */
  function switchPlan(name: string, opts?: { force?: boolean }): void {
    if (name === planFilter) return;
    if (!opts?.force && isDirty) {
      setPendingPlanSwitch(name);
      return;
    }
    setPlanFilter(name);
  }

  /**
   * Complete a switch the user confirmed, throwing away the edits first.
   *
   * The revert has to happen BEFORE the filter moves: `revertToSaved` operates
   * on `planFilter`, so flipping it first would revert the wrong plan — and
   * leave the abandoned one still dirty, which is the state this whole dialog
   * exists to prevent.
   *
   * Discard means discard. The plan goes back to its saved version, so
   * switching back shows what is actually stored rather than resurrecting
   * edits the dialog said were gone.
   */
  function confirmPlanSwitchDiscardingChanges(): void {
    const target = pendingPlanSwitch;
    setPendingPlanSwitch(null);
    if (target === null) return;
    revertToSaved({ silent: true });
    setPlanFilter(target);
  }

  async function reloadPlanFromServer(): Promise<void> {
    if (!planFilter) return;
    await refreshPlansCache();
    const saved = getSavedPlanByName(planFilter);
    // Re-arm the assertion against whatever is now latest, so the next save
    // is checked against the version the user is actually looking at.
    const freshVersionNo = saved?.latestVersion?.versionNo;
    if (saved?.id != null && typeof freshVersionNo === 'number') {
      loadedVersionByPlanRef.current.set(saved.id, freshVersionNo);
    }
    revertToSaved();
    setConflictedPlan(null);
    setPlanCacheVersion(v => v + 1);
  }

  // ─── Plan version history ────────────────────────────────────────────────────
  //
  // The version chip in the MPB header opens a popup that lists every
  // saved version for the active Snowflake plan. Clicking a row opens
  // the snapshot viewer modal with the full deal payload locked to the
  // historical estimate version.

  // Alias to the shared helper declared near `VersionHistoryPopup`. The
  // snapshot viewer modal still calls `formatVersionDate(...)` directly
  // (kept here so the call site reads cleanly).
  const formatVersionDate = formatVersionDateExternal;

  /**
   * Fetch the version-history list for a Snowflake plan. Called when
   * the user opens the popup; resets the loading/error state so the
   * popup always reflects the latest fetch (no stale data shown after
   * a previous failure).
   */
  async function loadVersionHistory(planId: number): Promise<void> {
    setVersionHistoryLoading(true);
    setVersionHistoryErr(null);
    setVersionHistory(null);
    const result = await listPlanVersions(planId);
    if (!result.ok) {
      setVersionHistoryErr(result.error);
      setVersionHistoryLoading(false);
      return;
    }
    setVersionHistory(result.versions);
    setVersionHistoryLoading(false);
  }

  /**
   * Name (or rename) a specific PLAN_VERSION via the name-version
   * endpoint. Uses a native prompt() for name entry as an MVP — a proper
   * inline modal can replace this later without touching the endpoint.
   * On success, reloads the version history so the popup re-renders
   * with the new milestone + label.
   *
   * Contractual key_types (initial, order, order_update) keep their
   * type; only the name changes. The backend enforces that — we can
   * safely offer "Name this version" on any row.
   */
  async function handleNameVersion(planId: number, versionNo: number): Promise<void> {
    const existing = versionHistory?.find(v => v.versionNo === versionNo);
    const suggestion = existing?.name ?? '';
    const input = window.prompt(
      `Name for v${versionNo} — a label future you (or a teammate) will recognize.`,
      suggestion,
    );
    if (input == null) return;              // user cancelled
    const trimmed = input.trim();
    if (trimmed.length === 0) {
      showToast('Name cannot be empty.');
      return;
    }
    // In revising-order mode, naming a version FINALIZES the revision
    // as an 'order_update' milestone (new Live Order, prior Live
    // Order becomes Superseded automatically via kindFor). Outside
    // of revising mode, keyType defaults to 'named' (bookmark).
    const keyType: 'named' | 'order_update' = isRevisingOrder ? 'order_update' : 'named';
    const result = await namePlanVersion(planId, versionNo, trimmed, keyType);
    if (!result.ok) {
      showToast(`Couldn't name version: ${result.error}`);
      return;
    }
    // Refresh so the milestone appears + label updates. Cheap round-trip
    // (list-versions is ~1 fast query).
    await loadVersionHistory(planId);
    // Post-finalize: refresh the plans cache too so latestVersion.keyType
    // reflects 'order_update', which flips hasInflightPostOrderRevision
    // to false and re-locks the plan automatically. Also clear the
    // client-side revising flag so the "Revise Order" button reappears
    // for the next round.
    if (isRevisingOrder) {
      await refreshPlansCache();
      setRevisingOrderMode(false);
      setPlanCacheVersion(v => v + 1);
      showToast(`Order revision finalized: "${trimmed}"`);
    } else {
      showToast(`Named v${versionNo}: "${trimmed}"`);
    }
  }

  /**
   * Load a specific historical version's data into the live grid.
   *
   * Previously this opened a read-only snapshot modal. Now it overlays
   * the picked version's deals + version identity onto the plan cache
   * so the main MPB grid re-renders that state — the user can view AND
   * edit the historical version, and a yellow banner at the top warns
   * them they're not on the latest.
   *
   * If they save from this state, saveToSnowflake creates a new
   * PLAN_VERSION on top of the ACTUAL latest — effectively restoring
   * the picked version's content as a new revision. After save,
   * viewingHistoricalRef clears (they're back on latest by definition).
   *
   * `planName` and `sourcePlanSfId` aren't needed anymore since we're
   * writing PlanDeal[] straight into the cache (no Deal[] conversion),
   * but the signature is kept to avoid rippling the picker callsite.
   */
  async function openHistoricalVersion(
    planId: number,
    planName: string,
    sourcePlanSfId: string | null,
    versionNo: number,
  ): Promise<void> {
    setShowVersionHistory(false);
    const savedNow = getSavedPlanById(planId);
    const currentViewingVersionNo = savedNow?.latestVersion?.versionNo;
    // Determine what the ACTUAL latest is — this survives overlays.
    // When viewingHistoricalRef is set, savedNow.latestVersion.versionNo
    // reflects the OVERLAID historical version, not the real latest.
    // So we prefer the stored latestVersionNo when a ref exists.
    const actualLatestVersionNo = viewingHistoricalRef?.latestVersionNo
      ?? savedNow?.latestVersion?.versionNo
      ?? versionNo;
    // Case A: picked row is the ACTUAL latest → this is a "return to
    // latest" click. Refetch fresh data + clear the banner rather
    // than overlaying (which would set the banner incorrectly).
    if (versionNo === actualLatestVersionNo) {
      if (!viewingHistoricalRef) return; // already on latest, no-op
      await returnToLatest();
      return;
    }
    // Case B: picked row is the version the user's already viewing
    // (they clicked the same historical row twice) → no-op.
    if (versionNo === currentViewingVersionNo && viewingHistoricalRef) {
      return;
    }
    // Case C: picked row is a different historical version → overlay it.
    setHistoricalVersionLoading(true);
    setHistoricalVersionErr(null);
    const result = await getPlanVersion(planId, versionNo);
    if (!result.ok) {
      setHistoricalVersionErr(result.error);
      setHistoricalVersionLoading(false);
      showToast(`Couldn't load version: ${result.error}`);
      return;
    }
    const v = result.version;
    // Snapshot the ACTUAL latest deals BEFORE overlay mutates the
    // cache — powers the diff-highlight feature. If viewingHistoricalRef
    // is already set (user is switching between historical versions),
    // reuse the earlier snapshot instead of grabbing the current-overlaid
    // deals (which would compare historical-to-historical instead of
    // historical-to-latest).
    if (!historicalDiffBaseline) {
      const preOverlaySaved = savedNow;
      const latestDeals = preOverlaySaved?.latestVersion?.deals ?? [];
      setHistoricalDiffBaseline({
        latestDeals,
        latestVersionNo: actualLatestVersionNo,
      });
    }
    // Stop asserting a base version for this plan. The grid is about to hold
    // an OLD version's rows, so "has anyone saved since I loaded" is the wrong
    // question — the user is deliberately reverting and already knows they are
    // replacing the current state. Asserting here would 409 every save made
    // from the history picker. Cleared back to a real number on the next save.
    loadedVersionByPlanRef.current.set(planId, null);
    // Overlay into cache so header chip + picker reflect the
    // historical version identity.
    overlayHistoricalVersion(planId, {
      versionNo: v.versionNo,
      savedAt: v.savedAt,
      savedBy: v.savedBy,
      deals: v.deals,
      isKey: v.isKey,
      keyType: v.keyType,
      name: v.name,
      milestoneIndex: v.milestoneIndex,
      keyVersionNo: v.keyVersionNo,
    });
    // The GRID reads from BOTH `allDeals` and `addedRows` state.
    // Replace this plan's rows in BOTH with the historical version:
    //   • allDeals — drop this plan's Deals, add v's deals
    //   • addedRows — drop this plan's entries entirely (a
    //     historical version by definition already has any "added"
    //     rows persisted as Deals; leaving addedRows behind
    //     duplicates them alongside the historical deals, which is
    //     what caused the "shared same program → SF duplicate"
    //     error users hit switching versions after + Add Show)
    const historicalDeals = planDealsToDeals(planName, sourcePlanSfId, v.deals);
    setAllDeals(prev => {
      const others = prev.filter(d => d.salesPlan !== planName);
      return [...others, ...historicalDeals];
    });
    setAddedRows(prev => prev.filter(r => r.salesPlan !== planName));
    // Compute the label the banner shows — reuse compactVersionLabel
    // so it matches exactly what the picker shows.
    const label = compactVersionLabel({
      versionNo: v.versionNo,
      milestoneIndex: v.milestoneIndex,
      isKey: v.isKey,
      keyType: v.keyType,
      name: v.name,
      // Fields not on PlanVersion — safe to leave undefined for the
      // label function, which only checks keyType/name/milestoneIndex.
      estimateVersionId: null,
      savedAt: v.savedAt,
      savedBy: v.savedBy,
      dealCount: v.deals.length,
      revisionNumber: 0,
      keyVersionNo: v.keyVersionNo,
    });
    // Compute the actual latest's milestoneIndex — needed to decide
    // whether this pick is a "previous lifecycle" (view-only) or a
    // "current lifecycle historical revision" (editable). We look
    // at the versionHistory list which server-side returns DESC by
    // versionNo, so [0] is the actual latest row. Falls back to the
    // picked version's milestoneIndex if the list isn't loaded yet
    // (defensive — the picker calls loadVersionHistory before it opens).
    const currentLifecycleIndex =
      versionHistory && versionHistory.length > 0
        ? versionHistory[0].milestoneIndex
        : v.milestoneIndex;
    setViewingHistoricalRef({
      planId,
      displayLabel: label,
      latestVersionNo: actualLatestVersionNo,
      viewingMilestoneIndex: v.milestoneIndex,
      currentLifecycleIndex,
    });
    setPlanCacheVersion(vv => vv + 1);
    setHistoricalVersionLoading(false);
  }

  /**
   * Restore the plan cache to the actual latest version. Refetches
   * from Snowflake to guarantee we're back on the real latest (in
   * case another save happened concurrently), re-derives allDeals
   * for the plan from the fresh cache, and clears the banner.
   */
  async function returnToLatest(): Promise<void> {
    setHistoricalVersionErr(null);
    const ref = viewingHistoricalRef;
    if (!ref) return;
    setHistoricalVersionLoading(true);
    try {
      await refreshPlansCache();
      // Pull the fresh SavedPlan and rebuild this plan's rows from
      // the actual latest — same replacement path openHistoricalVersion
      // takes. Also drop this plan's addedRows so any AddedRow that
      // was in-flight during history browsing doesn't collide with
      // the freshly-loaded Deals (same dupe root cause).
      const saved = getSavedPlanById(ref.planId);
      if (saved) {
        const freshDeals = savedPlanToDeals(saved);
        setAllDeals(prev => {
          const others = prev.filter(d => d.salesPlan !== saved.name);
          return [...others, ...freshDeals];
        });
        setAddedRows(prev => prev.filter(r => r.salesPlan !== saved.name));
      }
      setPlanCacheVersion(v => v + 1);
      setViewingHistoricalRef(null);
      // Diff highlighting is only relevant while viewing history;
      // clear the baseline snapshot once the user's back on latest.
      setHistoricalDiffBaseline(null);
    } finally {
      setHistoricalVersionLoading(false);
    }
  }

  // ─── New plan modal ──────────────────────────────────────────────────────────

  /**
   * Apply a 25/26 SF plan as the New Plan modal's roll-forward source.
   * Auto-fills the editable metadata fields with whatever data we have
   * on the SF plan (advertiser/brand/demo from the deal grain) and
   * pre-seeds the plan name with the source name — the user is
   * expected to differentiate (phase 95's name-collision check will
   * force this).
   *
   * Passing `null` clears the selection without resetting fields the
   * user has already edited.
   */
  function pickRollForwardSource(
    source:
      | {
          name: string;
          year: '25/26' | '26/27';
          sfId: string | null;
          planId: number | null;
          advertiser: string | null;
          brand: string | null;
          demo: string | null;
          // 26/27 clone extras — the SavedPlan/PlanVersion carries every
          // meta field the New Plan modal needs, so a clone lands with
          // the full form pre-populated (agency, buyer, marketplace, AE,
          // and the SF lookup IDs so re-picking isn't required either).
          agency?: string | null;
          buyer?: string | null;
          ae?: string | null;
          marketplace?: string | null;
          advertiserId?: string | null;
          brandId?: string | null;
          contactId?: string | null;
          accountId?: string | null;
        }
      | null,
  ): void {
    if (!source) {
      setNpRollForwardSource(null);
      setNpRollForwardSfId(null);
      setNpRollForwardPlanId(null);
      setNpRollForwardYear(null);
      setNpUnitLength('');
      // Clearing the source means "start blank" — drop any card inherited
      // from the previous pick. This picker runs repeatedly from inside the
      // open modal, and only closeNewPlanModal resets npRateCard, so without
      // this a WPP pick would survive being cleared.
      setNpRateCard('general');
      return;
    }
    // Reset before re-deriving. Every branch below either sets the card
    // explicitly (26/27 clone) or leaves it general (25/26 roll-forward,
    // which has no card of its own). Switching from a WPP 26/27 clone to a
    // 25/26 source must not silently keep WPP.
    setNpRateCard('general');
    setNpRollForwardSource(source.name);
    setNpRollForwardSfId(source.sfId);
    setNpRollForwardPlanId(source.planId);
    setNpRollForwardYear(source.year);
    setNpRollForwardOpen(false);
    setNpRollForwardSearch('');
    // Unit-length transform only applies to 25/26 → 26/27 roll-forwards.
    // 26/27 clones are verbatim (same-season, no length remap), so the
    // picker is hidden and its state cleared.
    if (source.year === '25/26') {
      // Default the Unit Length picker to whichever length appears on the
      // most ROWS of this source plan (Q3=C — row-count, not unit-count,
      // not equiv-count). Falls back to '30' if the source is somehow
      // empty. User can override in the picker below.
      const lengthRowCounts = new Map<string, number>();
      for (const d of allDeals) {
        if (d.salesPlan !== source.name) continue;
        const raw = String(d.unitLength ?? '').trim();
        if (!raw) continue;
        lengthRowCounts.set(raw, (lengthRowCounts.get(raw) ?? 0) + 1);
      }
      let mostCommonLen = '30';
      let bestCount = 0;
      Array.from(lengthRowCounts.entries()).forEach(([len, count]) => {
        if (count > bestCount) {
          bestCount = count;
          mostCommonLen = len;
        }
      });
      setNpUnitLength(mostCommonLen);
    } else {
      // 26/27 verbatim clone. Deals copy across untouched, so no
      // consolidation/transform picker — but the NEW plan still needs
      // its own defaultUnitLength for future + Add Show / + Add Row
      // seeding. Pre-populate from the source plan's default so the
      // typical "clone a plan, keep the same length" flow is one
      // click. User can override before Create Plan or later via
      // Edit Details. Empty when the source predates the column.
      setNpUnitLength(resolveDefaultUnitLength(source.name) || '');
    }
    // Seed the modal with whatever fields we can derive from the source
    // plan. For 25/26 sources the deal grain gives us advertiser/brand
    // display names + demo string; the async resolve below fills the
    // rest (contact / account / SF lookup IDs). For 26/27 clones the
    // caller (pickCloneSource) already threaded through every field
    // from the plan's PlanVersion meta, so we just apply them.
    setNpDealname(source.name);
    if (source.advertiser) setNpAdvertiser(source.advertiser);
    if (source.brand) setNpBrand(source.brand);
    if (source.demo) {
      setNpDemo(source.demo);
      setNpDemoInput(source.demo);
    }
    if (source.year === '26/27') {
      // Clone path — copy the entire meta over so the form arrives fully
      // populated. Nothing to fetch from SF (this plan is Snowflake-native).
      if (source.agency)      setNpAgency(source.agency);
      if (source.buyer)       setNpBuyer(source.buyer);
      if (source.ae)          setNpAe(source.ae);
      if (source.marketplace) setNpMarketplace(source.marketplace);
      if (source.advertiserId) setNpAdvertiserId(source.advertiserId);
      if (source.brandId)      setNpBrandId(source.brandId);
      if (source.contactId)    setNpContactId(source.contactId);
      if (source.accountId)    setNpAccountId(source.accountId);
      // Inherit the source plan's rate card as the DEFAULT — the picker
      // stays visible and changeable before Create Plan.
      //
      // The rate card isn't a field on the plan. A plan is pinned to one
      // estimate version and the card is a property of that version, so
      // resolve it through the binding. Cloning a WPP plan onto General
      // rates stays possible; it just isn't the default, since the common
      // case is cloning within the same card and the card locks at
      // creation.
      const srcVersionId = getSavedPlanByName(source.name)
        ?.latestVersion?.estimateVersionId ?? null;
      const srcVersion = srcVersionId != null ? getVersionById(srcVersionId) : null;
      if (srcVersion) setNpRateCard(cardOf(srcVersion));
      return;
    }

    // ── Lookup pre-population from the source SF plan ─────────────
    //
    // The SF SALES_PLAN row already has Advertiser__c / Brand__c /
    // Contact__c IDs from when it was first created in Salesforce.
    // Reading them here means a roll-forward arrives with the four
    // lookup fields already populated — the user just confirms (or
    // overrides) instead of re-picking everything from scratch.
    //
    // Async — shows a full-modal overlay via `isResolvingSalesPlan`
    // while the resolve is in flight. If the resolve fails (deleted SF
    // plan, network blip) the lookups just stay empty and the user
    // picks them manually.
    const sfId = source.sfId;
    if (sfId) {
      (async () => {
        setIsResolvingSalesPlan(true);
        try {
          const resolved = await resolveSalesPlan(sfId);
          if (!resolved) return;
          if (resolved.advertiser) {
            setNpAdvertiserId(resolved.advertiser.id);
            setNpAdvertiser(resolved.advertiser.name);
          }
          if (resolved.brand) {
            setNpBrandId(resolved.brand.id);
            setNpBrand(resolved.brand.name);
          }
          if (resolved.contact) {
            setNpContactId(resolved.contact.id);
            setNpBuyer(resolved.contact.name);
          }
          if (resolved.account) {
            setNpAccountId(resolved.account.id);
            setNpAgency(resolved.account.name);
          }
        } finally {
          setIsResolvingSalesPlan(false);
        }
      })();
    }
  }

  // ─── Edit Plan Details modal ─────────────────────────────────────────────

  /**
   * Open the Edit Plan Details modal pre-filled from the active plan's
   * metadata. No-op if there's no plan selected or no meta resolves
   * (shouldn't happen — the Edit Details button is hidden on those
   * surfaces, but defensive).
   */
  function openEditPlanModal(): void {
    if (!planFilter) return;
    const meta = getPlanMeta(planFilter);
    if (!meta) return;
    setEditAdvertiser(meta.advertiser ?? '');
    setEditAdvertiserId(meta.advertiserId ?? null);
    setEditBrand(meta.brand ?? '');
    setEditBrandId(meta.brandId ?? null);
    setEditBuyer(meta.buyer ?? '');
    setEditContactId(meta.contactId ?? null);
    setEditAgency(meta.agency ?? '');
    setEditAccountId(meta.accountId ?? null);
    setEditNickname(meta.dealName || planFilter);
    setEditAe(meta.ae ?? '');
    setEditDemo(meta.demo ?? '');
    setEditDemoInput(meta.demo ?? '');
    setEditMarketplace(meta.marketplace ?? '');
    setEditEquiv(meta.equiv ?? '');
    setEditDefaultUnitLength(meta.defaultUnitLength ?? '');
    setEditError('');
    setEditShowDemoDropdown(false);
    setShowEditPlanModal(true);
  }

  function closeEditPlanModal(): void {
    setShowEditPlanModal(false);
    setEditError('');
    setEditShowDemoDropdown(false);
    // Leave the rest of the state populated — it gets fully reset on
    // the next open() call. Avoids a brief flash of empty fields if
    // the user re-opens immediately.
  }

  /**
   * Commit the user's edits by writing a new PLAN_VERSION to Snowflake
   * with the freshly-built meta.
   */
  async function submitEditPlan(): Promise<void> {
    if (!planFilter) return;
    // Validate the same way the Create modal does — Advertiser, Brand,
    // Buyer, Demo, Marketplace are required; Agency derives from
    // Buyer's Account; AE is optional.
    //
    // TBD counts as filled, exactly as in Create: name "TBD" + null SF id
    // is an intentional draft state, and the push-to-SF gate still refuses
    // to send the plan until every TBD is replaced with a real pick. This
    // check used to demand real SF ids, so a plan created with any TBD
    // could not be edited at all — not even renamed — while the modal
    // offered TBD as an option.
    //
    // EXCEPT on a pushed plan. Edit Details is reachable there while
    // revising the order, and finalizing a revision snapshots to Snowflake
    // WITHOUT passing the push gate — so TBD would land on a live order
    // whose Salesforce record still holds the real values. Pushed plans
    // keep the strict rule they always had.
    const missing = missingPlanFields({
      advertiserId: editAdvertiserId, advertiser: editAdvertiser,
      nickname: editNickname,
      brandId: editBrandId, brand: editBrand,
      contactId: editContactId, buyer: editBuyer,
      demo: editDemo, marketplace: editMarketplace,
    }, { allowTbd: !isPushed });
    if (missing.length > 0) {
      setEditError(
        isPushed && [editAdvertiser, editBrand, editBuyer].includes('TBD')
          ? `This plan is a live Order — ${joinFieldList(missing)} must be a real Salesforce value, not TBD.`
          : `Please fill in ${joinFieldList(missing)}.`,
      );
      return;
    }
    if (editNameCollision) {
      setEditError(
        `A plan with Sales Plan Name "${editComputedSalesPlanName}" already exists for 26/27` +
        (editCollidingPlanNickname ? ` (${editCollidingPlanNickname})` : '') +
        `. Change Advertiser, Brand, Buyer, or Demo to differentiate.`
      );
      return;
    }

    const newNickname = editNickname.trim();
    const existing = getPlanMeta(planFilter);

    // Marketplace-change guard. If the user is switching to a marketplace
    // whose visibleQuarters set excludes quarters this plan currently has
    // data in (deals or AddedRows), surface a warning modal listing the
    // row counts before persisting. Data is preserved either way —
    // switching back recovers it — but a silent shrink would be
    // disorienting on plans with significant scheduling already done.
    //
    // Only fires when the *previous* marketplace was non-empty (we don't
    // warn on initial selection — there's nothing to lose yet) and the
    // values actually differ.
    if (
      existing?.marketplace
      && existing.marketplace !== editMarketplace
    ) {
      // Build both marketplace patterns AND extend each by the user's
      // explicit extras so the diff reflects what the user actually
      // sees on screen. Extras are anchored to the user's chosen end
      // quarter — switching marketplace should preserve that end, not
      // wipe the extras and shift the user backward. e.g.:
      //   BY Upfront + 2 extras ends at 1Q'28
      //   → switching to CY Upfront should still end at 1Q'28
      //   → new extras count = quartersBetween(CY's natural last, 1Q'28) = 1
      // That means only the BY-only quarters (4Q'26 here) actually
      // disappear; the user-added extras get re-anchored to the new
      // marketplace's tail.
      const oldVisBase = buildVisibleQuarters(
        existing.marketplace,
        activeVersion?.quarters ?? QUARTER_TABS,
        activeVersion?.primaryYear ?? null,
      );
      const newVisBase = buildVisibleQuarters(
        editMarketplace,
        activeVersion?.quarters ?? QUARTER_TABS,
        activeVersion?.primaryYear ?? null,
      );
      const oldVis = [...oldVisBase];
      for (let i = 0; i < extraQuarters; i++) {
        oldVis.push(nextQuarterAfter(oldVis[oldVis.length - 1]));
      }
      // Recompute the extras count needed so the new marketplace's
      // last visible quarter matches the user's previous last. If
      // the new marketplace already ends at or past the previous
      // last, no extras needed (the user just gained ground); the
      // quartersBetween helper returns 0 or null in those cases.
      const previousLast = oldVis[oldVis.length - 1];
      const newBaseLast = newVisBase[newVisBase.length - 1];
      const newExtraCount = newBaseLast
        ? (quartersBetween(newBaseLast, previousLast) ?? 0)
        : 0;
      const newVis = [...newVisBase];
      for (let i = 0; i < newExtraCount; i++) {
        newVis.push(nextQuarterAfter(newVis[newVis.length - 1]));
      }
      const newKeys = new Set(newVis.map(t => t.key));
      const hidden = oldVis
        .filter(t => !newKeys.has(t.key))
        .map(t => {
          const dealCount = allDeals.filter(d =>
            d.salesPlan === planFilter
            && d.quarter === t.quarter
            && d.year === t.year
          ).length;
          const addedRowCount = addedRows.filter(r =>
            r.salesPlan === planFilter
            && r.quarter === t.quarter
            && r.year === t.year
          ).length;
          return { key: t.key, label: t.label, dealCount, addedRowCount };
        })
        .filter(h => h.dealCount + h.addedRowCount > 0);
      if (hidden.length > 0) {
        const proceed = await showMarketplaceWarn({
          oldMarketplace: existing.marketplace,
          newMarketplace: editMarketplace,
          hidden,
        });
        if (!proceed) {
          // User cancelled. Leave editMarketplace as-is so they can
          // reconsider — either pick a different marketplace, address
          // the data first (delete rows from the excluded quarters),
          // or close the modal entirely. No state change here.
          return;
        }
      }
      // Proceeding: commit the recomputed extras count so the new
      // marketplace's visibleQuarters preserves the user's chosen
      // end quarter. Update component state AND the localStorage
      // fallback; the backend save below will pick this up via the
      // request payload (saveToSnowflake reads extraQuarters state).
      if (newExtraCount !== extraQuarters) {
        setExtraQuartersState(newExtraCount);
        setExtraQuartersCount(planFilter, newExtraCount);
      }
    }

    const updatedMeta: ManualPlanMeta = {
      // Keyed by current planFilter — the rename block in
      // saveToSnowflake bumps this to the new nickname (via
      // nicknameOverride below) alongside planFilter.
      planName: planFilter,
      advertiser:  editAdvertiser,
      dealName:    newNickname,
      agency:      editAgency,
      status:      existing?.status ?? 'Working',
      brand:       editBrand,
      ae:          editAe,
      buyer:       editBuyer,
      demo:        editDemo,
      marketplace: editMarketplace,
      equiv:       editEquiv,
      year:        '26/27',
      sourcePlanName: existing?.sourcePlanName,
      sourcePlanSfId: existing?.sourcePlanSfId,
      advertiserId:  editAdvertiserId,
      brandId:       editBrandId,
      contactId:     editContactId,
      accountId:     editAccountId,
      salesPlanName: editComputedSalesPlanName,
      // Plan-level default unit length. Empty picker value = no
      // default (null). Consumed by + Add Show / + Add Row to seed
      // new rows' unitLength.
      defaultUnitLength: editDefaultUnitLength || null,
    };

    // ── Primary Demo change: re-stamp and recompute every row ──────────
    //
    // The plan's demo isn't only a label. Every rating, (000s) and demo
    // CPM in the grid is looked up per-row by that row's own `demo`
    // (calcEstimateFields → getEstimate), so changing the plan-level value
    // without re-stamping the rows would leave the column headers reading
    // the new demo over numbers still computed against the old one.
    //
    // Two things happen per row:
    //   • demo := the new demo, so estimate lookups hit the right entries.
    //   • overrideDemo000 := null. recalcDeal honours a per-row override
    //     ABOVE the estimate, so any row where someone typed a manual
    //     Demo (000) would keep the old demo's impressions and silently
    //     ignore this change. Those overrides were entered against the
    //     old demo and can't be carried over meaningfully.
    //
    // recalcDeal holds CPM constant and derives rate + dollars from it
    // (rate = demo000 × cpm), so the plan's TOTAL DOLLARS will move — the
    // same CPM against a different guarantee is a different amount of
    // money. That's the intended reading of a demo switch.
    //
    // Writing the refs as well as the state is deliberate: saveToSnowflake
    // reads allDealsRef/addedRowsRef, not the closure, and React won't
    // have re-rendered by the time it runs a few lines below.
    const demoChanged = (existing?.demo ?? '') !== editDemo;
    if (demoChanged) {
      const nextDeals = allDealsRef.current.map(d =>
        d.salesPlan === planFilter
          ? recalcDeal({ ...d, demo: editDemo, overrideDemo000: null })
          : d,
      );
      const nextAdded = addedRowsRef.current.map(r =>
        r.salesPlan === planFilter
          ? { ...r, demo: editDemo, overrideDemo000: null }
          : r,
      );
      allDealsRef.current = nextDeals;
      addedRowsRef.current = nextAdded;
      setAllDeals(nextDeals);
      setAddedRows(nextAdded);
      // The row inputs are uncontrolled with their value baked into their
      // React key; bump the remount token so they re-read the recomputed
      // numbers instead of showing what was typed against the old demo.
      setInputRemountKey(k => k + 1);
    }

    // Queue the nickname rename for saveToSnowflake's rename block.
    // We pass it explicitly via the override below — setPlanNameDraft
    // would not be visible to the immediately-following save call
    // because React hasn't re-rendered yet.
    if (newNickname !== planFilter) {
      setPlanNameDraft(newNickname);
    }

    // Close the modal immediately so the user sees the action register;
    // the Snowflake save runs in the background (saveToSnowflake's
    // setIsSaving drives the save button's spinner). If the save fails,
    // an error toast surfaces it and the user can retry via the main
    // Save button.
    closeEditPlanModal();
    try {
      await saveToSnowflake({
        metaOverride: updatedMeta,
        nicknameOverride: newNickname !== planFilter ? newNickname : undefined,
      });
      // saveToSnowflake already surfaces its own success/failure toasts;
      // no additional toast here to avoid double-firing.
    } catch (err: any) {
      // Defensive — saveToSnowflake catches its own errors and toasts,
      // but if something escapes (programmer error), still notify so
      // the user knows the save didn't reach Snowflake.
      // eslint-disable-next-line no-console
      console.error('submitEditPlan: unexpected error during saveToSnowflake', err);
      showToast('Save failed. Click main Save to retry.');
    }
  }

  function closeNewPlanModal() {
    setShowNewPlanModal(false);
    setNpRollForwardSource(null); setNpRollForwardSfId(null); setNpRollForwardPlanId(null); setNpRollForwardYear(null);
    setNpRollForwardSearch(''); setNpRollForwardOpen(false);
    setNpUnitLength('');
    setNpAdvertiser(''); setNpAdvertiserId(null);
    setNpDealname('');
    setNpAgency('');  setNpAccountId(null);
    setNpBrand('');   setNpBrandId(null);
    setNpBuyer('');   setNpContactId(null);
    // Reset AE back to the team-wide default for the next open.
    setNpAe('Tiffany DeDeo');
    setNpDemoInput(''); setNpDemo('');
    setNpMarketplace(''); setNpError('');
    // Back to the master estimate for the next open. A rate card is a
    // deliberate per-plan choice, never sticky across modal opens.
    setNpRateCard('general');
    setNpShowDemoDropdown(false);
  }

  async function submitNewPlan() {
    // Year + status are no longer modal inputs: every new plan is 26/27
    // (the only writable season) and defaults to Working in the picklist.
    // Required: Advertiser, Plan Nickname, Brand, Buyer (its Account
    // auto-fills Agency), Demo, Marketplace. Agency isn't checked —
    // it derives from the picked Contact. AE is optional.
    // Name the specific missing fields so the user doesn't have to
    // scan the modal for the red-bordered inputs. Order matches the
    // modal's visual top-to-bottom flow so the list reads naturally.
    // TBD placeholder = valid draft state; see npMissingRequired above.
    const missingRequiredFields: string[] = [];
    if (!npAdvertiserId && npAdvertiser !== 'TBD') missingRequiredFields.push('Advertiser');
    if (!npDealname) missingRequiredFields.push('Plan Nickname');
    if (!npBrandId && npBrand !== 'TBD') missingRequiredFields.push('Brand');
    if (!npContactId && npBuyer !== 'TBD') missingRequiredFields.push('Buyer');
    if (!npDemo) missingRequiredFields.push('Primary Demo');
    if (!npMarketplace) missingRequiredFields.push('Marketplace');
    if (missingRequiredFields.length > 0) {
      const list = missingRequiredFields.length === 1
        ? missingRequiredFields[0]
        : missingRequiredFields.length === 2
          ? `${missingRequiredFields[0]} and ${missingRequiredFields[1]}`
          : `${missingRequiredFields.slice(0, -1).join(', ')}, and ${missingRequiredFields[missingRequiredFields.length - 1]}`;
      setNpError(`Please fill in ${list}.`);
      return;
    }
    // Defensive — the Create button is already gated on this, but
    // double-check here so we never persist a colliding name even if a
    // race somehow lets the click through (e.g. paste-edit + Enter
    // before the memo re-runs).
    if (npNameCollision) {
      setNpError(
        `A plan with Sales Plan Name "${npComputedSalesPlanName}" already exists for 26/27` +
        (npCollidingPlanNickname ? ` (${npCollidingPlanNickname})` : '') +
        `. Change Advertiser, Brand, Buyer, or Demo to differentiate.`
      );
      return;
    }
    // Clone-nickname collision guard.
    //
    // Backend PLAN uniqueness is (name, season_primary_year,
    // source_plan_sf_id). For 26/27 verbatim clones sourceSfId is
    // null, so a Create Plan whose nickname matches the source's
    // triggers the backend's "adopt existing plan_id" race-safety
    // branch — the clone silently lands as a new PLAN_VERSION on
    // the SOURCE plan instead of creating a new PLAN row. That
    // permanently pollutes the source's version history (and made
    // the user's clone attempt look like a phantom "combined" plan).
    //
    // The at-rest `npNameCollision` check above only fires for
    // PUSHED plans (per task #88 — scenario variants like GENENTECH
    // 250K/500K/800K need to share Sales Plan Names at draft time).
    // But that carve-out doesn't apply here: this is a CREATE flow,
    // where an unchanged nickname will bump into the backend uniqueness
    // constraint regardless of push state.
    //
    // Block with a specific, actionable error so the user knows to
    // rename before submitting.
    if (
      npRollForwardYear === '26/27'
      && npRollForwardSource
      && npDealname.trim() === npRollForwardSource.trim()
    ) {
      setNpError(
        `Nickname "${npDealname.trim()}" is the same as the source plan. ` +
        `Rename the new plan (e.g. add a suffix like " – v2" or " – Alt") ` +
        `so it doesn't collide with the source.`
      );
      return;
    }
    // Plan name = exactly what the user typed in the "Plan Name" field.
    // Agency / advertiser / brand / demo are persisted on the saved
    // PLAN_VERSION row via the save request built below.
    const planName = npDealname.trim();

    // ── Roll-forward: build 25/26 → 26/27 deals in memory ─────────────
    //
    // Fires only when the user picked a 25/26 plan in the modal. Each
    // copied deal slides forward one broadcast year (Q4 25 → Q4 26,
    // Q1 26 → Q1 27, …) — the `year + 1` happens inline below. Each
    // lands under the new plan name with a fresh dealKey to avoid
    // collisions in updateDeal(), then recalcs against the 26/27
    // estimate so totals + impressions reflect this season's HH 000s.
    //
    // Drop rules:
    //   - Program missing from the 26/27 estimate → skipped (per spec).
    //   - Program present but no demo000 for the plan's demo → kept.
    //
    // NOTE: we build these into a local array here rather than pushing
    // to allDeals directly. They get included in the initial POST
    // payload below so PLAN_VERSION v1 has the roll-forward deals from
    // the start (no phantom empty v1 followed by a v2 with data).
    // setAllDeals(...) only fires after the save succeeds.
    // Resolve the estimate this plan will be pinned to, scoped to all three
    // dimensions: season, kind, and rate card.
    //
    // This previously called getLatestVersionByYear(2026), which filters on
    // year ALONE — no kind, no rate card — so whichever file happened to be
    // uploaded most recently won. That made a new plan's estimate a function
    // of upload order rather than of anything the user chose. With rate cards
    // it stops being a wrong-numbers problem and becomes a confidentiality
    // one: it could stamp WPP's negotiated rates onto another agency's plan.
    //
    // KIND now follows the Marketplace the user picked. A Scatter plan binds
    // to the scatter estimate; BY/CY Upfront bind to upfront. Falls back to
    // the general upfront master when the matching track has no upload yet —
    // there is no 26/27 scatter estimate today, so that fallback is the live
    // path for Scatter plans until one is uploaded.
    //
    // RATE CARD is re-derived from applicability rather than trusting that
    // the reset effect has flushed. It does flush today — React commits the
    // effect before it can dispatch another user event — but that makes the
    // guarantee temporal, and a temporal guarantee about which agency's rates
    // a plan gets is not one worth relying on.
    const npKind: EstimateKind = npMarketplace === 'Scatter' ? 'scatter' : 'upfront';
    // Re-check BOTH invariants the reset effect enforces — applicability AND
    // membership in the offered options. Checking only applicability left a
    // real hole: pickRollForwardSource sets the card straight from the clone
    // source's bound version with no options check, so cloning a plan bound
    // to a card that has no 26/27 estimate leaves npRateCard = that card. If
    // the effect hasn't flushed, target26 resolves null, estimateVersionId is
    // saved as null, and the plan renders against 25/26 estimates.
    const effectiveRateCard: RateCard =
      (npRateCardApplies && npRateCardOptions.includes(npRateCard))
        ? npRateCard
        : 'general';
    const target26 =
      getLatestVersionByYearKindAndCard(CURRENT_SEASON_PRIMARY_YEAR, npKind, effectiveRateCard)
      ?? getLatestVersionByYearKindAndCard(CURRENT_SEASON_PRIMARY_YEAR, 'upfront', effectiveRateCard)
      // Last resort for a GENERAL plan only: any general version for the
      // season, either kind. Restores the reach of the old year-only call —
      // a season with a general scatter but no general upfront used to
      // resolve and would otherwise now hard-fail the roll-forward path.
      // Never widened for a custom card: a WPP plan with no WPP estimate
      // must stay null rather than silently land on the general master.
      ?? (effectiveRateCard === 'general'
            ? getLatestGeneralVersionByYear(CURRENT_SEASON_PRIMARY_YEAR)
            : null);
    const rolledDeals: Deal[] = [];
    let droppedProgramsCount = 0;
    if (npRollForwardSource) {
      if (!target26) {
        // Defensive — the picker shouldn't have been usable without a
        // 26/27 estimate, but bail loudly if we somehow got here.
        const cardLabel = formatRateCard(effectiveRateCard);
        setNpError(cardLabel
          ? `Cannot roll forward — no 26/27 ${cardLabel} estimate is uploaded yet.`
          : 'Cannot roll forward — no 26/27 estimate is uploaded yet.');
        return;
      }
      // Switch the module-level estimate refs to 26/27 *before*
      // recalcDeal, since recalcDeal reads from ESTIMATE_DB.
      setActiveEstimates(target26);
      const programIndex = target26.programNameIndex || {};
      // Source-of-truth for the clone payload: for 26/27 clones, pull
      // from the saved-plan cache's LATEST version (the definitive
      // Snowflake row), NOT from allDeals. allDeals can be transiently
      // empty for the source plan when the user is viewing a
      // historical revision (openHistoricalVersion overlays allDeals
      // with the picked version's deals — a 0-deal ancestor of the
      // source shows as 0 deals in state). Cloning from that state
      // used to silently produce a new plan with 0 deals.
      //
      // For 25/26 SF-source roll-forwards, allDeals is still the right
      // source: those deals come from the source_deals API load, not a
      // saved PLAN_VERSION, so there's no cache to fall back to.
      let sourceDeals: Deal[];
      if (npRollForwardYear === '26/27') {
        const savedSource = getSavedPlanByName(npRollForwardSource);
        const latestDeals = savedSource?.latestVersion?.deals ?? [];
        sourceDeals = planDealsToDeals(
          npRollForwardSource,
          savedSource?.sourcePlanSfId ?? null,
          latestDeals,
        );
      } else {
        sourceDeals = allDeals.filter(d => d.salesPlan === npRollForwardSource);
      }
      // ── Unit-Length transform ─────────────────────────────────────
      //
      // Applies the New Plan modal's "Unit Length for Plan" pick to the
      // source rows before we clone them into the new plan. The rules
      // (see product discussion for the full walkthrough):
      //
      //   • Rows at the target length pass through untouched — no
      //     re-merging between them; make-goods (units==1) survive as
      //     their own row.
      //   • Rows at other lengths get converted with new_units =
      //     round(old_units × src_len / target_len). Rounding is
      //     half-up (Math.round in JS for positive numbers).
      //   • Converted units fold into the target-length row in the
      //     same (program, quarter, year, demo) group with units > 1.
      //     Preference: rate+CPM match first, then highest units. In
      //     practice a group has one dominant row, so the tiebreak
      //     is defensive.
      //   • If no eligible sink exists (either no target-length row
      //     at all, or only make-goods with units==1), a new target-
      //     length row is created from the converted units. Multiple
      //     converted rows sharing the same (rate, cpm) merge into
      //     ONE new row; different-rate converted rows produce
      //     separate new rows (matches the two-15s-at-different-rates
      //     → two-new-30-rows behavior from the discussion).
      //   • Zero-unit converted rows (e.g., 1 × :15 → 0.5 → rounds to 1
      //     via half-up; but 0 × :15 → 0) are skipped.
      //
      // "As is" (or an empty picker value — legacy path) skips this
      // block entirely; source rows roll forward verbatim.
      let dealsToRoll = sourceDeals;
      // Unit-length consolidation is a 25/26 → 26/27 roll-forward
      // feature only. For 26/27 verbatim clones, the picker's value
      // sets the NEW plan's default for future + Add Show / + Add Row
      // rows — it doesn't retroactively convert the source's existing
      // rows. Gate the transform accordingly; without this gate,
      // cloning a plan with source LEN=30 and picking Default :45
      // would incorrectly rewrite every deal to LEN=45.
      const targetLenNum =
        npRollForwardYear === '25/26'
        && npUnitLength
        && npUnitLength !== 'as-is'
          ? parseFloat(npUnitLength)
          : null;
      if (targetLenNum && targetLenNum > 0) {
        const groupKey = (d: Deal) =>
          `${d.program}|${d.quarter}|${d.year}|${d.demo ?? ''}`;
        const groups = new Map<string, Deal[]>();
        for (const d of sourceDeals) {
          const gk = groupKey(d);
          let bucket = groups.get(gk);
          if (!bucket) { bucket = []; groups.set(gk, bucket); }
          bucket.push(d);
        }
        const transformed: Deal[] = [];
        for (const bucket of Array.from(groups.values())) {
          const targetRows: Deal[] = [];
          const nonTargetRows: Deal[] = [];
          for (const d of bucket) {
            const srcLen = parseFloat(String(d.unitLength)) || 0;
            if (srcLen === targetLenNum) {
              targetRows.push(d);
            } else {
              nonTargetRows.push(d);
            }
          }
          // Mutable sink pool — each entry tracks the running unit
          // total so we can absorb converted units without mutating
          // the source deals themselves.
          const sinkPool: Array<{ deal: Deal; totalUnits: number }> =
            targetRows.map(d => ({ deal: d, totalUnits: d.totalUnits ?? 0 }));

          // Bucket non-target rows by CPM (rounded to 2 decimals) so
          // we sum their *decimal* converted units before rounding.
          // Rounding once per bucket instead of once per row avoids
          // cumulative half-up error: two rows of 5 × :15 → :30 must
          // sum to 5, not round to 3+3 = 6.
          //
          // Bucketing on CPM alone (not CPM + newRate) is deliberate.
          // SF stores per-spot rate at 2-decimal precision — a plan
          // saved with 5 spots at CPM $4.50 can have per-spot rates
          // that drift by cents between otherwise-identical rows.
          // Keying on newRate would spuriously split matching rows
          // into separate buckets and reintroduce the per-row-round
          // problem this refactor was meant to fix. Users identify
          // "same rate class" by CPM (that's what the sales convo
          // is about — "at $4.50 CPM"); make-goods differ visibly
          // by CPM, which is what keeps them out of the merge.
          const cpmKey = (cpm: number | null | undefined) =>
            cpm == null ? '' : cpm.toFixed(2);
          const nonTargetBuckets = new Map<string, {
            representative: Deal;
            rawUnits: number;
          }>();
          for (const nt of nonTargetRows) {
            const srcLen = parseFloat(String(nt.unitLength)) || 0;
            if (srcLen <= 0) continue;
            const rawConverted = (nt.totalUnits ?? 0) * srcLen / targetLenNum;
            if (rawConverted <= 0) continue;
            const key = cpmKey(nt.cpm);
            const existing = nonTargetBuckets.get(key);
            if (existing) {
              existing.rawUnits += rawConverted;
            } else {
              nonTargetBuckets.set(key, { representative: nt, rawUnits: rawConverted });
            }
          }

          // For each CPM bucket: round the summed decimal, then
          // either absorb into an eligible sink (CPM-match first,
          // else highest-units, units>1) or emit a new target-length
          // row from the bucket's representative source deal.
          const newRowsToEmit: Array<{ deal: Deal; totalUnits: number }> = [];
          Array.from(nonTargetBuckets.values()).forEach(bucket => {
            const convertedUnits = Math.round(bucket.rawUnits);
            if (convertedUnits <= 0) return;
            const nt = bucket.representative;
            const eligibleSinks = sinkPool.filter(s => s.totalUnits > 1);
            let sink: { deal: Deal; totalUnits: number } | undefined;
            if (eligibleSinks.length > 0) {
              const targetCpmKey = cpmKey(nt.cpm);
              sink =
                eligibleSinks.find(s => cpmKey(s.deal.cpm) === targetCpmKey)
                ?? eligibleSinks.reduce((a, b) =>
                  a.totalUnits >= b.totalUnits ? a : b,
                );
            }
            if (sink) {
              sink.totalUnits += convertedUnits;
              return;
            }
            const converted: Deal = {
              ...nt,
              unitLength: String(targetLenNum),
              totalUnits: convertedUnits,
            };
            newRowsToEmit.push({ deal: converted, totalUnits: convertedUnits });
          });
          for (const s of sinkPool) {
            transformed.push({ ...s.deal, totalUnits: s.totalUnits });
          }
          newRowsToEmit.forEach(nr => {
            transformed.push({ ...nr.deal, totalUnits: nr.totalUnits });
          });
        }
        dealsToRoll = transformed;
      }
      let rfCounter = 0;
      // Whether we're doing a same-season clone (26/27 → 26/27) vs a
      // cross-season roll-forward (25/26 → 26/27). Drives:
      //   • Year shift: clones keep source year; roll-forwards add +1.
      //   • Program-index drop: clones skip the drop (same estimate on
      //     both sides — programs are guaranteed present); roll-forwards
      //     drop anything not in the 26/27 estimate.
      //   • Value preservation: clones carry demo/rate/dollars/CPM
      //     verbatim from the source. Roll-forwards clear overrides
      //     and re-derive from the 26/27 estimate (necessary because
      //     the estimate itself is new). Clone users expect an exact
      //     copy — "$500K plan cloned to a $500K plan", not "$500K
      //     recomputed to $502,235 after wiping the overrides".
      const isClone = npRollForwardYear === '26/27';
      const droppedPrograms = new Set<string>();
      for (const src of dealsToRoll) {
        if (!isClone) {
          const key = normalizeProgramName(src.program);
          if (!programIndex[key]) {
            droppedPrograms.add(src.program);
            continue;
          }
        }
        rfCounter += 1;
        const nd: Deal = isClone
          ? {
              // Verbatim copy — every user-visible number (CPM, rate,
              // demo (000), totalDollars, guaranteed, overrides) carries
              // over exactly. Only identity fields change: fresh dealKey,
              // new salesPlan, source-key pointer for traceability.
              ...src,
              dealKey: `D-RF-${Date.now()}-${rfCounter}`,
              _rolledFromSfDealKey: src.dealKey,
              salesPlan: planName,
              year: src.year,
            }
          : {
              // Roll-forward — wipe overrides + guaranteed and re-derive
              // via recalcDeal because we're crossing into a new estimate
              // where those values wouldn't be meaningful.
              ...src,
              dealKey: `D-RF-${Date.now()}-${rfCounter}`,
              _rolledFromSfDealKey: src.dealKey,
              salesPlan: planName,
              year: src.year + 1,
              overrideDemo000: undefined,
              _cpmManuallyEdited: false,
              guaranteed: 0,
              originalCpm: src.cpm ?? null,
              _baselineCpm: src.cpm ?? null,
            };
        if (!isClone) {
          recalcDeal(nd);
        }
        nd.baseCpm = nd.cpm;
        nd.baseRate = nd.newRate ?? null;
        nd.baseDollars = nd.totalDollars;
        nd.baseCpmHH = nd.newHhCpm ?? null;
        rolledDeals.push(nd);
      }
      droppedProgramsCount = droppedPrograms.size;
    }

    // ── Build the deal payload from rolled deals (empty for scratch) ──
    const dealsPayload: SavePlanRequest['deals'] = rolledDeals.map(d => {
      const est = calcEstimateFields(d);
      const eff = d.overrideDemo000 != null
        ? d.overrideDemo000
        : (est.demo000 ?? null);
      return {
        program: d.program,
        day: DAY_TIME[d.program] ?? null,
        quarter: d.quarter,
        year: d.year,
        unitLength: parseFloat(String(d.unitLength)) || null,
        demo: d.demo ?? null,
        totalUnits: d.totalUnits ?? null,
        cpm: d.cpm ?? null,
        originalCpm: d.originalCpm ?? null,
        rate: d.newRate ?? null,
        totalDollars: d.totalDollars ?? null,
        impressions_000: eff,
        // A plan being created has no secondary demos selected yet — the
        // picker lives on the grid, not in the New Plan modal — so there's
        // nothing to resolve. The backend stores an empty map as NULL.
        secondaryImpressions000: {},
        cpmManuallyEdited: d._cpmManuallyEdited === true,
        account: d.account ?? null,
        brand: d.brand ?? null,
        sourceSfDealKey: d._rolledFromSfDealKey ?? d.dealKey ?? null,
        // Salesforce Programs__c.ID — inherited from the 25/26 source deal
        // via the source-deals query. Nullable if the SF join returned no
        // match; push-to-SF will refuse the plan in that case.
        programId: d.programId ?? null,
      };
    });

    // ── POST /snowflake/plans — create the row NOW, not on green Save ──
    const request: SavePlanRequest = {
      planId: null,
      name: planName,
      seasonPrimaryYear: CURRENT_SEASON_PRIMARY_YEAR,
      sourcePlanName: npRollForwardSource ?? null,
      sourcePlanSfId: npRollForwardSfId ?? null,
      status: 'Working',
      // The activeVersion fallback only applies to a general plan. For a
      // custom rate card it would be actively wrong — activeVersion is
      // whatever the app is currently anchored to, which is the general
      // master in every ordinary case, so falling back would create a "WPP"
      // plan silently bound to general estimates. If no estimate exists for
      // the picked card we send null and let the plan bind on a later save;
      // the picker only offers a card that has an estimate, so this is
      // already unreachable from the UI.
      // baseVersionIdOf on the fallback: whatever plan the user had open
      // before hitting New Plan may be showing a MERGED version, whose id is
      // a synthetic negative. Persisting it would create a plan bound to no
      // estimate at all.
      estimateVersionId: effectiveRateCard === 'general'
        ? (target26?.id ?? baseVersionIdOf(activeVersion))
        : (target26?.id ?? null),
      agency:      npAgency      || null,
      advertiser:  npAdvertiser  || null,
      brand:       npBrand       || null,
      demo:        npDemo        || null,
      ae:          npAe          || null,
      buyer:       npBuyer       || null,
      marketplace: npMarketplace,
      // Equivalized field removed from the New Plan modal — no flow
      // consumes it downstream. Persist null so existing plan_meta
      // rows keep their column shape without carrying stale values.
      equiv:       null,
      dealName:    npDealname    || null,
      year: SEASON_LABEL,
      advertiserId: npAdvertiserId,
      brandId:      npBrandId,
      contactId:    npContactId,
      accountId:    npAccountId,
      salesPlanName: npComputedSalesPlanName,
      extraQuartersCount: 0,
      // Plan-level default unit length — the "Unit Length for Plan"
      // picker's value on the New Plan modal. Stored so new rows
      // seeded via + Add Show / + Add Row later can prefill their
      // unitLength. "as-is" (a copy-plan-only transform mode) doesn't
      // translate to a numeric default; null it out so the plan
      // doesn't inherit an unusable literal.
      defaultUnitLength: (npUnitLength && npUnitLength !== 'as-is')
        ? npUnitLength
        : null,
      deals: dealsPayload,
    };

    const userEmail = getEmailFromSessionStorage() || undefined;
    setNpError('');
    setIsCreatingPlan(true);
    let result;
    try {
      result = await savePlan(request, userEmail);
    } finally {
      setIsCreatingPlan(false);
    }
    if (!result.ok) {
      setNpError('Failed to create plan: ' + result.error);
      return;
    }

    // Response-shape sanity check. The backend create path now runs
    // autocommit-per-statement (see save.ts): every read the response
    // is built from reads COMMITTED data, so the response can be
    // trusted again — no server refetch needed. A malformed response
    // (missing version / wrong deal count) can only mean a genuine
    // partial failure that slipped past the backend's compensating
    // cleanup, so surface it rather than pretending success.
    const resVersionNo = result.plan.latestVersion?.versionNo;
    const resDealCount = result.plan.latestVersion?.deals?.length ?? 0;
    if (resVersionNo == null || resDealCount !== dealsPayload.length) {
      setNpError(
        `Save didn't fully complete — ${resDealCount}/${dealsPayload.length} deals ` +
        `persisted (v${resVersionNo ?? '?'}, plan ID ${result.plan.id}). ` +
        `Retry Create Plan — the failed attempt will be recycled automatically.`
      );
      return;
    }

    // ── Post-save UI updates (only after Snowflake acknowledges) ──────
    // savePlan() has already committed the returned plan to planCache.
    // Bump the counter so memos that read listSavedPlans() re-evaluate.
    setPlanCacheVersion(v => v + 1);

    // Mirror rolled-forward deals into React state so they render in
    // this session. They're already persisted in v1; this is UI only.
    // Compute the post-merge deals array up front so we can also baseline
    // the new plan below — otherwise React state hasn't applied yet and
    // reading `allDeals` would sign the pre-create data.
    const nextAllDeals = rolledDeals.length > 0
      ? [...allDeals, ...rolledDeals]
      : allDeals;
    if (rolledDeals.length > 0) {
      setAllDeals(nextAllDeals);
    }

    // Stamp the "just saved" baseline for the NEW plan so isDirty starts at
    // false for it. Without this, the very first cell edit would compare
    // against a missing baseline and Save would look enabled even though the
    // plan was just persisted. Scoped to this plan so creating one cannot
    // re-baseline anything the user has unsaved elsewhere.
    snapshotPlans([planName], nextAllDeals, addedRows);
    if (droppedProgramsCount > 0) {
      showToast(
        `Rolled forward ${rolledDeals.length} deals from ${npRollForwardSource}. ` +
        `Skipped ${droppedProgramsCount} program${droppedProgramsCount === 1 ? '' : 's'} ` +
        `not in the 26/27 estimate.`,
      );
    } else if (rolledDeals.length > 0) {
      showToast(`Rolled forward ${rolledDeals.length} deals from ${npRollForwardSource}.`);
    }

    switchPlan(planName, { force: true });  // plan just created
    // Explicitly reset seasonView to '26/27' — don't rely on the
    // `[planFilter]` useEffect further up to fire, because when a user
    // rolls a plan forward and keeps the source's nickname (default
    // auto-fill in the modal), setPlanFilter(planName) is a no-op
    // (same value as the plan they were viewing). React skips the
    // useEffect, and the user gets stuck looking at the 25/26 source
    // view even though they just created the 26/27 plan.
    setSeasonView('26/27');
    // Switch to the first quarter tab so the user lands somewhere they
    // can actually add deals — the Summary tab has no "+ Add Show"
    // affordance, which is confusing when starting from an empty plan.
    // Land on the new plan's first VISIBLE quarter, not just QUARTER_TABS[0].
    // The visibleQuarters memo hasn't recomputed yet (state update queued),
    // so derive it inline from the marketplace the user just picked.
    const visForNewPlan = buildVisibleQuarters(
      npMarketplace,
      activeVersion?.quarters ?? QUARTER_TABS,
      activeVersion?.primaryYear ?? null,
    );
    const firstTab = visForNewPlan[0] ?? QUARTER_TABS[0];
    if (firstTab) {
      setActiveQuarter(firstTab.key);
    }
    closeNewPlanModal();
    showToast('✓ Plan created: ' + planName);
  }

  /**
   * Delete the currently-selected plan from every place it can live.
   *
   * Two cases handled in one flow:
   *   - Snowflake-saved plan: soft-delete via DELETE /snowflake/plans/:id.
   *     Backend sets deleted_at + deleted_by; plan-storage drops it from
   *     the cache. The plan's SF source plan becomes visible again in
   *     SF query results on next reload (the NOT IN filter sees
   *     deleted_at IS NOT NULL and lets it through).
   *   - Legacy `_manual` flagged deals (pre-cleanup): drop from allDeals.
   *
   * Both are cleaned up unconditionally so a plan that exists in
   * multiple places doesn't leave orphans behind.
   *
   * Pure SF templates can't be deleted from this UI — the button is
   * hidden via `isDeletablePlan`. SF owns those plans; the only way to
   * remove an SF template is in Salesforce.
   */
  async function deleteCurrentPlan() {
    if (!planFilter) return;

    const saved = getSavedPlanByName(planFilter);
    const hasLegacyManualDeals = allDeals.some(d => d.salesPlan === planFilter && d._manual);
    if (!saved && !hasLegacyManualDeals) return;

    // In-app confirmation — Promise-based, same shape as showMarketplaceWarn.
    // Replaces the native window.confirm(): consistent modal chrome
    // with the rest of the app and interactable by UI automation.
    // Narrow the count to visibleQuarters so consolidated-nickname plans
    // (rolled 26/27 + 25/26 SF source) don't double-count — same
    // rationale as the SF Structure preview fix.
    const deleteVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    const planDealCount = allDeals.filter(d =>
      d.salesPlan === planFilter
      && d.totalUnits > 0
      && deleteVisibleKeys.has(`${d.quarter}|${d.year}`)
    ).length;
    const proceed = await new Promise<boolean>(resolve => {
      setDeleteConfirm(prev => {
        if (prev) prev.resolve(false);
        return { planName: planFilter, dealCount: planDealCount, resolve };
      });
    });
    if (!proceed) return;

    // 1. Soft-delete in Snowflake first. If it fails, bail out so the
    //    user sees the error and the local state stays intact.
    if (saved) {
      const userEmail = getEmailFromSessionStorage() || undefined;
      const result = await deleteSavedPlan(saved.id, userEmail);
      if (!result.ok) {
        showToast('Delete failed: ' + result.error);
        return;
      }
      // Bump the saved-plans cache version so memos that consume
      // listSavedPlans() inline re-evaluate and stop showing the
      // just-deleted plan. Mirrors the save-success bump.
      setPlanCacheVersion(v => v + 1);
    }

    // 2. Drop any rows for this plan from allDeals + addedRows. Covers
    //    saved-plan deals (folded in by the merge effect), legacy
    //    _manual deals, and any user-added rows from the current session.
    setAllDeals(prev => prev.filter(d => d.salesPlan !== planFilter));
    setAddedRows(prev => prev.filter(r => r.salesPlan !== planFilter));

    switchPlan('', { force: true });        // plan was just deleted
    showToast('Plan deleted.');
  }


  // ─── SF Structure modal ──────────────────────────────────────────────────────

  function openSFStructure() {
    if (!planFilter) return;
    const meta = getPlanMeta(planFilter);
    if (!meta) return;
    setShowSFModal(true);
  }

  // ─── Secondary demos ─────────────────────────────────────────────────────────

  function addSecondaryDemo(demo: string) {
    if (!secondaryDemos.includes(demo)) {
      setSecondaryDemos(prev => [...prev, demo]);
    }
    setDemoPillDropdownOpen(false);
    setDemoSearchFilter('');
  }

  function removeSecondaryDemo(demo: string) {
    setSecondaryDemos(prev => prev.filter(d => d !== demo));
  }

  // ─── Stats ───────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    // Deals (SF-loaded or Snowflake-saved). For totalDemo: prefer the
    // estimate's demo (000) value, fall back to the SF actual (000)
    // when no estimate exists.
    const dealUnits   = filteredDeals.reduce((s, d) => s + d.totalUnits, 0);
    const dealDollars = filteredDeals.reduce((s, d) => s + (d.totalDollars || 0), 0);
    const dealDemo    = filteredDeals.reduce((s, d) => {
      const est = calcEstimateFields(d);
      // Honor the per-row Demo (000) override before falling back to
      // the estimate value or the SF-loaded actual — same precedence
      // the row-level cell uses. Without this, an added row with a
      // reduced demo (e.g. a make-good priced at 64k instead of the
      // estimate's 169k) aggregates as if it still had 169k, and the
      // stat card / subtotals overstate impressions.
      const v = d.overrideDemo000 != null
        ? d.overrideDemo000
        : (est.demo000 != null ? est.demo000 : (d.actual000 || 0));
      return s + v * (d.totalUnits || 0);
    }, 0);

    // AddedRows (manual "+ Add Show" / "+ Add Row" entries that exist
    // only in client state until the next save). totalDollars is
    // tracked on the row directly (set by the rate/units edit
    // handlers); totalDemo uses the same effective-demo chain as the
    // table render — user override first, then the estimate.
    const addedUnits   = filteredAddedRows.reduce((s, r) => s + (r.totalUnits || 0), 0);
    const addedDollars = filteredAddedRows.reduce((s, r) => s + (r.totalDollars || 0), 0);
    const addedDemo    = filteredAddedRows.reduce((s, r) => {
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      const v = getEffectiveDemo000(r, est);
      return s + (v ?? 0) * (r.totalUnits || 0);
    }, 0);

    return {
      totalUnits:   dealUnits   + addedUnits,
      totalDollars: dealDollars + addedDollars,
      totalDemo:    dealDemo    + addedDemo,
    };
  }, [filteredDeals, filteredAddedRows]);

  /**
   * Plan-wide dollars — sum of every deal and added row for the current
   * plan, ignoring the active quarter tab. Powers the "Total Plan Dollars"
   * stat card so users on a quarter tab can see BOTH:
   *   • Total Dollars    — this quarter's dollars (existing card)
   *   • Total Plan Dollars — every quarter combined (this memo)
   * On the Summary tab the two cards read the same value (Summary already
   * spans every quarter). On All Plans the memo scopes to whatever the
   * quarter-tab filter would show at Summary — same math the Total
   * Dollars card runs on Summary today.
   */
  const planWideTotalDollars = useMemo(() => {
    if (!planFilter) {
      // All Plans: sum every visible row's dollars.
      return allDeals.reduce((s, d) => {
        if (d.salesPlan && d.salesPlan !== planFilter && !planFilter) {
          // fall through — no plan filter means include everyone in
          // scope of the current season view; matches how stats behaves
          // on Summary tab in the All Plans view.
        }
        return s + (d.totalDollars || 0);
      }, 0);
    }
    let sum = 0;
    for (const d of allDeals) {
      if (d.salesPlan === planFilter) sum += d.totalDollars || 0;
    }
    for (const r of addedRows) {
      if (r.salesPlan === planFilter) sum += r.totalDollars || 0;
    }
    return sum;
  }, [planFilter, allDeals, addedRows]);

  // ─── Export to Excel ─────────────────────────────────────────────────────────

  /**
   * Builds one quarter's sheet and appends it to `wb`.
   * Shared between single-quarter and "Export All" exports.
   */
  function appendQuarterSheetToWorkbook(
    wb: XLSX.WorkBook,
    params: {
      deals: Deal[];
      quarterKey: string;       // e.g. "4Q'25"
      sheetName: string;        // e.g. "MVMS 4Q25 Various-PAID"
      grandLabel: string;       // e.g. "MVMS 4Q25 Quarter Totals"
      primaryDemo: string;
      meta: ManualPlanMeta;
      agency: string;
      advertiser: string;
      dealName: string;
      brand: string;
    }
  ) {
    const { deals, quarterKey, sheetName, grandLabel,
            primaryDemo, meta, agency, advertiser, dealName, brand } = params;
    const numSec = secondaryDemos.length;

    const COL: Record<string, number> = {
      prog:0,period:1,daytime:2,type:3,len:4,units:5,equiv:6,
      rtgHH:7,hh000:8,totHH:9,cpmHH:10,spacer1:11,
      rtgDemo:12,vpvh:13,demo000:14,totDemo:15,cpmDemo:16,spacer2:17,
    };
    const secStart = 18;
    const secDemoCol = (i: number, field: string) => {
      const base = secStart + i * 3;
      if (field === '000') return base;
      if (field === 'cpm') return base + 1;
      return base + 2;
    };
    // Template layout: each secondary block = 000s, cpm, spacer (3 cols).
    // Rate sits one column AFTER the last spacer, so offset is +3 not +2.
    // numSec=0: rate at col 18 (S). numSec=1: 18+3=21 (V). numSec=2: 18+3+3=24, etc.
    const rateCol = numSec > 0 ? secStart + (numSec - 1) * 3 + 3 : 18;
    const costCol = rateCol + 1;
    const totalCols = costCol + 1;

    const ws_data: any[][] = [];
    for (let i = 0; i < 11; i++) ws_data.push(new Array(totalCols).fill(null));
    ws_data[1][0]='Advertiser:'; ws_data[1][1]=advertiser; ws_data[1][12]='Deal Name:'; ws_data[1][13]=dealName;
    ws_data[2][0]='Agency:'; ws_data[2][1]=agency; ws_data[2][12]='Deal Status:'; ws_data[2][13]=meta.status||'';
    ws_data[3][0]='Brand:'; ws_data[3][1]=brand; ws_data[3][12]='Account Exec:'; ws_data[3][13]=meta.ae||'';
    ws_data[4][0]='Buyer:'; ws_data[4][1]=meta.buyer||''; ws_data[4][12]='Primary Demo:'; ws_data[4][13]=primaryDemo; ws_data[4][14]='(Guaranteed)';
    ws_data[5][0]='Marketplace:'; ws_data[5][1]=meta.marketplace||''; ws_data[5][12]='Equivalized:'; ws_data[5][13]=meta.equiv||'';
    ws_data[6][0]='Calendar Type:'; ws_data[6][1]='STANDARD BROADCAST'; ws_data[6][12]='Deal Year:'; ws_data[6][13]=meta.year||'';
    ws_data[7][COL.rtgHH]='Household'; ws_data[7][COL.rtgDemo]='Primary Demo';
    // Secondary demos render as a single group header on the same row
    // as "Household" / "Primary Demo" — black bar, merged across the
    // full secondary block. The per-demo names ("A18-24", "A21-39", …)
    // still live in row 11 as column titles, so each column is
    // individually labeled there.
    if (numSec > 0) ws_data[7][secStart] = numSec === 1 ? 'Secondary Demo' : 'Secondary Demos';
    ws_data[8][COL.totHH]='Total'; ws_data[8][COL.totDemo]='Total';
    ws_data[9][COL.len]='Unit'; ws_data[9][COL.units]='Unit'; ws_data[9][COL.equiv]='Equiv.';
    ws_data[9][COL.rtgHH]='Rtg'; ws_data[9][COL.hh000]='(000)'; ws_data[9][COL.cpmHH]='CPM'; ws_data[9][COL.totHH]='(000)';
    ws_data[9][COL.rtgDemo]='Rtg'; ws_data[9][COL.vpvh]='VPVH'; ws_data[9][COL.totDemo]='(000)'; ws_data[9][COL.demo000]='(000)'; ws_data[9][COL.cpmDemo]='CPM';
    for (let i=0;i<numSec;i++){ws_data[9][secDemoCol(i,'000')]='(000)';ws_data[9][secDemoCol(i,'cpm')]='CPM';}
    ws_data[9][rateCol]=''; ws_data[9][costCol]='Total';
    ws_data[10][0]='Network/Program'; ws_data[10][1]='Period'; ws_data[10][2]='Day/Time'; ws_data[10][COL.type]='Type';
    ws_data[10][4]='Len'; ws_data[10][5]='#'; ws_data[10][6]='Units';
    ws_data[10][COL.rtgHH]='HH'; ws_data[10][COL.hh000]='HH'; ws_data[10][COL.cpmHH]='HH'; ws_data[10][COL.totHH]='HH';
    ws_data[10][COL.rtgDemo]=primaryDemo; ws_data[10][COL.vpvh]=primaryDemo; ws_data[10][COL.totDemo]=primaryDemo;
    ws_data[10][COL.demo000]=primaryDemo; ws_data[10][COL.cpmDemo]=primaryDemo;
    for (let i=0;i<numSec;i++){ws_data[10][secDemoCol(i,'000')]=secondaryDemos[i];ws_data[10][secDemoCol(i,'cpm')]=secondaryDemos[i];}
    ws_data[10][rateCol]='Rate'; ws_data[10][costCol]='Cost';

    const grandTotals = { units:0, equiv:0, totHH:0, totDemo:0, dollars:0, unitsByLen:{} as Record<string,number> };
    const rows: any[] = [];

    function makeDataRow(d: Deal): any[] {
      const row = new Array(totalCols).fill(null);
      const est = calcEstimateFields(d);
      row[0]=PROGRAM_DISPLAY[d.program]||d.program; row[1]=fmtPeriod(d.quarter, d.year); row[2]=DAY_TIME[d.program]||'';
      row[4]=parseFloat(String(d.unitLength)); row[5]=d.totalUnits;
      row[6]=parseFloat(((parseFloat(String(d.unitLength))/30)*d.totalUnits).toFixed(1));
      if (est.rtgHH!=null) row[COL.rtgHH]=parseFloat(est.rtgHH.toFixed(4));
      if (est.hh000!=null) row[COL.hh000]=parseFloat(est.hh000.toFixed(1));
      if (est.hh000&&d.newRate) row[COL.cpmHH]=parseFloat((d.newRate/est.hh000).toFixed(4));
      if (est.totalHH!=null) row[COL.totHH]=parseFloat(est.totalHH.toFixed(1));
      if (est.rtgDemo!=null) row[COL.rtgDemo]=parseFloat(est.rtgDemo.toFixed(4));
      if (est.vpvh!=null) row[COL.vpvh]=parseFloat(est.vpvh.toFixed(4));
      // Per-row export: honor the manual Demo (000) override the user
      // set in the grid. Without this, exported spreadsheets show the
      // estimate value (169) even though the on-screen row cell shows
      // the override (64), so the exported total also inflates.
      if (est.demo000!=null){
        const effDemo=d.overrideDemo000!=null?d.overrideDemo000:est.demo000;
        row[COL.totDemo]=parseFloat((effDemo*d.totalUnits).toFixed(1));
        row[COL.demo000]=parseFloat(effDemo.toFixed(1));
      }
      row[COL.cpmDemo]=d.cpm;
      for (let i=0;i<numSec;i++){
        // effectiveSecondary000, not the raw estimate — a typed override
        // must show in the export, not just in the grid.
        const se={demo000:effectiveSecondary000(d,secondaryDemos[i])};
        if(se.demo000!=null){
          row[secDemoCol(i,'000')]=parseFloat(se.demo000.toFixed(1));
          // CPM = newRate / demo000. Must guard demo000 > 0 — a zero
          // demo000 (legitimately possible when an estimate row exists
          // but the impression value is 0, especially for AddedRows
          // pulling from the merged set) would divide to Infinity, which
          // serializes as <v>Infinity</v> in the numeric cell and trips
          // Excel's repair flow ("Repaired Records: Cell information").
          if(d.newRate && se.demo000>0) row[secDemoCol(i,'cpm')]=parseFloat((d.newRate/se.demo000).toFixed(4));
        }
      }
      if (d.newRate) row[rateCol]=d.newRate;
      if (d.totalDollars) row[costCol]=d.totalDollars;
      return row;
    }

    function makeSubtotalRow(label: string, progDeals: Deal[]){
      const row=new Array(totalCols).fill(null);
      row[0]=label;
      const tU=progDeals.reduce((s,d)=>s+d.totalUnits,0);
      const tE=parseFloat((progDeals.reduce((s,d)=>s+(parseFloat(String(d.unitLength))/30)*d.totalUnits,0)).toFixed(1));
      const tHH=progDeals.reduce((s,d)=>{const e=calcEstimateFields(d);return s+(e.totalHH||0);},0);
      // Excel subtotal: same override-honoring precedence the per-row
      // export uses above so subtotal matches the sum of the rows.
      const tD=progDeals.reduce((s,d)=>{
        const e=calcEstimateFields(d);
        const v=d.overrideDemo000!=null?d.overrideDemo000:(e.demo000!=null?e.demo000:0);
        return s+(v*d.totalUnits);
      },0);
      const tDol=progDeals.reduce((s,d)=>s+(d.totalDollars||0),0);
      row[5]=tU;row[6]=tE;
      if(tHH>0)row[COL.cpmHH]=parseFloat((tDol/tHH).toFixed(4));
      row[COL.totHH]=parseFloat(tHH.toFixed(1));row[COL.totDemo]=parseFloat(tD.toFixed(1));
      if(tD>0)row[COL.cpmDemo]=parseFloat((tDol/tD).toFixed(4));
      row[costCol]=tDol;
      return {row,tU,tE,tHH,tD,tDol};
    }

    const byNetwork: Record<string,Deal[]> = {};
    NETWORK_ORDER.forEach(n=>{byNetwork[n]=[];});
    deals.forEach(d=>{const net=NETWORK_MAP[d.program]||'OTHER';if(!byNetwork[net])byNetwork[net]=[];byNetwork[net].push(d);});

    NETWORK_ORDER.forEach(net=>{
      const netDeals=byNetwork[net];if(!netDeals?.length)return;
      const orderedProgs=(PROGRAM_ORDER[net]||[]).filter(p=>netDeals.some(d=>d.program===p));
      const programs=Array.from(new Set([...orderedProgs,...netDeals.map(d=>d.program)]));
      const netTot={units:0,equiv:0,totHH:0,totDemo:0,dollars:0};
      programs.forEach(prog=>{
        const pd=netDeals.filter(d=>d.program===prog);
        pd.forEach(d=>rows.push({row:makeDataRow(d),bold:false}));
        const sub=makeSubtotalRow(PROGRAM_DISPLAY[prog]||prog,pd);
        rows.push({row:sub.row,bold:true,isSubtotal:true});
        rows.push({row:new Array(totalCols).fill(null),bold:false});
        netTot.units+=sub.tU;netTot.equiv+=sub.tE;netTot.totHH+=sub.tHH;netTot.totDemo+=sub.tD;netTot.dollars+=sub.tDol;
        grandTotals.units+=sub.tU;grandTotals.equiv+=sub.tE;grandTotals.totHH+=sub.tHH;grandTotals.totDemo+=sub.tD;grandTotals.dollars+=sub.tDol;
        pd.forEach(d=>{const l=String(d.unitLength);grandTotals.unitsByLen[l]=(grandTotals.unitsByLen[l]||0)+d.totalUnits;});
      });
      const netRow=new Array(totalCols).fill(null);
      netRow[0]=quarterTotalsLabel(net, quarterKey);netRow[5]=netTot.units;netRow[6]=netTot.equiv;
      if(netTot.totHH>0)netRow[COL.cpmHH]=parseFloat((netTot.dollars/netTot.totHH).toFixed(4));
      netRow[COL.totHH]=parseFloat(netTot.totHH.toFixed(1));netRow[COL.totDemo]=parseFloat(netTot.totDemo.toFixed(1));
      if(netTot.totDemo>0)netRow[COL.cpmDemo]=parseFloat((netTot.dollars/netTot.totDemo).toFixed(4));
      netRow[costCol]=netTot.dollars;
      rows.push({row:netRow,bold:true,isNetTotal:true});
      rows.push({row:new Array(totalCols).fill(null),bold:false});
    });

    const grandRow=new Array(totalCols).fill(null);
    grandRow[0]=grandLabel;grandRow[5]=grandTotals.units;grandRow[6]=grandTotals.equiv;
    if(grandTotals.totHH>0)grandRow[COL.cpmHH]=parseFloat((grandTotals.dollars/grandTotals.totHH).toFixed(4));
    grandRow[COL.totHH]=parseFloat(grandTotals.totHH.toFixed(1));grandRow[COL.totDemo]=parseFloat(grandTotals.totDemo.toFixed(1));
    if(grandTotals.totDemo>0)grandRow[COL.cpmDemo]=parseFloat((grandTotals.dollars/grandTotals.totDemo).toFixed(4));
    grandRow[costCol]=grandTotals.dollars;
    rows.push({row:grandRow,bold:true,isGrand:true});

    const sumRows: [string, any][] = [
      ['Total Dollars', grandTotals.dollars],
      ...(['15','30','45','60','90','120'].map(l=>['Total  :'+l, grandTotals.unitsByLen[l]||0] as [string,any])),
      ['Total Units', grandTotals.units],['Total Equiv Units', grandTotals.equiv],
      ['HH (000)', parseFloat(grandTotals.totHH.toFixed(1))],
      ['HH CPM', grandTotals.totHH>0?parseFloat((grandTotals.dollars/grandTotals.totHH).toFixed(4)):''],
      [primaryDemo+' (000)', parseFloat(grandTotals.totDemo.toFixed(1))],
      [primaryDemo+' CPM', grandTotals.totDemo>0?parseFloat((grandTotals.dollars/grandTotals.totDemo).toFixed(4)):''],
      ['Universe Type','TOTAL US'],['HH Universe',UNIVERSE_DATA['HH']||''],
      [primaryDemo+' Universe',UNIVERSE_DATA[primaryDemo]||''],
    ];
    secondaryDemos.forEach(d=>{
      const tot=deals.reduce((s,deal)=>{const v=effectiveSecondary000(deal,d);return s+(v!=null?v*deal.totalUnits:0);},0);
      sumRows.push([d+' (000)',parseFloat(tot.toFixed(1))],[d+' CPM',tot>0?parseFloat((grandTotals.dollars/tot).toFixed(4)):''],[d+' Universe',UNIVERSE_DATA[d]||'']);
    });
    sumRows.forEach(r=>{const row=new Array(totalCols).fill(null);row[0]=r[0];row[1]=r[1];rows.push({row,bold:false,isSummary:true});});
    rows.forEach(r=>ws_data.push(r.row));

    const ws: any = XLSX.utils.aoa_to_sheet(ws_data);

    ws['!cols']=[{wch:35},{wch:12.2},{wch:10},{wch:6.8},{wch:5.6},{wch:6},{wch:8},{wch:5.6},{wch:8},{wch:8},{wch:6.8},{wch:2},{wch:7.8},{wch:8.4},{wch:13},{wch:13},{wch:13},{wch:1.8}];
    for(let i=0;i<numSec;i++)ws['!cols'].push({wch:8.2},{wch:8},{wch:1.8});
    ws['!cols'].push({wch:11.2},{wch:10.4});
    ws['!rows']=Array.from({length:ws_data.length},()=>({hpt:12.75}));

    const merges:any[]=[
      {s:{r:7,c:COL.rtgHH},e:{r:7,c:COL.totHH}},
      {s:{r:7,c:COL.rtgDemo},e:{r:7,c:COL.cpmDemo}},
    ];
    // "Secondary Demo(s)" group header on row 8 — sits on the same row
    // as "Household" and "Primary Demo" and spans the full secondary
    // block. Starts at `secStart` (first data column — the (000) of the
    // first demo), NOT at `secStart-1`: column R is the spacer between
    // Primary Demo and the secondary block, intentionally left white so
    // there's a visible gap between the two black header bars (mirrors
    // column L between Household and Primary Demo). End col = cpm of
    // the last secondary. Per-demo names appear in the column-titles
    // row (row 11) so every column is still individually labeled.
    if (numSec > 0) {
      merges.push({ s:{r:7,c:secStart}, e:{r:7,c:secStart+numSec*3-2} });
    }
    rows.forEach((robj,i)=>{if(robj.isNetTotal||robj.isGrand)merges.push({s:{r:11+i,c:0},e:{r:11+i,c:1}});});
    ws['!merges']=merges;

    const WHITE={patternType:'solid',fgColor:{rgb:'FFFFFF'}};
    const BLACK_FILL={patternType:'solid',fgColor:{rgb:'000000'}};
    const F=(bold:boolean,color:string,sz=10)=>({bold,color:{rgb:color},sz,name:'Arial'});
    const A=(h='left',v='bottom')=>({horizontal:h,vertical:v});
    const mk=(font:any,fill:any=WHITE,align?:any,numFmt?:string)=>{const s:any={font,fill};if(align)s.alignment=align;if(numFmt)s.numFmt=numFmt;return s;};
    const S:Record<string,any>={
      NORMAL:mk(F(false,'000000')),NORMAL_R:mk(F(false,'000000'),WHITE,A('right')),
      BOLD:mk(F(true,'000000')),BOLD_R:mk(F(true,'000000'),WHITE,A('right')),
      META_LBL:mk(F(true,'000000'),WHITE,A('right')),META_VAL:mk(F(false,'000000')),
      COL_HDR:mk(F(true,'000000'),WHITE,A('center','center')),COL_HDR_L:mk(F(true,'000000'),WHITE,A('left','center')),
      BLACK_HDR:mk(F(true,'FFFFFF'),BLACK_FILL,A('center','center')),
      BLACK_ROW:mk(F(true,'FFFFFF'),BLACK_FILL),BLACK_ROW_R:mk(F(true,'FFFFFF'),BLACK_FILL,A('right')),
      DOLLAR:mk(F(false,'000000'),WHITE,A('right'),'"$"#,##0'),DOLLAR_BOLD:mk(F(true,'000000'),WHITE,A('right'),'"$"#,##0'),
      DOLLAR_BLK:mk(F(true,'FFFFFF'),BLACK_FILL,A('right'),'"$"#,##0'),
      CPM_FMT:mk(F(false,'000000'),WHITE,A('right'),'"$"#,##0.00'),CPM_BOLD:mk(F(true,'000000'),WHITE,A('right'),'"$"#,##0.00'),
      CPM_BLK:mk(F(true,'FFFFFF'),BLACK_FILL,A('right'),'"$"#,##0.00'),
    };

    function cellRef(r:number,c:number){return colLetter(c)+String(r+1);}
    function setS(r:number,c:number,style:any){const ref=cellRef(r,c);if(!ws[ref]||ws[ref].t==='z')ws[ref]={t:'s',v:''};ws[ref].s=style;}
    function rowS(r:number,from:number,to:number,style:any){for(let c=from;c<=to;c++)setS(r,c,style);}

    const totalRows=ws_data.length;
    ws['!ref'] = XLSX.utils.encode_range({s:{r:0,c:0}, e:{r:totalRows-1,c:totalCols-1}});
    ws['!sheetViews']=[{showGridLines:false,state:'frozen',xSplit:5,ySplit:11,topLeftCell:'F12'}];
    for(let r=1;r<=6;r++){setS(r,0,S.META_LBL);setS(r,1,S.META_VAL);setS(r,12,S.META_LBL);setS(r,13,S.META_VAL);}
    setS(4,14,S.META_VAL); // O5 only — "(Guaranteed)" label; leave O2–O4,O6–O7 unstyled so N can overflow
    for(let c=COL.rtgHH;c<=COL.totHH;c++)setS(7,c,S.BLACK_HDR);
    for(let c=COL.rtgDemo;c<=COL.cpmDemo;c++)setS(7,c,S.BLACK_HDR);
    // Secondary-demo header uses the same BLACK_HDR style as Household
    // / Primary Demo and spans the secondary data columns (S onward —
    // excluding the spacer at column R, which stays white like the
    // spacer at column L between Household and Primary Demo). Apply
    // the style to every cell in the merged range so the merge renders
    // cleanly without color-bleed at the seams.
    if (numSec > 0) {
      const secEnd = secStart + numSec*3 - 2;
      for (let c=secStart; c<=secEnd; c++) setS(7, c, S.BLACK_HDR);
    }
    for(let r=8;r<=10;r++)rowS(r,0,totalCols-1,S.COL_HDR);
    setS(10,0,S.COL_HDR_L);

    const cpmCols=[COL.cpmHH,COL.cpmDemo];
    for(let i=0;i<numSec;i++)cpmCols.push(secDemoCol(i,'cpm'));
    const num000Cols=[COL.hh000,COL.totHH,COL.totDemo,COL.demo000];
    for(let i=0;i<numSec;i++)num000Cols.push(secDemoCol(i,'000'));
    const firstSumIdx=rows.findIndex(r=>r.isSummary);
    let lastSumIdx=firstSumIdx;
    for(let k=rows.length-1;k>=0;k--){if(rows[k].isSummary){lastSumIdx=k;break;}}

    rows.forEach((robj,i)=>{
      const ri=11+i;
      if(robj.isSummary){
        for(let c=0;c<totalCols;c++){const ref=cellRef(ri,c);if(!ws[ref]||ws[ref].t==='z')ws[ref]={t:'s',v:''};ws[ref].s=S.NORMAL;}
        const label=robj.row[0]||'';const isLenRow=label.startsWith('Total  :');const isSumBold=!isLenRow;
        const isFirst=(i===firstSumIdx);const isLast=(i===lastSumIdx);
        const bdr:any=Object.assign({},isFirst?{top:{style:'thin'}}:{},isLast?{bottom:{style:'thin'}}:{});
        const bdrB:any=Object.assign({right:{style:'thin'}},bdr);
        let numFmt:string|undefined;
        if(label==='Total Dollars')numFmt='"$"#,##0';
        else if(isLenRow||label==='Total Units')numFmt='#,##0.0';
        else if(label==='Total Equiv Units')numFmt='#,##0.00';
        else if(label.includes('CPM'))numFmt='"$"#,##0.00';
        else if(label.includes('(000)'))numFmt='#,##0';
        else if(label.includes('Universe')&&label!=='Universe Type')numFmt='#,##0';
        const sA:any={font:F(isSumBold,'000000'),fill:WHITE,alignment:{horizontal:isLenRow?'right':'left'}};
        if(Object.keys(bdr).length)sA.border=bdr;setS(ri,0,sA);
        const sB:any={font:F(isSumBold,'000000'),fill:WHITE,alignment:{horizontal:'right'},border:bdrB};
        if(numFmt)sB.numFmt=numFmt;setS(ri,1,sB);
        return;
      }
      const isBlk=robj.isNetTotal||robj.isGrand;const isBld=robj.isSubtotal;
      for(let c=0;c<totalCols;c++){const ref=cellRef(ri,c);if(!ws[ref]||ws[ref].t==='z')ws[ref]={t:'s',v:''};ws[ref].s=isBlk?S.BLACK_ROW:isBld?S.BOLD:S.NORMAL;}
      for(const c of cpmCols)setS(ri,c,isBlk?S.CPM_BLK:isBld?S.CPM_BOLD:S.CPM_FMT);
      for(const c of num000Cols)setS(ri,c,isBlk?S.BLACK_ROW_R:isBld?S.BOLD_R:S.NORMAL_R);
      for(const c of[COL.rtgHH,COL.rtgDemo,COL.vpvh])setS(ri,c,isBlk?S.BLACK_ROW_R:isBld?S.BOLD_R:S.NORMAL_R);
      setS(ri,rateCol,isBlk?S.DOLLAR_BLK:isBld?S.DOLLAR_BOLD:S.DOLLAR);
      setS(ri,costCol,isBlk?S.DOLLAR_BLK:isBld?S.DOLLAR_BOLD:S.DOLLAR);
    });

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }

  /**
   * Writes the workbook to a downloadable file with frozen-pane fix-up applied
   * to every sheet (xlsx-js-style doesn't natively emit frozen panes).
   */
  function downloadWorkbook(wb: XLSX.WorkBook, filename: string) {
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    JSZip.loadAsync(wbout).then(zip => {
      const sheetFiles = Object.keys(zip.files).filter(
        name => /^xl\/worksheets\/sheet\d+\.xml$/.test(name)
      );
      if (!sheetFiles.length) {
        // Fallback: download as-is
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        showToast('✓ Excel exported');
        return;
      }

      const frozenPane = '<pane xSplit="5" ySplit="11" topLeftCell="F12" activePane="bottomRight" state="frozen"/><selection pane="topRight"/><selection pane="bottomLeft"/><selection pane="bottomRight" activeCell="F12" sqref="F12"/>';

      /**
       * Force landscape + fit-to-one-page-wide on a sheet.
       *
       * WHY THIS IS DONE BY STRING SURGERY: SheetJS 0.18.5 (community)
       * accepts `ws['!pageSetup']` and then silently drops it — verified by
       * round-tripping a workbook and reading back sheet1.xml. Only
       * `pageMargins` survives. So the workbook ships with no print
       * settings at all, Excel applies its defaults (portrait, no scaling),
       * and File > Save as PDF produced a 15-page portrait document with
       * every cost column off the right edge.
       *
       * Same reason the frozen panes above are injected this way. If the
       * spreadsheet layer ever moves to ExcelJS, all of this becomes
       * `sheet.pageSetup = {...}` and this function loses ~30 lines.
       *
       * ⚠ ELEMENT ORDER IS SCHEMA-ENFORCED in CT_Worksheet. The sequence
       * runs: sheetPr … sheetData … printOptions, pageMargins, pageSetup,
       * headerFooter, … ignoredErrors. Insert pageSetup anywhere else and
       * Excel reports the file as corrupt and offers to repair it. Hence
       * the anchored insertion below rather than a naive append.
       */
      const PAGE_MARGINS = '<pageMargins left="0.25" right="0.25" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>';
      // fitToWidth=1 / fitToHeight=0 means "one page wide, as many pages
      // tall as it takes" — the right behaviour for a long schedule.
      const PAGE_SETUP = '<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0" horizontalDpi="300" verticalDpi="300"/>';

      const applyPrintSetup = (xml: string): string => {
        // 1. fitToPage must be declared on sheetPr, or Excel ignores the
        //    fitToWidth/fitToHeight attributes on pageSetup entirely.
        if (/<sheetPr[\s>]/.test(xml)) {
          // Merge into the existing sheetPr rather than adding a second one.
          xml = xml.replace(/<sheetPr([^>]*)\/>/, '<sheetPr$1><pageSetUpPr fitToPage="1"/></sheetPr>');
          xml = xml.replace(/(<sheetPr[^>]*>)(?!<pageSetUpPr)/, '$1<pageSetUpPr fitToPage="1"/>');
        } else {
          // sheetPr is the FIRST child of <worksheet>, so anchor on the
          // opening tag.
          xml = xml.replace(/(<worksheet[^>]*>)/, '$1<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>');
        }

        // 2. pageSetup, positioned per the schema order above.
        if (/<pageSetup[\s/>]/.test(xml)) return xml;          // already present
        if (/<pageMargins[^>]*\/>/.test(xml)) {
          // Margins exist → pageSetup goes immediately after them.
          return xml.replace(/(<pageMargins[^>]*\/>)/, '$1' + PAGE_SETUP);
        }
        if (xml.includes('<ignoredErrors')) {
          // ignoredErrors sorts AFTER pageSetup, so insert before it.
          return xml.replace('<ignoredErrors', PAGE_MARGINS + PAGE_SETUP + '<ignoredErrors');
        }
        return xml.replace('</worksheet>', PAGE_MARGINS + PAGE_SETUP + '</worksheet>');
      };

      Promise.all(sheetFiles.map(name => {
        const sheetFile = zip.file(name);
        if (!sheetFile) return Promise.resolve();
        return sheetFile.async('string').then(xml => {
          xml = xml.replace('<sheetView ', '<sheetView showGridLines="0" ');
          xml = xml.replace(/(<sheetView[^>]*?)(\s*\/>)/, '$1>' + frozenPane + '</sheetView>');
          xml = xml.replace(/(<sheetView[^>]*?>)(<\/sheetView>)/, '$1' + frozenPane + '$2');
          xml = applyPrintSetup(xml);
          zip.file(name, xml);
        });
      })).then(() => {
        zip.generateAsync({ type: 'uint8array' }).then(content => {
          const blob = new Blob([content as unknown as ArrayBuffer], { type: 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = filename; a.click();
          URL.revokeObjectURL(url);
          showToast('✓ Excel exported');
        });
      });
    });
  }

  /** Convert "4Q'25" → { sheet: "MVMS 4Q25 Various-PAID", grand: "MVMS 4Q25 Quarter Totals" } */
  function quarterLabels(quarterKey: string) {
    const compact = quarterKey.replace(/'/g, '');
    return {
      sheet: `MVMS ${compact} Various-PAID`,
      grand: `MVMS ${compact} Quarter Totals`,
    };
  }

  /** Common params shared across all quarter sheets for the active plan. */
  function buildExportParams() {
    if (!planFilter) return null;
    const meta = getPlanMeta(planFilter) || {} as ManualPlanMeta;
    // Season-narrow so nickname-consolidated plans don't export 25/26
    // source rows alongside the rolled 26/27 rows — same class of bug
    // fixed elsewhere (Summary rollups, SF preview, push totals, Save).
    const exportVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    const planDeals = allDeals.filter(d =>
      d.salesPlan === planFilter
      && exportVisibleKeys.has(`${d.quarter}|${d.year}`)
    );
    const planAddedRows = addedRows.filter(r =>
      r.salesPlan === planFilter
      && exportVisibleKeys.has(`${r.quarter}|${r.year}`)
    );
    // Primary demo resolution mirrors the on-screen demoName fallback:
    //   1. Plan metadata (most authoritative — set at create time)
    //   2. First saved deal
    //   3. First unsaved AddedRow
    // Without (3), brand-new plans (no saved deals yet) would land with
    // primaryDemo='' and the workbook headers ("Primary Demo:", column
    // titles) would render blank.
    const primaryDemo = meta.demo || planDeals[0]?.demo || planAddedRows[0]?.demo || '';
    return {
      meta,
      primaryDemo,
      agency: meta.agency || planFilter.split(' - ')[0] || '',
      advertiser: meta.advertiser || planFilter.split(' - ')[1] || '',
      dealName: meta.dealName || planFilter.split(' - ')[2] || '',
      brand: meta.brand || planDeals[0]?.brand || '',
      planAddedRows,
    };
  }

  /**
   * Convert an unsaved AddedRow into a Deal-shaped object for export.
   *
   * The export sheet builders (appendSummarySheetToWorkbook,
   * appendQuarterSheetToWorkbook) iterate `Deal[]` and read a small
   * fixed set of fields off each: program, quarter, unitLength,
   * totalUnits, demo, cpm, newRate, totalDollars (plus calcEstimateFields
   * uses guaranteed). Everything else on `Deal` (sf ids, baselines,
   * roll-forward flags, etc.) is irrelevant once the workbook starts
   * being assembled — that's why this helper can synthesize a "Deal"
   * loosely instead of producing the full interface.
   *
   * `newRate` mirrors renderAddedRow's compute path:
   *   newRate = round(demo000 × cpm)
   *   demo000 = overrideDemo000 ?? est.demo000
   * so the spreadsheet matches what the user sees on screen.
   */
  function enrichAddedRowAsDeal(r: AddedRow, fallbackDemo: string): Deal {
    const demo = r.demo || fallbackDemo;
    const est = r.unitLength
      ? calcEstimateFields({
          program: r.program, quarter: r.quarter, year: r.year, demo,
          unitLength: r.unitLength, totalUnits: r.totalUnits,
          guaranteed: r.guaranteed,
        })
      : null;
    const demo000 = r.overrideDemo000 != null ? r.overrideDemo000 : (est?.demo000 ?? null);
    const newRate = r.newRate != null
      ? r.newRate
      : (demo000 && r.cpm ? perSpotRate(demo000, r.cpm) : null);
    const totalDollars = rowDollars(newRate, r.totalUnits);
    // `as any` because AddedRow doesn't carry the SF-side fields (sf id,
    // sourceDollars, rate, hh_cpm, …) that Deal/SourceDeal declare —
    // the export path doesn't read them, so leaving them undefined is
    // safe and clearer than fabricating placeholder values.
    return {
      ...r,
      dealKey: r.key,
      demo,
      newRate,
      totalDollars,
      originalCpm: null,
    } as any as Deal;
  }

  /**
   * Builds the "MVMS Program Summary-PAID" sheet — program × quarter
   * aggregated view with HH and primary-demo metrics, network totals,
   * and a 25/26 annual rollup row per program.
   */
  function appendSummarySheetToWorkbook(
    wb: XLSX.WorkBook,
    params: {
      planDeals: Deal[];
      primaryDemo: string;
      meta: ManualPlanMeta;
      agency: string;
      advertiser: string;
      dealName: string;
      brand: string;
    }
  ) {
    const { planDeals, primaryDemo, meta, agency, advertiser, dealName, brand } = params;

    // Column layout (1-indexed via 0-indexed array):
    //   0  Program
    //   1  Period
    //   2  Day/Time
    //   3  # (Unit)
    //   4  Equiv Units
    //   5  Avg Rtg HH       (HH section)
    //   6  Avg HH (000)
    //   7  CPM HH
    //   8  Total HH (000)
    //   9  spacer
    //  10  Avg Rtg Demo     (Demo section)
    //  11  VPVH Demo
    //  12  Avg Demo (000)
    //  13  Total Demo (000)
    //  14  CPM Demo
    //  15  spacer
    //  16  Avg Equiv Unit Rate
    //  17  Total Cost
    const TOTAL_COLS = 18;

    const ws_data: any[][] = [];
    for (let i = 0; i < 11; i++) ws_data.push(new Array(TOTAL_COLS).fill(null));
    // Meta header (matches per-quarter sheet structure)
    ws_data[1][0] = 'Advertiser:'; ws_data[1][1] = advertiser;
    ws_data[1][10] = 'Deal Name:'; ws_data[1][11] = dealName;
    ws_data[2][0] = 'Agency:'; ws_data[2][1] = agency;
    ws_data[2][10] = 'Deal Status:'; ws_data[2][11] = meta.status || '';
    ws_data[3][0] = 'Brand:'; ws_data[3][1] = brand;
    ws_data[3][10] = 'Account Exec:'; ws_data[3][11] = meta.ae || '';
    ws_data[4][0] = 'Buyer:'; ws_data[4][1] = meta.buyer || '';
    ws_data[4][10] = 'Primary Demo:'; ws_data[4][11] = primaryDemo; ws_data[4][12] = '(Guaranteed)';
    ws_data[5][0] = 'Marketplace:'; ws_data[5][1] = meta.marketplace || '';
    ws_data[5][10] = 'Equivalized:'; ws_data[5][11] = meta.equiv || '';
    ws_data[6][0] = 'Calendar Type:'; ws_data[6][1] = 'STANDARD BROADCAST';
    ws_data[6][10] = 'Deal Year:'; ws_data[6][11] = meta.year || '';

    // Section labels (R8): Household / Primary Demo
    ws_data[7][5] = 'Household';
    ws_data[7][10] = 'Primary Demo';

    // Sub-section labels (R9): mostly blank, "Total" goes above totals cols
    ws_data[8][8] = 'Total';
    ws_data[8][13] = 'Total';

    // Column-group headers (R10)
    ws_data[9][3] = 'Unit';        ws_data[9][4] = 'Equiv.';
    ws_data[9][5] = 'Rtg';         ws_data[9][6] = '(000)';
    ws_data[9][7] = 'CPM';         ws_data[9][8] = '(000)';
    ws_data[9][10] = 'Rtg';        ws_data[9][11] = 'VPVH';
    ws_data[9][12] = '(000)';      ws_data[9][13] = '(000)';
    ws_data[9][14] = 'CPM';
    ws_data[9][16] = 'Avg Equiv';  ws_data[9][17] = 'Total';

    // Column titles (R11)
    ws_data[10][0] = 'Program';   ws_data[10][1] = 'Period';
    ws_data[10][2] = 'Day/Time';  ws_data[10][3] = '#';
    ws_data[10][4] = 'Units';
    ws_data[10][5] = 'HH';        ws_data[10][6] = 'HH';
    ws_data[10][7] = 'HH';        ws_data[10][8] = 'HH';
    ws_data[10][10] = primaryDemo; ws_data[10][11] = primaryDemo;
    ws_data[10][12] = primaryDemo; ws_data[10][13] = primaryDemo;
    ws_data[10][14] = primaryDemo;
    ws_data[10][16] = 'Unit Rate'; ws_data[10][17] = 'Cost';

    // Build a row from aggregated metrics
    function makeRow(program: string, period: string, daytime: string, m: ReturnType<typeof aggregateSummaryMetrics>): any[] {
      const row = new Array(TOTAL_COLS).fill(null);
      row[0] = program;
      row[1] = period;
      row[2] = daytime;
      row[3] = m.units || 0;
      // Decimal counts match the Liberty Mutual Excel template:
      //   Ratings (Avg Rtg HH/Demo, VPVH) → 3 decimals
      //   Impressions ((000) cols) → 0 decimals
      //   CPM cols → 2 decimals
      row[4] = parseFloat(m.equiv.toFixed(1));
      if (m.avgRtgHH > 0) row[5] = parseFloat(m.avgRtgHH.toFixed(3));
      if (m.avgHH > 0) row[6] = Math.round(m.avgHH);
      if (m.cpmHH > 0) row[7] = parseFloat(m.cpmHH.toFixed(2));
      if (m.totalHH > 0) row[8] = Math.round(m.totalHH);
      if (m.avgRtgDemo != null && m.avgRtgDemo > 0) row[10] = parseFloat(m.avgRtgDemo.toFixed(3));
      if (m.vpvh > 0) row[11] = parseFloat(m.vpvh.toFixed(3));
      if (m.avgDemo > 0) row[12] = Math.round(m.avgDemo);
      if (m.totalDemo > 0) row[13] = Math.round(m.totalDemo);
      if (m.cpmDemo > 0) row[14] = parseFloat(m.cpmDemo.toFixed(2));
      if (m.avgEquivUnitRate > 0) row[16] = parseFloat(m.avgEquivUnitRate.toFixed(2));
      if (m.totalCost > 0) row[17] = m.totalCost;
      return row;
    }

    // Data rows: programs grouped by network
    const programsWithSpots = Array.from(new Set(planDeals.map(d => d.program)));
    const programsByNet: Record<string, string[]> = {};
    for (const prog of programsWithSpots) {
      const net = NETWORK_MAP[prog] || 'Other';
      if (!programsByNet[net]) programsByNet[net] = [];
      programsByNet[net].push(prog);
    }
    const orderedNets = [
      ...NETWORK_ORDER.filter(n => programsByNet[n]?.length),
      ...Object.keys(programsByNet).filter(n => !NETWORK_ORDER.includes(n)),
    ];

    const rowMeta: Array<{ row: any[]; type: 'data' | 'annual' | 'netTotal' | 'blank' }> = [];

    for (const net of orderedNets) {
      const progs = programsByNet[net] || [];
      const order = PROGRAM_ORDER[net] || [];
      const ordered = [
        ...order.filter(p => progs.includes(p)),
        ...progs.filter(p => !order.includes(p)),
      ];

      for (const prog of ordered) {
        const progDeals = planDeals.filter(d => d.program === prog);
        const daytime = DAY_TIME[prog] || '';

        // Iterate the plan's visible quarters (marketplace-driven) so the
        // Summary sheet's per-quarter rows match the on-screen tab strip.
        // CY plans show 1Q-4Q (one year); Scatter shows 5 rows per program.
        //
        // Skip quarters where the program has NO deals — otherwise the
        // summary carries empty "0 unit" rows for every marketplace
        // quarter the program didn't run in, which reads as noise on
        // the export (users only want to see quarters with activity).
        for (const tab of visibleQuarters) {
          const qd = progDeals.filter(d => d.quarter === tab.quarter && d.year === tab.year);
          if (qd.length === 0) continue;
          rowMeta.push({ row: makeRow(prog, tab.label, daytime, aggregateSummaryMetrics(qd)), type: 'data' });
        }
        // Annual rollup
        rowMeta.push({ row: makeRow(`${prog} ${SEASON_LABEL} Total`, '', daytime, aggregateSummaryMetrics(progDeals)), type: 'annual' });
        rowMeta.push({ row: new Array(TOTAL_COLS).fill(null), type: 'blank' });
      }

      // Network total
      const netDeals = planDeals.filter(d => ordered.includes(d.program));
      const netRow = makeRow(`${net} ${SEASON_RANGE_LABEL} Total`, '', '', aggregateSummaryMetrics(netDeals));
      rowMeta.push({ row: netRow, type: 'netTotal' });
      rowMeta.push({ row: new Array(TOTAL_COLS).fill(null), type: 'blank' });
    }

    rowMeta.forEach(r => ws_data.push(r.row));

    const ws: any = XLSX.utils.aoa_to_sheet(ws_data);

    // Column widths
    ws['!cols'] = [
      { wch: 28 }, { wch: 10 }, { wch: 10 }, { wch: 6 }, { wch: 8 },
      { wch: 8 }, { wch: 9 }, { wch: 8 }, { wch: 11 }, { wch: 2 },
      { wch: 8 }, { wch: 7 }, { wch: 9 }, { wch: 11 }, { wch: 8 },
      { wch: 2 }, { wch: 11 }, { wch: 12 },
    ];
    ws['!rows'] = Array.from({ length: ws_data.length }, () => ({ hpt: 12.75 }));
    ws['!merges'] = [
      { s: { r: 7, c: 5 }, e: { r: 7, c: 8 } },     // "Household" spans HH cols
      { s: { r: 7, c: 10 }, e: { r: 7, c: 14 } },   // "Primary Demo" spans demo cols
    ];

    // Styling
    const WHITE = { patternType: 'solid', fgColor: { rgb: 'FFFFFF' } };
    const BLACK_FILL = { patternType: 'solid', fgColor: { rgb: '000000' } };
    const F = (bold: boolean, color: string) => ({ bold, color: { rgb: color }, sz: 10, name: 'Arial' });
    const A = (h = 'left', v = 'bottom') => ({ horizontal: h, vertical: v });
    const mk = (font: any, fill: any = WHITE, align?: any, numFmt?: string) => {
      const s: any = { font, fill };
      if (align) s.alignment = align;
      if (numFmt) s.numFmt = numFmt;
      return s;
    };
    const S: Record<string, any> = {
      NORMAL: mk(F(false, '000000')),
      NORMAL_R: mk(F(false, '000000'), WHITE, A('right')),
      META_LBL: mk(F(true, '000000'), WHITE, A('right')),
      META_VAL: mk(F(false, '000000')),
      COL_HDR: mk(F(true, '000000'), WHITE, A('center', 'center')),
      COL_HDR_L: mk(F(true, '000000'), WHITE, A('left', 'center')),
      BLACK_HDR: mk(F(true, 'FFFFFF'), BLACK_FILL, A('center', 'center')),
      DOLLAR: mk(F(false, '000000'), WHITE, A('right'), '"$"#,##0'),
      DOLLAR_BOLD: mk(F(true, 'FFFFFF'), BLACK_FILL, A('right'), '"$"#,##0'),
      CPM_FMT: mk(F(false, '000000'), WHITE, A('right'), '"$"#,##0.00'),
      CPM_BOLD: mk(F(true, 'FFFFFF'), BLACK_FILL, A('right'), '"$"#,##0.00'),
      // Ratings (Avg Rtg HH/Demo, VPVH) — 3 decimals, no thousands sep
      RTG_FMT: mk(F(false, '000000'), WHITE, A('right'), '0.000'),
      RTG_BOLD: mk(F(true, 'FFFFFF'), BLACK_FILL, A('right'), '0.000'),
      // Impressions ((000) cols) — integer with thousands separator
      IMP_FMT: mk(F(false, '000000'), WHITE, A('right'), '#,##0'),
      IMP_BOLD: mk(F(true, 'FFFFFF'), BLACK_FILL, A('right'), '#,##0'),
      ANNUAL: mk(F(true, '000000'), { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } }, A('right')),
      ANNUAL_L: mk(F(true, '000000'), { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } }),
      NET_TOTAL: mk(F(true, 'FFFFFF'), BLACK_FILL, A('right')),
      NET_TOTAL_L: mk(F(true, 'FFFFFF'), BLACK_FILL),
    };

    function cellRef(r: number, c: number) { return colLetter(c) + String(r + 1); }
    function setS(r: number, c: number, style: any) {
      const ref = cellRef(r, c);
      if (!ws[ref] || ws[ref].t === 'z') ws[ref] = { t: 's', v: '' };
      ws[ref].s = style;
    }

    const totalRows = ws_data.length;
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: totalRows - 1, c: TOTAL_COLS - 1 } });

    // Meta header styling
    for (let r = 1; r <= 6; r++) {
      setS(r, 0, S.META_LBL); setS(r, 1, S.META_VAL);
      setS(r, 10, S.META_LBL); setS(r, 11, S.META_VAL);
    }
    setS(4, 12, S.META_VAL);

    // Section header styling (R8)
    for (let c = 5; c <= 8; c++) setS(7, c, S.BLACK_HDR);
    for (let c = 10; c <= 14; c++) setS(7, c, S.BLACK_HDR);

    // Column header rows R9, R10, R11
    for (let r = 8; r <= 10; r++) {
      for (let c = 0; c < TOTAL_COLS; c++) setS(r, c, S.COL_HDR);
    }
    setS(10, 0, S.COL_HDR_L);

    // Data row styling.
    // Column-specific number formats match the Liberty Mutual Excel template
    // (verified via openpyxl read of LIBERTY MUTUAL 25_26 UPFRONT V-1):
    //   c=5,10,11 (Avg Rtg HH, Avg Rtg Demo, VPVH)        → 0.000
    //   c=6,8,12,13 (Avg/Total HH 000s, Avg/Total Demo 000s) → #,##0
    //   c=7,14 (CPM HH, CPM Demo)                          → "$"#,##0.00
    //   c=16,17 (Avg Equiv Unit Rate, Total Cost)         → "$"#,##0
    const ANNUAL_FILL = { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } };
    const annualFmt = (numFmt: string) => mk(F(true, '000000'), ANNUAL_FILL, A('right'), numFmt);
    rowMeta.forEach((rm, i) => {
      const ri = 11 + i;
      if (rm.type === 'blank') return;
      const isNet = rm.type === 'netTotal';
      const isAnnual = rm.type === 'annual';
      for (let c = 0; c < TOTAL_COLS; c++) {
        const ref = cellRef(ri, c);
        if (!ws[ref] || ws[ref].t === 'z') ws[ref] = { t: 's', v: '' };
        if (c === 0) {
          ws[ref].s = isNet ? S.NET_TOTAL_L : isAnnual ? S.ANNUAL_L : S.NORMAL;
        } else if (c === 5 || c === 10 || c === 11) {
          // Rating / VPVH cols → 3-decimal plain number
          ws[ref].s = isNet ? S.RTG_BOLD : isAnnual ? annualFmt('0.000') : S.RTG_FMT;
        } else if (c === 6 || c === 8 || c === 12 || c === 13) {
          // (000) impression cols → integer with thousands separator
          ws[ref].s = isNet ? S.IMP_BOLD : isAnnual ? annualFmt('#,##0') : S.IMP_FMT;
        } else if (c === 7 || c === 14) {
          // CPM cols → currency, 2 decimals
          ws[ref].s = isNet ? S.CPM_BOLD : isAnnual ? annualFmt('"$"#,##0.00') : S.CPM_FMT;
        } else if (c === 16 || c === 17) {
          // Rate / Cost cols → currency, no decimals
          ws[ref].s = isNet ? S.DOLLAR_BOLD : isAnnual ? annualFmt('"$"#,##0') : S.DOLLAR;
        } else {
          ws[ref].s = isNet ? S.NET_TOTAL : isAnnual ? S.ANNUAL : S.NORMAL_R;
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'MVMS Program Summary-PAID');
  }

  /**
   * Pre-flight for both export buttons.
   *
   * Rule (per Kiyana's spec):
   *   • If the latest saved version of this plan is ALREADY a key
   *     version (initial, order, order_update, named, or a prior
   *     export snapshot), skip the modal and download directly. Re-
   *     exporting an already-tagged milestone doesn't need a
   *     duplicate key.
   *   • Otherwise, show the "Save as new version?" modal. User picks:
   *       - Save & Export → auto-save any pending edits, snapshot the
   *         freshly-saved version as key_type='export', then download.
   *       - Just Export   → download only, no key created.
   *       - Cancel        → abort entirely.
   *
   * The snapshot happens BEFORE the file download so a snapshot failure
   * aborts the download too — better to bail than have the file
   * circulate without the promised versioned trail.
   *
   * Returns true when the caller should proceed with the actual export
   * function, false when the user cancelled.
   */
  async function handleExportGate(): Promise<boolean> {
    const saved = planFilter ? getSavedPlanByName(planFilter) : null;
    // View-only 25/26 SF sources never route through here — Export
    // buttons are `disabled` upstream. All-plans (no planFilter) also
    // shouldn't hit this — Export is gated on planFilter existing.
    if (!saved) return true;

    // What's the latest saved version's key state? The
    // `saved.latestVersion` shape carries isKey/keyType/name post-migration.
    // If the working copy has unsaved edits, they'll be flushed by
    // saveToSnowflake below before we snapshot.
    const latestIsKey = !!saved.latestVersion?.isKey;
    if (latestIsKey) return true;

    const choice = await new Promise<{ save: true; name: string } | { save: false } | null>((resolve) => {
      setPostOrderExportModal({ resolve });
    });
    if (choice == null) return false;

    if (choice.save) {
      // Flush any pending edits first so the snapshot captures what
      // the user is actually looking at — otherwise the export file
      // and the snapshot could diverge (user edited in-memory but
      // never blurred / never saved).
      if (isDirty) {
        await saveToSnowflake();
        // Refresh planCache-derived `saved` after the save; we need
        // the new latestVersion.versionNo for the snapshot source.
        // The saveToSnowflake path updates planCache in-place so a
        // re-lookup here reads the fresh version.
      }
      const refreshed = getSavedPlanByName(planFilter!);
      const sourceVersionNo = refreshed?.latestVersion?.versionNo;

      // In revising-order mode, tag the snapshot as 'order_update'
      // instead of 'export'. This creates a new Live Order milestone
      // (superseding the prior one via kindFor's newest-order rule)
      // AND — because latestVersion.keyType flips back to 'order_update'
      // — flips hasInflightPostOrderRevision to false, re-locking the
      // plan automatically.
      const finalizeKeyType: 'export' | 'order_update' =
        isRevisingOrder ? 'order_update' : 'export';
      const result = await snapshotVersionForExport(
        saved.id,
        choice.name,
        sourceVersionNo,
        finalizeKeyType,
      );
      if (!result.ok) {
        showToast(`Couldn't save version: ${result.error}`);
        return false;
      }
      await refreshPlansCache();
      setPlanCacheVersion(v => v + 1);
      void loadVersionHistory(saved.id);
      if (isRevisingOrder) {
        setRevisingOrderMode(false);
        showToast(`Order revision finalized: "${choice.name}"`);
      } else {
        showToast(`Saved v${result.version.versionNo}: "${choice.name}"`);
      }
    }
    return true;
  }

  /** Single-tab export — exports whichever tab is currently active (Summary or a quarter). */
  async function exportToExcelWithGate() {
    const ok = await handleExportGate();
    if (ok) exportToExcel();
  }

  /**
   * Export All → ask for a format first.
   *
   * The post-order gate runs BEFORE the format prompt, not after. The gate
   * can create a new version (post-order export flow), and doing that only
   * to have the user cancel at the format step would leave a spurious
   * version behind. Gate first, then prompt, then write the file.
   */
  async function exportAllQuartersWithFormatPrompt() {
    const ok = await handleExportGate();
    if (ok) setExportFormatPrompt(true);
  }

  /** Single-tab export — exports whichever tab is currently active (Summary or a quarter). */
  function exportToExcel() {
    if (!planFilter) return;
    const common = buildExportParams();
    if (!common) return;

    // Merge in the plan's unsaved AddedRows so the export mirrors what's
    // on screen — without this, brand-new plans (no saved deals yet)
    // would always hit the "No data to export" bail-out, and saved plans
    // with mid-session additions would silently drop them from the file.
    // AddedRows are enriched into Deal shape so the existing sheet
    // builders need no changes; they look identical to saved rows in
    // the spreadsheet (no "NEW" marker — intentional per UX call).
    const { planAddedRows, primaryDemo } = common;
    const enrichedAdded = planAddedRows.map(r => enrichAddedRowAsDeal(r, primaryDemo));

    if (activeQuarter === SUMMARY_TAB_KEY) {
      // Season-narrow — export Summary tab shouldn't include 25/26
      // source rows when the plan's nickname consolidates with the
      // source (same bug class as SF preview / Plan Total / etc.).
      const exportSummaryVisKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
      const savedDeals = allDeals.filter(d =>
        d.salesPlan === planFilter
        && exportSummaryVisKeys.has(`${d.quarter}|${d.year}`)
      );
      const planDeals = [...savedDeals, ...enrichedAdded];
      if (!planDeals.length) { showToast('No data to export'); return; }
      const wb = XLSX.utils.book_new();
      appendSummarySheetToWorkbook(wb, { planDeals, ...common });
      const planLabel = planFilter.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
      downloadWorkbook(wb, planLabel + '_Summary_Export.xlsx');
      return;
    }

    // Resolve the active tab from visibleQuarters first so synthesized
    // tabs (4Q'27 on a CY/Scatter plan) still export.
    const tab = visibleQuarters.find(t => t.key === activeQuarter)
             || QUARTER_TABS.find(t => t.key === activeQuarter);
    if (!tab) return;
    const savedDeals = allDeals.filter(d =>
      d.salesPlan === planFilter && d.quarter === tab.quarter && d.year === tab.year
    );
    const addedForTab = enrichedAdded.filter(d => d.quarter === tab.quarter && d.year === tab.year);
    const deals = [...savedDeals, ...addedForTab];
    if (!deals.length) { showToast('No data to export'); return; }

    const wb = XLSX.utils.book_new();
    const labels = quarterLabels(tab.key);
    appendQuarterSheetToWorkbook(wb, {
      deals,
      quarterKey: tab.key,
      sheetName: labels.sheet,
      grandLabel: labels.grand,
      ...common,
    });

    const planLabel = planFilter.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
    downloadWorkbook(wb, planLabel + '_Export.xlsx');
  }

  /** Export All — Summary tab + one sheet per QUARTER_TAB, all in one workbook. */
  function exportAllQuartersToExcel() {
    if (!planFilter) return;
    const common = buildExportParams();
    if (!common) return;

    // Same merge pattern as exportToExcel — see comment there.
    const { planAddedRows, primaryDemo } = common;
    const enrichedAdded = planAddedRows.map(r => enrichAddedRowAsDeal(r, primaryDemo));
    // Season-narrow: nickname-consolidated plans would otherwise export
    // BOTH 26/27 rolled deals AND 25/26 source deals in the same
    // workbook.
    const exportAllVisKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    const savedDeals = allDeals.filter(d =>
      d.salesPlan === planFilter
      && exportAllVisKeys.has(`${d.quarter}|${d.year}`)
    );
    const planDeals = [...savedDeals, ...enrichedAdded];
    if (!planDeals.length) { showToast('No data to export'); return; }

    const wb = XLSX.utils.book_new();

    // Summary sheet first (matches Excel template tab order)
    appendSummarySheetToWorkbook(wb, { planDeals, ...common });

    // Then one sheet per quarter that has data — `planDeals` here
    // includes the enriched AddedRows, so a quarter that has only
    // unsaved rows still gets a sheet (matches the "exports what's on
    // the UI" contract). Use visibleQuarters so CY/Scatter plans get
    // the right quarter range (e.g. CY exports 1Q-4Q of primaryYear,
    // not the broadcast-year window).
    for (const tab of visibleQuarters) {
      const deals = planDeals.filter(d => d.quarter === tab.quarter && d.year === tab.year);
      if (!deals.length) continue;
      const labels = quarterLabels(tab.key);
      appendQuarterSheetToWorkbook(wb, {
        deals,
        quarterKey: tab.key,
        sheetName: labels.sheet,
        grandLabel: labels.grand,
        ...common,
      });
    }

    const planLabel = planFilter.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
    downloadWorkbook(wb, planLabel + '_AllQuarters_Export.xlsx');
  }

  /**
   * Build the row data for the landscape PDF proposal report.
   *
   * Aggregation notes — these mirror the legacy Salesforce report, verified
   * against a known-good export (FANDUEL 2027 Planning Costs):
   *   equiv units = units × (unitLength / 30)
   *   total (000) = per-spot (000) × units
   *   CPM         = totalCost / total(000)      — impressions are already
   *                                               in thousands, so no ×1000
   *   avg unit rate = totalCost / units
   * Spot-checked: $6,408 / 922 = $6.95 CPM HH, $6,408 / 267 = $24.00 CPM
   * A25-54, $6,408 / 1 unit = $6,408 avg unit rate. All match the source.
   *
   * Per-spot figures for a group are derived back out of the totals rather
   * than averaged directly, so a program with mixed unit lengths in one
   * quarter reports a unit-weighted number instead of a plain mean.
   */
  function buildProposalPdfRows(planDeals: Deal[]) {
    interface Agg {
      units: number; equivUnits: number; totalCost: number;
      totalHh000: number; totalDemo000: number;
      rtgHhW: number; rtgDemoW: number; vpvhW: number;
    }
    const blank = (): Agg => ({
      units: 0, equivUnits: 0, totalCost: 0,
      totalHh000: 0, totalDemo000: 0,
      rtgHhW: 0, rtgDemoW: 0, vpvhW: 0,
    });
    const add = (a: Agg, d: Deal) => {
      const est = calcEstimateFields(d);
      const units = d.totalUnits || 0;
      const len = parseFloat(String(d.unitLength)) || 30;
      const demo000 = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
      a.units += units;
      a.equivUnits += units * (len / 30);
      a.totalCost += d.totalDollars || 0;
      a.totalHh000 += (est.hh000 ?? 0) * units;
      a.totalDemo000 += (demo000 ?? 0) * units;
      // Unit-weighted so a 1-spot row can't outvote a 10-spot row.
      a.rtgHhW += (est.rtgHH ?? 0) * units;
      a.rtgDemoW += (est.rtgDemo ?? 0) * units;
      a.vpvhW += (est.vpvh ?? 0) * units;
    };
    const safeDiv = (num: number, den: number) => (den > 0 ? num / den : null);

    /**
     * Total (000) and CPM for each secondary demo over a set of deals.
     *
     * getSecondaryDemoEst returns PER-SPOT impressions already scaled for
     * unit length, so the total is `demo000 × units` — the same shape as
     * the primary demo. CPM divides the group's dollars by that total, so
     * a secondary demo's CPM is the same spend measured against a
     * different audience, which is exactly what the column means.
     *
     * Returns null (renders blank) when the estimate has no row for that
     * program/demo, rather than a misleading zero.
     */
    const secondaryDemoTotals = (deals: Deal[], totalCost: number) =>
      secondaryDemos.map(sd => {
        let total000 = 0;
        let sawAny = false;
        for (const d of deals) {
          // Overrides reach the proposal report too.
          const est = { demo000: effectiveSecondary000(d, sd) };
          if (est.demo000 == null) continue;
          sawAny = true;
          total000 += est.demo000 * (d.totalUnits || 0);
        }
        if (!sawAny || total000 <= 0) return { total000: null, cpm: null };
        return { total000, cpm: safeDiv(totalCost, total000) };
      });

    const planAgg = blank();
    planDeals.forEach(d => add(planAgg, d));

    // Quarter range label for the grand-total row, e.g. "4Q26-3Q27".
    // Built from visibleQuarters so CY / Scatter plans read correctly.
    const firstTab = visibleQuarters[0];
    const lastTab = visibleQuarters[visibleQuarters.length - 1];
    const shortQ = (t: typeof visibleQuarters[number]) =>
      `${t.quarter}${String(t.year).slice(-2)}`;
    const rangeLabel = firstTab && lastTab
      ? `MVMS ${shortQ(firstTab)}-${shortQ(lastTab)} Totals`
      : 'MVMS Totals';

    // ── Page 1: per program → its quarters → program subtotal,
    //           then a grand total. Mirrors the legacy report, which
    //           groups by program before rolling up. ────────────────
    const programs = Array.from(new Set(planDeals.map(d => d.program))).sort();
    const quarterRows: any[] = [];
    for (const program of programs) {
      const progDeals = planDeals.filter(d => d.program === program);
      const progAgg = blank();
      // visibleQuarters is already in chronological order — iterate it
      // rather than sorting tab keys, which would put 1Q'27 before 4Q'26.
      for (const tab of visibleQuarters) {
        const a = blank();
        progDeals
          .filter(d => d.quarter === tab.quarter && d.year === tab.year)
          .forEach(d => { add(a, d); add(progAgg, d); });
        if (a.units <= 0) continue;
        quarterRows.push({
          quarter: `${tab.quarter.replace('Q', '')}Q ${tab.year}`,
          units: a.units,
          equivUnits: a.equivUnits,
          cpmHh: safeDiv(a.totalCost, a.totalHh000),
          totalHh000: a.totalHh000,
          cpmDemo: safeDiv(a.totalCost, a.totalDemo000),
          totalDemo000: a.totalDemo000,
          totalCost: a.totalCost,
        });
      }
      if (progAgg.units > 0) {
        quarterRows.push({
          quarter: program,
          units: progAgg.units,
          equivUnits: progAgg.equivUnits,
          cpmHh: safeDiv(progAgg.totalCost, progAgg.totalHh000),
          totalHh000: progAgg.totalHh000,
          cpmDemo: safeDiv(progAgg.totalCost, progAgg.totalDemo000),
          totalDemo000: progAgg.totalDemo000,
          totalCost: progAgg.totalCost,
          isTotal: true,
        });
      }
    }
    if (quarterRows.length > 0) {
      // Blank separator, then the one black-band grand total.
      quarterRows.push({ quarter: '', isSpacer: true } as any);
      quarterRows.push({
        quarter: rangeLabel,
        units: planAgg.units,
        equivUnits: planAgg.equivUnits,
        cpmHh: safeDiv(planAgg.totalCost, planAgg.totalHh000),
        totalHh000: planAgg.totalHh000,
        cpmDemo: safeDiv(planAgg.totalCost, planAgg.totalDemo000),
        totalDemo000: planAgg.totalDemo000,
        totalCost: planAgg.totalCost,
        isGrandTotal: true,
      });
    }

    // ── Page 2: one row per (program, quarter), then a plan total ─────
    const byProgQtr = new Map<string, { tab: typeof visibleQuarters[number]; program: string; a: Agg }>();
    for (const tab of visibleQuarters) {
      for (const d of planDeals) {
        if (d.quarter !== tab.quarter || d.year !== tab.year) continue;
        const key = `${d.program}|${tab.key}`;
        let e = byProgQtr.get(key);
        if (!e) { e = { tab, program: d.program, a: blank() }; byProgQtr.set(key, e); }
        add(e.a, d);
      }
    }
    // Chronological within a program: index into visibleQuarters rather
    // than comparing tab keys, since "1Q'27" < "4Q'26" alphabetically.
    const tabOrder = new Map(visibleQuarters.map((t, i) => [t.key, i]));
    const detailRows = Array.from(byProgQtr.values())
      .filter(e => e.a.units > 0)
      .sort((x, y) => x.program.localeCompare(y.program)
                   || (tabOrder.get(x.tab.key) ?? 0) - (tabOrder.get(y.tab.key) ?? 0))
      .map(({ tab, program, a }) => ({
        program,
        period: fmtPeriod(tab.quarter, tab.year),
        dayTime: DAY_TIME[program] || '',
        units: a.units,
        equivUnits: a.equivUnits,
        rtgHh: safeDiv(a.rtgHhW, a.units),
        hh000: safeDiv(a.totalHh000, a.units),
        cpmHh: safeDiv(a.totalCost, a.totalHh000),
        totalHh000: a.totalHh000,
        rtgDemo: safeDiv(a.rtgDemoW, a.units),
        vpvh: safeDiv(a.vpvhW, a.units),
        demo000: safeDiv(a.totalDemo000, a.units),
        totalDemo000: a.totalDemo000,
        cpmDemo: safeDiv(a.totalCost, a.totalDemo000),
        // Rate per EQUIVALIZED unit, not per spot — matches the legacy
        // report's "Avg Equiv Unit Rate". Identical to per-spot when
        // every row is :30, which is why the FANDUEL sample can't
        // distinguish them.
        avgUnitRate: safeDiv(a.totalCost, a.equivUnits),
        totalCost: a.totalCost,
        // Trailing equivalized CPM column. Same denominator as cpmDemo
        // today; kept separate because the legacy report prints both and
        // they diverge once equivalization is actually applied.
        equivCpmDemo: safeDiv(a.totalCost, a.totalDemo000),
      }));

    if (detailRows.length > 0) {
      detailRows.push({
        program: rangeLabel,
        period: '', dayTime: '',
        units: planAgg.units,
        equivUnits: planAgg.equivUnits,
        rtgHh: null, hh000: null,
        cpmHh: safeDiv(planAgg.totalCost, planAgg.totalHh000),
        totalHh000: planAgg.totalHh000,
        rtgDemo: null, vpvh: null, demo000: null,
        totalDemo000: planAgg.totalDemo000,
        cpmDemo: safeDiv(planAgg.totalCost, planAgg.totalDemo000),
        avgUnitRate: safeDiv(planAgg.totalCost, planAgg.equivUnits),
        totalCost: planAgg.totalCost,
        equivCpmDemo: safeDiv(planAgg.totalCost, planAgg.totalDemo000),
        isTotal: true,
      } as any);
    }

    // ── Pages 3..n: one per quarter, with a totals box ────────────────
    // Structure per page mirrors the legacy report:
    //   program rows → program subtotal → "<net> <Q> Quarter Totals"
    //   (black) → spacer → "MVMS <Q> Quarter Totals" (black) → box.
    const universe = activeVersion?.universeData ?? null;
    const demoName = planDeals[0]?.demo || '';
    const quarterPages = visibleQuarters.map(tab => {
      const qDeals = planDeals.filter(d => d.quarter === tab.quarter && d.year === tab.year);
      if (!qDeals.length) return null;

      const qAgg = blank();
      qDeals.forEach(d => add(qAgg, d));

      const rows: any[] = [];
      const qPrograms = Array.from(new Set(qDeals.map(d => d.program))).sort();
      for (const program of qPrograms) {
        const pDeals = qDeals.filter(d => d.program === program);
        const pAgg = blank();
        pDeals.forEach(d => add(pAgg, d));
        // ONE ROW PER DEAL — the same grain as the Excel quarter sheet and as
        // Salesforce, whose uniqueness key is Program + Length + Quarter +
        // Year + Demo (000) + CPM + Rate. A unit re-priced to land on budget
        // is a distinct schedule in SF, so it must be a distinct line here.
        //
        // This used to group by (program, length) and print
        // totalCost ÷ equivUnits as "Rate": ten units at $5,386 plus one at
        // $4,000 became eleven at $5,260 — a number that existed nowhere but
        // this page, and disagreed with both Excel and what SF would show.
        // Every value below is read off the row, exactly as Excel's
        // makeDataRow does, rather than derived from an aggregate.
        for (const d of pDeals) {
          const est = calcEstimateFields(d);
          const units = d.totalUnits || 0;
          const len = parseFloat(String(d.unitLength)) || 30;
          const demo000 = d.overrideDemo000 != null ? d.overrideDemo000 : (est.demo000 ?? null);
          const rate = d.newRate ?? null;
          rows.push({
            program, period: fmtPeriod(tab.quarter, tab.year),
            dayTime: DAY_TIME[program] || '',
            unitType: '', unitLen: String(d.unitLength),
            secondary: secondaryDemoTotals([d], d.totalDollars || 0),
            units, equivUnits: units * (len / 30),
            rtgHh: est.rtgHH ?? null,
            hh000: est.hh000 ?? null,
            // Per-spot rate over per-spot HH, as Excel's cpmHH column.
            cpmHh: est.hh000 && rate ? rate / est.hh000 : null,
            totalHh000: est.hh000 != null ? est.hh000 * units : null,
            rtgDemo: est.rtgDemo ?? null,
            vpvh: est.vpvh ?? null,
            demo000,
            totalDemo000: demo000 != null ? demo000 * units : null,
            // The stored CPM, not dollars ÷ impressions — the two differ by the
            // rate's rounding, and Excel prints the stored one.
            cpmDemo: d.cpm,
            // Per-SPOT rate, matching Excel's Rate column. Not per equivalized
            // :30 — a :60 row shows its actual $ per spot, not half of it.
            avgUnitRate: rate,
            totalCost: d.totalDollars || 0,
          });
        }
        rows.push({
          program, period: '', dayTime: '', unitType: '', unitLen: '',
          units: pAgg.units, equivUnits: pAgg.equivUnits,
          rtgHh: null, hh000: null,
          cpmHh: safeDiv(pAgg.totalCost, pAgg.totalHh000), totalHh000: pAgg.totalHh000,
          rtgDemo: null, vpvh: null, demo000: null, totalDemo000: pAgg.totalDemo000,
          cpmDemo: safeDiv(pAgg.totalCost, pAgg.totalDemo000),
          avgUnitRate: safeDiv(pAgg.totalCost, pAgg.equivUnits),
          totalCost: pAgg.totalCost,
          secondary: secondaryDemoTotals(pDeals, pAgg.totalCost),
          isTotal: true,
        });
      }

      const qLabel = `${tab.quarter}${String(tab.year).slice(-2)}`;
      const totalRow = (label: string) => ({
        program: label, period: '', dayTime: '', unitType: '', unitLen: '',
        units: qAgg.units, equivUnits: qAgg.equivUnits,
        rtgHh: null, hh000: null,
        cpmHh: safeDiv(qAgg.totalCost, qAgg.totalHh000), totalHh000: qAgg.totalHh000,
        rtgDemo: null, vpvh: null, demo000: null, totalDemo000: qAgg.totalDemo000,
        cpmDemo: safeDiv(qAgg.totalCost, qAgg.totalDemo000),
        avgUnitRate: safeDiv(qAgg.totalCost, qAgg.equivUnits),
        totalCost: qAgg.totalCost,
        secondary: secondaryDemoTotals(qDeals, qAgg.totalCost),
        isGrandTotal: true,
      });
      rows.push(totalRow(`FOS ${qLabel} Quarter Totals`));
      rows.push({ isSpacer: true });
      rows.push(totalRow(`MVMS ${qLabel} Quarter Totals`));

      // Equivalized units per unit length, for the box's ladder.
      const byLen: Record<string, number> = {};
      for (const d of qDeals) {
        const len = String(parseFloat(String(d.unitLength)) || 30);
        const units = d.totalUnits || 0;
        byLen[len] = (byLen[len] ?? 0) + units * ((parseFloat(len) || 30) / 30);
      }

      return {
        label: qLabel,
        rows,
        box: {
          totalDollars: qAgg.totalCost,
          byLen,
          totalUnits: qAgg.units,
          totalEquivUnits: qAgg.equivUnits,
          hh000: qAgg.totalHh000,
          hhCpm: safeDiv(qAgg.totalCost, qAgg.totalHh000),
          demo000: qAgg.totalDemo000,
          demoCpm: safeDiv(qAgg.totalCost, qAgg.totalDemo000),
          universeType: 'TOTAL US',
          // Universes come from the bound estimate version, so the box
          // reflects the same source as every other number on the page.
          hhUniverse: universe?.['HH'] ?? null,
          demoUniverse: universe?.[demoName] ?? null,
        },
      };
    }).filter(Boolean) as any[];

    return { quarterRows, detailRows, quarterPages };
  }

  /** Landscape PDF export — the client-facing proposal report. */
  function exportAllQuartersToPdf() {
    if (!planFilter) return;
    const common = buildExportParams();
    if (!common) return;

    const { planAddedRows, primaryDemo, meta, agency, advertiser, brand } = common;
    const enrichedAdded = planAddedRows.map(r => enrichAddedRowAsDeal(r, primaryDemo));
    const visKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    const savedDeals = allDeals.filter(d =>
      d.salesPlan === planFilter && visKeys.has(`${d.quarter}|${d.year}`)
    );
    const planDeals = [...savedDeals, ...enrichedAdded];
    if (!planDeals.length) { showToast('No data to export'); return; }

    const { quarterRows, detailRows, quarterPages } = buildProposalPdfRows(planDeals);

    // Header fields MPB doesn't store are derived rather than left blank —
    // a client-facing document with empty labels looks broken. Calendar
    // Type is fixed because every MPB plan is standard broadcast today.
    const savedPlan = getSavedPlanByName(planFilter);
    downloadProposalPdf({
      filename: planFilter.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40) + '_Proposal.pdf',
      demoLabel: primaryDemo || 'Demo',
      header: {
        advertiser,
        agency,
        brand,
        buyer: meta.buyer || meta.ae || '',
        marketplace: meta.marketplace || '',
        calendarType: 'STANDARD BROADCAST',
        // Prefer the generated Sales Plan Name (the long client-facing
        // form) over the nickname — that's what the legacy report shows.
        dealName: savedPlan?.latestVersion?.salesPlanName || meta.dealName || planFilter,
        dealStatus: (savedPlan?.status || meta.status || 'Working').toUpperCase(),
        accountExec: meta.ae || getEmailFromSessionStorage() || '',
        primaryDemo: primaryDemo || '',
        guaranteed: planDeals.some(d => (d.guaranteed ?? 0) > 0),
        equivalized: meta.equiv || 'No',
        dealYear: '26/27',
        estimatesAsOf: activeVersion?.asOfDate ?? null,
      },
      quarterRows,
      detailRows,
      quarterPages,
      secondaryDemos,
    });
  }

  // ─── Render helpers ──────────────────────────────────────────────────────────

  const primaryDemos = useMemo(() => Array.from(new Set(filteredDeals.map(d => d.demo).filter(Boolean))), [filteredDeals]);
  // Resolve the plan's primary demo for column-header labels (e.g.
  // "RTG A35+", "A35+ (000s)"). Source priority:
  //   1. Plan metadata — most reliable; every saved/draft plan has
  //      a Primary Demo field set in the Create modal. Catches the
  //      brand-new-plan case where filteredDeals is empty.
  //   2. First filtered deal's demo — covers SF source plans and
  //      All Plans view (no planFilter, so meta lookup is skipped).
  //   3. First filtered added row's demo — covers fresh plans that
  //      only have manual "+ Add Show" rows so far.
  //   4. Generic "Demo" fallback — should rarely fire; CSS uppercases
  //      it to "DEMO" which is the symptom we used to see.
  const demoName =
    (planFilter ? getPlanMeta(planFilter)?.demo : null)
    || primaryDemos[0]
    || filteredDeals.find(d => d.demo)?.demo
    || filteredAddedRows.find(r => r.demo)?.demo
    || 'Demo';
  const planSelected = !!planFilter;

  /**
   * Look up the prior season's demo (000) for a deal — shifts the
   * deal's quarter back one broadcast year and queries the previous
   * EstimateVersion (e.g. 26/27 deal at Q1 2027 → 25/26 estimate
   * at 1Q'26).
   *
   * Returns a tagged union so the caller can distinguish:
   *   - `value`: 25/26 has a number for this cell → render a real %
   *   - `new`:   program is missing from 25/26's program index →
   *              render a "NEW" badge instead of a percent
   *   - `none`:  no prior estimate exists, or the specific
   *              (program, day, period, demo) entry is absent → hide
   *              the subtext entirely
   */
  function getPriorYearDemo000(
    d: { program: string; quarter: string; year: number; demo?: string | null; unitLength?: string | number | null },
    // `source` names the estimate the number came from, for the tooltip.
    // The baseline used to be invisible — the tooltip said "vs 25/26",
    // naming a SEASON, while the actual file was whichever version won a
    // date comparison. That is how a scatter estimate ended up as the
    // silent baseline for every upfront plan. Naming it makes the choice
    // auditable from the UI instead of only from the code.
  ): { kind: 'value'; value: number; source: string } | { kind: 'new' } | { kind: 'none' } {
    if (!activeVersion || !d.demo) return { kind: 'none' };
    // Compare like with like: same kind, same rate card, one season back —
    // then fall back to the general master.
    //
    // This was `getLatestVersionByYear(primaryYear - 1)`, which filters on
    // year ALONE. Two ways that goes wrong. A 25/26 scatter upload dated
    // later than the 25/26 upfront silently becomes the YoY baseline for
    // every plan, general and WPP alike. And once a custom card exists for a
    // prior season, an ordinary plan's baseline flips to that agency's
    // negotiated numbers — the same leak getLatestVersion() was scoped to
    // prevent, one function over.
    //
    // Today there is no 25/26 WPP file, so a WPP plan lands on the general
    // 25/26 master via the fallback. That is the right answer; it just
    // wasn't being expressed deliberately.
    const priorPY = activeVersion.primaryYear - 1;
    const priorVersion =
      getLatestVersionByYearKindAndCard(priorPY, activeVersion.kind, cardOf(activeVersion))
      // Then the general master of the same kind, then any general version
      // for that season. The last term matters for a plain non-WPP user: a
      // prior season holding only a general SCATTER upload used to resolve
      // via the old year-only call, and without it the YoY column would
      // silently blank where it previously showed a number.
      ?? getLatestVersionByYearKindAndCard(priorPY, activeVersion.kind, 'general')
      ?? getLatestGeneralVersionByYear(priorPY);
    if (!priorVersion) return { kind: 'none' };

    // Two-step canonical name resolution against the prior version's
    // program index, mirroring `getEstimate`'s fallback chain so cross-
    // season renames like "Tamron Hall" ↔ "Tamron Hall with Bounce"
    // (mapped via ESTIMATE_NAME_MAP) don't false-positive as NEW.
    //
    //   1. Direct index hit on the deal's program name as-typed
    //   2. Same lookup with the program first run through
    //      ESTIMATE_NAME_MAP — handles the case where the current
    //      season's name is the "short form" and the prior season
    //      had the longer/aliased name (or vice versa).
    let canonical = priorVersion.programNameIndex?.[normalizeProgramName(d.program)];
    if (!canonical) {
      const aliased = ESTIMATE_NAME_MAP[d.program];
      if (aliased) {
        canonical = priorVersion.programNameIndex?.[normalizeProgramName(aliased)];
      }
    }
    if (!canonical) return { kind: 'new' };

    const qNum = d.quarter.replace('Q', '');
    const yy = ((d.year - 1) % 100).toString().padStart(2, '0');
    const priorPeriod = `${qNum}Q'${yy}`;
    const demoSuffix = `${d.demo}RTG`;

    // Length adjustment — apples-to-apples. The estimate's `t` is per-spot
    // at a 30s reference length, but the row's displayed demo (000) is
    // already length-adjusted (244 × 15/30 = 122 for a 15s spot). Without
    // this factor, comparing 134 (display) to 244 (raw 30s) gives a fake
    // -45% drop. Default to 30 when unitLength is missing — matches
    // calcEstimateFields' fallback.
    const lenNum = parseFloat(String(d.unitLength ?? '30')) || 30;
    const lenFactor = lenNum / 30;
    const adjust = (rawT: number) => rawT * lenFactor;

    // e.g. "Upfront V1" / "Scatter V2" / "WPP V1" — the stream and sequence
    // that actually produced this baseline.
    const sourceLabel =
      `${formatEstimateStream(priorVersion.kind, cardOf(priorVersion))} ${getVersionLabel(priorVersion)}`;

    // Primary lookup: exact key with day from our local DAY_TIME map.
    const day = DAY_TIME[d.program] || DAY_TIME[canonical] || '';
    if (day) {
      const key = `${canonical}|${day}|${priorPeriod}|${demoSuffix}`;
      const entry = priorVersion.estimateDb[key];
      if (entry?.t != null && entry.t !== 0) {
        return { kind: 'value', value: adjust(entry.t), source: sourceLabel };
      }
    }

    // Day wildcard fallback. Programs not in our hardcoded DAY_TIME map
    // (or programs whose day in the prior estimate differs from what
    // DAY_TIME says) would otherwise silently fail. Scan the prior
    // estimate's keys for the canonical/period/demo triple and pick the
    // first matching day — (program, period, demo) is usually unique
    // by day in practice, so picking "any day" gives the right number.
    const keyPrefix = `${canonical}|`;
    const keySuffix = `|${priorPeriod}|${demoSuffix}`;
    for (const k of Object.keys(priorVersion.estimateDb)) {
      if (!k.startsWith(keyPrefix) || !k.endsWith(keySuffix)) continue;
      const entry = priorVersion.estimateDb[k];
      if (entry?.t != null && entry.t !== 0) {
        return { kind: 'value', value: adjust(entry.t), source: sourceLabel };
      }
    }

    return { kind: 'none' };
  }

  /**
   * Render the YoY subtext under a demo (000) cell. `currentDemo000`
   * is the row's *displayed* value — estimate when no override, or
   * the user's override when set — so editing demo imp shifts the
   * YoY in real time (per spec). The 25/26 estimate is the fixed
   * baseline ("vs 25/26") regardless of overrides.
   *
   * Returns null on view-only plans (25/26 inventory) — the user
   * already sees "this is last season" via the banner.
   */
  function renderYoYBadge(
    d: { program: string; quarter: string; year: number; demo?: string | null; unitLength?: string | number | null },
    currentDemo000: number | null,
  ): React.ReactNode {
    // No YoY when the current view IS the prior year (SF source plan
    // direct view, OR the 25/26 toggle view of a 26/27 rolled-forward
    // plan) — there's no further-prior to compare against.
    if (planIsViewOnly) return null;
    const lookup = getPriorYearDemo000(d);
    if (lookup.kind === 'none') return null;
    if (lookup.kind === 'new') {
      return (
        <div
          title="Not in 25/26 estimate"
          style={{
            fontSize: 9.5, fontWeight: 700, color: '#1e40af',
            background: '#dbeafe', padding: '0 4px', borderRadius: 3,
            display: 'inline-block', marginTop: 2, letterSpacing: 0.4,
          }}
        >NEW</div>
      );
    }
    // value branch — compute the % delta against 25/26.
    if (currentDemo000 == null || lookup.value === 0) return null;
    const pct = (currentDemo000 - lookup.value) / lookup.value;
    const pctText = `${pct >= 0 ? '+' : ''}${(pct * 100).toFixed(1)}%`;
    const color = pct > 0 ? colors.success : pct < 0 ? colors.danger : colors.textMuted;
    // Names the exact estimate behind the number, e.g.
    // "vs 25/26 Upfront V1 (118): -3.4%". Previously just "vs 25/26",
    // which named the season but not the file — so nobody could tell the
    // baseline had quietly become the scatter estimate.
    const tooltip = `vs 25/26 ${lookup.source} (${fmt(lookup.value)}): ${pctText}`;
    return (
      <div
        title={tooltip}
        style={{
          // A percent change annotates the value above it — it should never
          // out-shout it. 9.5px/600 keeps it legible while making the
          // forecast number the thing your eye lands on, and it stops the
          // Primary Demo block reading as a column of red warnings.
          fontSize: 9.5, fontWeight: 600, color, marginTop: 2, lineHeight: 1.1,
        }}
      >YoY: {pctText}</div>
    );
  }

  /**
   * Aggregate apples-to-apples YoY for a set of rows (subtotal, network
   * total, grand total, summary cell, etc.).
   *
   * Apples-to-apples: rows whose program is missing from the 25/26
   * estimate are EXCLUDED from both numerator and denominator. Mixed
   * with new-program rows the total YoY would explode upward (denom
   * shrinks, numerator includes new impressions); excluding them
   * matches the per-row "NEW" treatment.
   *
   * Math: sum of (per-spot displayed demo × units) over included rows
   * vs sum of (per-spot 25/26 estimate × length adjustment × units)
   * over the same rows.
   *
   * Returns null when no row contributed (everything NEW / outside the
   * comparison) so the caller can render "—" / nothing.
   */
  function computeAggregateYoY(
    rows: Array<{
      program: string;
      quarter: string;
      year: number;
      demo?: string | null;
      unitLength?: string | number | null;
      totalUnits?: number | null;
      overrideDemo000?: number | null;
    }>,
  ): { current: number; prior: number; pct: number; source: string | null } | null {
    if (!activeVersion) return null;
    let currentSum = 0;
    let priorSum = 0;
    let matched = 0;
    const sources = new Set<string>();
    for (const r of rows) {
      if (!r.demo) continue;
      const lookup = getPriorYearDemo000(r);
      if (lookup.kind !== 'value') continue; // exclude NEW / none
      sources.add(lookup.source);
      const units = r.totalUnits ?? 0;
      if (units <= 0) continue;
      const est = calcEstimateFields({
        program: r.program,
        quarter: r.quarter,
        year: r.year,
        demo: r.demo,
        unitLength: r.unitLength ?? '',
        totalUnits: units,
      });
      const perSpot = r.overrideDemo000 != null
        ? r.overrideDemo000
        : (est.demo000 ?? null);
      if (perSpot == null) continue;
      currentSum += perSpot * units;
      priorSum += lookup.value * units;
      matched++;
    }
    if (matched === 0 || priorSum === 0) return null;
    return {
      current: currentSum, prior: priorSum,
      pct: (currentSum - priorSum) / priorSum,
      // Carried up so the aggregate tooltip names its baseline the same way
      // the per-cell one does. Every contributing row resolves through the
      // same activeVersion-derived chain, so they all share one source.
      source: sources.size === 1 ? Array.from(sources)[0] : null,
    };
  }

  /** Render the aggregate YoY badge in an aggregate row's TOTAL (000) cell. */
  function renderAggregateYoYBadge(
    rows: Parameters<typeof computeAggregateYoY>[0],
  ): React.ReactNode {
    // Same rationale as renderYoYBadge — no YoY in any view that's
    // already the prior year.
    if (planIsViewOnly) return null;
    const result = computeAggregateYoY(rows);
    if (!result) return null;
    const pctText = `${result.pct >= 0 ? '+' : ''}${(result.pct * 100).toFixed(1)}%`;
    const color = result.pct > 0 ? colors.success : result.pct < 0 ? colors.danger : colors.textMuted;
    const tooltip = `vs 25/26${result.source ? ` ${result.source}` : ''} (${fmt(result.prior)}): ${pctText}`;
    return (
      <div
        title={tooltip}
        style={{
          // A percent change annotates the value above it — it should never
          // out-shout it. 9.5px/600 keeps it legible while making the
          // forecast number the thing your eye lands on, and it stops the
          // Primary Demo block reading as a column of red warnings.
          fontSize: 9.5, fontWeight: 600, color, marginTop: 2, lineHeight: 1.1,
        }}
      >YoY: {pctText}</div>
    );
  }

  function renderDealRow(d: Deal, programCpmHh?: number | null) {
    const est = calcEstimateFields(d);
    const equivUnits = ((parseFloat(String(d.unitLength)) / 30) * d.totalUnits).toFixed(1);
    const rtgHH = est.rtgHH != null ? est.rtgHH.toFixed(3) : '—';
    const hh000v = est.hh000 != null ? fmt(est.hh000) : '—';
    const totHH = est.totalHH != null ? fmt(est.totalHH) : '—';
    const vpvh = est.vpvh != null ? est.vpvh.toFixed(3) : '—';
    const rtgDemo = est.rtgDemo != null ? est.rtgDemo.toFixed(3) : '—';
    // Effective demo000: override (if set) wins over the estimate.
    const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
    const demo000val = effDemo != null ? effDemo : d.actual000;
    const totalDemo = fmt(demo000val * d.totalUnits);
    const nr = d.newRate != null ? d.newRate : (d.baseRate || d.rate);
    const nd = d.totalDollars; const bd = d.baseDollars || d.sourceDollars;
    const dolCls = nd > bd ? 'diff-up' : nd < bd ? 'diff-down' : 'diff-same';
    // CPM HH = total cost / total HH (000) — per-row.
    //   total cost     = d.totalDollars
    //   total HH (000) = est.hh000 (from estimate sheet) × d.totalUnits
    // The `programCpmHh` argument is preserved for backward compatibility
    // but no longer used here — earlier behavior derived a single program-
    // level CPM HH from the highest-rate :15 spot, which made every row
    // in a program show the same cell. Switched to the per-row formula
    // so different-length rows in the same program reflect their own
    // total-cost-to-total-HH ratio.
    void programCpmHh;
    const newHhCpm = est.totalHH && nd ? nd / est.totalHH : null;

    // Per-input edit state — derived from baseline comparison so editing
    // a value then reverting to baseline auto-clears the orange style.
    // No bookkeeping flags needed.
    const cpmInvalid = !(d.cpm > 0); // NaN, 0, negative all read as "blank/invalid"
    const cpmEdited = !cpmInvalid && d.baseCpm != null && Math.abs(d.cpm - d.baseCpm) > 1e-9;
    const rateEdited = d.newRate != null && d.baseRate != null && d.newRate !== d.baseRate;
    const demoEdited = d.overrideDemo000 != null;
    const editedColor = colors.warn;
    const invalidStyle: React.CSSProperties = { border: '1.5px solid #dc2626', background: '#fef2f2' };
    const cpmInputStyle: React.CSSProperties | undefined = cpmInvalid
      ? invalidStyle
      : (cpmEdited ? { color: editedColor, fontWeight: 600 } : undefined);
    const rateInvalid = d.newRate == null;
    const rateInputStyle: React.CSSProperties | undefined = rateInvalid
      ? invalidStyle
      : (rateEdited ? { color: editedColor, fontWeight: 600 } : undefined);
    const demoInputStyle: React.CSSProperties | undefined = demoEdited
      ? { color: editedColor, fontWeight: 600 } : undefined;
    // Demo Imp: show rounded whole number in the UI but keep the decimal
    // value for back-end calculation. The override stores whatever the
    // user types (decimal-precision preserved).
    const demoRaw = d.overrideDemo000 != null
      ? d.overrideDemo000
      : (est.demo000 != null ? est.demo000 : null);
    const demoInputValue = demoRaw != null ? Math.round(demoRaw) : '';
    const demoPlaceholder = est.demo000 != null ? String(Math.round(est.demo000)) : '—';

    return (
      // Key must be (dealKey, salesPlan), not dealKey alone. A rolled-forward
      // 26/27 plan's deals reload with `dealKey = sourceSfDealKey` (the
      // original 25/26 SF deal key) → both the 25/26 source plan and its
      // 26/27 rolled-forward counterpart carry the same dealKey for the
      // same source row, just with different salesPlan + year. Keying on
      // dealKey alone produces duplicate React keys in any render that
      // includes both plans (notably the initial All-Plans render before
      // a plan is picked), and reconciliation leaves stale TRs in the
      // tbody when the user later filters down to a single plan. Matches
      // the `(dealKey, salesPlan)` dedup the savedPlanToDeals merge uses.
      //
      // isDuplicate: red highlight when another row in the plan shares
      // this row's SF-dup key (Program+Length+Quarter+Year+Demo000+CPM+Rate).
      // Prevents the user from hitting SF's DUPLICATE_VALUE on push.
      <tr
        key={`${d.dealKey}|${d.salesPlan}`}
        style={isDealDuplicate(d) ? { background: '#fef2f2', boxShadow: 'inset 0 0 0 2px #dc2626' } : undefined}
        title={isDealDuplicate(d) ? 'Duplicate row — matches another row on Program, Length, Quarter, Year, Demo (000), CPM, and Rate. Salesforce will reject the push. Change Units or delete one of the duplicates.' : undefined}
      >
        <td className="program sticky-col">
          {/* Delete control sits to the LEFT of the name, in a flex row.
              Flex (rather than inline) matters now that the column no
              longer forces nowrap: a long program name wraps onto a second
              line beside the button instead of dragging it down. */}
          <div className="prog-cell">
            {/* Per-row delete — only offered on editable plans. Removes
                the row from allDeals; the next Save writes a plan_version
                without it. Mirrors the ✕ on AddedRows for consistency. */}
            {!planIsViewOnly && (
              <button
                className="row-del"
                onClick={() => requestDeleteSavedRow(d)}
                title="Delete this row from the plan (takes effect on next Save)"
              >✕</button>
            )}
            <span className="prog-cell-name">{d.program}</span>
          </div>
        </td>
        <td className="num muted">{fmtPeriod(d.quarter, d.year)}</td>
        {/* Per-row Demo cell — All Plans only (each row may carry a
            different primary demo). Per-plan view omits it because every
            row shares the selected plan's demo. */}
        {!planFilter && <td className="num muted">{d.demo || ''}</td>}
        <td className="num muted">{DAY_TIME[d.program] || ''}</td>
        {/* LEN — red border when blank on editable plans. Without a
            unit length the (program, quarter, year, unitLength, demo)
            estimate lookup can't match, so RTG/(000)/CPM cells stay
            empty and save is blocked (hasMissingUnitLength). Red
            border is the same signal we use for empty CPM/Rate. */}
        {(() => {
          const isBlank = !String(d.unitLength ?? '').trim();
          const flag = isBlank && !planIsViewOnly;
          return (
            <td
              className="num muted len"
              style={flag ? { boxShadow: 'inset 0 0 0 1.5px #dc2626', background: '#fef2f2' } : undefined}
              title={flag ? 'Set unit length so estimates can populate' : undefined}
            >{d.unitLength}</td>
          );
        })()}
        {(() => {
          // Diff-highlight amber wrapper around the Units cell when
          // we're viewing a historical version and this specific
          // cell differs from the actual latest.
          const rowDiff = historicalDiff?.diffedByKey.get(historicalDiff.naturalKeyFor(d));
          const diff = rowDiff?.fields.get('totalUnits');
          const tdStyle = diff ? diffCellBg : undefined;
          const tdTitle = diff
            ? `Historical: ${formatDiffValue(diff.historical)} · Latest: ${formatDiffValue(diff.latest)}`
            : undefined;
          return (
            <td className="num" style={tdStyle} title={tdTitle}>
              {/* key includes totalUnits so the uncontrolled input
                  remounts and re-displays when the underlying value
                  changes (e.g. after switching versions, saving, or
                  order-revision push). Without the key, defaultValue
                  is ignored on subsequent renders and the DOM keeps
                  whatever was last typed/shown — same pattern used
                  on the CPM, Rate, and Demo inputs.

                  DO NOT add onChange here. Committing on every keystroke
                  changes d.totalUnits, which changes this key, which
                  remounts the input mid-edit — the field loses focus and
                  snaps back to the partially-typed number after a single
                  keypress, making it impossible to clear and retype. The
                  value-in-key and per-keystroke commit cannot coexist.
                  onBlur only, exactly like the CPM and Rate inputs; Save
                  blurs the active element first, so a typed-but-unblurred
                  value is still captured. */}
              <input
                key={`units-${d.dealKey}-${d.salesPlan}-${d.totalUnits}-${inputRemountKey}`}
                className="units-input" type="number" step="1" min="0" defaultValue={d.totalUnits}
                readOnly={planIsViewOnly}
                data-cell={`units-${d.dealKey}-${d.salesPlan}`}
                onChange={onArrowStep(`units-${d.dealKey}-${d.salesPlan}`,
                  v => onUnitsChange(d.dealKey, d.salesPlan, v))}
                onBlur={e => onUnitsChange(d.dealKey, d.salesPlan, e.target.value)} />
            </td>
          );
        })()}
        <td className="num muted">{equivUnits}</td>
        <td className="num muted">{rtgHH}</td>
        <td className="num">{hh000v}</td>
        <td className="num">{totHH}</td>
        <td className="num">{newHhCpm != null ? '$' + newHhCpm.toFixed(2) : '—'}</td>
        <td className="num">{rtgDemo}</td>
        <td className="num">{vpvh}</td>
        {(() => {
          const rowDiff = historicalDiff?.diffedByKey.get(historicalDiff.naturalKeyFor(d));
          const demoDiff = rowDiff?.fields.get('overrideDemo000');
          const tdStyle: React.CSSProperties = { ...(demoDiff ? diffCellBg : {}) };
          const tdTitle = demoDiff
            ? `Historical: ${formatDiffValue(demoDiff.historical)} · Latest: ${formatDiffValue(demoDiff.latest)}`
            : undefined;
          return (
            <td className="num yellow" style={tdStyle} title={tdTitle}>
              <input
                key={`demo-${d.dealKey}-${d.salesPlan}-${demoInputValue}-${inputRemountKey}`}
                className="demo-input"
                type="number" step="1" min="0"
                defaultValue={demoInputValue}
                placeholder={demoPlaceholder}
                readOnly={planIsViewOnly}
                data-cell={`demo-${d.dealKey}-${d.salesPlan}`}
                onChange={onArrowStep(`demo-${d.dealKey}-${d.salesPlan}`,
                  (v, el) => onDealDemo000Change(d.dealKey, d.salesPlan, v, el))}
                onBlur={e => onDealDemo000Change(d.dealKey, d.salesPlan, e.target.value, e.target)}
                style={demoInputStyle}
              />
              {renderYoYBadge(d, demoRaw)}
            </td>
          );
        })()}
        <td className="num">{totalDemo}</td>
        <td className="num muted">{fmtOrigCpm(d.originalCpm)}</td>
        {(() => {
          const rowDiff = historicalDiff?.diffedByKey.get(historicalDiff.naturalKeyFor(d));
          const cpmDiff = rowDiff?.fields.get('cpm');
          const tdStyle: React.CSSProperties = { ...(cpmDiff ? diffCellBg : {}) };
          const tdTitle = cpmDiff
            ? `Historical: ${formatDiffValue(cpmDiff.historical)} · Latest: ${formatDiffValue(cpmDiff.latest)}`
            : undefined;
          return (
        <td className="num" style={tdStyle} title={tdTitle}>
          {(() => {
            // Dual-input CPM cell. `$` is the canonical value; `± %`
            // applies a baseline-relative adjustment then clears.
            const baseline = getBaseline(d);
            const pctDelta = !cpmInvalid && baseline && baseline !== 0
              ? (d.cpm - baseline) / baseline
              : 0;
            // Displayed % = signed one-decimal text, blank when 0.
            const pctText = Math.abs(pctDelta) < 0.0005
              ? ''
              : `${pctDelta >= 0 ? '+' : ''}${(pctDelta * 100).toFixed(1)}`;
            const pctDisabled = planIsViewOnly || !baseline || baseline === 0;
            return (
              <div className="cpm-cell" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input className="cpm-input"
                  key={`cpm-${d.dealKey}-${d.salesPlan}-${cpmInvalid ? 'blank' : d.cpm}-${inputRemountKey}`}
                  type="number" step="0.01"
                  defaultValue={cpmInvalid ? '' : d.cpm.toFixed(2)}
                  placeholder="—"
                  readOnly={planIsViewOnly}
                  // Uncontrolled + onBlur only (matches Demo Imp). See
                  // the AddedRow CPM input for why onChange is
                  // intentionally omitted (would cause per-keystroke
                  // remount because key includes d.cpm).
                  data-cell={`cpm-${d.dealKey}-${d.salesPlan}`}
                  onChange={onArrowStep(`cpm-${d.dealKey}-${d.salesPlan}`,
                    (v, el) => onCpmChange(d.dealKey, d.salesPlan, v, el))}
                  onBlur={e => onCpmChange(d.dealKey, d.salesPlan, e.target.value, e.target)}
                  style={cpmInputStyle} />
                <input className="cpm-pct-input"
                  // Re-key on (cpm, baseline) so the input always remounts
                  // showing the freshly-derived deviation after any edit.
                  key={`cpmpct-${d.dealKey}-${d.salesPlan}-${d.cpm}-${baseline}`}
                  type="text"
                  defaultValue={pctText}
                  placeholder="± %"
                  readOnly={pctDisabled}
                  title={pctDisabled
                    ? 'Enter a CPM first to enable percent adjustments.'
                    : 'Adjust CPM by % of the current baseline. Type 5, +5, or -3.'}
                  onBlur={e => onCpmPercentChange(d.dealKey, d.salesPlan, e.target.value, e.target)}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
              </div>
            );
          })()}
        </td>
          );
        })()}
        {secondaryDemos.map(demo => {
          // Impressions are editable; the CPM beside them is derived
          // (rate / impressions) and stays read-only — a secondary demo
          // has no rate of its own, so letting CPM be typed would have to
          // silently rewrite the impressions the user just entered.
          const eff = effectiveSecondary000(d, demo);
          const isOverridden = d.overrideSecondary000?.[demo] != null;
          const cpmSec = eff && d.newRate ? '$' + (d.newRate / eff).toFixed(2) : '—';
          return (
            <React.Fragment key={demo}>
              <td className="num">
                <input
                  key={`sec-${d.dealKey}-${d.salesPlan}-${demo}-${eff ?? 'blank'}-${inputRemountKey}`}
                  className="demo-input"
                  type="number" step="1" min="0"
                  defaultValue={eff != null ? Math.round(eff) : ''}
                  placeholder="—"
                  readOnly={planIsViewOnly}
                  title={isOverridden ? `Typed value — clear to fall back to the estimate` : undefined}
                  // onBlur only. The key contains the value, so committing
                  // mid-typing would remount the input and eat the caret —
                  // same trap documented on the CPM and units inputs.
                  data-cell={`sec-${d.dealKey}-${d.salesPlan}-${demo}`}
                  onChange={onArrowStep(`sec-${d.dealKey}-${d.salesPlan}-${demo}`,
                    (v, el) => onSecondaryDemo000Change(d.dealKey, d.salesPlan, demo, v, el))}
                  onBlur={e => onSecondaryDemo000Change(d.dealKey, d.salesPlan, demo, e.target.value, e.target)}
                  style={isOverridden ? undefined : { background: '#fff', borderColor: '#e5e7eb' }}
                />
              </td>
              <td className="num muted">{cpmSec}</td>
            </React.Fragment>
          );
        })}
        {(() => {
          const rowDiff = historicalDiff?.diffedByKey.get(historicalDiff.naturalKeyFor(d));
          const rateDiff = rowDiff?.fields.get('rate');
          const dollarDiff = rowDiff?.fields.get('totalDollars');
          const rateStyle: React.CSSProperties = { ...(rateDiff ? diffCellBg : {}) };
          const rateTitle = rateDiff
            ? `Historical: ${formatDiffValue(rateDiff.historical)} · Latest: ${formatDiffValue(rateDiff.latest)}`
            : undefined;
          const dollarStyle: React.CSSProperties = { ...(dollarDiff ? diffCellBg : {}) };
          const dollarTitle = dollarDiff
            ? `Historical: ${formatDiffValue(dollarDiff.historical)} · Latest: ${formatDiffValue(dollarDiff.latest)}`
            : undefined;
          return (<>
        <td className="num" style={rateStyle} title={rateTitle}>
          {/* Rate display:
              - null    → blank (placeholder "—"); rate is genuinely unset
              - 0       → renders as "0" (explicit zero, e.g. ADU plans
                          where the spots are comped — distinguishes
                          intentional zero from missing data)
              - >0      → renders as the numeric value
              The validation rule (rateInvalid) only flags null, not 0,
              so ADU rows don't carry a red border. */}
          <input
            key={`rate-${d.dealKey}-${d.salesPlan}-${nr}-${inputRemountKey}`}
            className="rate-input"
            type="number" step="1" min="0"
            defaultValue={nr != null ? nr : ''}
            placeholder="—"
            readOnly={planIsViewOnly}
            // Uncontrolled + onBlur only. onChange omitted for the same
            // reason as the CPM input — key includes newRate.
            data-cell={`rate-${d.dealKey}-${d.salesPlan}`}
            onChange={onArrowStep(`rate-${d.dealKey}-${d.salesPlan}`,
              (v, el) => onDealRateChange(d.dealKey, d.salesPlan, v, el))}
            onBlur={e => onDealRateChange(d.dealKey, d.salesPlan, e.target.value, e.target)}
            style={rateInputStyle}
          />
        </td>
        {/* Total $: always show the formatted dollar value (including
            $0 for ADU rows). The diff-class still drives green/red/grey
            based on movement from baseDollars; for ADUs both sides are
            $0 so it renders as "same" (grey). */}
        <td className="num" style={dollarStyle} title={dollarTitle}><span className={dolCls}>${fmt(nd)}</span></td>
        </>);
        })()}
      </tr>
    );
  }

  function renderAddedRow(r: AddedRow) {
    const demo = r.demo || filteredDeals.find(x => x.program === r.program)?.demo || '';
    const est = r.unitLength ? calcEstimateFields({ program: r.program, quarter: r.quarter, year: r.year, demo, unitLength: r.unitLength, totalUnits: r.totalUnits }) : null;
    const equivUnits = ((parseFloat(r.unitLength) || 0) / 30 * r.totalUnits).toFixed(1);
    const rtgHH = est?.rtgHH != null ? est.rtgHH.toFixed(3) : '—';
    const hh000v = est?.hh000 != null ? fmt(est.hh000) : '—';
    const totHH = est?.totalHH != null ? fmt(est.totalHH) : '—';
    const rtgDemo = est?.rtgDemo != null ? est.rtgDemo.toFixed(3) : '—';
    const vpvh = est?.vpvh != null ? est.vpvh.toFixed(3) : '—';
    const newRate = r.newRate ?? null;
    const newTotal = newRate != null ? newRate * r.totalUnits : null;
    // CPM HH = total cost / total HH (000) — per-row.
    //   total cost     = rate × units              (newTotal)
    //   total HH (000) = hh000-per-spot × units    (est.totalHH, from estimate sheet)
    // Mathematically identical to (rate / hh000) but expressed in totals
    // so the formula matches the row's own visible columns.
    const cpmHH = est?.totalHH && newTotal
      ? '$' + (newTotal / est.totalHH).toFixed(2)
      : '—';
    // Derived edit state, same approach as renderDealRow.
    const editedColor = colors.warn;
    const cpmEdited = r.baseCpm != null && r.cpm > 0 && Math.abs(r.cpm - r.baseCpm) > 1e-9;
    const rateEdited = r.newRate != null && r.baseRate != null && r.newRate !== r.baseRate;
    const demoEdited = r.overrideDemo000 != null;
    const cpmInvalid = !(r.cpm > 0);
    const rateInvalid = r.newRate == null;
    const invalidBorder = '1.5px solid #dc2626';

    // Duplicate-row highlight overrides the standard NEW row amber tint.
    // AddedRows can also trigger SF's Duplicate_Check__c so the flag has
    // to catch them alongside saved deals.
    const isDupe = isAddedRowDuplicate(r);
    return (
      <tr
        key={r.key}
        style={isDupe
          ? { background: '#fef2f2', boxShadow: 'inset 0 0 0 2px #dc2626' }
          : { background: colors.warnBg, borderLeft: '3px solid #f59e0b' }}
        title={isDupe ? 'Duplicate row — matches another row on Program, Length, Quarter, Year, Demo (000), CPM, and Rate. Salesforce will reject the push. Change Units or delete one of the duplicates.' : undefined}
      >
        <td className="program sticky-col" style={{ color: '#92400e' }}>
          {/* Same left-hand ✕ + flex layout as saved deal rows. */}
          <div className="prog-cell">
            <button className="row-del" onClick={() => removeAddedRow(r.key)}>✕</button>
            <span className="prog-cell-name">
              {r.program}{' '}
              <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '1px 4px', borderRadius: 3 }}>NEW</span>
            </span>
          </div>
        </td>
        <td className="num muted">{fmtPeriod(r.quarter, r.year)}</td>
        <td className="num muted">{DAY_TIME[r.program] || ''}</td>
        {(() => {
          // Red border when unit length is blank on an editable plan —
          // without a length, estimates won't populate and save is
          // blocked (see hasMissingUnitLength).
          const isBlank = !String(r.unitLength ?? '').trim();
          const flag = isBlank && !planIsViewOnly;
          const selectStyle: React.CSSProperties = flag
            ? { fontSize: 12, border: '1.5px solid #dc2626', borderRadius: 4, padding: '2px 4px', background: '#fef2f2' }
            : { fontSize: 12, border: '1px solid #fde68a', borderRadius: 4, padding: '2px 4px', background: colors.warnBg };
          return (
            <td className="num muted len" title={flag ? 'Set unit length so estimates can populate' : undefined}>
              <select onChange={e => onAddedLengthChange(r.key, e.target.value)} value={r.unitLength}
                disabled={planIsViewOnly}
                style={selectStyle}>
                <option value="">--</option>
                {PLAN_LENGTHS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </td>
          );
        })()}
        <td className="num">
          {/* Uncontrolled input — same pattern as the saved-Deal units
              cell. Controlled `value=` + a `|| 1` fallback used to
              force the field back to 1 the instant the user pressed
              backspace, making it impossible to clear and re-type
              (e.g., "1" → clear → type "5" got stuck on 1). Now the
              input owns its own DOM state; onChange commits every
              keystroke, onBlur re-commits (defensive), and the
              handler safely no-ops on empty / invalid input so the
              in-flight state doesn't fight the user.
              `key` on r.key so React remounts if the row identity
              changes (e.g., the row is regenerated after a save). */}
          <input key={`addedunits-${r.key}`} className="units-input" type="number" step="1" min="0" defaultValue={r.totalUnits}
            readOnly={planIsViewOnly}
            onBlur={e => onAddedUnitsChange(r.key, e.target.value)}
            onChange={e => onAddedUnitsChange(r.key, e.target.value)} />
        </td>
        <td className="num muted">{equivUnits}</td>
        <td className="num muted">{rtgHH}</td>
        <td className="num">{hh000v}</td>
        <td className="num">{totHH}</td>
        <td className="num">{cpmHH}</td>
        <td className="num">{rtgDemo}</td>
        <td className="num">{vpvh}</td>
        <td className="num">
          {/* Uncontrolled + key-on-value pattern, same as the Deal row's
              demo input. Lets onBlur fire the guard modal exactly once
              per commit instead of on every keystroke (which would
              happen if this were a controlled value+onChange input). */}
          <input
            key={`addeddemo-${r.key}-${r.overrideDemo000 != null ? Math.round(r.overrideDemo000) : (est?.demo000 != null ? Math.round(est.demo000) : '')}`}
            type="number" step="1"
            defaultValue={r.overrideDemo000 != null ? Math.round(r.overrideDemo000) : (est?.demo000 != null ? Math.round(est.demo000) : '')}
            placeholder={est?.demo000 != null ? String(Math.round(est.demo000)) : '—'}
            readOnly={planIsViewOnly}
            data-cell={`addeddemo-${r.key}`}
            onChange={onArrowStep(`addeddemo-${r.key}`,
              (v, el) => onAddedDemo000Change(r.key, v, el))}
            onBlur={e => onAddedDemo000Change(r.key, e.target.value, e.target)}
            style={{ width: 68, fontSize: 11, border: '1px solid #fde68a', borderRadius: 4, padding: '2px 4px', background: colors.warnBg, textAlign: 'right', fontFamily: 'inherit', color: demoEdited ? editedColor : colors.textDark, fontWeight: demoEdited ? 600 : 400 }} />
          {/* YoY subtext — same as renderDealRow. For added rows the
              displayed demo (000) is override-if-set, else the
              estimate's demo000. */}
          {renderYoYBadge(
            { program: r.program, quarter: r.quarter, year: r.year, demo: r.demo ?? filteredDeals[0]?.demo ?? '', unitLength: r.unitLength },
            r.overrideDemo000 != null ? r.overrideDemo000 : (est?.demo000 ?? null),
          )}
        </td>
        <td className="num">{est?.demo000 != null ? fmt((r.overrideDemo000 ?? est.demo000) * r.totalUnits) : '—'}</td>
        <td className="num muted">{fmtOrigCpm(r.originalCpm)}</td>
        <td className="num">
          {(() => {
            const baseline = getBaseline(r);
            const pctDelta = !cpmInvalid && baseline && baseline !== 0
              ? (r.cpm - baseline) / baseline
              : 0;
            const pctText = Math.abs(pctDelta) < 0.0005
              ? ''
              : `${pctDelta >= 0 ? '+' : ''}${(pctDelta * 100).toFixed(1)}`;
            const pctDisabled = planIsViewOnly || !baseline || baseline === 0;
            return (
              <div className="cpm-cell" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input className="cpm-input"
                  key={`addedcpm-${r.key}-${cpmInvalid ? 'blank' : r.cpm}`}
                  type="number" step="0.01"
                  defaultValue={cpmInvalid ? '' : r.cpm}
                  placeholder="—"
                  readOnly={planIsViewOnly}
                  // Uncontrolled + onBlur only (matches the Demo Imp
                  // input pattern). Do NOT add onChange — the key prop
                  // includes r.cpm, so a per-keystroke state update
                  // remounts the input and kicks the user's focus out
                  // after every character. Paste-then-Save is handled
                  // by the activeElement.blur() flush at the top of
                  // saveToSnowflake.
                  data-cell={`addedcpm-${r.key}`}
                  onChange={onArrowStep(`addedcpm-${r.key}`,
                    (v, el) => onAddedCpmChange(r.key, v, el))}
                  onBlur={e => onAddedCpmChange(r.key, e.target.value, e.target)}
                  style={cpmInvalid
                    ? { border: invalidBorder, background: '#fef2f2' }
                    : (cpmEdited ? { color: editedColor, fontWeight: 600 } : undefined)} />
                <input className="cpm-pct-input"
                  key={`addrowpct-${r.key}-${r.cpm}-${baseline}`}
                  type="text"
                  defaultValue={pctText}
                  placeholder="± %"
                  readOnly={pctDisabled}
                  title={pctDisabled
                    ? 'Enter a CPM first to enable percent adjustments.'
                    : 'Adjust CPM by % of the current baseline. Type 5, +5, or -3.'}
                  onBlur={e => onAddedCpmPercentChange(r.key, e.target.value, e.target)}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
              </div>
            );
          })()}
        </td>
        {secondaryDemos.map(demo => {
          // Match the Deal row's secondary-demo path. AddedRows used to
          // hard-code "—" here, which made every new-plan row render
          // empty secondary cells regardless of whether the underlying
          // estimate data existed in Snowflake.
          const eff = effectiveSecondary000(r, demo);
          const isOverridden = r.overrideSecondary000?.[demo] != null;
          const cpmSec = eff && newRate ? '$' + (newRate / eff).toFixed(2) : '—';
          return (
            <React.Fragment key={demo}>
              <td className="num">
                <input
                  key={`addedsec-${r.key}-${demo}-${eff ?? 'blank'}`}
                  className="demo-input"
                  type="number" step="1" min="0"
                  defaultValue={eff != null ? Math.round(eff) : ''}
                  placeholder="—"
                  readOnly={planIsViewOnly}
                  title={isOverridden ? `Typed value — clear to fall back to the estimate` : undefined}
                  data-cell={`addedsec-${r.key}-${demo}`}
                  onChange={onArrowStep(`addedsec-${r.key}-${demo}`,
                    (v, el) => onAddedSecondaryDemo000Change(r.key, demo, v, el))}
                  onBlur={e => onAddedSecondaryDemo000Change(r.key, demo, e.target.value, e.target)}
                  style={isOverridden ? undefined : { background: '#fff', borderColor: '#e5e7eb' }}
                />
              </td>
              <td className="num muted">{cpmSec}</td>
            </React.Fragment>
          );
        })}
        <td className="num">
          <input
            key={`addedrate-${r.key}-${newRate ?? 'blank'}`}
            type="number" step="1"
            defaultValue={newRate ?? ''}
            placeholder="—"
            readOnly={planIsViewOnly}
            // Uncontrolled + onBlur only. See the matching CPM input
            // above for why onChange is intentionally omitted.
            data-cell={`addedrate-${r.key}`}
            onChange={onArrowStep(`addedrate-${r.key}`,
              (v, el) => onAddedRateChange(r.key, v, el))}
            onBlur={e => onAddedRateChange(r.key, e.target.value, e.target)}
            style={{
              width: 68, fontSize: 11, borderRadius: 4, padding: '2px 4px',
              background: rateInvalid ? '#fef2f2' : colors.warnBg,
              border: rateInvalid ? invalidBorder : '1px solid #fde68a',
              textAlign: 'right', fontFamily: 'inherit',
              color: rateEdited ? editedColor : colors.textDark,
              fontWeight: rateEdited ? 600 : 400,
            }} />
        </td>
        <td className="num">{newTotal != null && newTotal > 0 ? <span className="diff-up">${fmt(newTotal)}</span> : '—'}</td>
      </tr>
    );
  }

  /**
   * The (000) + CPM cell pair for every secondary demo on a TOTAL row.
   *
   * On total rows the (000) cell holds the TOTAL — per-spot × units, summed —
   * because secondaries have no separate Total (000) column. It is the exact
   * figure the CPM next to it divides by, so the pair can be checked by hand.
   */
  function renderSecondaryTotalCells(
    rows: ReadonlyArray<Deal | AddedRow>,
    dollars: number,
    cellStyle?: React.CSSProperties,
  ) {
    const totals = secondaryTotalsFor(rows, secondaryDemos, dollars);
    return secondaryDemos.map((d, i) => (
      <React.Fragment key={d}>
        <td className="num" style={cellStyle}>{totals[i].total000 != null && totals[i].total000! > 0 ? fmt(totals[i].total000!) : '—'}</td>
        <td className="num" style={cellStyle}>{totals[i].cpm != null ? '$' + totals[i].cpm!.toFixed(2) : '—'}</td>
      </React.Fragment>
    ));
  }

  function renderSubtotalRow(program: string, progDeals: Deal[], planAddedRows: AddedRow[]) {
    // A rollup ADDS the rows above it. It does not re-derive them.
    //
    // This used to recompute each added row's rate as round(estimate × cpm)
    // and its dollars from that — which silently discarded a typed Demo (000)
    // override AND a typed Rate, so the subtotal could disagree with the rows
    // printed directly above it, and with the plan total at the bottom (which
    // has always summed the stored value). Read what the row holds.
    //
    // The map still resolves `demo`, because AddedRow.demo is optional and the
    // impressions/HH rollups below need the same fallback chain renderAddedRow
    // uses. Only the money was ever the problem.
    const enriched = planAddedRows.filter(r => r.program === program).map(r => {
      const demo = r.demo || filteredDeals.find(x => x.program === r.program)?.demo || '';
      return { ...r, demo, actual000: 0 } as any;
    });
    const progAll = [...progDeals, ...enriched];
    const pUnits = progAll.reduce((s, x) => s + x.totalUnits, 0);
    const pDollars = progAll.reduce((s, x) => s + (x.totalDollars || 0), 0);
    const pEquiv = (progAll.reduce((s, x) => s + (parseFloat(String(x.unitLength)) || 0) * x.totalUnits, 0) / 30).toFixed(1);
    const pTotalHH = progAll.reduce((s, x) => { const e = calcEstimateFields(x); return s + (e.totalHH || 0); }, 0);
    // Honor per-row overrideDemo000 — same helper the row-level cells
    // use. Without this the subtotal computes 45 × 169 = 7,605 when
    // one of the rows has been manually re-priced to 64 (correct
    // total: 44 × 169 + 1 × 64 = 7,500).
    const pTotalDemo = progAll.reduce((s, x) => {
      const e = calcEstimateFields(x);
      const v = x.overrideDemo000 != null
        ? x.overrideDemo000
        : (e.demo000 != null ? e.demo000 : x.actual000);
      return s + (v * x.totalUnits);
    }, 0);
    const pDemo000Sum = progAll.reduce((s, x) => {
      const e = calcEstimateFields(x);
      const v = x.overrideDemo000 != null
        ? x.overrideDemo000
        : (e.demo000 != null ? e.demo000 : x.actual000);
      return s + v;
    }, 0);
    // CPM HH on subtotal: weighted by impressions (Total Cost / Total HH 000s),
    // not the per-row "highest-rate :15" value used in the data rows above.
    const pCpmHH = pTotalHH > 0 ? pDollars / pTotalHH : null;
    // CPM Demo on subtotal: weighted by impressions (Total Cost / Total Demo 000s),
    // not an average of the per-row CPMs.
    const pCpmDemo = pTotalDemo > 0 ? pDollars / pTotalDemo : null;
    const progKey = program.replace(/[^a-z0-9]/gi, '_');
    // Subtotal row — black + bold across all numeric cells, matching the
    // top-level TOTALS row. Label cell stays muted/uppercase for hierarchy.
    // No fontSize here — an inline style beats the 14.5px in
    // 'tbody tr.subtotal-row td', so setting it here silently pinned every
    // numeric subtotal cell at 11px.
    const subCellStyle: React.CSSProperties = { fontWeight: 700, color: colors.textDark };
    return (
      <tr key={`sub-${progKey}`} className="subtotal-row">
        {/* Label split off column 0 so "<program> Subtotal" pins while
            scrolling right. colSpan drops 4 → 3 to preserve alignment. */}
        {/* No whiteSpace:nowrap — a long "<PROGRAM> SUBTOTAL" on one line was
            forcing column 0 wider than its 190px target. Two wrapped lines of
            11px still clear the 44px row height. */}
        {/* #475569 rather than #64748b — two steps darker on the slate ramp.
            A subtotal closes out a program block, so its label needs to read
            as a heading, not as de-emphasised metadata. */}
        <td className="sticky-col" style={{ padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.25 }}><div className="prog-box">{program} Subtotal</div></td>
        <td colSpan={3} />
        <td className="num" style={subCellStyle}>{pUnits.toFixed(0)}</td>
        <td className="num" style={subCellStyle}>{pEquiv}</td>
        <td /><td />
        <td className="num" style={subCellStyle}>{pTotalHH > 0 ? fmt(pTotalHH) : '—'}</td>
        <td className="num" style={subCellStyle}>{pCpmHH != null ? '$' + pCpmHH.toFixed(2) : '—'}</td>
        <td /><td />
        <td className="num" style={subCellStyle}>
          {pDemo000Sum > 0 ? fmt(pDemo000Sum) : '—'}
          {/* YoY badge stacks here (under the per-spot W18-64 (000s)
              column) to line up vertically with the per-row YoY in
              the same column. The math is still aggregate (total /
              total ratio) — only the visual placement matches. */}
          {renderAggregateYoYBadge(progAll)}
        </td>
        <td className="num" style={subCellStyle}>{pTotalDemo > 0 ? fmt(pTotalDemo) : '—'}</td>
        {/* Original CPM — no meaningful aggregate at the subtotal level
            (each row has its own frozen value); render dash. */}
        <td />
        <td className="num" style={subCellStyle}>{pCpmDemo != null ? '$' + pCpmDemo.toFixed(2) : '—'}</td>
        {renderSecondaryTotalCells(progAll, pDollars, subCellStyle)}
        <td />
        <td className="num" style={subCellStyle}>${fmt(pDollars)}</td>
      </tr>
    );
  }

  function renderNetworkTotalRow(network: string, netDeals: Deal[], netAddedRows: AddedRow[], keyPrefix?: string) {
    // Sums the stored row values — see the note in renderSubtotalRow for why
    // this no longer recomputes. `demo` is still resolved for the impressions
    // and HH rollups further down.
    const enriched = netAddedRows.map(r => {
      const demo = r.demo || filteredDeals.find(x => x.program === r.program)?.demo || '';
      return { ...r, demo, actual000: 0 } as any;
    });
    const all = [...netDeals, ...enriched];
    const nUnits = all.reduce((s, x) => s + x.totalUnits, 0);
    const nDollars = all.reduce((s, x) => s + (x.totalDollars || 0), 0);
    const nEquiv = (all.reduce((s, x) => s + (parseFloat(String(x.unitLength)) || 0) * x.totalUnits, 0) / 30).toFixed(1);
    const nTotalHH = all.reduce((s, x) => { const e = calcEstimateFields(x); return s + (e.totalHH || 0); }, 0);
    // Honor per-row overrideDemo000 — matches the subtotal and stat-card
    // fixes. Applies to every rollup that runs through this helper
    // (network totals, per-quarter totals, plan-total row).
    const nTotalDemo = all.reduce((s, x) => {
      const e = calcEstimateFields(x);
      const v = x.overrideDemo000 != null
        ? x.overrideDemo000
        : (e.demo000 != null ? e.demo000 : x.actual000);
      return s + (v * x.totalUnits);
    }, 0);
    const nDemo000Sum = all.reduce((s, x) => {
      const e = calcEstimateFields(x);
      const v = x.overrideDemo000 != null
        ? x.overrideDemo000
        : (e.demo000 != null ? e.demo000 : x.actual000);
      return s + v;
    }, 0);
    // Total dollars ÷ total impressions — the same rule the program subtotal
    // and the Summary tab use. These used to be a plain MEAN of the per-row
    // CPMs, so ten units at $14.34 plus one re-priced at $12.00 showed
    // $13.17 here while the program subtotal above it showed the weighted
    // figure.
    const nCpmHH = cpmOf(nDollars, nTotalHH);
    const nCpmDemo = cpmOf(nDollars, nTotalDemo);
    // The "Quarter Totals" row only renders on the Quarterly tab (one per
    // quarter), so derive its label from the currently-active quarter.
    // Fall back to a season-range label on the Summary tab, where this
    // function is also called (network total across all quarters).
    // Active tab can be a synthesized quarter (e.g., 4Q'27 on a
    // CY/Scatter plan); look in visibleQuarters first.
    const activeTab = visibleQuarters.find(t => t.key === activeQuarter)
                   || QUARTER_TABS.find(t => t.key === activeQuarter);
    const label = activeTab
      ? quarterTotalsLabel(network, activeTab.label)
      : `${network} ${SEASON_RANGE_LABEL} Total`;
    // Network total row — black + bold across all numeric cells.
    // Same as subCellStyle: no inline fontSize, or it overrides the CSS.
    const netCellStyle: React.CSSProperties = { fontWeight: 700, color: colors.textDark };
    return (
      <tr key={keyPrefix ? `net-tot-${keyPrefix}-${network}` : `net-tot-${network}`} className="net-total-row">
        {/* Label pinned in column 0; the rest of the old span becomes a
            filler cell. Originally spanned Program/Period/(Demo)/Day-Time/Len
            — +1 in All Plans for the inserted Demo column — so the filler is
            that count minus the pinned column. */}
        <td className="sticky-col" style={{ padding: '5px 8px', fontSize: 11, fontWeight: 700, color: colors.textDark, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.25 }}><div className="prog-box">{label}</div></td>
        <td colSpan={!planFilter ? 4 : 3} />
        <td className="num" style={netCellStyle}>{nUnits.toFixed(0)}</td>
        <td className="num" style={netCellStyle}>{nEquiv}</td>
        <td /><td />
        <td className="num" style={netCellStyle}>{nTotalHH > 0 ? fmt(nTotalHH) : '—'}</td>
        <td className="num" style={netCellStyle}>{nCpmHH != null ? '$' + nCpmHH.toFixed(2) : '—'}</td>
        <td /><td />
        <td className="num" style={netCellStyle}>
          {nDemo000Sum > 0 ? fmt(nDemo000Sum) : '—'}
          {renderAggregateYoYBadge(all)}
        </td>
        <td className="num" style={netCellStyle}>{nTotalDemo > 0 ? fmt(nTotalDemo) : '—'}</td>
        {/* Original CPM — no aggregate at the network-total level. */}
        <td />
        <td className="num" style={netCellStyle}>{nCpmDemo != null ? '$' + nCpmDemo.toFixed(2) : '—'}</td>
        {renderSecondaryTotalCells(all, nDollars, netCellStyle)}
        <td />
        <td className="num" style={netCellStyle}>${fmt(nDollars)}</td>
      </tr>
    );
  }

  function renderTotalsRow() {
    // Deal aggregates — values come from the persisted Deal rows.
    const totUnits       = filteredDeals.reduce((s, d) => s + d.totalUnits, 0);
    const totDollars     = filteredDeals.reduce((s, d) => s + (d.totalDollars || 0), 0);
    const totOrigDollars = filteredDeals.reduce((s, d) => s + (d.baseDollars || d.sourceDollars), 0);
    const totNewRate     = filteredDeals.reduce((s, d) => s + (d.newRate != null ? d.newRate : d.rate), 0);
    const totOrigRate    = filteredDeals.reduce((s, d) => s + (d.baseRate || d.rate), 0);
    const totTotalHH     = filteredDeals.reduce((s, d) => { const e = calcEstimateFields(d); return s + (e.totalHH || 0); }, 0);
    // Same override-honoring precedence as the stat card and per-program
    // subtotal — override → estimate → SF actual. Plan-total row was
    // reading estimate.demo000 for every row, inflating the total
    // whenever a make-good had a manually reduced demo (000).
    const totTotalDemo   = filteredDeals.reduce((s, d) => {
      const e = calcEstimateFields(d);
      const v = d.overrideDemo000 != null
        ? d.overrideDemo000
        : (e.demo000 != null ? e.demo000 : d.actual000);
      return s + v * d.totalUnits;
    }, 0);

    // AddedRow contributions — manual "+ Add Show" / "+ Add Row"
    // entries living only in client state until the next save. Each
    // bucket mirrors the deal aggregate above so the totals line up.
    // For totOrig* baselines we assume "this row IS its own original"
    // until it's saved (no baseline-vs-current diff applies to a
    // never-yet-saved row).
    const addUnits       = filteredAddedRows.reduce((s, r) => s + (r.totalUnits || 0), 0);
    const addDollars     = filteredAddedRows.reduce((s, r) => s + (r.totalDollars || 0), 0);
    const addNewRate     = filteredAddedRows.reduce((s, r) => s + (r.newRate != null ? r.newRate : 0), 0);
    const addTotalHH     = filteredAddedRows.reduce((s, r) => {
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      return s + ((est?.hh000 ?? 0) * (r.totalUnits || 0));
    }, 0);
    const addTotalDemo   = filteredAddedRows.reduce((s, r) => {
      const est = r.unitLength ? calcEstimateFields({
        program: r.program, quarter: r.quarter, year: r.year, demo: r.demo || '',
        unitLength: r.unitLength, totalUnits: r.totalUnits,
      }) : null;
      const v = getEffectiveDemo000(r, est);
      return s + (v ?? 0) * (r.totalUnits || 0);
    }, 0);

    // Combine: deal totals + added totals.
    const grandUnits     = totUnits     + addUnits;
    const grandDollars   = totDollars   + addDollars;
    const grandOrigDollars = totOrigDollars + addDollars; // see comment above
    const grandNewRate   = totNewRate   + addNewRate;
    const grandOrigRate  = totOrigRate  + addNewRate;
    const grandTotalHH   = totTotalHH   + addTotalHH;
    const grandTotalDemo = totTotalDemo + addTotalDemo;
    const grandCpmHH     = cpmOf(grandDollars, grandTotalHH);
    const grandCpmDemo   = cpmOf(grandDollars, grandTotalDemo);
    const grandRowCount  = filteredDeals.length + filteredAddedRows.length;

    const dolCls  = grandDollars > grandOrigDollars ? 'diff-up' : grandDollars < grandOrigDollars ? 'diff-down' : 'diff-same';
    const rateCls = grandNewRate > grandOrigRate    ? 'diff-up' : grandNewRate < grandOrigRate    ? 'diff-down' : 'diff-same';
    // +1 in All Plans for the inserted Demo column. Without this, every
    // numeric column in the totals row shifts one cell left — the value
    // labelled "Total HH (000)" actually lands under "HH (000s)", etc.
    const labelColspan = !planFilter ? 5 : 4;
    return (
      <tr className="totals-row">
        {/* Pinned label + filler for the remainder of the old span. */}
        <td className="label sticky-col" style={{ padding: '6px 8px', lineHeight: 1.25 }}><div className="prog-box">TOTALS — {grandRowCount} deals</div></td>
        <td className="label" colSpan={labelColspan - 1} />
        <td className="num">{grandUnits.toFixed(0)}</td>
        <td className="num muted" />
        <td className="num muted">—</td><td className="num muted">—</td><td className="num">{fmt(grandTotalHH)}</td>
        {/* Plan CPMs: total dollars ÷ total impressions, never a mean of
            the per-row CPMs. */}
        <td className="num">{grandCpmHH != null ? '$' + grandCpmHH.toFixed(2) : '—'}</td>
        <td className="num muted">—</td><td className="num muted">—</td>
        {/* AVG (000) / per-spot demo (000): no meaningful aggregate
            across the plan. Show a dash; the Total (000) cell to the
            right carries the impressions sum. */}
        <td className="num muted">
          —
          {renderAggregateYoYBadge(filteredDeals)}
        </td>
        <td className="num">{fmt(grandTotalDemo)}</td>
        {/* Original CPM — no plan-wide aggregate (each row has its own
            frozen value). Show dash. */}
        <td className="num muted">—</td>
        <td className="num">{grandCpmDemo != null ? '$' + grandCpmDemo.toFixed(2) : '—'}</td>
        {renderSecondaryTotalCells([...filteredDeals, ...filteredAddedRows], grandDollars)}
        <td className="num"><span className={rateCls}>${fmt(grandNewRate)}</span></td>
        <td className="num"><span className={dolCls}>${fmt(grandDollars)}</span></td>
      </tr>
    );
  }

  // ─── Summary view (program × quarter aggregated table) ───────────────────────


  /**
   * Renders one row of the Summary table given a label, day/time, and metrics.
   * Keeps the row layout in one place so per-quarter rows, 25/26 rollups,
   * and network totals all use the same columns.
   */
  function renderSummaryRow(
    key: string,
    program: string,
    period: string,
    daytime: string,
    m: ReturnType<typeof aggregateSummaryMetrics>,
    style: 'normal' | 'annual' | 'netTotal' = 'normal',
    yoyBadge?: React.ReactNode,
  ) {
    const cellStyle: React.CSSProperties = style === 'netTotal'
      ? { background: '#1e3a8a', color: '#fff', fontWeight: 700 }
      : style === 'annual'
        ? { background: colors.bgPanel, fontWeight: 600, borderTop: '1px solid #e2e8f0' }
        : {};
    const fmtNum = (v: number, decimals = 0) =>
      v != null && !isNaN(v) && v !== 0
        ? Number(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : '—';
    const fmtCpm = (v: number) => (v > 0 ? '$' + v.toFixed(2) : '—');
    // Decimal counts match the Liberty Mutual Excel template (ratings: 3, impressions: 0).
    // Accepts null so the "demo not in UNIVERSE_DATA" branch in
    // aggregateSummaryMetrics flows through unchanged (rendered as '—'
    // same as a computed zero).
    const fmtRtg = (v: number | null) => (v != null && v > 0 ? v.toFixed(3) : '—');

    return (
      <tr key={key} style={cellStyle}>
        <td className="program" style={cellStyle}>{program}</td>
        <td className="num muted" style={cellStyle}>{period}</td>
        <td className="num muted" style={cellStyle}>{daytime}</td>
        <td className="num" style={cellStyle}>{m.units || 0}</td>
        <td className="num muted" style={cellStyle}>{m.equiv > 0 ? m.equiv.toFixed(1) : '—'}</td>
        <td className="num" style={cellStyle}>{fmtRtg(m.avgRtgHH)}</td>
        <td className="num" style={cellStyle}>{fmtNum(m.avgHH, 0)}</td>
        <td className="num" style={cellStyle}>{fmtCpm(m.cpmHH)}</td>
        <td className="num" style={cellStyle}>{fmtNum(m.totalHH, 0)}</td>
        <td className="num" style={cellStyle}>{fmtRtg(m.avgRtgDemo)}</td>
        <td className="num" style={cellStyle}>{m.vpvh > 0 ? m.vpvh.toFixed(3) : '—'}</td>
        {/* Centred, not right-aligned like its neighbours: this is the one
            cell carrying two stacked lines, and the YoY line is always wider
            than the value above it. Right-aligning pins the wide line to the
            edge and leaves the number floating short of it. */}
        <td className="num" style={{ ...cellStyle, textAlign: 'center' }}>
          {fmtNum(m.avgDemo, 0)}
          {yoyBadge}
        </td>
        <td className="num" style={cellStyle}>{fmtNum(m.totalDemo, 0)}</td>
        {/* Original CPM — Summary view doesn't carry per-deal info,
            and the aggregate has no meaningful "original" (each deal
            has its own frozen v1 CPM). Render dash. */}
        <td className="num muted" style={cellStyle}>—</td>
        <td className="num" style={cellStyle}>{fmtCpm(m.cpmDemo)}</td>
        <td className="num" style={cellStyle}>{m.avgEquivUnitRate > 0 ? '$' + fmtNum(m.avgEquivUnitRate, 0) : '—'}</td>
        <td className="num" style={cellStyle}>{m.totalCost > 0 ? '$' + fmtNum(m.totalCost, 0) : '—'}</td>
      </tr>
    );
  }

  /**
   * Builds all rows for the Summary table:
   *   - For each program (with spots in this plan), grouped by network:
   *     - 4 quarter rows
   *     - 1 "25/26" annual rollup row
   *     - blank separator row
   *   - Network total row at the end of each network
   */
  /**
   * Phase 2 — CPM input grid.
   * Renders a small bulk-edit grid above the main table.
   *   scope='summary'  → 4 quarters × N programs (one row per quarter)
   *   scope='quarter'  → 1 row × N programs (scoped to activeQuarter)
   * Edits propagate via setCpmForProgramAllQuarters (one CPM per
   * program across the whole broadcast year).
   *
   * Programs shown: any with at least one Deal or AddedRow in the plan
   * (and within scope for quarter mode). Programs are grouped by network
   * (NETWORK_ORDER) and sorted within network by PROGRAM_ORDER.
   *
   * Cell display value is the most-common CPM among the matching
   * schedules (computed inline below).
   * The input uses `defaultValue` plus a `key` keyed on the underlying
   * value so external state changes (e.g., editing the same cell from a
   * different tab's grid) cause the input to remount and re-render.
   */
  function renderCpmInputGrid(scope: 'summary' | 'quarter'): React.ReactNode {
    if (!planFilter) return null;
    // 25/26 SF plans are read-only reference inventory — no bulk-edit
    // grid. Same for the 25/26 toggle view of a 26/27 plan (decision:
    // strictly view-only). The per-row inputs are already locked via
    // readOnly; hiding the bulk grid keeps the UI from advertising
    // editing affordances we don't want here.
    if (planIsViewOnly) return null;

    // Bulk-grid scope. Summary scope iterates all of the PLAN'S visible
    // quarters (marketplace-driven); quarter scope filters to the active
    // tab. visibleQuarters covers synthesized tabs too (4Q'27 etc).
    const quarters = scope === 'summary'
      ? visibleQuarters
      : visibleQuarters.filter(t => t.key === activeQuarter);
    if (!quarters.length) return null;

    // Season-narrow so nickname-consolidated plans don't inject 25/26
    // source programs into the bulk-edit grid's program columns. Uses
    // `quarters` (already scoped to summary/quarter above) instead of
    // visibleQuarters directly so quarter-scope views only aggregate
    // the active quarter's rows.
    const cpmGridVisibleKeys = new Set(quarters.map(t => `${t.quarter}|${t.year}`));
    const planDeals = allDeals.filter(d =>
      d.salesPlan === planFilter
      && cpmGridVisibleKeys.has(`${d.quarter}|${d.year}`)
    );
    const planAddedRows = addedRows.filter(r =>
      r.salesPlan === planFilter
      && cpmGridVisibleKeys.has(`${r.quarter}|${r.year}`)
    );

    // For 'quarter' scope only: restrict to the active quarter's slice
    const inActiveScope = (qName: string, year: number) =>
      scope === 'summary'
      || (qName === quarters[0].quarter && year === quarters[0].year);

    const programsWithSpots = new Set<string>();
    for (const d of planDeals) {
      if (inActiveScope(d.quarter, d.year)) programsWithSpots.add(d.program);
    }
    for (const r of planAddedRows) {
      if (inActiveScope(r.quarter, r.year)) programsWithSpots.add(r.program);
    }
    if (!programsWithSpots.size) return null;

    // Order programs by network (NETWORK_ORDER), then by PROGRAM_ORDER within
    const programsByNet: Record<string, string[]> = {};
    for (const p of Array.from(programsWithSpots)) {
      const net = NETWORK_MAP[p] || 'Other';
      if (!programsByNet[net]) programsByNet[net] = [];
      programsByNet[net].push(p);
    }
    const orderedNets = [
      ...NETWORK_ORDER.filter(n => programsByNet[n]?.length),
      ...Object.keys(programsByNet).filter(n => !NETWORK_ORDER.includes(n)),
    ];
    type Group = { net: string; programs: string[] };
    const groups: Group[] = [];
    for (const net of orderedNets) {
      const progs = programsByNet[net] || [];
      const order = PROGRAM_ORDER[net] || [];
      const ordered = [
        ...order.filter(p => progs.includes(p)),
        ...progs.filter(p => !order.includes(p)),
      ];
      if (ordered.length) groups.push({ net, programs: ordered });
    }
    if (!groups.length) return null;

    const cellValue = (program: string, q: typeof QUARTER_TABS[0]): number | null => {
      // Combine SF-sourced deals AND user-added rows for this cell — so a
      // brand-new plan with only addedRows still shows a CPM in the grid
      // once the user types one in. Without this, the grid would render
      // "—" for every cell of a manual plan, defeating the program-CPM
      // memory the inheritance step below relies on.
      const dealCpms = planDeals
        .filter(d => d.program === program && d.quarter === q.quarter && d.year === q.year)
        .map(d => d.cpm);
      const addedCpms = planAddedRows
        .filter(r => r.program === program && r.quarter === q.quarter && r.year === q.year)
        .map(r => r.cpm);
      const all = [...dealCpms, ...addedCpms].filter((c): c is number => typeof c === 'number' && c > 0);
      if (!all.length) return null;
      // Most-common value (ties: first-seen wins). Inlined here so we
      // can read from both Deals and AddedRows without juggling
      // generic types — a single typed-helper version was once
      // factored out but is no longer needed.
      const counts = new Map<number, number>();
      let topCpm = all[0];
      let topCount = 0;
      for (const c of all) {
        const n = (counts.get(c) || 0) + 1;
        counts.set(c, n);
        if (n > topCount) { topCount = n; topCpm = c; }
      }
      return topCpm;
    };

    /**
     * Display fallback for blank cells. A program's CPM is treated as
     * uniform across quarters unless the user explicitly varied it, so
     * any quarter without booked schedules (and therefore null cellValue)
     * inherits the value from the program's first populated quarter in
     * calendar order. Display-only — editing a fallback cell is a no-op
     * because setCpmForProgramAllQuarters has no rows to update there.
     */
    const programFallbackCpm = (program: string): number | null => {
      // Iterate the plan's visible quarters (marketplace-driven) so the
      // fallback only considers quarters this plan can actually carry —
      // a CY plan shouldn't inherit a CPM from 4Q'26 because it doesn't
      // display 4Q'26 at all.
      for (const tab of visibleQuarters) {
        const v = cellValue(program, tab);
        if (v != null) return v;
      }
      return null;
    };

    /** Returns the cell's displayed value with fallback applied. */
    const displayedCellValue = (program: string, q: typeof QUARTER_TABS[0]): number | null => {
      const direct = cellValue(program, q);
      if (direct != null) return direct;
      return programFallbackCpm(program);
    };

    /** True when the displayed value comes from the fallback (no deals here). */
    const isCellInherited = (program: string, q: typeof QUARTER_TABS[0]): boolean => {
      return cellValue(program, q) == null && programFallbackCpm(program) != null;
    };

    /**
     * Cell shows orange text when any underlying schedule's CPM has
     * been edited (per-row OR via this bulk grid). Checked program-wide
     * (not just this quarter's deals) because the grid's commit
     * propagates to every quarter — so an edit in any cell visually
     * applies to all quarter cells for that program.
     */
    const isCellEdited = (program: string): boolean => {
      return planDeals.some(d =>
        d.program === program
        && d.baseCpm != null
        && Math.abs(d.cpm - d.baseCpm) > 1e-9
      );
    };

    const handleCommit = async (program: string, q: typeof QUARTER_TABS[0], val: string, inputEl?: HTMLInputElement) => {
      const cpm = parseFloat(val);
      if (isNaN(cpm) || cpm < 0) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        return;
      }
      // Compare against the cell's CURRENT displayed value (which uses
      // inheritance) so editing an inherited cell to its existing value
      // is a no-op. Without this, every blur on a fallback cell would
      // re-push the same CPM through setCpmForProgramAllQuarters.
      const cur = displayedCellValue(program, q);
      if (cur != null && Math.abs(cur - cpm) < 0.005) return;
      // 20% guard. Bulk grid edits propagate to every quarter for the
      // program — guard once on the displayed cell's delta. Skips when
      // cur is null (inherited / empty cell with no anchor value).
      if (cur != null) {
        const ok = await maybeConfirmChange({
          fieldLabel: `CPM (${program}, all quarters)`,
          oldValue: cur,
          newValue: cpm,
        });
        if (!ok) {
          if (inputEl) inputEl.value = inputEl.defaultValue;
          return;
        }
      }
      // Propagate to every quarter for this program in the plan — CPM
      // is a program-level number for the broadcast year, not per-quarter.
      //
      // Locked-row feedback: setCpmForProgramAllQuarters deliberately
      // skips rows with `_cpmManuallyEdited` (a hand-typed per-row CPM
      // outranks a bulk push). But when EVERY candidate row is locked,
      // the bulk edit silently no-ops — the strip keeps showing the
      // typed number while the grid never changes, which reads as a
      // broken input. Detect that case, rewind the strip, and say why.
      const candidates = planDeals.filter(
        d => d.salesPlan === planFilter && d.program === program,
      );
      const lockedCount = candidates.filter(d => d._cpmManuallyEdited).length;
      if (candidates.length > 0 && lockedCount === candidates.length) {
        if (inputEl) inputEl.value = inputEl.defaultValue;
        showToast(
          `${program}: CPM is locked by a per-row edit. Change it on the row ` +
          `itself, or clear the row's CPM back to baseline to re-enable bulk edits.`,
        );
        return;
      }
      setCpmForProgramAllQuarters(planFilter, program, cpm);
    };

    return (
      <div style={{
        marginBottom: 12,
        padding: '10px 12px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 4,
        overflowX: 'auto',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
        }}>
          ENTER CPM — bulk-edits all schedules in scope
        </div>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{
                padding: '4px 8px', textAlign: 'left',
                color: '#64748b', fontWeight: 600,
                background: colors.bgPanel, borderBottom: '1px solid #e2e8f0',
              }}>
                {scope === 'summary' ? 'Quarter' : ''}
              </th>
              {groups.map((g, gi) => (
                <th key={`net-${g.net}`}
                    colSpan={g.programs.length}
                    style={{
                      padding: '4px 8px', textAlign: 'center',
                      color: '#fff', background: '#1e3a8a',
                      fontWeight: 700, fontSize: 11,
                      borderLeft: gi > 0 ? '2px solid #fff' : undefined,
                    }}>
                  {g.net}
                </th>
              ))}
            </tr>
            <tr>
              <th style={{
                padding: '4px 8px', textAlign: 'left',
                color: '#64748b', fontWeight: 600,
                background: colors.bgPanel, borderBottom: '1px solid #e2e8f0',
              }} />
              {groups.map((g, gi) => g.programs.map((p, pi) => (
                <th key={`prog-${p}`}
                    style={{
                      padding: '4px 6px', textAlign: 'center',
                      color: colors.textSecondary, fontWeight: 600, fontSize: 11,
                      background: colors.bgPanel, minWidth: 84,
                      borderBottom: '1px solid #e2e8f0',
                      borderLeft: pi === 0 && gi > 0 ? '2px solid #cbd5e1' : undefined,
                    }}>
                  {PROGRAM_DISPLAY[p] || p}
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {quarters.map(q => (
              <tr key={`row-${q.key}`}>
                <td style={{
                  padding: '4px 8px', fontWeight: 600,
                  color: colors.textSecondary, background: colors.bgPanel,
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  {q.label}
                </td>
                {groups.map((g, gi) => g.programs.map((p, pi) => {
                  // `v` is the value rendered (incl. inheritance fallback).
                  // `inherited` is true when no deals exist in this quarter
                  // and we're showing the program's first-populated CPM.
                  const v = displayedCellValue(p, q);
                  const inherited = isCellInherited(p, q);
                  const edited = isCellEdited(p);
                  return (
                    <td key={`cell-${p}-${q.key}`}
                        style={{
                          padding: '2px 4px', textAlign: 'center',
                          borderBottom: '1px solid #f1f5f9',
                          borderLeft: pi === 0 && gi > 0 ? '2px solid #cbd5e1' : undefined,
                        }}>
                      <input
                        key={`${p}-${q.key}-${v ?? 'null'}-${inputRemountKey}`}
                        type="number"
                        step="0.01"
                        min="0"
                        defaultValue={v != null ? v.toFixed(2) : ''}
                        placeholder="—"
                        readOnly={planIsViewOnly}
                        onBlur={e => handleCommit(p, q, e.target.value, e.target)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        title={
                          // Unreachable when planIsViewOnly — the grid
                          // early-returns above. Kept for defense if that
                          // branch ever changes.
                          planIsViewOnly ? '25/26 plan — read-only reference inventory.'
                          : edited ? 'CPM has been changed from the source value'
                          : inherited ? 'No schedules booked in this quarter yet — editing applies the CPM across every quarter for this program'
                          : 'Editing applies the CPM across every quarter for this program'
                        }
                        style={{
                          width: 64, padding: '4px 6px',
                          textAlign: 'right', fontSize: 11,
                          border: '1px solid #e2e8f0', borderRadius: 3,
                          color: edited ? colors.warn : colors.textDark,
                          fontWeight: edited ? 600 : 400,
                        }}
                      />
                    </td>
                  );
                }))}
              </tr>
            ))}
            {/* ± % bulk-adjust row — one input per program column.
                Typing N (or N%) multiplies every quarter cell for that
                program by (1 + N/100). Empty input does nothing. The
                input clears on apply so the next entry is fresh.
                Per-row CPM locks (`_cpmManuallyEdited`) are respected,
                matching the dollar-set bulk path's policy. */}
            <tr key="bulk-pct-row" style={{ background: colors.bgPanel, borderTop: '1px solid #cbd5e1' }}>
              <td style={{
                padding: '4px 8px', fontWeight: 700, fontSize: 11,
                color: '#1e40af', background: colors.bgPanel,
                borderBottom: '1px solid #e2e8f0',
              }}>
                ± %
              </td>
              {groups.map((g, gi) => g.programs.map((p, pi) => (
                <td key={`pct-${p}`}
                    style={{
                      padding: '2px 4px', textAlign: 'center',
                      borderBottom: '1px solid #e2e8f0',
                      borderLeft: pi === 0 && gi > 0 ? '2px solid #cbd5e1' : undefined,
                    }}>
                  <input
                    type="text"
                    defaultValue=""
                    placeholder="± %"
                    readOnly={planIsViewOnly}
                    onBlur={async e => {
                      const el = e.target as HTMLInputElement;
                      const trimmed = el.value.trim().replace(/%$/g, '').trim();
                      if (trimmed === '') return;
                      const pct = parseFloat(trimmed);
                      if (!Number.isFinite(pct) || pct === 0) return;
                      // 20% guard. Use the representative quarter cell
                      // value (with inheritance fallback) as the anchor
                      // so the modal can quote "old → new" in dollars
                      // instead of just a percent. The bulk action then
                      // applies the factor to every quarter for this
                      // program; per-row locks are still respected
                      // inside applyCpmPercentToProgramAllQuarters.
                      const anchor = displayedCellValue(p, quarters[0]);
                      if (anchor != null && anchor > 0) {
                        const factor = 1 + pct / 100;
                        const newCpm = anchor * factor;
                        const ok = await maybeConfirmChange({
                          fieldLabel: `CPM (${p}, all quarters)`,
                          oldValue: anchor,
                          newValue: newCpm,
                        });
                        if (!ok) { el.value = ''; return; }
                      }
                      applyCpmPercentToProgramAllQuarters(planFilter, p, pct);
                      el.value = '';
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    title="Adjust every quarter's CPM for this program by a percent (e.g. 5, +5, -3). Per-row locked rows are skipped."
                    style={{
                      width: 64, padding: '4px 6px',
                      textAlign: 'right', fontSize: 11,
                      border: '1px solid #cbd5e1', borderRadius: 3,
                      background: '#fff', color: colors.textDark,
                    }}
                  />
                </td>
              )))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  function buildSummaryTableRows(): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    // effectivePlanFilter so the Summary tab on the 25/26 toggle view
    // aggregates the source SF plan's deals instead of the 26/27 plan's.
    //
    // Season-narrow by visibleQuarters: without this filter, a rolled
    // 26/27 plan that shares a nickname with its 25/26 SF source pulls
    // BOTH plans' deals into `planDeals` via nickname consolidation, and
    // every rollup below (program subtotals, network totals, Plan Total
    // grand total via aggregateSummaryMetrics) double-counts — 148
    // units renders as 296, $327k as $703k, etc. Individual quarter
    // rows are unaffected because they filter by (quarter, year), but
    // the aggregate rows below them wouldn't. Same class of bug already
    // fixed for SF Structure preview, push totals, Save path, Summary
    // tab count, and Delete confirm count.
    const summaryVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
    const planDeals = allDeals.filter(d =>
      d.salesPlan === effectivePlanFilter
      && summaryVisibleKeys.has(`${d.quarter}|${d.year}`)
    );

    // Group programs by network, keeping only programs with at least 1 spot in this plan
    const programsWithSpots = Array.from(new Set(planDeals.map(d => d.program)));
    const programsByNet: Record<string, string[]> = {};
    for (const prog of programsWithSpots) {
      const net = NETWORK_MAP[prog] || 'Other';
      if (!programsByNet[net]) programsByNet[net] = [];
      programsByNet[net].push(prog);
    }

    // Use NETWORK_ORDER for stable ordering, falling back to any extra networks
    const orderedNets = [
      ...NETWORK_ORDER.filter(n => programsByNet[n]?.length),
      ...Object.keys(programsByNet).filter(n => !NETWORK_ORDER.includes(n)),
    ];

    for (const net of orderedNets) {
      const progs = programsByNet[net] || [];
      // Sort programs within a network by PROGRAM_ORDER if available
      const order = PROGRAM_ORDER[net] || [];
      const ordered = [
        ...order.filter(p => progs.includes(p)),
        ...progs.filter(p => !order.includes(p)),
      ];

      for (const prog of ordered) {
        const progDeals = planDeals.filter(d => d.program === prog);
        const daytime = DAY_TIME[prog] || '';

        // Render one row per visible quarter (marketplace-driven). For
        // CY plans this is 1Q-4Q of primaryYear; Scatter shows 5 rows.
        for (const tab of visibleQuarters) {
          const quarterDeals = progDeals.filter(d => d.quarter === tab.quarter && d.year === tab.year);
          // Skip quarter rows with zero booked units — they're just noise in the
          // plan summary. The annual rollup below still aggregates everything.
          const totalUnits = quarterDeals.reduce((s, d) => s + (d.totalUnits || 0), 0);
          if (totalUnits <= 0) continue;
          const m = aggregateSummaryMetrics(quarterDeals);
          const yoy = renderAggregateYoYBadge(quarterDeals);
          rows.push(renderSummaryRow(`${prog}-${tab.key}`, prog, tab.label, daytime, m, 'normal', yoy));
        }

        // Annual 25/26 rollup
        const annualM = aggregateSummaryMetrics(progDeals);
        const annualYoy = renderAggregateYoYBadge(progDeals);
        rows.push(renderSummaryRow(`${prog}-annual`, `${prog} ${SEASON_LABEL} Total`, '', daytime, annualM, 'annual', annualYoy));

        // Blank spacer
        rows.push(<tr key={`${prog}-spacer`}><td colSpan={17} style={{ height: 4, background: '#f0f2f5' }} /></tr>);
      }

      // Network total row (across all programs in network and all 4 quarters)
      const netDeals = planDeals.filter(d => ordered.includes(d.program));
      const netM = aggregateSummaryMetrics(netDeals);
      const netYoy = renderAggregateYoYBadge(netDeals);
      rows.push(renderSummaryRow(
        `${net}-total`,
        `${net} ${SEASON_RANGE_LABEL} Total`,
        '',
        '',
        netM,
        'netTotal',
        netYoy,
      ));
      rows.push(<tr key={`${net}-spacer`}><td colSpan={17} style={{ height: 8, background: '#f0f2f5' }} /></tr>);
    }

    // Grand total — aggregates every deal in the plan across all networks,
    // programs, and quarters. Mirrors the Quarterly tab's bottom totals
    // row: the absolute totals (units, equiv, total HH/Demo (000), total
    // dollars) plus the plan CPMs, which aggregateSummaryMetrics already
    // computes as total dollars ÷ total impressions. The per-deal averages
    // are suppressed because they don't average meaningfully across the
    // whole plan — zeroing them lets the row formatters render dashes.
    if (planDeals.length > 0) {
      const grandM = aggregateSummaryMetrics(planDeals);
      const grandRow = {
        ...grandM,
        avgRtgHH: 0,
        avgHH: 0,
        avgRtgDemo: 0,
        vpvh: 0,
        avgDemo: 0,
        avgEquivUnitRate: 0,
      };
      const grandYoy = renderAggregateYoYBadge(planDeals);
      rows.push(renderSummaryRow(
        'grand-total',
        `Plan Total ${SEASON_RANGE_LABEL}`,
        '',
        '',
        grandRow,
        'netTotal',
        grandYoy,
      ));
    }

    return rows;
  }

  /**
   * One rolled-up row for the All Plans view: every schedule sharing a
   * (plan, program, quarter) collapses into a single line.
   *
   * Why All Plans rolls up but the single-plan quarter tabs don't: those tabs
   * are where you edit individual schedules, so they have to show them
   * individually. All Plans is read-only (planIsViewOnly is true whenever
   * there's no planFilter) and spans every plan, so line-item detail there is
   * noise — two schedules on the same show and quarter differing only by unit
   * count read as duplicate rows.
   *
   * Aggregation is delegated to aggregateSummaryMetrics, the same helper the
   * Summary tab uses, rather than re-derived here. It already handles the
   * parts that are easy to get wrong: units/impressions/cost are summed,
   * while CPM and rating are impression-weighted (NOT averages of the
   * per-row values), and per-row overrideDemo000 is honoured.
   *
   * Rollup never crosses a plan boundary. Different plans can carry
   * different primary demos, so combining them would average A18-49 against
   * A25-54 impressions and produce a meaningless CPM.
   *
   * Column layout mirrors renderDealRow's All Plans output exactly —
   * 19 cells plus two per secondary demo. Cells with no meaningful
   * aggregate (Original CPM, the secondary-demo pairs) render empty, the
   * same convention the subtotal rows use.
   */
  function renderAllPlansRollupRow(
    salesPlan: string,
    program: string,
    quarterLabel: string,
    deals: Deal[],
  ) {
    const m = aggregateSummaryMetrics(deals);
    // Uniform-or-dash: these are row-identity fields, so a mixed group has
    // no single right answer and a dash is more honest than picking one.
    const uniq = (vals: (string | number)[]) => Array.from(new Set(vals.map(v => String(v ?? '')).filter(Boolean)));
    const lens = uniq(deals.map(d => d.unitLength));
    const demos = uniq(deals.map(d => d.demo || ''));
    // Average per-spot rate, weighted by spend rather than a mean of rates.
    const avgRate = m.units > 0 ? m.totalCost / m.units : 0;
    return (
      <tr key={`rollup-${salesPlan}-${program}-${quarterLabel}`}>
        <td className="program sticky-col">
          <div className="prog-cell"><span className="prog-cell-name">{program}</span></div>
        </td>
        <td className="num muted">{quarterLabel}</td>
        <td className="num muted">{demos.length === 1 ? demos[0] : '—'}</td>
        <td className="num muted">{DAY_TIME[program] || ''}</td>
        <td className="num muted len">{lens.length === 1 ? lens[0] : '—'}</td>
        <td className="num">{m.units.toFixed(0)}</td>
        <td className="num muted">{m.equiv > 0 ? m.equiv.toFixed(1) : '—'}</td>
        <td className="num muted">{m.avgRtgHH > 0 ? m.avgRtgHH.toFixed(3) : '—'}</td>
        <td className="num">{m.avgHH > 0 ? fmt(m.avgHH) : '—'}</td>
        <td className="num">{m.totalHH > 0 ? fmt(m.totalHH) : '—'}</td>
        <td className="num">{m.cpmHH > 0 ? '$' + m.cpmHH.toFixed(2) : '—'}</td>
        <td className="num muted">{m.avgRtgDemo != null && m.avgRtgDemo > 0 ? m.avgRtgDemo.toFixed(3) : '—'}</td>
        <td className="num muted">{m.vpvh > 0 ? m.vpvh.toFixed(3) : '—'}</td>
        <td className="num" style={{ textAlign: 'center' }}>
          {m.avgDemo > 0 ? fmt(m.avgDemo) : '—'}
          {renderAggregateYoYBadge(deals)}
        </td>
        <td className="num">{m.totalDemo > 0 ? fmt(m.totalDemo) : '—'}</td>
        {/* Original CPM — each schedule carries its own frozen v1 value, so
            the group has no single "original". */}
        <td className="num muted">—</td>
        <td className="num">{m.cpmDemo > 0 ? '$' + m.cpmDemo.toFixed(2) : '—'}</td>
        {secondaryDemos.map(d => <React.Fragment key={d}><td /><td /></React.Fragment>)}
        <td className="num">{avgRate > 0 ? '$' + fmt(Math.round(avgRate)) : '—'}</td>
        <td className="num">{m.totalCost > 0 ? '$' + fmt(m.totalCost) : '—'}</td>
      </tr>
    );
  }

  function buildTableRows(): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    // Filter manual rows by sales plan AND active quarter tab — rows added on
    // one tab shouldn't appear when the user switches to a different quarter.
    // Summary tab has no quarter filter (shows all manual rows for the plan).
    const isSummary = activeQuarter === SUMMARY_TAB_KEY;
    // Look in visibleQuarters first so the active tab resolves even when
    // it's a synthesized quarter (4Q'27 etc not in the estimate version).
    const tab = isSummary
      ? null
      : (visibleQuarters.find(t => t.key === activeQuarter)
        || QUARTER_TABS.find(t => t.key === activeQuarter));
    // Use effectivePlanFilter so the 25/26 toggle view doesn't pull in
    // the 26/27 plan's unsaved Add Row entries. SF source plans don't
    // have addedRows anyway, so planAddedRows is empty in 25/26 view.
    const planAddedRows = addedRows.filter(r =>
      r.salesPlan === effectivePlanFilter &&
      (!tab || (r.quarter === tab.quarter && r.year === tab.year))
    );
    const totalCols = 18;

    // Sort: salesPlan (all-plans only) → network order → program order within network
    const renderDeals = [...filteredDeals].sort((a, b) => {
      if (!effectivePlanFilter) {
        const sp = a.salesPlan.localeCompare(b.salesPlan);
        if (sp !== 0) return sp;
      }
      const niA = NETWORK_ORDER.indexOf(NETWORK_MAP[a.program] || '');
      const niB = NETWORK_ORDER.indexOf(NETWORK_MAP[b.program] || '');
      const na = niA === -1 ? 999 : niA;
      const nb = niB === -1 ? 999 : niB;
      if (na !== nb) return na - nb;
      const net = NETWORK_MAP[a.program] || '';
      const po = PROGRAM_ORDER[net] || [];
      const piA = po.indexOf(a.program); const piB = po.indexOf(b.program);
      return (piA === -1 ? 999 : piA) - (piB === -1 ? 999 : piB);
    });

    const netHdrRow = (network: string, key: string, bg: string) => (
      <tr key={`net-hdr-${key}`} className="net-hdr-row" style={{ background: bg, borderTop: '2px solid #c8d3e8' }}>
        {/* Network name pinned in column 0 so it survives a horizontal
            scroll; the remainder of the original full-width span becomes a
            filler cell. +1 in All Plans view for the inserted Demo column. */}
        <td className="sticky-col"
          style={{ padding: '3px 12px', fontSize: 13.5, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.1em', background: bg, whiteSpace: 'nowrap' }}>
          {network}
        </td>
        <td colSpan={totalCols - 1 + (!planFilter ? 1 : 0) + secondaryDemos.length * 2} />
      </tr>
    );

    /**
     * Add-a-row control for a program, rendered inside that program's name
     * row rather than as a row of its own.
     *
     * It was briefly its own table row sitting before the subtotal, which
     * cost a blank row per program — ten programs meant ten rows of dead
     * space. Sitting inline beside the name didn't work either: a labelled
     * pill next to a 13px show name reads as the loudest thing in the block.
     *
     * So it goes on its own LINE inside the name row — .band-label is a
     * column flex, name first, link underneath. Same footprint whatever the
     * name's length (no reflow between short and long shows), still no extra
     * table row, and the link keeps its text label because it has the line
     * to itself and isn't crowding anything.
     *
     * Returns null on view-only plans (SF-pushed, and the 25/26 toggle view).
     * React renders a null child as nothing, so callers don't need a guard.
     */
    const addRowIcon = (prog: string) => {
      if (planIsViewOnly) return null;
      return (
        <button className="icon-link"
          onClick={() => addRow(prog, planFilter)}
          title={`Add another row to ${prog} (e.g. a different unit length)`}>
          <PlusCircleIcon /><span>Add Row</span>
        </button>
      );
    };

    // Helper: group an ordered array of deals into [[network, [program, deals[]][]][]]
    // preserving the sorted order
    function groupDeals(deals: Deal[]) {
      const networks: { net: string; programs: { prog: string; deals: Deal[] }[] }[] = [];
      for (const d of deals) {
        const net = NETWORK_MAP[d.program] || 'Other';
        let netGroup = networks.find(n => n.net === net);
        if (!netGroup) { netGroup = { net, programs: [] }; networks.push(netGroup); }
        let progGroup = netGroup.programs.find(p => p.prog === d.program);
        if (!progGroup) { progGroup = { prog: d.program, deals: [] }; netGroup.programs.push(progGroup); }
        progGroup.deals.push(d);
      }
      // Within-program sort:
      //   1. If the user clicked a column header (sortCol !== 'program',
      //      the default), respect that column + direction first.
      //   2. Then fall back to length ascending, then newRate descending
      //      as deterministic tiebreakers (matches the legacy "show
      //      shorter spots before longer at the same rate, highest rate
      //      first within a length" ordering).
      // This makes the column sort headers actually work — previously
      // the visible row order was determined entirely by these
      // tiebreakers, even when the user had picked a different column.
      const lenRateCompare = (a: Deal, b: Deal) => {
        const lenA = parseFloat(String(a.unitLength)) || 0;
        const lenB = parseFloat(String(b.unitLength)) || 0;
        if (lenA !== lenB) return lenA - lenB;
        return (b.newRate || 0) - (a.newRate || 0);
      };
      const userSortActive = sortCol && sortCol !== 'program';
      const userSortCompare = (a: Deal, b: Deal) => {
        const key = sortCol as keyof Deal;
        const av = a[key];
        const bv = b[key];
        // String-valued columns (period, day, unitLength) use locale
        // compare; numeric columns use numeric subtraction. unitLength
        // arrives as a string ("15","30",…) so we coerce to number to
        // get the natural ordering.
        if (sortCol === 'program' || sortCol === 'salesPlan' || sortCol === 'demo') {
          return String(av ?? '').localeCompare(String(bv ?? '')) * sortDir;
        }
        return (Number(av) - Number(bv)) * sortDir;
      };
      for (const netGroup of networks) {
        for (const progGroup of netGroup.programs) {
          progGroup.deals.sort((a, b) => {
            if (userSortActive) {
              const u = userSortCompare(a, b);
              if (u !== 0) return u;
            }
            return lenRateCompare(a, b);
          });
        }
      }
      return networks;
    }


    if (!planFilter) {
      // ── ALL-PLANS VIEW: salesPlan → network → program ──────────────────
      const planOrder: string[] = [];
      const dealsByPlan: Record<string, Deal[]> = {};
      for (const d of renderDeals) {
        if (!dealsByPlan[d.salesPlan]) { planOrder.push(d.salesPlan); dealsByPlan[d.salesPlan] = []; }
        dealsByPlan[d.salesPlan].push(d);
      }
      // Full-width header spans for All Plans view. Base layout = 17 data
      // columns + 1 extra row-meta column (the legacy +1 the single-plan
      // branch carries through), and +1 again for the Demo column that
      // All Plans inserts between Period and Day/Time.
      const allPlansFullCols = 20 + secondaryDemos.length * 2;
      for (const sp of planOrder) {
        rows.push(
          <tr key={`sp-${sp}`} className="plan-header" style={{ background: '#e8edf5' }}>
            <td colSpan={allPlansFullCols} style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: colors.textSecondary }}>📋 {sp}</td>
          </tr>
        );
        for (const { net, programs } of groupDeals(dealsByPlan[sp])) {
          const allNetDeals = programs.flatMap(p => p.deals);
          rows.push(netHdrRow(net, `${sp}-${net}`, '#f1f5f9'));
          for (const { prog, deals } of programs) {
            rows.push(
              <tr key={`prog-${sp}-${prog}`} style={{ background: '#ffffff' }}>
                {/* Program name pinned; remainder of the old full-width
                    span becomes filler. */}
                <td className="sticky-col" style={{ padding: '4px 12px', fontSize: 11.5, color: colors.textSecondary, fontWeight: 600, background: '#ffffff', whiteSpace: 'nowrap' }}>
                  {prog}
                </td>
                <td colSpan={allPlansFullCols - 1} />
              </tr>
            );
            // One rolled-up row per quarter instead of one per schedule.
            // Iterating visibleQuarters (rather than grouping and sorting)
            // gets chronological order for free — the previous code emitted
            // deals in array order, so a program's quarters came out
            // scrambled (3Q, 1Q, 2Q, 1Q…) and read as duplicate rows.
            // Quarters with nothing booked are skipped rather than shown
            // empty.
            for (const q of visibleQuarters) {
              const qDeals = deals.filter(d => d.quarter === q.quarter && d.year === q.year);
              if (!qDeals.length) continue;
              if (qDeals.reduce((s, d) => s + (d.totalUnits || 0), 0) <= 0) continue;
              rows.push(renderAllPlansRollupRow(sp, prog, q.label, qDeals));
            }
          }
          rows.push(renderNetworkTotalRow(net, allNetDeals, [], sp));
        }
      }
    } else {
      // ── SINGLE-PLAN VIEW: network → program ────────────────────────────
      // effectivePlanFilter so the 25/26 toggle view filters to the
      // source SF plan's deals instead of the 26/27 plan's.
      const planOnlyDeals = renderDeals.filter(d => d.salesPlan === effectivePlanFilter);
      const existingGroups = groupDeals(planOnlyDeals);
      const existingNets = new Set(existingGroups.map(g => g.net));
      const addedNets = new Set(planAddedRows.map(r => NETWORK_MAP[r.program] || 'Other'));
      const allNets = NETWORK_ORDER.filter(n => existingNets.has(n) || addedNets.has(n));

      // Add Show used to be a full-width strip row here. It now lives in the
      // PROGRAM column header instead — one fewer row, and the control sits
      // with the column it acts on. See the thead.

      for (const net of allNets) {
        const netGroup = existingGroups.find(g => g.net === net);
        const programs = netGroup?.programs || [];
        const allNetDeals = programs.flatMap(p => p.deals);
        const netAddedRows = planAddedRows.filter(r => (NETWORK_MAP[r.program] || 'Other') === net);
        rows.push(netHdrRow(net, net, '#ffffff'));

        // Existing programs
        for (const { prog, deals } of programs) {
          rows.push(
            <tr key={`prog-${prog}`} className="plan-header">
              {/* Program name lives in column 0 so it pins on horizontal
                  scroll — it used to sit in the wide span, which slid out
                  of view. The name is now the ONLY thing in this row: Add
                  Row moved to its own row below the schedules. */}
              <td className="sticky-col" style={{ padding: '4px 8px' }}>
                <div className="band-label">
                  <span className="prog-name">{prog}</span>
                  {addRowIcon(prog)}
                </div>
              </td>
              <td colSpan={17 + secondaryDemos.length * 2} style={{ padding: '5px 8px' }} />
            </tr>
          );
          const programCpmHh = computeProgramCpmHh(deals);
          deals.forEach(d => rows.push(renderDealRow(d, programCpmHh)));
          const progAddedRows = planAddedRows.filter(r => r.program === prog);
          progAddedRows.forEach(r => rows.push(renderAddedRow(r)));
          rows.push(renderSubtotalRow(prog, deals, planAddedRows));
        }

        // New programs added via Add Show (not yet in plan)
        const newProgs = Array.from(new Set(netAddedRows.map(r => r.program)))
          .filter(p => !programs.some(pg => pg.prog === p));
        for (const prog of newProgs) {
          const progAddedRows = netAddedRows.filter(r => r.program === prog);
          rows.push(
            <tr key={`new-prog-hdr-${prog}`} className="plan-header new-prog-row" style={{ background: colors.warnBg }}>
              {/* Same as the saved-program header above: name only, in
                  column 0 so it pins. display:flex on a sticky cell is fine
                  — position:sticky applies to the box, not its inner layout
                  mode. */}
              <td className="sticky-col" style={{ padding: '4px 8px', background: colors.warnBg }}>
                <div className="band-label">
                  <span className="prog-name" style={{ color: '#92400e' }}>
                    {prog}{' '}
                    <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3, fontWeight: 700 }}>NEW</span>
                  </span>
                  {addRowIcon(prog)}
                </div>
              </td>
              <td colSpan={17 + secondaryDemos.length * 2} style={{ padding: '5px 8px', color: '#92400e' }} />
            </tr>
          );
          progAddedRows.forEach(r => rows.push(renderAddedRow(r)));
          rows.push(renderSubtotalRow(prog, [], planAddedRows));
        }

        rows.push(renderNetworkTotalRow(net, allNetDeals, netAddedRows));
      }
    }

    rows.push(renderTotalsRow());
    return rows;
  }

  /**
   * Demo universe for the "+ Add Demo" (secondary demo) picker.
   *
   * Sourced from the ACTIVE ESTIMATE VERSION's `universeData`, not the
   * hardcoded ALL_ESTIMATE_DEMOS constant. Rationale:
   *
   *   • Secondary demos render real (000s) + CPM columns, so every
   *     option must have estimate rows behind it. `universeData` is
   *     keyed by the exact demo strings the uploaded file carries, so
   *     it can't offer a demo that would render as dashes.
   *   • The constant had already drifted from reality — it was missing
   *     M25+, M45-64 and M65+ (present in the estimate, so silently
   *     unpickable) while listing demos the file doesn't carry.
   *   • Self-maintaining: the next estimate upload updates the picker
   *     with no code change. Mirrors what + Add Show already does for
   *     programs (see the programNameIndex gate in the Add Show modal).
   *
   * Note this deliberately does NOT read the Salesforce primary-demo
   * picklist. SF governs what's a legal *primary* demo (a label pushed
   * to Sales_Plan__c); the estimate governs what can actually be
   * measured. A demo can exist in SF with no estimate coverage — e.g.
   * M21-54 — and must stay out of this list.
   *
   * Falls back to the constant only when no estimate is bound yet, so
   * the picker is never empty on a cold load.
   */
  const estimateDemoUniverse = useMemo<string[]>(() => {
    const ud = activeVersion?.universeData;
    if (!ud) return ALL_ESTIMATE_DEMOS;
    const keys = Object.keys(ud)
      // HH is the household baseline, rendered in its own fixed columns
      // — not a selectable "secondary demo".
      .filter(d => d.toUpperCase() !== 'HH')
      .sort();
    return keys.length > 0 ? keys : ALL_ESTIMATE_DEMOS;
  }, [activeVersion]);

  const filteredDemoOpts = useMemo(() => {
    const primaryDemo = filteredDeals.length ? (filteredDeals[0].demo || '') : '';
    return estimateDemoUniverse.filter(d =>
      d !== primaryDemo && !secondaryDemos.includes(d) &&
      d.toLowerCase().includes(demoSearchFilter.toLowerCase())
    );
  }, [estimateDemoUniverse, filteredDeals, secondaryDemos, demoSearchFilter]);

  // "Deletable" = any plan the user owns and can remove from this UI:
  //   - legacy `_manual` flagged deals
  //   - Snowflake-saved plan (soft-deletable via API)
  // Pure SF templates aren't deletable from here — Salesforce owns them.
  const isDeletablePlan = !!planFilter && (
    allDeals.some(d => d.salesPlan === planFilter && d._manual)
    || getSavedPlanByName(planFilter) != null
  );

  // ─── Click outside to close demo dropdown ────────────────────────────────────
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (demoDropdownRef.current && !demoDropdownRef.current.contains(e.target as Node)) {
        setDemoPillDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ─── Click outside / Esc to close version-history popup ──────────────────────
  // Gated on `showVersionHistory` so the global listeners only run while
  // the popup is open. The old snapshot modal is gone — historical
  // versions now load INTO the grid, so there's no second surface to
  // dismiss on Esc.
  useEffect(() => {
    if (!showVersionHistory) return;
    function handleClick(e: MouseEvent) {
      if (versionChipWrapperRef.current && !versionChipWrapperRef.current.contains(e.target as Node)) {
        setShowVersionHistory(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowVersionHistory(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showVersionHistory]);

  // ─── Close version popup + revert to latest when active plan changes ────────
  // Popup is anchored to the current plan's chip; a plan switch should
  // dismiss it. If we're currently viewing a historical version and the
  // user navigates to a different plan, quietly clear the viewingRef —
  // when they come back to this plan we'll refetch fresh data anyway.
  useEffect(() => {
    setShowVersionHistory(false);
    setVersionHistory(null);
    setVersionHistoryErr(null);
    setVersionHistoryLoading(false);
    setViewingHistoricalRef(null);
    // Clear diff baseline too — belongs to the previous plan's history.
    setHistoricalDiffBaseline(null);
  }, [planFilter]);

  // ─── Click outside to close status picklist ──────────────────────────────────
  // Same pattern as the demo dropdown. Gated on `statusDropdownOpen` so
  // we only register a listener while the picklist is actually open —
  // avoids running on every click everywhere else in the app.
  useEffect(() => {
    if (!statusDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);

  // ─── Click outside to close roll-forward picker ──────────────────────────────
  useEffect(() => {
    if (!npRollForwardOpen) return;
    function handleClick(e: MouseEvent) {
      if (npRollForwardRef.current && !npRollForwardRef.current.contains(e.target as Node)) {
        setNpRollForwardOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [npRollForwardOpen]);

  // ─── Click outside to close pill version picker ─────────────────────────────
  useEffect(() => {
    if (!pillDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (pillDropdownRef.current && !pillDropdownRef.current.contains(e.target as Node)) {
        setPillDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pillDropdownOpen]);

  // ─── Click outside to close the unified Load Plan picker ────────────────────
  // One handler covers the combined picker (input + dropdown).
  // Resets the query too so the next open starts from a clean state.
  useEffect(() => {
    if (!planPickerOpen) return;
    function handleClick(e: MouseEvent) {
      if (planPickerRef.current && !planPickerRef.current.contains(e.target as Node)) {
        setPlanPickerOpen(false);
        setPlanSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [planPickerOpen]);

  // ─── Chunked rendering: grow + reset hooks ──────────────────────────────────
  //
  // Must live ABOVE the early-return below — React's rules-of-hooks
  // forbid hooks from running conditionally, so all useEffects need to
  // sit above any `return` that's not the final one.
  //
  // Build the full row array ONCE per render here so both the growth
  // useEffect (which needs .length to know when to stop) and the
  // <tbody> JSX below (which slices to visibleRowCount) share the
  // same array. Earlier versions used an estimated upper bound
  // (filteredDeals.length + 50), but the real row count includes plan
  // headers, network headers, program headers, and per-network totals
  // — easily 1.5×–2× the deal count on All Plans — and the estimate
  // would stop the streamer short, silently dropping rows past the
  // ceiling. Calling buildTableRows here costs ~30-50ms per render
  // even at scale, vastly less than the second-scale cost of actually
  // mounting the rows to the DOM. Safe to call before hydration:
  // returns an empty array when filteredDeals is empty.
  const allTableRows = buildTableRows();

  // Grow visibleRowCount one chunk at a time after each paint until
  // the whole table is rendered. setTimeout(50) (rather than
  // requestAnimationFrame) yields long enough for the browser to
  // process any user input between chunks.
  useEffect(() => {
    if (visibleRowCount >= allTableRows.length) return;
    const handle = setTimeout(() => {
      setVisibleRowCount(c => Math.min(c + TABLE_CHUNK_SIZE, allTableRows.length));
    }, 50);
    return () => clearTimeout(handle);
  }, [visibleRowCount, allTableRows.length]);

  // Reset to the first chunk whenever the underlying data set changes
  // (different season, plan, quarter, sort) so the user always sees
  // the head of the new view immediately rather than waiting for the
  // streamer to catch up to the previous count.
  //
  // Deps simplification: `filteredDeals` is already a useMemo that
  // depends on effectivePlanFilter, activeQuarter, sortCol, sortDir,
  // allPlansSeason, and (transitively) seasonView. Its reference
  // changes whenever any of those change, so observing it alone is
  // sufficient — listing the other deps in addition caused this effect
  // to fire twice per data change (once on the upstream state, once
  // on filteredDeals refreshing), causing a brief flash of "first 100
  // rows" before the streamer caught up.
  useEffect(() => {
    setVisibleRowCount(TABLE_CHUNK_SIZE);
  }, [filteredDeals]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (!estimatesHydrated || !dataLoaded) {
    return (
      <MediaPlanBuilderLoadingScreen
        estimatesHydrated={estimatesHydrated}
        dataLoaded={dataLoaded}
        plansHydrated={plansHydrated}
        bindingsHydrated={isSfBindingsHydrated()}
        sourceDealsError={sourceDealsError ?? null}
      />
    );
  }

  // minHeight on the root div is `100vh - 64px` (not `100vh`) because
  // the parent StyledContent (App.tsx) has margin-top:64 to clear the
  // fixed Nosey toolbar. With plain minHeight:100vh, MPB would extend
  // 64px past the viewport → the WINDOW becomes scrollable → as the
  // user scrolls the window, the inner table-scroll container's top
  // slides up under the sticky bar → the table thead, sticky-anchored
  // at top:0 of that container, ends up rendering BEHIND the sticky
  // bar. Tying minHeight to the actual visible area kills the window
  // scroll, so the only scroll is the inner table one, and the sticky
  // thead always anchors right below the sticky bar.

  // ── Plan-action pills (rendered into the Plan Details panel header) ──
  //
  // The Status pill (clickable picklist on 26/27 plans, static badge on
  // 25/26 surfaces) and the Estimates-as-of pill (with the SF-binding
  // version picker on 25/26 SF source plans) used to live in the stats
  // bar, top-right. They moved into the Plan Details panel so the
  // user has all per-plan controls in one place. Extracted as render
  // variables here to keep the JSX render tree readable.

  const planStatusPillEl: React.ReactNode = planFilter ? (() => {
    // On the 25/26 toggle view, surface the source SF plan's
    // status (always synthetic Order) rather than the 26/27
    // plan's status — the user is staring at the source.
    const statusPlanName = (seasonView === '25/26' && sourcePlanInAllDeals)
      ? sourcePlanInAllDeals
      : planFilter;
    const status = getPlanStatus(statusPlanName);
    const palette: Record<'working' | 'hold' | 'order',
      { bg: string; border: string; text: string }> = {
      working: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
      hold:    { bg: colors.warnBg, border: '#fcd34d', text: '#92400e' },
      order:   { bg: '#ecfdf5', border: '#a7f3d0', text: '#047857' },
    };
    const tone = palette[status.tone];
    const title =
      isViewOnly                              ? 'This is 25/26 inventory — status is fixed at Order.'
      : (seasonView === '25/26')              ? 'Viewing 25/26 source — switch to 26/27 to change status.'
      : status.tone === 'working'             ? 'Plan is actively being worked on. Click to change.'
      : status.tone === 'hold'                ? 'Plan is on hold — not yet ordered. Click to change.'
      :                                         'Plan is fully ordered. Click to change.';

    // Pushed-to-SF: render a read-only locked badge instead of the
    // interactive status pill. Pushed plans are terminal one-way — the
    // status will never change again, and the SF sales plan id gives a
    // handy jump-link into the SF org for humans investigating the plan.
    if (isPushed && currentSavedPlanForLock) {
      const pushedAtRaw = currentSavedPlanForLock.pushedToSfAt ?? '';
      const pushedAtFriendly = pushedAtRaw
        ? formatVersionDateExternal(pushedAtRaw) || pushedAtRaw.slice(0, 10)
        : '';
      const sfId = currentSavedPlanForLock.sfSalesPlanId;
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <div
            title={`Pushed to Salesforce on ${pushedAtFriendly || 'unknown date'} — plan is locked.`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px',
              background: palette.order.bg,
              border: `1px solid ${palette.order.border}`,
              borderRadius: 999,
              fontSize: 11.5, fontWeight: 600, color: palette.order.text,
              whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden="true">🔒</span>
            Order • Pushed to SF{pushedAtFriendly ? ` on ${pushedAtFriendly}` : ''}
          </div>
          {/* Requires BOTH the record id and a known org. sfInstanceUrl comes
              from the backend (see the fetch near the top of this component)
              so the link always points at the same org the push authenticated
              against. Hidden while unknown — a missing link beats one that
              silently resolves against the wrong org. */}
          {sfId && sfInstanceUrl && (
            <a
              href={`${sfInstanceUrl}/${sfId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 11.5, fontWeight: 600,
                color: colors.primary, textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
              title="Open the Sales Plan record in Salesforce (new tab)"
            >
              View in Salesforce ↗
            </a>
          )}
        </div>
      );
    }
    if (planIsViewOnly) {
      return (
        <div title={title} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px',
          background: tone.bg, border: `1px solid ${tone.border}`,
          borderRadius: 999,
          fontSize: 11.5, fontWeight: 600, color: tone.text,
          whiteSpace: 'nowrap',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: tone.text, display: 'inline-block',
          }} />
          Status: {status.label}
        </div>
      );
    }
    return (
      <div ref={statusDropdownRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setStatusDropdownOpen(o => !o)}
          title={title}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px',
            background: tone.bg, border: `1px solid ${tone.border}`,
            borderRadius: 999,
            fontSize: 11.5, fontWeight: 600, color: tone.text,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: tone.text, display: 'inline-block',
          }} />
          Status: {status.label}
          <span style={{ fontSize: 9, marginLeft: 2, opacity: 0.7 }}>▾</span>
        </button>
        {statusDropdownOpen && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: '#fff', border: '1px solid #d1d5db',
            borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 50, minWidth: 130, padding: 4,
          }}>
            {PLAN_STATUSES.map(opt => {
              const optTone = palette[opt.toLowerCase() as 'working' | 'hold' | 'order'];
              const selected = opt === status.label;
              // Gate the Order option for two distinct reasons:
              //   1. Same Sales Plan Name is already pushed by another
              //      26/27 plan (scenario-variant flow; only ONE can
              //      be live in SF at a time).
              //   2. This plan still has TBD placeholders on any of
              //      Advertiser / Brand / Agency / Buyer — SF's lookup
              //      fields need real records, not placeholders.
              // Either reason disables the option with a tooltip that
              // spells out what needs fixing before push can happen.
              const isOrderOption = opt.toLowerCase() === 'order';
              const orderBlockedByTbd = isOrderOption && pushTbdFields.length > 0;
              const orderBlockedBySpn = isOrderOption && pushSpnConflictNickname !== null;
              const orderBlocked = orderBlockedByTbd || orderBlockedBySpn;
              const orderBlockedTitle = orderBlockedByTbd
                ? `Cannot push — ${pushTbdFields.join(', ')} ${pushTbdFields.length === 1 ? 'is' : 'are'} still set to TBD. Edit the plan and pick real Salesforce ${pushTbdFields.length === 1 ? 'value' : 'values'} before pushing.`
                : orderBlockedBySpn
                ? `Cannot push — "${pushSpnConflictNickname}" is already pushed to Salesforce with the same Sales Plan Name. Edit this plan's Advertiser / Brand / Buyer / Demo to differentiate, or wait for the other plan to be revised/released.`
                : undefined;
              return (
                <button
                  key={opt}
                  onClick={() => { if (!orderBlocked) void changeStatus(opt); }}
                  disabled={orderBlocked}
                  title={orderBlockedTitle}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', padding: '6px 10px',
                    background: selected ? optTone.bg : 'transparent',
                    border: 'none', borderRadius: 4,
                    color: orderBlocked ? colors.textDisabled : optTone.text,
                    fontSize: 12, fontWeight: 600,
                    cursor: orderBlocked ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    opacity: orderBlocked ? 0.55 : 1,
                  }}
                  onMouseEnter={e => {
                    if (!selected && !orderBlocked) e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={e => {
                    if (!selected && !orderBlocked) e.currentTarget.style.background = 'transparent';
                  }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: orderBlocked ? colors.textDisabled : optTone.text,
                    display: 'inline-block',
                  }} />
                  {opt}
                  {selected && <span style={{ marginLeft: 'auto', fontSize: 11 }}>✓</span>}
                  {orderBlocked && <span style={{ marginLeft: 'auto', fontSize: 10 }}>🔒</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  })() : null;

  const planEstimatePillEl: React.ReactNode = activeVersion ? (() => {
    // Find the SF id of the current plan when applicable. SF source
    // plans carry the plan's SF id on every deal's salesPlanSfId
    // field; we read the first matching deal. Null for 26/27 saved/
    // manual plans and the All Plans view — those branches don't
    // get the picker.
    const currentSfPlanSfId: string | null = (
      isViewOnly && planFilter
    ) ? (allDeals.find(d => d.salesPlan === planFilter)?.salesPlanSfId ?? null)
      : null;
    const isPillClickable = !!currentSfPlanSfId;

    const dropdownVersions = isPillClickable
      ? listVersionsByYear(LAST_SEASON_PRIMARY_YEAR)
      : [];
    const sortedDropdown = [...dropdownVersions].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'upfront' ? -1 : 1;
      return b.id - a.id;
    });

    // Must use the SAME scoping as `dropdownVersions` above, which is
    // general-only. If these two disagree, `persistedVersionId` below can
    // point at a version that isn't in the dropdown: the green persisted
    // badge never renders on any row, `isActive` never matches, and the
    // pending-change button misfires — with no way for the user to select
    // the version the plan actually resolves to.
    //
    // Also matches resolveSfSourceVersion's default, which is what actually
    // decides the estimate for an unbound plan. All three have to agree.
    const defaultVersion = isPillClickable
      ? getLatestGeneralVersionByYear(LAST_SEASON_PRIMARY_YEAR)
      : null;
    const binding = currentSfPlanSfId ? getSfPlanBinding(currentSfPlanSfId) : null;
    const persistedVersionId = binding?.estimateVersionId ?? defaultVersion?.id ?? null;
    // baseVersionIdOf throughout: this pill only ever shows on 25/26 SF source
    // plans, which never take the late-quarter merge — but comparing and
    // persisting the REAL id costs nothing and keeps the invariant ("a
    // synthetic id never leaves the component") true without exception.
    const hasPendingBindingChange = isPillClickable
      && activeVersion != null
      && persistedVersionId != null
      && baseVersionIdOf(activeVersion) !== persistedVersionId;

    const handleSaveBinding = async () => {
      if (!currentSfPlanSfId || !activeVersion) return;
      const realVersionId = baseVersionIdOf(activeVersion);
      if (realVersionId == null) return;
      setSavingBinding(true);
      const userEmail = getEmailFromSessionStorage() || undefined;
      const result = await setSfPlanBinding(currentSfPlanSfId, realVersionId, userEmail);
      setSavingBinding(false);
      if (!result.ok) {
        showToast('Save failed: ' + result.error);
      } else {
        showToast(`Locked plan to ${formatEstimateStream(activeVersion.kind, cardOf(activeVersion))} ${getVersionLabel(activeVersion)}.`);
      }
    };

    const pillTooltip =
      isPillClickable
        ? `Click to switch estimate version. Current: ${formatEstimateStream(activeVersion.kind, cardOf(activeVersion))} ${getVersionLabel(activeVersion)}.`
      : planFilter
        ? `This plan is locked to ${formatEstimateStream(activeVersion.kind, cardOf(activeVersion))} ${getVersionLabel(activeVersion)}. New estimate uploads will not affect it.`
        : `Latest uploaded estimate version. New sales plans created from here use this version; existing plans keep whichever version they were created with.`;

    const pillCommonStyle: React.CSSProperties = {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px',
      background: '#eff6ff', border: '1px solid #bfdbfe',
      borderRadius: 999,
      fontSize: 11.5, fontWeight: 600, color: '#1e40af',
      whiteSpace: 'nowrap',
    };

    const pillBody = (
      <>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        Estimates as of {formatAsOf(activeVersion.asOfDate)}
        {/* Three tones, one per stream: violet for a custom agency card,
            amber for scatter, blue for the general upfront master. Keying
            on kind alone rendered "WPP V1" in the Upfront blue, which reads
            as an ordinary upfront at a glance. Matches VersionsTab. */}
        <span style={{
          marginLeft: 4, padding: '0 5px', borderRadius: 4,
          background: cardOf(activeVersion) !== 'general'
            ? '#ede9fe'
            : activeVersion.kind === 'scatter' ? '#fef3c7' : '#dbeafe',
          color: cardOf(activeVersion) !== 'general'
            ? '#5b21b6'
            : activeVersion.kind === 'scatter' ? '#78350f' : '#1e3a8a',
          fontSize: 10, fontWeight: 700,
        }}>
          {formatEstimateStream(activeVersion.kind, cardOf(activeVersion))} {getVersionLabel(activeVersion)}
        </span>
        {isPillClickable && (
          <span style={{ fontSize: 9, marginLeft: 2, opacity: 0.8 }}>▾</span>
        )}
      </>
    );

    return (
      <div ref={pillDropdownRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {isPillClickable ? (
          <button
            type="button"
            title={pillTooltip}
            onClick={() => setPillDropdownOpen(o => !o)}
            style={{ ...pillCommonStyle, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {pillBody}
          </button>
        ) : (
          <div title={pillTooltip} style={pillCommonStyle}>
            {pillBody}
          </div>
        )}

        {hasPendingBindingChange && (
          <button
            type="button"
            onClick={handleSaveBinding}
            disabled={savingBinding}
            title={`Lock this plan to ${formatEstimateStream(activeVersion.kind, cardOf(activeVersion))} ${getVersionLabel(activeVersion)}.`}
            style={{
              padding: '5px 10px',
              background: savingBinding ? '#94a3b8' : colors.success,
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              fontSize: 11.5, fontWeight: 600,
              cursor: savingBinding ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            {savingBinding ? '⏳ Saving…' : '💾 Save binding'}
          </button>
        )}

        {isPillClickable && pillDropdownOpen && sortedDropdown.length > 0 && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60,
            background: '#fff', border: '1px solid #dde1e9', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 280,
            padding: 4,
          }}>
            <div style={{
              padding: '6px 10px 4px 10px',
              fontSize: 10, fontWeight: 700, color: colors.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Switch estimate version
            </div>
            {sortedDropdown.map(v => {
              const isActive = v.id === baseVersionIdOf(activeVersion);
              const isPersisted = v.id === persistedVersionId;
              const kindTone = v.kind === 'scatter'
                ? { bg: colors.warnBg, text: '#78350f' }
                : { bg: '#eff6ff', text: '#1e40af' };
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setActiveVersion(v);
                    setPillDropdownOpen(false);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', textAlign: 'left',
                    padding: '8px 10px',
                    background: isActive ? colors.primaryLight : 'transparent',
                    border: 'none', borderRadius: 5,
                    cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 12,
                    color: colors.text,
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{
                    padding: '1px 6px', borderRadius: 4,
                    background: kindTone.bg, color: kindTone.text,
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                  }}>
                    {formatEstimateStream(v.kind, cardOf(v))} {getVersionLabel(v)}
                  </span>
                  <span style={{ color: colors.textMuted, fontSize: 11 }}>
                    {formatAsOf(v.asOfDate)}
                  </span>
                  {isPersisted && (
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: 9, fontWeight: 700, color: colors.success,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      ● Saved
                    </span>
                  )}
                  {isActive && !isPersisted && (
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: 9, fontWeight: 700, color: colors.warn,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      ○ Pending
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  })() : null;

  return (
    <div style={{ fontFamily: "'Inter', Arial, sans-serif", background: '#f0f2f5', color: colors.text, minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT}px)`, fontSize: 13 }}>
      <style>{`
        /* MUST stay the first rule in this stylesheet — a CSS @import is
           ignored anywhere else. Inter is loaded ONLY here — App.css
           still imports Roboto for the surrounding app chrome, so the two
           are intentionally different and this import is not redundant. DM Mono is still needed: version chips,
           inline code snippets and the debug panels use it deliberately. */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        :root{--bg:#f0f2f5;--surface:#fff;--surface2:#f7f8fa;--border:#dde1e9;--border2:#e8eaef;--accent:#2563eb;--accent-light:#eff4ff;--accent2:#059669;--accent2-light:#ecfdf5;--warn:#d97706;--warn-light:#fffbeb;--danger:#dc2626;--text:#111827;--text2:#374151;--muted:#6b7280;--muted2:#9ca3af;--green:#059669;--yellow:#d97706;--shadow:0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.04);--shadow-md:0 4px 6px rgba(0,0,0,0.07),0 2px 4px rgba(0,0,0,0.04);}
        *{box-sizing:border-box;margin:0;padding:0;}
        .btn{padding:7px 14px;border-radius:6px;border:1px solid var(--border);background:var(--surface);color:var(--text2);font-family:'Inter',Arial,sans-serif;font-size:12px;font-weight:500;cursor:pointer;transition:all 0.15s;display:inline-flex;align-items:center;gap:6px;box-shadow:var(--shadow);white-space:nowrap;}
        .btn:hover{border-color:var(--accent);color:var(--accent);background:var(--accent-light);}
        .btn:disabled{opacity:0.45;cursor:not-allowed;}
        .btn:disabled:hover{border-color:var(--border);color:var(--text2);background:var(--surface);}
        .btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600;}
        .btn.primary:hover{background:#1d4ed8;border-color:#1d4ed8;color:#fff;}
        .btn.success{background:var(--accent2);border-color:var(--accent2);color:#fff;font-weight:600;}
        .btn.success:hover{background:#047857;}
        .btn.warn{border-color:var(--warn);color:var(--warn);background:var(--warn-light);}
        .btn.warn:hover{background:var(--warn);color:#fff;}
        .sep{width:1px;height:26px;background:var(--border2);margin:0 2px;}
        .filter-select{padding:7px 10px;border-radius:6px;border:1px solid var(--border);background:var(--surface);color:var(--text2);font-family:'Inter',Arial,sans-serif;font-size:12px;min-width:240px;cursor:pointer;outline:none;box-shadow:var(--shadow);transition:border-color 0.15s;}
        .filter-select:focus{border-color:var(--accent);}
        .search-input{padding:7px 10px 7px 30px;border-radius:6px;border:1px solid var(--border);background:var(--surface);color:var(--text2);font-family:'Inter',Arial,sans-serif;font-size:12px;width:180px;outline:none;box-shadow:var(--shadow);transition:border-color 0.15s;}
        .search-input:focus{border-color:var(--accent);}
        .search-wrap{position:relative;}
        .search-wrap::before{content:'⌕';position:absolute;left:9px;top:50%;transform:translateY(-50%);color:var(--muted2);font-size:14px;pointer-events:none;}
        .row-count{font-size:11px;color:var(--muted);font-family:'Inter',Arial,sans-serif;margin-left:auto;background:var(--surface2);padding:4px 10px;border-radius:4px;border:1px solid var(--border2);}
        /* Compact stat cards — slimmed down from the original chunky
           variant since the Plan card moved into the Plan Details
           panel above. Lower padding + smaller value font keeps the
           stats easy to glance at while taking ~half the vertical
           real estate. */
        .stat-card{background:var(--surface);border:1px solid var(--border);border-radius:6px;padding:7px 14px;flex:1;}
        .stat-label{font-size:9.5px;font-weight:600;text-transform:uppercase;letter-spacing:0.7px;color:var(--muted);margin-bottom:2px;}
        .stat-value{font-family:'Inter',Arial,sans-serif;font-size:15px;font-weight:600;color:var(--text);line-height:1.2;}
        .stat-value.accent{color:var(--accent);}
        .stat-value.green{color:var(--green);}
        .stat-value.yellow{color:var(--yellow);}
        /* ─── One continuous grid surface ────────────────────────────────
           BUG THIS FIXES: the white background used to stop partway across
           the grid — around "Original CPM" — with everything to its right
           sitting on the grey page background, looking like a second pane.

           Cause was width:100% on the table. 100% resolves against the
           container, but the grid's content is far wider than any viewport,
           so the table element's background box ended at the container
           width while the overflowing cells kept painting past it. The grey
           was the scroll container's #f0f2f5 showing through.

           max-content sizes the element to its actual content, so the
           white surface, borders and radius extend the full scroll width.
           min-width:100% keeps a narrow plan (few columns) filling the
           container instead of shrink-wrapping to a stub.

           NOTE: this whole block lives inside a template literal, so no
           backticks in these comments — they terminate the literal.

           .table-container needs the same treatment — otherwise its white
           background and border still stop at container width and the table
           overflows ITS box instead. */
        .table-container{background:var(--surface);border:1px solid #dbe3ef;border-radius:10px;overflow:visible;box-shadow:var(--shadow-md);width:max-content;min-width:100%;}
        table{width:max-content;min-width:100%;border-collapse:separate;border-spacing:0;font-size:14px;background:var(--surface);}
        /* No position/z-index on the thead element itself — setting
           position:relative on <thead> is a long-standing footgun
           that intermittently breaks position:sticky on descendant
           <th> cells in Chrome + Safari. The sticky cells below
           carry their own z-index (29/30) which is what actually
           matters when stacking against tbody. */
        thead tr{background:#f1f3f7;}
        /* Defensive backdrop on the <tr> itself, not just the <th>
           cells. If subpixel rendering ever produces a hairline gap
           between adjacent <th> elements, tbody can briefly peek
           through during scroll — the tr's background blocks that. */
        thead tr.group-header{background:var(--accent);}
        /* Column headers: bigger and darker than before (was 10px/muted).
           white-space:normal lets long labels like "TOTAL HH (000s)" wrap to
           two lines instead of forcing the whole header row smaller. */
        /* No text-align here: dropping it lets th fall back to the UA
           default (centre), which is what the numeric columns want anyway.
           The PROGRAM header is unaffected — its contents sit in .prog-hdr,
           a flex row, so text-align has no say in where they land. */
        th{padding:7px 8px;font-size:12px;font-weight:650;text-transform:uppercase;letter-spacing:0px;color:#253147;border-bottom:1px solid #d6dfeb;border-right:1px solid #e8edf4;white-space:normal;line-height:1.25;cursor:pointer;user-select:none;transition:color 0.15s;vertical-align:bottom;}
        th:last-child{border-right:none;}
        th:hover{color:var(--accent);}
        /* Data headers are CENTRED over their column even though the values
           beneath them are right-aligned. That is what the design calls for:
           a two-line label reads as one block when centred, whereas
           right-aligning it leaves a ragged left edge against the wrapped
           second line. */
        th.num{text-align:center;}
        /* text-transform escape hatch — see GridHdr. The uppercase lives on
           the th rule because the CPM strip and Summary tables share it, so
           per-fragment casing has to be opted out of rather than removed. */
        th .lc{text-transform:none;}
        /* Group bars (HOUSEHOLD / PRIMARY DEMO): were saturated royal blue
           with white text, which shouted louder than the data underneath.
           Pale blue with blue text keeps the grouping legible without
           dominating. */
        /* Group bars sit flush against each other with only a hairline of
           white between them, and square corners — the 3px gaps + 4px radius
           read as separate pills rather than one banded header. */
        thead tr.group-header th{background:#eaf1ff;color:#0753c7;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;text-align:center;padding:5px 8px;border-right:1px solid #fff;border-left:1px solid #fff;border-radius:0;position:sticky;top:0;z-index:30;}
        thead tr.group-header th.group-empty{background:#fff;border:none;}
        thead tr.group-header{background:#fff;}
        thead tr:not(.group-header) th{position:sticky;top:var(--group-header-h,26px);z-index:29;background:var(--surface);}
        tbody tr{transition:background 0.1s;}
        tbody td{border-bottom:1px solid #e1e7f0;}
        tbody tr:last-child td{border-bottom:none;}
        tbody tr:hover{background:#f8f9fc;}

        /* ─── Row hierarchy ──────────────────────────────────────────────
           Strongest → weakest: program band, row identity, editable value,
           business value, YoY, subtotal, plan total. Previously almost
           everything sat at the same visual weight. Hierarchy now comes
           from background + type weight rather than heavy borders. */
        tbody tr.plan-header{background:#f1f6fd;border-top:1px solid #cdddf2;}
        tbody tr.plan-header td{border-bottom:1px solid #dce6f2;font-size:15px;font-weight:650;color:#172033;}
        tbody tr.subtotal-row td{background:#fafbfd;font-size:14.5px;font-weight:700;color:#172033;border-top:1px solid #d6dfeb;border-bottom:1px solid #d6dfeb;}
        tbody tr.net-total-row td{background:#eef2fb;font-size:14.5px;font-weight:700;}
        tbody tr.totals-row td{background:#f0f5fd;font-size:14.5px;font-weight:700;border-top:1px solid #bfd0ea;}
        /* Program name inside a band header — the single easiest thing to
           locate in a row, per the hierarchy spec. */
        tbody tr.plan-header .prog-name{font-size:13px;font-weight:650;color:#172033;}
        /* Band rows are structure, not data — they don't need the 44px
           data-row height. Overriding the tbody tr rule pulls the network
           label and the program band in tight so more actual schedule rows
           fit on screen. */
        tbody tr.plan-header{height:30px;}
        tbody tr.net-hdr-row{height:26px;}
        tbody tr.plan-header td,
        tbody tr.net-hdr-row td{padding-top:2px;padding-bottom:2px;}
        td.program{font-weight:600;color:#172033;}
        /* Add Show / Add Row: a text link with a circled plus, not a button.
           Both are low-frequency, additive actions sitting inside dense data;
           a bordered or filled button at that size reads as a primary CTA and
           competes with the numbers. text-transform:none because these can
           appear inside a th, which upper-cases everything by default. */
        .icon-link{display:inline-flex;align-items:center;gap:7px;padding:0;border:none;background:none;font-family:inherit;font-size:12px;font-weight:600;letter-spacing:0;text-transform:none;color:#1a6dff;cursor:pointer;white-space:nowrap;}
        .icon-link:hover{color:#0b47b3;}
        .icon-link:hover span{text-decoration:underline;}
        /* PROGRAM label left, Add Show pushed to the right edge of the
           column. Same 184px box as every other column-0 cell so the header
           can't widen the column. */
        .prog-hdr{display:flex;align-items:center;justify-content:space-between;gap:10px;width:184px;}
        /* Held back at rest so it doesn't compete with the show name above
           it; full strength once you're in that program's row. */
        .band-label .icon-link{opacity:0.7;transition:opacity 0.12s;}
        tbody tr.plan-header:hover .band-label .icon-link{opacity:1;}
        tbody tr.plan-header td{font-family:'Inter',Arial,sans-serif;font-size:11px;font-weight:700;color:var(--accent);padding:7px 14px;letter-spacing:0.3px;border-top:1px solid #c7d7fc;border-bottom:1px solid #c7d7fc;}
        /* Body cells: padding DOWN (9px→6px vertical) while text goes UP
           (12px→14px). The space inside a cell should belong to the text,
           not to empty padding. height:44px on the row gives a consistent
           rhythm without the 56px the spec first suggested — at 28+
           schedules that difference is a full extra screen of scrolling. */
        td{padding:6px 9px;border-right:1px solid #e8edf4;color:#172033;vertical-align:middle;white-space:nowrap;}
        tbody tr{height:44px;}
        td:last-child{border-right:none;}
        /* Numbers use Inter with tabular figures, not DM Mono.
           font-variant-numeric:tabular-nums makes every digit the same
           advance width, so columns still form a clean vertical edge —
           but without the typewriter look DM Mono has at 14px. */
        /* Centred, matching the headers. Right-alignment is the usual choice
           for a financial grid, but here the header labels are centred and
           several columns carry a value stacked over a YoY line, both of
           which read better on a centre axis. */
        td.num{text-align:center;font-family:'Inter',Arial,sans-serif;font-variant-numeric:tabular-nums;font-size:14.5px;padding-left:7px;padding-right:7px;}
        td.program{font-size:14px;}

        /* ─── Column widths ─────────────────────────────────────────────
           The table is width:max-content (needed so the white surface spans
           the full scroll width), which means columns expand to fit their
           header on ONE line and never wrap. That made every column much
           wider than the mock.

           Giving the numeric columns an explicit width lets the header text
           wrap to two lines — "TOTAL HH (000s)" — instead of stretching the
           column. In the table layout algorithm a width on a cell is a
           preferred size: it's honoured when the content can reflow, which
           it now can because white-space is normal.

           A specified width is only a FLOOR though — the column still grows
           to the widest min-content in it, and no max-width can stop that
           (see the column-0 block further down for how that is handled).

           NOTE: never use backticks in this comment block — the whole style
           string is a JS template literal and a backtick terminates it. */
        th.num{width:72px;}
        /* Program column. 200px is set here AND encoded as the 184px inner
           box width further down — change both together or the column and
           its contents drift apart. Sized so the longest real show name
           ("Front Office Sports Tonight") fits beside the delete control. */
        th:first-child{width:200px;}
        /* Period / Day-Time / Len.
           INVARIANT: 74 + 78 + 48 = 200 = the pinned Program column width.
           That is the whole point of these three numbers. The Program column
           is position:sticky, so scrolling right slides these row-meta
           columns underneath it; when they sum to exactly the pinned width,
           the scroll position where the last of them disappears is also the
           position where the pinned column's right edge meets the # column's
           border. Any other sum leaves a sliver of a half-covered column
           against the pinned edge.
           Change one, change the others (or th:first-child) to match.
           Floors only, as ever: DAY/TIME can't go below its own header's
           min-content (~74px), which is why it holds the largest share. */
        thead tr:not(.group-header) th:nth-child(2){width:74px;}
        thead tr:not(.group-header) th:nth-child(3){width:78px;}
        thead tr:not(.group-header) th:nth-child(4){width:48px;}
        /* LEN is a 2-character value in a deliberately narrow column —
           centred rather than right-aligned, since there is nothing to its
           right to align against and it reads as adrift when pushed to the
           edge under the pinned column. */
        td.len{text-align:center;}
        /* "muted" is now only a size/semantic hint, not a colour. Period,
           Day/Time, Len and the ratings columns read as full-strength text
           like everything else — no colour declaration here so they inherit
           the td colour. The td.num.muted grey override was removed for the
           same reason; leaving it in would have greyed out most of these
           cells anyway, since nearly every muted cell is also .num. */
        td.muted{font-size:14.5px;}
        td.program{font-weight:600;color:var(--text);}

        /* ─── Grid scrollbar ────────────────────────────────────────────
           The default macOS/Chrome scrollbar renders as a thick bar across
           the bottom of the grid and visually competes with the data. Thin
           it down and tint it to match the surrounding chrome. Applies to
           both axes — this container scrolls vertically too. */
        .grid-scroll::-webkit-scrollbar{height:9px;width:9px;}
        .grid-scroll::-webkit-scrollbar-track{background:#edf1f6;border-radius:999px;}
        .grid-scroll::-webkit-scrollbar-thumb{background:#aeb9c8;border-radius:999px;}
        .grid-scroll::-webkit-scrollbar-thumb:hover{background:#94a2b5;}
        .grid-scroll::-webkit-scrollbar-corner{background:#edf1f6;}
        /* Firefox */
        .grid-scroll{scrollbar-width:thin;scrollbar-color:#aeb9c8 #edf1f6;}

        /* ─── Sticky Program column ──────────────────────────────────────
           The grid is far wider than any screen, so scrolling right used to
           lose which show you were looking at. Column 0 is pinned.

           Two things make this fiddly:

           1. A sticky cell MUST carry its own opaque background. It paints
              in its own layer, so a transparent cell lets the scrolling
              columns show straight through it. The <tr> background is not
              enough. Hence a background rule for every row variant below —
              if a new row type is added and its pinned cell looks like it
              has ghost text sliding under it, it's missing from this list.

           2. z-index has to slot into the existing header stack: the group
              header row is 30 and the column-label row is 29. Body pinned
              cells sit at 5 (above sibling cells, below both header rows),
              and the two pinned header cells go above their own rows so the
              top-left corner stays put in both axes.

           The 1px right-edge shadow is the seam — without it, pinned
           numbers visually merge into whatever scrolls beneath. */
        .sticky-col{position:sticky;left:0;z-index:5;background:var(--surface);box-shadow:1px 0 0 var(--border2);}
        /* SPECIFICITY TRAP: the rule that gives every column-label header
           z-index:29 is "thead tr:not(.group-header) th" — three elements
           plus two pseudo/class parts, which OUTWEIGHS a plain
           "thead th.sticky-col". So the pinned Program header lost and,
           being earlier in the DOM, got painted over by Period as soon as
           the grid scrolled right — the header read "PRO" while the body's
           pinned cell stayed put. Both selectors below have to match the
           losing rule's shape or they are silently ignored. */
        thead th.sticky-col{background:var(--surface);}
        thead tr:not(.group-header) th.sticky-col{z-index:31;}
        /* White, not grey — the pinned corner above PROGRAM is part of the
           empty run of the group row, so it should read as blank page rather
           than as a filled cell. Still opaque, which sticky requires. */
        thead tr.group-header th.sticky-col{z-index:32;background:#fff;}
        /* Row-variant backgrounds. Order matters — later rules win. */
        tbody tr.plan-header .sticky-col{background:#f0f4ff;}
        tbody tr.subtotal-row .sticky-col{background:#fafbfe;}
        tbody tr.net-total-row .sticky-col{background:#eef2fb;}
        tbody tr.totals-row .sticky-col{background:#e8edf9;}
        tbody tr.net-hdr-row .sticky-col{background:inherit;}
        tbody tr.new-prog-row .sticky-col{background:var(--warn-bg,#fffbeb);}
        /* Hover must repaint the pinned cell too, or it stays the old
           colour while the rest of the row highlights. */
        tbody tr:hover .sticky-col{background:#f8f9fc;}
        tbody tr.plan-header:hover .sticky-col{background:#e8eeff;}
        /* ─── Column 0 ───────────────────────────────────────────────────
           Everything in the pinned column wraps rather than running on one
           line. max-width on a table cell can't clamp a column (CSS leaves
           it undefined and Chrome ignores it), so wrapping plus the fixed
           inner boxes further down is what actually holds the width. */
        td.sticky-col{white-space:normal;}
        .prog-cell{display:flex;align-items:center;gap:6px;}
        .prog-cell-name{min-width:0;}
        /* Bare glyph, no box. A pink chip with a border on every schedule row
           put a hard-edged red rectangle in the leftmost column of the whole
           grid, which pulled the eye far more than a per-row delete deserves.
           Now it's a small red ✕ that sits quietly at ~45% opacity and comes
           up to full strength on hover, so it's still obviously a control
           when you go looking for it. */
        .row-del{flex:0 0 auto;align-self:flex-start;margin-top:1px;padding:0;background:none;border:none;font-size:11px;line-height:1;color:var(--danger,#dc2626);opacity:0.45;cursor:pointer;transition:opacity 0.12s;}
        tbody tr:hover .row-del{opacity:0.75;}
        .row-del:hover{opacity:1;}
        /* ─── Boxing column 0 to a fixed 200px ──────────────────────────
           The table is width:max-content, so every column is sized to its
           MAX-content — the longest cell laid out on one line. A specified
           width on the header is only a floor, so "FRONT OFFICE SPORTS
           TONIGHT SUBTOTAL" was still dragging the column out to ~205px
           even though it wraps when rendered.

           Fix: give each column-0 cell an inner box with an explicit width.
           A block with a specified width contributes exactly that width as
           its max-content size, so no label can widen the column again.
           .band-label always stacks: the show name takes the first line and
           Add Row the second, whatever the name's length, so the + never
           juts out over the # and EQUIV columns. Leaving the name at
           its default min-width:auto is deliberate — it lets the name keep
           a full line to itself and pushes the button down, instead of the
           name being squeezed into the ~43px left beside the button. */
           Every column-0 cell uses the SAME box width, so the column is a
           single known number instead of whatever the longest label happened
           to be. 184 + 8 + 8 of cell padding = the 200px on the header. */
        /* Column, not a row: program name on the first line, Add Row on the
           second. Stacking is what lets the link keep its text label — side
           by side it either crowded the name or had to shrink to a bare
           icon, and it also made the row's height depend on how long the
           show name was. Stacked, every band row is the same two lines. */
        /* Plain block layout, deliberately NOT flex. A block-level program
           name forces the link onto the next line by normal flow, so the
           stacking can't be undone by a stray flex-direction or flex-wrap
           further down the cascade — which is exactly how this ended up
           inline for short names before. */
        /* Both lines centred in the 184px box. text-align does the work for
           the name (a block) and for the link alike, since .icon-link is
           inline-flex and therefore an inline-level box its parent can
           align. */
        .band-label{display:block;width:184px;text-align:center;align-items:center;}
        .band-label .prog-name{display:block;white-space:normal;}
        .band-label .icon-link{margin-top:3px;}
        .prog-box{width:184px;}
        .prog-cell{width:184px;}
        td.program.sticky-col{padding-left:8px;padding-right:8px;}
        td.green{color:var(--green);font-weight:500;}
        td.accent{color:var(--accent);}
        td.yellow{color:var(--yellow);font-weight:500;}
        /* Matches the program-subtotal label's darker slate — this styles the
           TOTALS row label, which is the same kind of closing heading. */
        td.label{font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.05em;}
        tr.totals-row{background:#f0f4ff;border-top:2px solid #c7d7fc;font-weight:700;}
        tr.totals-row td{font-weight:700;}
        .cpm-cell{display:flex;align-items:center;justify-content:flex-end;gap:3px;}
        .cpm-input{width:64px;background:var(--warn-light);border:1px solid #fde68a;border-radius:4px;color:#0f172a;font-family:'Inter',Arial,sans-serif;font-size:11.5px;font-weight:500;text-align:right;padding:3px 6px;outline:none;transition:all 0.15s;}
        .cpm-input:focus{border-color:var(--warn);background:#fff;box-shadow:0 0 0 3px rgba(217,119,6,0.1);}
        .units-input{width:52px;border:1px solid #bbf7d0;border-radius:4px;padding:3px 6px;font-family:'Inter',Arial,sans-serif;font-size:11.5px;text-align:right;outline:none;background:#f0fdf4;color:#065f46;}
        .units-input:focus{border-color:#059669;background:#fff;box-shadow:0 0 0 3px rgba(5,150,105,0.1);}
        .demo-input{width:64px;background:var(--warn-light);border:1px solid #fde68a;border-radius:4px;color:#0f172a;font-family:'Inter',Arial,sans-serif;font-size:11.5px;font-weight:500;text-align:right;padding:3px 6px;outline:none;transition:all 0.15s;}
        .demo-input:focus{border-color:var(--warn);background:#fff;box-shadow:0 0 0 3px rgba(217,119,6,0.1);}
        .rate-input{width:72px;background:#fff;border:1px solid #e5e7eb;border-radius:4px;color:#0f172a;font-family:'Inter',Arial,sans-serif;font-size:11.5px;font-weight:500;text-align:right;padding:3px 6px;outline:none;transition:all 0.15s;}
        .rate-input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,0.1);}
        /* Per-row CPM percent-adjustment input. Sits to the right of
           the dollar cpm-input as a sibling — same height, narrower,
           light grey background to read as a secondary action. */
        .cpm-pct-input{width:48px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;color:#0f172a;font-family:'Inter',Arial,sans-serif;font-size:11px;text-align:right;padding:3px 5px;outline:none;transition:all 0.15s;}
        .cpm-pct-input:focus{border-color:#2563eb;background:#fff;box-shadow:0 0 0 3px rgba(37,99,235,0.1);}
        .cpm-pct-input[readonly]{background:#f3f4f6 !important;border-color:#e5e7eb !important;color:#6b7280 !important;cursor:default;}
        .cpm-pct-input[readonly]:focus{box-shadow:none !important;border-color:#e5e7eb !important;}
        /* ─── In-grid inputs: match the surrounding numbers ───────────────
           These were all 11.5px DM Mono, which made a typed value visibly
           smaller and in a different typeface than the calculated number
           next to it. Same 14.5px Inter with tabular figures as td.num, so
           an editable cell and a computed cell sit on the same baseline and
           the same digit grid. Widths grow to match the larger type.
           Declared as one group AFTER the individual rules so it wins
           without having to touch each one. */
        /* Centred, like the columns around them. NOTE: these are all
           type="number" and the spin arrows are wanted, so Chrome reserves a
           ~13px strip on the right for them. Centred text therefore centres
           in what's left and sits a little left of true centre — that is the
           cost of keeping the arrows, not a bug. */
        /* padding:1px 0 — zero horizontal padding is deliberate. At 40px the
           box has to fund 2px of border and the ~13px the spin arrows
           reserve, so 6px of padding either side was the difference between
           holding one digit and holding three. The text is centred, so it
           doesn't sit against the edges anyway. */
        .cpm-input,.units-input,.demo-input,.rate-input,.cpm-pct-input{
          font-family:'Inter',Arial,sans-serif;font-variant-numeric:tabular-nums;font-size:14.5px;text-align:center;padding:1px 0px;
        }
        /* Widths below are all "usable text space + 15px", where the 15 is
           2px of border plus the ~13px the spin arrows reserve. A digit at
           14.5px Inter is ~8.7px, so each of these carries one more digit
           than the tight fit for its own domain — headroom for the outlier
           without sizing every column for it.
           CPM is the exception and stays as-is: it is hard-capped at 99.99,
           so 58px is already exactly its full domain and a wider box would
           only ever hold whitespace. */
        .units-input{width:50px;}
        .cpm-input{width:58px;}
        .demo-input{width:50px;}
        .rate-input{width:60px;}
        /* The percent-change box is a secondary reading, so smaller type than
           the value it annotates — but it still has to hold "-18.2". */
        .cpm-pct-input{width:56px;font-size:12px;}
        /* Read-only state — used for 25/26 view-only plans. Strips the
           colored editable backgrounds so locked inputs blend into the
           row and don't invite typing. */
        .cpm-input[readonly],.units-input[readonly],.demo-input[readonly],.rate-input[readonly]{
          background:#f3f4f6 !important;border-color:#e5e7eb !important;color:#6b7280 !important;cursor:default;
        }
        .cpm-input[readonly]:focus,.units-input[readonly]:focus,.demo-input[readonly]:focus,.rate-input[readonly]:focus{
          box-shadow:none !important;border-color:#e5e7eb !important;
        }
        .unsaved-pill{display:inline-flex;align-items:center;gap:4px;background:#fef3c7;color:#92400e;border:1px solid #fde68a;border-radius:12px;padding:2px 8px;font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;margin-left:6px;}
        .unsaved-pill::before{content:'';width:6px;height:6px;border-radius:50%;background:#d97706;}
        .diff-up{color:#059669;font-weight:600;}
        .diff-down{color:#dc2626;font-weight:600;}
        .diff-same{color:var(--text2);}
        .demo-pill{display:inline-flex;align-items:center;gap:4px;background:var(--accent-light);color:var(--accent);border:1px solid #bfdbfe;border-radius:20px;padding:3px 10px;font-size:12px;font-weight:500;}
        .demo-pill button{background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0;line-height:1;}
        .add-demo-btn{display:inline-flex;align-items:center;gap:4px;background:#fff;border:1px dashed var(--accent);color:var(--accent);border-radius:20px;padding:3px 10px;font-size:12px;font-weight:500;cursor:pointer;}
        .add-demo-btn:hover{background:var(--accent-light);}
        .toast{position:fixed;bottom:24px;right:24px;background:#1f2937;color:#fff;padding:10px 18px;border-radius:8px;font-size:13px;z-index:9999;opacity:0;transition:opacity 0.2s;pointer-events:none;}
        .toast.show{opacity:1;}
        /* Snowflake plan version history popup — anchored to the version
           chip in the MPB header. Position is absolute relative to the
           chip wrapper so it tracks the chip's location on resize. */
        .version-popup{position:absolute;top:calc(100% + 8px);right:0;width:400px;background:#fff;border:1px solid var(--border);border-radius:12px;box-shadow:0 12px 32px rgba(15,23,42,0.14);padding:0;z-index:200;max-height:70vh;overflow-y:auto;}
        /* Timeline container — no global spine. Each milestone group
           draws its own bounded spine that terminates at its last
           revision's dot, so the vertical line never extends into
           dead space below the group. See renderMilestoneRow. */
        .vp-timeline{position:relative;}
        .version-popup-row{cursor:pointer;transition:background 0.12s;}
        .version-popup-row:hover{background:#f9fafb;}
        .version-popup-row.current{background:#eff6ff;}
        .version-chip{background:#f7f8fa;border:1px solid #dde1e9;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:500;color:#6b7280;transition:all 0.15s;display:inline-flex;align-items:center;gap:6px;}
        .version-chip.clickable{cursor:pointer;}
        .version-chip.clickable:hover{background:#eef2f7;border-color:#c9d2dc;color:#374151;}
        .version-chip.aspirational{cursor:default;color:#9ca3af;font-style:italic;}
        /* Spinner reused by both the popup and the snapshot viewer modal */
        .vh-spin{width:14px;height:14px;border:2px solid #dbeafe;border-top-color:var(--accent);border-radius:50%;animation:vhspin 0.8s linear infinite;}
        @keyframes vhspin{to{transform:rotate(360deg);}}
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1000;display:flex;align-items:center;justify-content:center;}
        .modal-box{background:#fff;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,0.2);padding:28px;}
        input[type=text],input[type=number],select{font-family:'Inter',Arial,sans-serif;}
      `}</style>

      {/* HEADER */}
      {/*
        MPB header sticks BELOW the parent app's fixed red toolbar
        (StyledHeader in App.tsx — `position: fixed; top: 0`, antd's
        default 64px height). Anchoring at top:0 used to put MPB on
        top of the parent toolbar once the user scrolled; sticking at
        top:64 leaves the parent visible at all times.
      */}
      <div style={{ background: '#fff', borderBottom: '1px solid #dde1e9', padding: '0 24px', height: MPB_HEADER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: FIXED_HEADER_HEIGHT, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: 13, fontWeight: 700, color: colors.primary, letterSpacing: '0.5px', background: colors.primaryLight, padding: '5px 12px', borderRadius: 6 }}>MVM</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.textSecondary }}>Media Plan Builder</span>
        </div>
        {/* Plan version chip — Snowflake version history.
            • Hidden in All Plans view and on 25/26 SF source plans.
            • Snowflake-saved plans render `v{N}` and are clickable (opens history popup). */}
        {(() => {
          if (!planFilter) return <span />;
          const saved = getSavedPlanByName(planFilter);
          if (!saved) return <span />;
          const v = saved.latestVersion?.versionNo ?? 1;
          // Chip label — the identity of the currently-viewed version.
          // Lifecycle model:
          //   • Named / exported closures → the name itself (truncated
          //     to keep the chip compact; hover reveals the full name).
          //   • Order closures → "v{N} · Live Order".
          //   • Working revisions → "v{N}" or "v{N}.{rev}".
          // compactVersionLabel encapsulates the same rules as the
          // picker — one source of truth for how a version identifies.
          const latest = saved.latestVersion;
          // Version prefix + primary name — matches the popup's
          // closure-row shape. The prefix chip sits inside the button
          // on the left, the primary name reads as the row title, and
          // any order badges tail on the right (added below).
          const headerPrefix = latest ? versionPrefix(latest as any) : `v${v}`;
          const headerPrimaryName = latest ? versionPrimaryName(latest as any) : '';
          // Truncate long names (e.g., user-typed "Upfront V1 - Client
          // Review Draft"). 22 chars keeps the chip readable in the
          // narrow header bar. Full name lives in the title attr for
          // tooltip on hover. Nothing to truncate for vN patterns —
          // they're always short.
          const MAX_CHIP_LEN = 22;
          const chipLabel = headerPrimaryName.length > MAX_CHIP_LEN
            ? `${headerPrimaryName.slice(0, MAX_CHIP_LEN - 1)}…`
            : headerPrimaryName;
          const chipTooltip = headerPrimaryName.length > MAX_CHIP_LEN
            ? `${headerPrefix} — ${headerPrimaryName} · click to view version history`
            : `${headerPrefix} — ${headerPrimaryName} · click to view version history`;
          // Header-chip order badges. Same double-pill treatment as the
          // popup's closure rows: identity (Original / Revised) + status
          // (Current / Superseded) whenever the currently-viewed version
          // is an order push. Non-order rows keep the plain chip.
          //
          // liveOrderVersionNo — the newest order/order_update in the
          // plan — needs the full version list to compute. Two paths:
          //   1) versionHistory is loaded (user opened the popup at
          //      least once) → derive it precisely.
          //   2) Not loaded → fall back to the safe assumption that
          //      `latest` is the newest (true whenever the user is
          //      viewing the actual latest, which is the only time
          //      the header chip reflects an order-family row without
          //      historical overlay ambiguity).
          const headerOrderBadges = latest
            ? (() => {
                let newestOrderNo: number | null = null;
                if (versionHistory) {
                  for (const v of versionHistory) {
                    if (v.keyType === 'order' || v.keyType === 'order_update') {
                      if (newestOrderNo == null || v.versionNo > newestOrderNo) {
                        newestOrderNo = v.versionNo;
                      }
                    }
                  }
                } else {
                  // Version history not fetched yet — assume `latest`
                  // is the newest order-family. Correct on the actual
                  // latest view; may show a stale Current pill briefly
                  // when historical-viewing before the popup loads.
                  newestOrderNo = latest.versionNo;
                }
                return orderBadges(latest as any, newestOrderNo);
              })()
            : null;
          return (
            <div ref={versionChipWrapperRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="version-chip clickable"
                title={chipTooltip}
                onClick={() => {
                  const willOpen = !showVersionHistory;
                  setShowVersionHistory(willOpen);
                  if (willOpen) loadVersionHistory(saved.id);
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span style={versionPrefixChipStyle}>{headerPrefix}</span>
                <span>{chipLabel}</span>
                {headerOrderBadges && (() => {
                  const idPill = orderIdentityPill(headerOrderBadges.identity);
                  const stPill = orderStatusPill(headerOrderBadges.status);
                  return (
                    <>
                      <span style={idPill.style}>{idPill.label}</span>
                      <span style={stPill.style}>{stPill.label}</span>
                    </>
                  );
                })()}
              </button>
              {showVersionHistory && (
                <VersionHistoryPopup
                  versions={versionHistory}
                  loading={versionHistoryLoading}
                  err={versionHistoryErr}
                  currentVersionNo={v}
                  onPickVersion={(versionNo) => openHistoricalVersion(saved.id, saved.name, saved.sourcePlanSfId, versionNo)}
                  onNameVersion={(versionNo) => handleNameVersion(saved.id, versionNo)}
                />
              )}
            </div>
          );
        })()}
      </div>

      {/* REVISING-ORDER BANNER — shows while the user has unlocked
          a pushed plan for post-order editing. Distinct from the
          historical-viewing banners; only appears when they're on the
          current post-order lifecycle (not viewing a past version).
          Also skipped when the historical banner is active — that
          takes priority since it's more specific to what's on screen. */}
      {isRevisingOrder && !viewingHistoricalRef && (
        <div style={{
          background: '#f5f3ff', borderBottom: '1px solid #ddd6fe',
          padding: '9px 24px', display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 12,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#7c3aed" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          <div style={{ minWidth: 0, flex: 1, color: '#5b21b6' }}>
            <span style={{ fontWeight: 700 }}>Revising this order</span>
            <span style={{ marginLeft: 8, color: '#6d28d9' }}>
              — edits create a new post-order version. Use Name &amp; Export to finalize the revision as the new Live Order (this doesn&apos;t push to Salesforce).
            </span>
          </div>
        </div>
      )}

      {/* VIEWING-HISTORICAL BANNER — one of three variants:
            • PUSHED plan viewing any historical version →
              purple "locked as Order" banner. Whole plan is
              locked, so no save-based copy makes sense.
            • Not pushed, PREVIOUS lifecycle → yellow warning,
              inputs locked (see planIsViewOnly), Return to
              latest to re-enable editing.
            • Not pushed, CURRENT lifecycle historical (v3.1
              while latest is v3.3) → light blue info banner;
              editable, Save creates the next revision on top
              of the current latest.
          Only renders for the currently-active plan; if the user
          switches plans we clear the ref. */}
      {(() => {
        if (!viewingHistoricalRef) return null;
        const activeSaved = planFilter ? getSavedPlanByName(planFilter) : null;
        if (!activeSaved || activeSaved.id !== viewingHistoricalRef.planId) return null;

        // Variant A' — PUSHED plan (order) and NOT actively revising.
        // Whole plan is locked; save is impossible from ANY version,
        // historical or not. Copy focuses on the SF lock state rather
        // than the historical-view mechanics. If the user IS actively
        // revising, we fall through to the current/previous lifecycle
        // logic below (revising unlocks a specific lifecycle).
        if (isPushed && !isRevisingOrder) {
          return (
            <div style={{
              background: '#f5f3ff', borderBottom: '1px solid #ddd6fe',
              padding: '9px 24px', display: 'flex', alignItems: 'center', gap: 12,
              fontSize: 12,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#7c3aed" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <div style={{ minWidth: 0, flex: 1, color: '#5b21b6' }}>
                <span style={{ fontWeight: 700 }}>You&apos;re viewing {viewingHistoricalRef.displayLabel}</span>
                <span style={{ marginLeft: 8, color: '#6d28d9' }}>
                  — this plan is locked as an Order (already pushed to Salesforce). All versions are read-only.
                </span>
                {historicalDiff && (historicalDiff.totalChangedCells > 0 || historicalDiff.addedInLatest.length > 0) && (
                  <span style={{
                    marginLeft: 10, padding: '2px 8px', background: '#fef3c7',
                    color: '#92400e', border: '1px solid #f59e0b', borderRadius: 10,
                    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  }} title="Amber cells differ from the latest version">
                    {historicalDiff.totalChangedCells} {historicalDiff.totalChangedCells === 1 ? 'cell' : 'cells'} differ from latest
                    {historicalDiff.addedInLatest.length > 0 ? ` · ${historicalDiff.addedInLatest.length} added since` : ''}
                  </span>
                )}
              </div>
              <button
                onClick={returnToLatest}
                disabled={historicalVersionLoading}
                style={{
                  padding: '4px 11px', background: '#7c3aed',
                  color: '#fff', border: 'none', borderRadius: 6,
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  cursor: historicalVersionLoading ? 'not-allowed' : 'pointer',
                  opacity: historicalVersionLoading ? 0.6 : 1,
                }}
                title="Return to the latest version"
              >
                {historicalVersionLoading ? 'Loading…' : 'Go to latest'}
              </button>
            </div>
          );
        }

        // Variant A — CURRENT lifecycle historical (editable + light info)
        if (isViewingCurrentLifecycleHistorical) {
          return (
            <div style={{
              background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
              padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 12,
              fontSize: 12,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#2563eb" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <div style={{ minWidth: 0, flex: 1, color: '#1e3a8a' }}>
                <span style={{ fontWeight: 700 }}>You&apos;re viewing {viewingHistoricalRef.displayLabel}</span>
                <span style={{ marginLeft: 8, color: '#1e40af' }}>
                  — an earlier working revision of v{viewingHistoricalRef.currentLifecycleIndex}. Save will create the next revision on top of the current latest.
                </span>
                {historicalDiff && (historicalDiff.totalChangedCells > 0 || historicalDiff.addedInLatest.length > 0) && (
                  <span style={{
                    marginLeft: 10, padding: '2px 8px', background: '#fef3c7',
                    color: '#92400e', border: '1px solid #f59e0b', borderRadius: 10,
                    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  }} title="Amber cells differ from the latest version">
                    {historicalDiff.totalChangedCells} {historicalDiff.totalChangedCells === 1 ? 'cell' : 'cells'} differ from latest
                    {historicalDiff.addedInLatest.length > 0 ? ` · ${historicalDiff.addedInLatest.length} added since` : ''}
                  </span>
                )}
              </div>
              <button
                onClick={returnToLatest}
                disabled={historicalVersionLoading}
                style={{
                  padding: '4px 11px', background: '#2563eb',
                  color: '#fff', border: 'none', borderRadius: 6,
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  cursor: historicalVersionLoading ? 'not-allowed' : 'pointer',
                  opacity: historicalVersionLoading ? 0.6 : 1,
                }}
                title="Return to the latest version"
              >
                {historicalVersionLoading ? 'Loading…' : 'Go to latest'}
              </button>
            </div>
          );
        }

        // Variant B — PREVIOUS lifecycle (view-only, yellow warning)
        return (
          <div style={{
            background: '#fffbeb', borderBottom: '1px solid #fde68a',
            padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12,
            fontSize: 12,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="#d97706" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div style={{ minWidth: 0, flex: 1, color: '#92400e' }}>
              <span style={{ fontWeight: 700 }}>You&apos;re viewing {viewingHistoricalRef.displayLabel}</span>
              <span style={{ marginLeft: 8, color: '#a16207' }}>
                — this is a previous version and is read-only. Return to the latest to make edits.
              </span>
              {historicalDiff && (historicalDiff.totalChangedCells > 0 || historicalDiff.addedInLatest.length > 0) && (
                <span style={{
                  marginLeft: 10, padding: '2px 8px', background: '#fff',
                  color: '#92400e', border: '1px solid #f59e0b', borderRadius: 10,
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                }} title="Amber cells differ from the latest version">
                  {historicalDiff.totalChangedCells} {historicalDiff.totalChangedCells === 1 ? 'cell' : 'cells'} differ from latest
                  {historicalDiff.addedInLatest.length > 0 ? ` · ${historicalDiff.addedInLatest.length} added since` : ''}
                </span>
              )}
            </div>
            <button
              onClick={returnToLatest}
              disabled={historicalVersionLoading}
              style={{
                padding: '5px 12px', background: '#f59e0b',
                color: '#fff', border: 'none', borderRadius: 6,
                fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                cursor: historicalVersionLoading ? 'not-allowed' : 'pointer',
                opacity: historicalVersionLoading ? 0.6 : 1,
              }}
              title="Discard this view and return to the actual latest version"
            >
              {historicalVersionLoading ? 'Loading…' : 'Return to latest'}
            </button>
          </div>
        );
      })()}

      {/* HYDRATE-FAILURE RIBBON — surfaces below the header when the
          estimate-versions cache couldn't load. Estimate cells will be
          blank until this is resolved. */}
      {hydrateErr && (
        <div style={{
          background: colors.warnBg, borderBottom: '1px solid #fde68a',
          padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#d97706" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span style={{ color: '#92400e', fontWeight: 600 }}>
            Estimate data couldn&apos;t load — values will be blank.
          </span>
          <span style={{ color: '#a16207' }}>
            {hydrateErr}
          </span>
          <button onClick={handleRetryHydrate} disabled={retryingHydrate}
            style={{
              marginLeft: 'auto', padding: '4px 12px',
              background: retryingHydrate ? '#fcd34d' : colors.warn,
              color: '#fff', border: 'none', borderRadius: 4,
              fontSize: 11, fontWeight: 600,
              cursor: retryingHydrate ? 'not-allowed' : 'pointer',
            }}>
            {retryingHydrate ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {/* STICKY BAR: tabs + toolbar + secondary demos + stats */}
      {/* Sticky bar stack: parent toolbar (64) + MPB header (54) = 118.
          Keeps the dropdown + Save + tabs + stats row visible below
          both headers as the user scrolls the table. */}
      <div ref={stickyBarRefCallback} style={{ position: 'sticky', top: STICKY_BAR_OFFSET, zIndex: 80 }}>

      {/* TOOLBAR */}
      <div style={{ background: '#fff', borderBottom: '1px solid #dde1e9', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn primary" onClick={() => {
          // Context-aware pre-fill of the modal's source picker. Two
          // triggers:
          //
          //   1. Viewing a 25/26 SF plan → pre-fill the roll-forward
          //      source with that plan (year+1 shift on create). The
          //      read-only banner literally instructs them to roll it
          //      forward, so making them re-search their own plan is
          //      a papercut.
          //
          //   2. Viewing a 26/27 saved plan → pre-fill as a CLONE source
          //      (no year shift on create; verbatim copy). Same-season
          //      duplicate workflow — user makes a variation of their
          //      current plan without hunting through the picker.
          //
          // Both paths hand a fully-formed source object to
          // pickRollForwardSource, which branches on `year`.
          if (isViewOnly && planFilter) {
            const src = rollForwardSources.find(s => s.name === planFilter);
            if (src) pickRollForwardSource(src);
          } else if (planFilter) {
            const src = cloneSources.find(s => s.name === planFilter);
            if (src) pickRollForwardSource(src);
          }
          setShowNewPlanModal(true);
        }}>+ New Plan</button>
        <div className="sep" />
        {/* ─── Unified "Load Plan" autocomplete ───────────────────────────
            Replaces the old dropdown pill + separate "Search plans…"
            input with one combined picker (LookupField-style):
              • Input shows current plan name (or "— All Plans · YY/YY (N) —"
                when nothing picked) when closed.
              • Click into it → select-all + dropdown opens showing every
                plan; current pick highlighted and scrolled into view.
              • Type → filters live (case-insensitive substring on NAME);
                pinned "All Plans" row stays visible at top.
              • × clear button on the input snaps back to All Plans view.
              • Enter picks the first match.
              • Cross-season: rows show a year pill (25/26 vs 26/27) and
                picking a plan whose season differs from the current toggle
                auto-flips the toggle via setPlanFilter's downstream logic. */}
        {(() => {
          const activeOpt = planFilter ? planOptions.find(p => p.name === planFilter) : null;
          // Per-season plan counts driven by PLAN METADATA, not deal
          // quarters. A plan is "26/27" if it's saved-or-manual (i.e.,
          // built/managed in the new system); everything else (raw SF
          // source) is "25/26". Same classification as PlanYearPill.
          const plans25 = new Set<string>();
          const plans26 = new Set<string>();
          for (const p of planOptions) {
            if (p.year === CURRENT_SEASON_PRIMARY_YEAR) plans26.add(p.name);
            else plans25.add(p.name);
          }
          const allPlansActiveLabel =
            allPlansSeason === '25/26'
              ? `— All Plans · 25/26 (${plans25.size}) —`
              : `— All Plans · 26/27 (${plans26.size}) —`;

          const q = planSearchTerm.trim().toLowerCase();
          const filteredPlans = q
            ? planOptions.filter(p => p.name.toLowerCase().includes(q))
            : planOptions;

          // Collapsed display (input value when picker is closed) vs.
          // open display (input shows the typed query, empty by default).
          // Auto-select on focus lets typing immediately replace either.
          const collapsedDisplay = activeOpt ? activeOpt.name : allPlansActiveLabel;
          const inputValue = planPickerOpen ? planSearchTerm : collapsedDisplay;

          const closePicker = () => {
            setPlanPickerOpen(false);
            setPlanSearchTerm('');
            planPickerInputRef.current?.blur();
          };

          return (
            <div ref={planPickerRef} style={{ position: 'relative', minWidth: 380 }}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={planPickerInputRef}
                  type="text"
                  autoComplete="off"
                  value={inputValue}
                  placeholder="Load a plan…"
                  title={activeOpt ? activeOpt.name : allPlansActiveLabel}
                  onFocus={e => {
                    setPlanPickerOpen(true);
                    const el = e.currentTarget;
                    requestAnimationFrame(() => { try { el.select(); } catch { /* ignore */ } });
                  }}
                  onChange={e => {
                    setPlanSearchTerm(e.target.value);
                    setPlanPickerOpen(true);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      closePicker();
                      return;
                    }
                    if (e.key === 'Enter') {
                      const first = filteredPlans[0];
                      if (first) {
                        switchPlan(first.name);
                        closePicker();
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    paddingRight: planFilter ? 32 : 12,
                    border: `1px solid ${planPickerOpen ? colors.primary : colors.border}`,
                    borderRadius: 6,
                    fontFamily: "'Inter', Arial, sans-serif", fontSize: 12.5,
                    color: activeOpt ? colors.textDark : colors.textMuted,
                    fontWeight: 500,
                    background: '#fff',
                    outline: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'border-color 0.15s',
                  }}
                />
                {planFilter && (
                  <button
                    type="button"
                    aria-label="Clear plan and return to All Plans"
                    title="Clear — return to All Plans view"
                    onMouseDown={e => {
                      e.preventDefault();
                      switchPlan('');
                      closePicker();
                    }}
                    style={{
                      position: 'absolute',
                      right: 6, top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: 14,
                      padding: '2px 6px',
                    }}
                  >×</button>
                )}
              </div>

              {planPickerOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60,
                  background: '#fff', border: '1px solid #dde1e9', borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  minWidth: '100%', maxWidth: 480,
                  maxHeight: 380, overflowY: 'auto',
                  padding: 4,
                }}>
                  {/* Pinned "All Plans" row — one-click return to the
                      multi-plan view. Muted styling makes it visually
                      distinct from concrete plans. */}
                  <PlanRow
                    selected={!planFilter}
                    onClick={() => {
                      switchPlan('');
                      closePicker();
                    }}
                    badge={null}
                    label={allPlansActiveLabel}
                    muted
                  />
                  <div style={{ height: 4 }} />
                  {filteredPlans.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
                      No plans match &ldquo;{planSearchTerm}&rdquo;
                    </div>
                  ) : filteredPlans.map(p => {
                    const isCurrent = p.name === planFilter;
                    return (
                      <div
                        key={p.name}
                        ref={el => {
                          // Scroll the currently-selected plan into view
                          // when the dropdown opens on a picked value —
                          // matches native OS select behavior.
                          if (isCurrent && el && planPickerOpen) {
                            requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));
                          }
                        }}
                      >
                        <PlanRow
                          selected={isCurrent}
                          onClick={() => {
                            switchPlan(p.name);
                            closePicker();
                          }}
                          badge={<PlanYearPill year={p.year} />}
                          label={p.name}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
        <div className="sep" />
        {/* Revise Order — appears when the plan is pushed to SF and
            there's no in-flight post-order revision. Click unlocks
            the plan for a new post-order lifecycle (client options /
            order revisions). Finalizing via Name & Export creates
            an order_update milestone that supersedes the current
            Live Order automatically. */}
        {planFilter && isPushed && !isRevisingOrder && (
          <button
            className="btn"
            title="Start a new revision of this order. Edits will create a new post-order version; you can finalize via Name & Export to make it the new Live Order."
            onClick={() => setRevisingOrderMode(true)}
            style={{
              background: '#7c3aed', color: '#fff', border: '1px solid #7c3aed',
              fontWeight: 600,
            }}
          >
            ✏ Revise Order
          </button>
        )}
        {/* Save button — always visible for saved plans so the chrome
            stays consistent. Disabled when locked (view-only reasons)
            with a tooltip that spells out why. Hidden entirely only
            for the "no plan selected" state where no version exists
            to save against. */}
        {planFilter && (() => {
          const saveDisabledReason = (
            // Revising-order supersedes the pushed lock — the plan
            // IS pushed but the user is actively creating a
            // post-order lifecycle, so save is allowed.
            // Deliberately NOT gated on the active tab. The user edits 4Q'27,
            // clicks over to Summary to check a total, and Save must still be
            // there — otherwise their work is stranded with no way to commit
            // it. The UI only ever permitted late-quarter edits on a pushed
            // plan (see planIsViewOnly), and the backend re-checks that on
            // every save, so enabling Save here can't widen what gets written.
            //
            // `planLateQuarter` is the test: no late quarter means nothing on
            // this plan is editable and the original lock message stands.
            // Someone else's save landed while this user was editing. Saving
            // would be refused by the backend anyway (409 PLAN_VERSION_CONFLICT)
            // — disabling here just turns a round-trip into an immediate,
            // explained "no". The banner above carries the remedy.
            (conflictedPlan === planFilter)
              ? 'Someone else saved this plan — reload it before saving (see the banner above).'
            : (isPushed && !isRevisingOrder && !planLateQuarter)
              ? 'This plan is locked as an Order — cannot save.'
            : isViewingPreviousLifecycle ? `Viewing ${viewingHistoricalRef?.displayLabel} — return to latest to edit and save.`
            : isViewOnly ? 'This plan is view-only.'
            : (seasonView === '25/26' && canShowSeasonToggle) ? 'Switch to 26/27 to save.'
            : hasInvalid ? 'One or more rows have empty CPM/Rate — fix the red cells before saving'
            : hasMissingUnitLength ? 'Set unit length on all rows to enable save — estimates need a length to populate'
            : isSaving ? 'Saving…'
            : !isDirty ? 'No changes to save'
            : null
          );
          const disabled = saveDisabledReason !== null;
          // Revert button — sits next to Save and undoes every unsaved
          // edit back to the last saved version. Enabled only when
          // there's something to revert (isDirty) AND the plan is
          // actually saved (a from-scratch draft with no cached version
          // has nothing to revert TO). Never enabled while a save is
          // in flight — reverting mid-save would race the response.
          // Off while the conflict banner is up. Revert would restore the
          // version this user loaded, which is now stale — and the banner is
          // already offering "Reload plan", which restores the CURRENT one.
          // Two discard buttons with different outcomes side by side is a
          // trap; the banner's is the right one, so it is the only one.
          const inConflict = conflictedPlan === planFilter;
          const canRevert =
            planFilter != null
            && !!currentSavedPlanForLock
            && isDirty
            && !isSaving
            && !inConflict;
          const revertTitle = canRevert
            ? 'Undo all changes since the last save'
            : inConflict ? 'Someone else saved this plan — use "Reload plan" in the banner above'
            : !isDirty ? 'Nothing to revert — no unsaved changes'
            : isSaving ? 'Saving — cannot revert right now'
            : 'This plan has no saved version to revert to';
          return (
            <>
              <button
                className="btn success"
                disabled={disabled}
                title={saveDisabledReason ?? 'Save this plan to Snowflake as a new version'}
                onClick={() => { void saveToSnowflake(); }}
              >
                {isSaving ? '⏳ Saving…' : '💾 Save'}
              </button>
              <button
                className="btn"
                disabled={!canRevert}
                title={revertTitle}
                onClick={() => setRevertConfirmOpen(true)}
              >
                ↺ Revert
              </button>
            </>
          );
        })()}
        {/* Export operates against `planFilter` (the 26/27 plan), so
            disable on the 25/26 toggle view — exporting from there
            would dump the 26/27 plan's data while the user is staring
            at the 25/26 source on screen. Direct-selected 25/26 SF
            plans stay exportable as before (their Export reads SF
            source-deals correctly). */}
        {(() => {
          const exportDisabledByToggle = seasonView === '25/26' && canShowSeasonToggle;
          const disabled = !planFilter || exportDisabledByToggle;
          const toggleHint = exportDisabledByToggle ? 'Switch to 26/27 to export.' : '';
          return (
            <>
              <button className="btn" disabled={disabled} onClick={() => { void exportToExcelWithGate(); }} title={toggleHint || 'Export the active quarter'}>⬇ Export</button>
              <button className="btn" disabled={disabled} onClick={() => { void exportAllQuartersWithFormatPrompt(); }} title={toggleHint || 'Export all quarters — choose Excel or PDF'}>⬇ Export All</button>
            </>
          );
        })()}
        {/* ⚠ `!isLateQuarterOnlyEditing` is load-bearing, not redundant.
            `planIsViewOnly` is now tab-dependent, so on a PUSHED plan it goes
            false the moment the user stands on the late-quarter tab — which
            would unhide "Delete Plan" for a plan already sent to Salesforce.
            Deleting a plan is plan-wide and irreversible; the late-quarter
            exception unlocks one quarter's data, nothing structural. */}
        {isDeletablePlan && !planIsViewOnly && !isLateQuarterOnlyEditing && (
          <>
            <button className="btn warn" onClick={() => { void deleteCurrentPlan(); }}>🗑 Delete Plan</button>
            <button className="btn" onClick={openSFStructure}>⬡ SF Structure</button>
          </>
        )}
        {/*
          Header chip shows the SF-grain Deal count (rows grouped by
          Program+Length+Quarter+Year). Users see the same count in the
          Plan Details "Deals" card, the push modal, and eventually in
          SF's Deals related list after push. Prior version showed the
          raw MPB row count, which diverged from SF's Deal__c count
          whenever multiple schedules share a (P,L,Q,Y) tuple.
        */}
        <span className="row-count">{filteredDealGroupCount} deals</span>
        {isDirty && (
          <span className="unsaved-pill" title="You have changes that haven't been saved yet">Unsaved</span>
        )}
      </div>

      {/* CONFLICT BANNER — someone else saved this plan while this user was
          editing it. Raised by a 409 PLAN_VERSION_CONFLICT on save, or by the
          background refresh spotting a newer version while the grid is dirty.

          Red rather than amber: this is not an informational state, it means
          the next save is blocked and there is work on screen that exists
          nowhere else.

          The reload says what it costs. Re-deriving the grid from the server
          is exactly "discard my edits", and a bare "refresh" link would read
          like a free action. Nothing here happens automatically — the user's
          unsaved work is the one thing the app must not throw away on its own. */}
      {conflictedPlan === planFilter && (
        <div style={{
          background: '#fef2f2', borderBottom: '1px solid #fca5a5',
          padding: '8px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          color: '#7f1d1d', fontSize: 12.5, fontWeight: 500,
        }}>
          <span style={{ fontSize: 14 }}>⚠</span>
          <span>
            Someone else saved this plan while you were editing.
            Your changes are still here but cannot be saved on top of theirs.
          </span>
          <button
            onClick={() => { void reloadPlanFromServer(); }}
            style={{
              marginLeft: 4, padding: '3px 10px', fontSize: 12, fontWeight: 600,
              color: '#7f1d1d', background: '#fff', cursor: 'pointer',
              border: '1px solid #fca5a5', borderRadius: 4, fontFamily: 'inherit',
            }}
            title="Replaces the grid with the current saved version. Your unsaved changes are lost."
          >
            Reload plan — discards your changes
          </button>
        </div>
      )}

      {/* VIEW-ONLY BANNER — shown only for 25/26 SF source plans.
          These are last-season reference inventory; users can browse
          them but can't edit. To make changes, the new-plan modal
          offers a roll-forward path to clone a 25/26 plan into a new
          editable 26/27 plan. Centered + amber so it reads as an
          informational state, not an error. */}
      {isViewOnly && (
        <div style={{
          background: '#fef3c7', borderBottom: '1px solid #fcd34d',
          padding: '8px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          color: '#78350f', fontSize: 12.5, fontWeight: 500,
        }}>
          <span style={{ fontSize: 14 }}>🔒</span>
          <span>
            Viewing a 25/26 plan (read-only). Use <strong>+ New Plan</strong> to
            roll it forward into a 26/27 plan you can edit.
          </span>
        </div>
      )}

      {/* SEASON TOGGLE — only visible on 26/27 plans that were rolled
          forward from a 25/26 SF source (canShowSeasonToggle). Lets
          the user flip between the editable 26/27 view and a read-
          only snapshot of the original 25/26 source plan, with
          QUARTER_TABS + estimates auto-resolving to the right season
          via the activeVersion resolution useEffect. Sits above the
          quarter tabs so the season nav reads "outer" and the quarter
          nav reads "inner" within the selected season. */}
      {canShowSeasonToggle && (
        <div style={{
          background: '#fff', borderBottom: '1px solid #dde1e9',
          padding: '0 24px', display: 'flex', alignItems: 'center',
          gap: 0,
        }}>
          {(['26/27', '25/26'] as const).map(s => {
            const isActive = seasonView === s;
            return (
              <button
                key={s}
                onClick={() => setSeasonView(s)}
                style={{
                  padding: '8px 16px',
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? colors.primary : colors.textMuted,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: 0.3,
                  transition: 'all 0.15s',
                  marginBottom: -1,
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}

      {/* SEASON TOGGLE — All Plans landing variant. Same underline-tab
          style as the rolled-forward-plan toggle above, but drives
          `allPlansSeason` and is visible whenever no specific plan is
          selected. Switching tabs rebuilds the synthetic activeVersion
          (5-quarter BY+CY window) via the version-resolve useEffect
          and re-filters the row list to that season's plans. */}
      {!planFilter && (
        <div style={{
          background: '#fff', borderBottom: '1px solid #dde1e9',
          padding: '0 24px', display: 'flex', alignItems: 'center',
          gap: 0,
        }}>
          {(['26/27', '25/26'] as const).map(s => {
            const isActive = allPlansSeason === s;
            return (
              <button
                key={s}
                onClick={() => setAllPlansSeason(s)}
                style={{
                  padding: '8px 16px',
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? colors.primary : colors.textMuted,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: 0.3,
                  transition: 'all 0.15s',
                  marginBottom: -1,
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}

      {/* TABS — only shown when a specific plan is selected.
          First tab is the Summary view, then one tab per quarter.
          Sits below the toolbar so the plan-level controls (+ New Plan,
          dropdown, search) stay at the top and the table-scoped quarter
          tabs read as drilling into the selected plan. */}
      {planFilter && (
        <div style={{ background: '#fff', borderBottom: '1px solid #dde1e9', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 0 }}>
          {/* Summary tab */}
          {(() => {
            const isActive = activeQuarter === SUMMARY_TAB_KEY;
            // effectivePlanFilter so the count reflects the visible
            // dataset on the 25/26 toggle view (source plan's deals).
            // Season-narrow by visibleQuarters so nickname-consolidated
            // plans (a rolled 26/27 plan sharing a nickname with its 25/26
            // SF source) don't double-count. Without this, a plan with 14
            // rolled 26/27 rows showed "Summary 28" because the source's
            // 14 rows leaked in — same class of bug I already fixed for
            // the SF preview, push modal totals, and the Save path.
            const summaryVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
            const planTotalDeals = allDeals.filter(d =>
              d.salesPlan === effectivePlanFilter
              && summaryVisibleKeys.has(`${d.quarter}|${d.year}`)
            ).length
            + addedRows.filter(r =>
              r.salesPlan === effectivePlanFilter
              && summaryVisibleKeys.has(`${r.quarter}|${r.year}`)
            ).length;
            return (
              <button
                key={SUMMARY_TAB_KEY}
                onClick={() => setActiveQuarter(SUMMARY_TAB_KEY)}
                style={{
                  padding: '10px 18px',
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? colors.primary : colors.textMuted,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  marginBottom: -1,
                }}
              >
                Summary
                <span
                  style={{ marginLeft: 6, fontSize: 10, color: isActive ? colors.primary : colors.textDisabled, fontFamily: 'DM Mono, monospace' }}
                  title="Total schedules (row count) across all visible quarters. Deal (grouped) count lives on the Deals stat card above."
                >
                  {planTotalDeals} schedules
                </span>
              </button>
            );
          })()}
          {/* Quarter tabs */}
          {/* Tab strip uses visibleQuarters (per-plan, marketplace-driven) — not
              the version's full quarter set. BY plans show 4Q'26-3Q'27, CY
              plans show 1Q'27-4Q'27, Scatter shows all 5. Synthesized tabs
              (quarters not in the active estimate version) still render and
              accept input; their estimate-derived cells just go blank. */}
          {visibleQuarters.map((tab, idx) => {
            const isActive = activeQuarter === tab.key;
            // Count everything the user actually sees in this quarter:
            // saved / rolled-forward deals from `allDeals` PLUS any rows
            // the user has added this session via + Add Row / + Add Show
            // (which live in `addedRows` until the next Save). This keeps
            // the tab badge in sync as the user builds out the plan.
            const tabDealCount =
              allDeals.filter(
                d => d.salesPlan === effectivePlanFilter && d.quarter === tab.quarter && d.year === tab.year
              ).length
              + addedRows.filter(
                r => r.salesPlan === effectivePlanFilter && r.quarter === tab.quarter && r.year === tab.year
              ).length;
            // Tab is "missing" when it appears in visibleQuarters but
            // no estimate version row covers it. Rendered with a ⚠
            // glyph + tooltip; the banner below the tab strip explains
            // the impact in detail when this tab is active.
            const missing = missingEstimateKeys.has(tab.key);
            // Per-tab dots. Red ⚠ when the quarter has unfinished
            // rows blocking save (blank LEN / CPM=0 / null rate).
            // Amber • when viewing a historical version and this
            // quarter has cells that differ from latest. Same slot
            // in the layout — a quarter is never both at once
            // (historical view snapshots what existed then, and
            // in-flight edits from + Add Row / + Add Show clear
            // when you enter a historical view).
            const tabKey = `${tab.quarter}|${tab.year}`;
            const attentionCount = quarterAttentionCounts.get(tabKey) ?? 0;
            const diffCount = quarterDiffCounts.get(tabKey) ?? 0;
            // Extras: the last `extraQuarters` tabs are user-added
            // (past the marketplace's natural pattern). Only the very
            // last tab gets a × — removing one bumps the next-extra
            // up to "last", so the user trims sequentially from the
            // tail. This keeps the count model simple (no holes).
            // The × counterpart to canAddQuarter. On a pushed plan the only
            // removable extra is the late quarter itself (it is chronologically
            // last by definition), so this reduces to "you may undo the
            // quarter you were allowed to add" — never a quarter that was part
            // of the order.
            const isLastExtra = extraQuarters > 0
              && idx === visibleQuarters.length - 1
              && !planIsViewOnly
              && (!isLateQuarterOnlyEditing || tab.key === planLateQuarter?.key);
            return (
              <button
                key={tab.key}
                onClick={() => setActiveQuarter(tab.key)}
                title={missing
                  ? `Estimates for ${tab.label} haven't been uploaded yet. You can still enter Units / CPM / Rate; RTG, (000), and CPM HH will fill in once the estimate file is uploaded.`
                  : undefined}
                style={{
                  padding: '10px 18px',
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? colors.primary : colors.textMuted,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  marginBottom: -1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0,
                }}
              >
                {tab.label}
                {missing && (
                  <span
                    aria-label="Estimates not yet uploaded"
                    style={{ marginLeft: 4, fontSize: 11, color: colors.warn }}>
                    ⚠
                  </span>
                )}
                <span style={{ marginLeft: 6, fontSize: 10, color: isActive ? colors.primary : colors.textDisabled, fontFamily: 'DM Mono, monospace' }}>
                  {tabDealCount}
                </span>
                {attentionCount > 0 && (
                  // Red ⚠ dot — rows in this quarter are blocking save
                  // (blank LEN, CPM=0, or null rate). Tooltip surfaces
                  // the exact count so the user knows what to fix.
                  <span
                    aria-label={`${attentionCount} row${attentionCount === 1 ? '' : 's'} in ${tab.label} need attention`}
                    title={`${attentionCount} row${attentionCount === 1 ? '' : 's'} in ${tab.label} need CPM, rate, or unit length before this plan can save.`}
                    style={{
                      marginLeft: 6, width: 8, height: 8, borderRadius: '50%',
                      background: '#dc2626', display: 'inline-block',
                      boxShadow: '0 0 0 2px rgba(220,38,38,0.15)',
                    }}
                  />
                )}
                {attentionCount === 0 && diffCount > 0 && (
                  // Amber • dot — historical-view diff signal. Only
                  // shows when the tab is clean of save-blockers so
                  // the two signals don't overlap.
                  <span
                    aria-label={`${diffCount} cell${diffCount === 1 ? '' : 's'} in ${tab.label} differ from latest`}
                    title={`${diffCount} cell${diffCount === 1 ? '' : 's'} in ${tab.label} differ from the current latest version.`}
                    style={{
                      marginLeft: 6, width: 8, height: 8, borderRadius: '50%',
                      background: '#d97706', display: 'inline-block',
                      boxShadow: '0 0 0 2px rgba(217,119,6,0.15)',
                    }}
                  />
                )}
                {isLastExtra && (
                  // Remove (×) for the last user-added quarter.
                  // stopPropagation so clicking × doesn't also fire
                  // the tab-switch handler on the parent button.
                  // If the user is currently on this tab, bounce
                  // them to Summary — otherwise activeQuarter would
                  // point at a tab that no longer exists.
                  <span
                    role="button"
                    aria-label={`Remove ${tab.label}`}
                    title={`Remove ${tab.label}`}
                    onClick={e => {
                      e.stopPropagation();
                      const next = Math.max(0, extraQuarters - 1);
                      setExtraQuartersState(next);
                      if (planFilter) setExtraQuartersCount(planFilter, next);
                      if (activeQuarter === tab.key) setActiveQuarter(SUMMARY_TAB_KEY);
                    }}
                    style={{
                      marginLeft: 6,
                      width: 16, height: 16,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, lineHeight: 1,
                      borderRadius: '50%',
                      color: colors.textMuted,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = '#f1f5f9';
                      (e.currentTarget as HTMLElement).style.color = colors.danger;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                      (e.currentTarget as HTMLElement).style.color = colors.textMuted;
                    }}
                  >
                    ×
                  </span>
                )}
              </button>
            );
          })}
          {/* + Add Quarter — appends the next chronological quarter
              past the current last tab (Q4 → Q1 of the following
              year). Only shown on editable plans; the planIsViewOnly
              check handles All Plans, 25/26 SF source, and the 25/26
              seasonView toggle in one expression. Persists to the
              localStorage extras map so a reload restores the count. */}
          {canAddQuarter && visibleQuarters.length > 0 && (
            <button
              type="button"
              onClick={() => {
                if (!planFilter) return;
                const next = extraQuarters + 1;
                setExtraQuartersState(next);
                setExtraQuartersCount(planFilter, next);
              }}
              title="Add the next chronological quarter to this plan"
              style={{
                padding: '10px 14px',
                fontSize: 12, fontWeight: 600,
                color: colors.textMuted,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'color 0.15s',
                marginBottom: -1,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = colors.primary; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = colors.textMuted; }}
            >
              + Add Quarter
            </button>
          )}
          {/* Plan-level pills (Status + Estimates as-of). Right-aligned
              in the tabs row via `marginLeft: auto`. Moved here from
              the Plan Details panel header so the always-visible
              sticky region surfaces them — the user wanted them
              anchored next to the tabs rather than buried in the
              scroll-away Plan Details panel. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            {planStatusPillEl}
            {planEstimatePillEl}
          </div>
        </div>
      )}

      {/* SECONDARY DEMO BAR (shown when plan selected) */}
      {planSelected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#fff', borderBottom: '1px solid #dde1e9', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Secondary Demos:</span>
          {secondaryDemos.map(d => (
            <span key={d} className="demo-pill">{d} <button onClick={() => removeSecondaryDemo(d)} title="Remove">✕</button></span>
          ))}
          <div ref={demoDropdownRef} style={{ position: 'relative' }}>
            <button className="add-demo-btn" onClick={() => { setDemoPillDropdownOpen(v => !v); setDemoSearchFilter(''); }}>+ Add Demo</button>
            {demoPillDropdownOpen && (
              <div style={{ position: 'absolute', background: '#fff', border: '1px solid #dde1e9', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, maxHeight: 260, overflowY: 'auto', minWidth: 160, top: '100%', left: 0 }}>
                <input autoFocus value={demoSearchFilter} onChange={e => setDemoSearchFilter(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: 'none', borderBottom: '1px solid #dde1e9', fontSize: 12, outline: 'none', borderRadius: '8px 8px 0 0' }}
                  placeholder="Search demos…" />
                {filteredDemoOpts.map(d => (
                  <div key={d} onClick={() => addSecondaryDemo(d)}
                    style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: colors.textSecondary }}
                    onMouseEnter={e => (e.currentTarget.style.background = colors.primaryLight)}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    {d}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      </div>{/* end sticky bar — toolbar + tabs + secondary demos stay
                  pinned; everything below scrolls with the table. */}

      {/* ── Scrollable content area ──
          The scroll-wrapper opens here (not at the table-container
          like it used to) so Plan Details + Stats live INSIDE the
          scroll and slide away with the table content. Only thing
          pinned at the top is the sticky bar above. Height fills
          the remaining viewport after subtracting the parent app
          toolbar + MPB header + measured sticky-bar height. */}
      <div className="grid-scroll" style={{ overflowX: 'auto', overflowY: 'auto', background: '#f0f2f5', padding: '0 24px 16px 24px', height: `calc(100vh - ${STICKY_BAR_OFFSET}px - ${stickyBarH}px)` }}>

      {/* PLAN DETAILS — scroll-with-content (not sticky). Sits above
          the stats row so users see Plan Details first when at the
          top of the page; once they scroll into the table, both
          Plan Details and the stats row scroll away and only the
          toolbar + tabs remain pinned.
          Sales Plan Nickname is the headline (big bold — the human-
          friendly label); Sales Plan Name (the SF-mirror formula)
          sits below as the uniqueness key. The Edit Details button
          opens the modal that lets the user change every field in
          one shot.
          Hidden on 25/26 SF source / toggle view + All Plans view
          (no specific plan to describe). */}
      {planFilter && !planIsViewOnly && activeQuarter === SUMMARY_TAB_KEY && (() => {
        const meta = getPlanMeta(planFilter);
        if (!meta) return null;
        // Prefer the saved snapshot (matches what's in Snowflake +
        // what the SF workflow produced) over a fresh compute, so
        // backfilled rows and workflow-set values render verbatim.
        // Fall back to the live formula for brand-new drafts that
        // haven't been saved yet.
        const displaySalesPlanName =
          meta.salesPlanName
          ?? computeSalesPlanName({
            agency: meta.agency, brand: meta.brand,
            advertiser: meta.advertiser, demo: meta.demo,
            year: '26/27',
          });
        const nickname = meta.dealName || planFilter;
        // Same resolution as + Add Show / + Add Row so the chip matches
        // whatever those handlers will actually seed on new rows. Meta
        // wins (edits made this session before save land in meta first);
        // otherwise falls back to the saved PLAN_VERSION column.
        const displayDefaultLen = resolveDefaultUnitLength(planFilter);
        const row = (label: string, value: string | null | undefined) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {label}
            </div>
            <div style={{ fontSize: 12.5, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={value || ''}>
              {value && value.trim() ? value : <span style={{ color: colors.textDisabled, fontStyle: 'italic' }}>—</span>}
            </div>
          </div>
        );
        return (
          <div style={{
            margin: '10px 24px 0', padding: '14px 18px',
            background: '#fff', border: `1px solid ${colors.borderInk}`,
            borderRadius: 8, boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
          }}>
            {/* Header row: title | Edit Details. Status + Estimate
                pills live in the tabs row above so they stay always-
                visible while the user scrolls into the table. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Plan Details
              </div>
              <button
                type="button"
                onClick={openEditPlanModal}
                style={{
                  background: '#fff', border: `1px solid ${colors.primary}`,
                  color: colors.primary, borderRadius: 5,
                  padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Edit Advertiser, Brand, Buyer, AE, Nickname, Demo, Marketplace, Equivalized"
              >
                ✎ Edit Details
              </button>
            </div>
            {/* Nickname — most prominent line. The human-friendly
                label users actually remember the plan by.
                Default Unit Length rides shotgun as a compact pill so
                users see the plan-wide default without opening Edit
                Details. Hidden when the plan has no default set (legacy
                or explicitly cleared). */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                Sales Plan Nickname
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, lineHeight: 1.2, wordBreak: 'break-word' }}>
                  {nickname}
                </div>
                {displayDefaultLen && (
                  <span
                    title="Plan-level default unit length — prefills new rows added via + Add Show / + Add Row."
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: colors.primaryLight, color: colors.primary,
                      border: `1px solid ${colors.primary}`,
                      borderRadius: 999, padding: '2px 9px',
                      fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ opacity: 0.75, fontWeight: 600 }}>Default</span>
                    :{displayDefaultLen}
                  </span>
                )}
              </div>
            </div>
            {/* Sales Plan Name (formula) — the SF uniqueness key. */}
            <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${colors.borderInk}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                Sales Plan Name
              </div>
              <div style={{ fontSize: 12, color: colors.textSecondary, fontFamily: "'Inter', Arial, sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={displaySalesPlanName}>
                {displaySalesPlanName || <span style={{ color: colors.textDisabled, fontStyle: 'italic' }}>—</span>}
              </div>
            </div>
            {/* Metadata grid — auto-fits 1-4 columns by width. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '10px 18px',
            }}>
              {row('Advertiser',   meta.advertiser)}
              {row('Brand',        meta.brand)}
              {row('Buyer',        meta.buyer)}
              {row('Agency',       meta.agency)}
              {row('Account Exec', meta.ae)}
              {row('Primary Demo', meta.demo)}
              {row('Marketplace',  meta.marketplace)}
              {row('Equivalized',  meta.equiv)}
            </div>
          </div>
        );
      })()}

      {/* STATS BAR */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 24px', background: '#f0f2f5', borderBottom: '1px solid #e8eaef' }}>
        {/* Row 1: optional 25/26 badge + estimates-as-of pill.
            The plan nickname used to live here in its own Plan card;
            now it's the headline of the Plan Details panel above.
            We keep this row for the 25/26 read-only badge (only
            visible on 25/26 SF source plans or the toggle view) and
            the right-aligned pills. When neither is rendered the row
            collapses to its 0px gap and the layout closes up cleanly. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          {/*
            Season/state badge. Three cases:
              1. Pure 25/26 SF source (isViewOnly=true): amber "25/26 ·
                 read-only" — matches the yellow "Viewing a 25/26 plan"
                 banner up top.
              2. 26/27 plan in 25/26 season-toggle view (canShowSeasonToggle
                 && seasonView='25/26'): also amber "25/26 · read-only".
              3. Pushed 26/27 plan (isPushed): green "26/27 · pushed &
                 locked" — the lock is a state change, not a season change.
                 Prior version incorrectly reused the amber 25/26 label
                 for pushed 26/27 plans, which read as if the plan had
                 changed season.
              4. Editable 26/27 plan: no badge (row collapses).
          */}
          {planFilter && (isViewOnly || (seasonView === '25/26' && canShowSeasonToggle)) && (
            <span style={{
              background: '#fef3c7', color: '#78350f',
              border: '1px solid #fcd34d',
              borderRadius: 4, padding: '3px 8px',
              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            }}>25/26 · read-only</span>
          )}
          {planFilter && isPushed && !isViewOnly && !(seasonView === '25/26' && canShowSeasonToggle) && !isRevisingOrder && (
            <span style={{
              background: '#dcfce7', color: '#166534',
              border: '1px solid #86efac',
              borderRadius: 4, padding: '3px 8px',
              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            }}>26/27 · pushed &amp; locked</span>
          )}
          {planFilter && isPushed && !isViewOnly && !(seasonView === '25/26' && canShowSeasonToggle) && isRevisingOrder && (
            <span style={{
              background: '#f5f3ff', color: '#5b21b6',
              border: '1px solid #ddd6fe',
              borderRadius: 4, padding: '3px 8px',
              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            }}>26/27 · revising order</span>
          )}
          {!planFilter && (
            // Keep an explicit affordance on All Plans (no nickname
            // header above for this case) so the user knows the
            // scope of the stats below.
            <div className="stat-value accent" style={{ fontSize: 13, fontWeight: 600 }}>All Plans</div>
          )}
          {/* Pills used to live here. Moved into the Plan Details
              panel header so all per-plan controls are co-located.
              Row 1 keeps the optional 25/26 badge / "All Plans" label
              and otherwise collapses to its 0px gap. */}
        </div>
        {/* Row 2: Stats — five equal-flex cards so they fit within the
            grid width. flex: '1 1 0' + min-width: 0 lets long values
            (e.g. $ formatting) truncate/wrap gracefully rather than
            blowing out the row. */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className="stat-label">Deals</div>
            {/*
              SF-grain Deal count — schedules sharing (Program, Length,
              Quarter, Year) collapse into one Deal__c on push. Matches
              the push modal's "Deals" count and the SF Deals list the
              user lands in after push. The raw row/schedule count still
              lives in the quarter tab badges (row-level scoping).
            */}
            <div className="stat-value">{filteredDealGroupCount}</div>
          </div>
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className="stat-label">Total Units</div>
            <div className="stat-value">{fmt(stats.totalUnits)}</div>
          </div>
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className="stat-label">Total Dollars</div>
            <div className="stat-value green">${stats.totalDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          {/* Total Plan Dollars — sums dollars across every quarter of
              this plan. Shown ONLY on the Summary tab: quarter tabs
              already scope Total Dollars to that quarter, and adding a
              plan-wide sibling there was cluttering the row. Users
              hopping to Summary get the grand total; on a quarter tab
              they focus on that quarter's spend. Also hidden in All
              Plans because "plan-wide" has no meaning without a plan. */}
          {planFilter && activeQuarter === SUMMARY_TAB_KEY && (
            <div className="stat-card" style={{ flex: '1 1 0', minWidth: 0 }}>
              <div className="stat-label">Total Plan Dollars</div>
              <div className="stat-value green">${planWideTotalDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          )}
          <div className="stat-card" style={{ flex: '1 1 0', minWidth: 0 }}>
            {/* In All Plans, different rows have different primary demos
                — labelling with one demo name would be misleading. Use a
                generic "Total (000s)" instead; the value is still the
                sum of every row's demo (000) × units, which matches the
                Total (000) column total in the bottom totals row. */}
            <div className="stat-label">{!planFilter ? 'Total (000s)' : `${demoName} (000s)`}</div>
            <div className="stat-value yellow">{fmtBig(stats.totalDemo)}</div>
          </div>
        </div>
      </div>{/* end stats bar — non-sticky, scrolls with content */}

      {/* DUPLICATE ROW WARNING — persistent banner listing duplicate
          groups so the user can find + fix them before Push to SF.
          Only shown on editable 26/27 plans; hidden in view-only 25/26
          and All Plans views (both non-pushable). */}
      {planFilter && !planIsViewOnly && duplicateRowKeys.size > 0 && (
        <div style={{
          margin: '8px 24px 0',
          padding: '10px 14px',
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          fontSize: 12.5,
          color: '#7f1d1d',
          lineHeight: 1.5,
        }}>
          <strong>⚠ {duplicateRowKeys.size} duplicate row group{duplicateRowKeys.size === 1 ? '' : 's'} detected.</strong>
          {' '}Rows highlighted red share the same Program, Length, Quarter, Year, Demo (000), CPM, and Rate — Salesforce will reject them as duplicates on push.
        </div>
      )}

      {/* TABLE — the scroll wrapper that used to open here moved up
          to right after the sticky bar so Plan Details + Stats live
          inside the same scroll. This block just opens the inner
          table-container, no new scroll wrapper. */}
        <div className="table-container">
          {seasonView === '25/26' && canShowSeasonToggle && !sourcePlanInAllDeals ? (
            // 25/26 toggle view but the SF source plan is no longer
            // present in allDeals (SF dropped / renamed / SF-id reused).
            // Show an explicit empty state instead of a blank table so
            // the user understands the toggle is intentional but the
            // data couldn't be resolved. Toggle itself stays visible
            // so the user can flip back to 26/27 with one click.
            <div style={{
              padding: '40px 32px', textAlign: 'center',
              color: colors.textMuted, fontSize: 13,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 28 }}>📭</span>
              <div style={{ fontWeight: 600, color: colors.textSecondary, fontSize: 14 }}>Source plan not found</div>
              <div style={{ maxWidth: 440, lineHeight: 1.5 }}>
                The 25/26 source for this plan isn&apos;t in the current dataset.
                It may have been removed from Salesforce or replaced.
                Switch back to <strong>26/27</strong> to keep working.
              </div>
            </div>
          ) : planFilter && activeQuarter === SUMMARY_TAB_KEY ? (
            // ── SUMMARY TABLE: program × quarter aggregated view ──
            <>
            {renderCpmInputGrid('summary')}
            <table>
              <thead>
                <tr className="group-header">
                  <th className="group-empty" colSpan={5} />
                  <th colSpan={4}>Household</th>
                  <th colSpan={6}>Primary Demo</th>
                  <th className="group-empty" colSpan={2} />
                </tr>
                <tr>
                  <th>Program</th>
                  <th className="num">Period</th>
                  <th className="num">Day/Time</th>
                  <th className="num">#</th>
                  <th className="num">Equiv Units</th>
                  <th className="num">Avg RTG HH</th>
                  <th className="num">Avg HH (000)</th>
                  <th className="num">CPM HH</th>
                  <th className="num">Total HH (000)</th>
                  <th className="num">Avg RTG {demoName}</th>
                  <th className="num">VPVH {demoName}</th>
                  <th className="num">Avg {demoName} (000)</th>
                  <th className="num">Total {demoName} (000)</th>
                  <th className="num">Original CPM</th>
                  <th className="num">CPM {demoName}</th>
                  <th className="num">Avg Equiv Unit Rate</th>
                  <th className="num">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {buildSummaryTableRows()}
              </tbody>
            </table>
            </>
          ) : (
            // ── EXISTING DATA TABLE: per-quarter or all-plans view ──
            <>
            {/* Missing-estimates banner. Renders only when the active
                tab is a quarter that's in the plan's visibleQuarters
                but NOT in the active estimate version (e.g., 4Q'27 on
                a CY/Scatter plan while MVMS still only ships the BY
                window). Tells the user why RTG/(000)/CPM HH cells are
                blank and confirms that Units/CPM/Rate inputs still
                work. Independent of edit mode — shows in view-only
                too so the cause of blank cells is always visible.
                Matches the inline ⚠ glyph on the tab itself for at-
                a-glance recognition across the tab strip. */}
            {/* Late-quarter-on-a-pushed-Order notice.
                By decision, rows added here do NOT go to Salesforce — they
                stay in MPB until someone runs a full Revise Order. That is a
                deliberate trade, but it means this plan's MPB totals and its
                SF Order will disagree, and an MPB export will show dollars the
                Order doesn't carry. Say so plainly rather than letting someone
                read the export as the order. */}
            {planFilter && isLateQuarterOnlyEditing && planLateQuarter && (
              <div style={{
                margin: '8px 0 12px 0',
                padding: '10px 14px',
                background: '#f5f3ff',
                border: '1px solid #ddd6fe',
                borderRadius: 6,
                fontSize: 12.5,
                color: colors.textSecondary,
                display: 'flex', alignItems: 'flex-start', gap: 9,
              }}>
                <span style={{ color: '#7c3aed', flexShrink: 0, fontWeight: 700 }}>🔓</span>
                {/* ⚠ Do NOT tell the user that Revise Order will send these to
                    Salesforce. It will not. push-to-sf refuses a second push
                    (409 once PUSHED_TO_SF_AT is set) and the Name & Export
                    finalize explicitly "doesn't push to Salesforce" — it only
                    relabels the new version as the Live Order inside MPB. An
                    earlier draft of this notice promised exactly that and was
                    wrong, on the one banner whose job is to prevent this
                    misunderstanding. State the fact; don't invent a remedy. */}
                <span>
                  This plan is an <strong>Order in Salesforce</strong> and is otherwise
                  locked — {planLateQuarter.label} stays open because its estimates
                  didn&rsquo;t exist when the order was pushed.{' '}
                  <strong>Rows added here stay in MPB and are not sent to
                  Salesforce</strong> — no action in this tool will send them, so the
                  SF Order and this plan&rsquo;s totals will differ, and an export will
                  show dollars the Order doesn&rsquo;t carry.
                </span>
              </div>
            )}
            {planFilter && missingEstimateKeys.has(activeQuarter) && (() => {
              const tabLabel = visibleQuarters.find(t => t.key === activeQuarter)?.label ?? activeQuarter;
              return (
                <div style={{
                  margin: '8px 0 12px 0',
                  padding: '10px 14px',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: 6,
                  color: '#92400e',
                  fontSize: 12,
                  lineHeight: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}>
                  <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }}>⚠</span>
                  <div>
                    <strong>Estimates for {tabLabel} haven&apos;t been uploaded yet.</strong>
                    {' '}You can enter Units, CPM, and Rate now — RTG, (000), and CPM HH cells will populate automatically once an estimate file covering this quarter is uploaded.
                  </div>
                </div>
              );
            })()}
            {/* Save-blocker banner — surfaces WHICH quarters are holding
                back the save so the user isn't stuck hunting through
                every tab. Only renders when the plan is otherwise
                saveable (not view-only / not pushed) but has rows with
                blank LEN, CPM=0, or null rate on quarters OTHER than
                the current active one. Same-tab issues are already
                covered by the red LEN borders + CPM cell styling. */}
            {planFilter && !planIsViewOnly && !isPushed && (hasInvalid || hasMissingUnitLength) && (() => {
              const affectedTabs = visibleQuarters.filter(t => {
                const k = `${t.quarter}|${t.year}`;
                return (quarterAttentionCounts.get(k) ?? 0) > 0;
              });
              if (affectedTabs.length === 0) return null;
              return (
                <div style={{
                  margin: '8px 0 12px 0',
                  padding: '10px 14px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  color: '#991b1b',
                  fontSize: 12,
                  lineHeight: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}>
                  <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }}>⚠</span>
                  <div>
                    <strong>Can&apos;t save yet</strong> — rows need CPM, rate, or unit length in:{' '}
                    {affectedTabs.map((t, i) => {
                      const count = quarterAttentionCounts.get(`${t.quarter}|${t.year}`) ?? 0;
                      return (
                        <React.Fragment key={t.key}>
                          {i > 0 && ', '}
                          <button
                            type="button"
                            onClick={() => setActiveQuarter(t.key)}
                            title={`Jump to ${t.label} to fix ${count} row${count === 1 ? '' : 's'}.`}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              color: '#991b1b',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontSize: 'inherit',
                              fontWeight: 700,
                            }}
                          >
                            {t.label}
                          </button>
                          <span style={{ color: '#b91c1c', fontWeight: 500 }}>{' '}({count})</span>
                        </React.Fragment>
                      );
                    })}
                    . Click a quarter to jump.
                  </div>
                </div>
              );
            })()}
            {planFilter && renderCpmInputGrid('quarter')}
            <table style={{ '--group-header-h': `${groupHeaderH}px` } as React.CSSProperties}>
              <thead>
                <tr className="group-header" ref={groupHeaderRowRefCallback}>
                  {/* group-empty spans the row-meta columns; +1 for the Demo
                      column we render in the All Plans view (different plans
                      have different primary demos, so the per-row Demo can't
                      be conveyed via the header label like it is per-plan). */}
                  {/* Column 0 peeled out of the group-empty span so it can
                      pin horizontally — a colSpan cell can't be partially
                      sticky. Remainder drops by 1 to keep the count right. */}
                  <th className="group-empty sticky-col" />
                  <th className="group-empty" colSpan={!planFilter ? 6 : 5} />
                  <th colSpan={4}>Household</th>
                  <th colSpan={6}>Primary Demo</th>
                  {secondaryDemos.map(d => <th key={d} colSpan={2} style={{ background: colors.primaryLight, color: colors.primary }}>Secondary: {d}</th>)}
                  <th className="group-empty" colSpan={2} />
                </tr>
                <tr>
                  <th className="sticky-col" onClick={() => sortBy('program')}>
                    <div className="prog-hdr">
                      <span>Program {sortCol === 'program' ? (sortDir === 1 ? '↑' : '↓') : ''}</span>
                      {/* Add Show lives here rather than in a strip row of
                          its own: it acts on this column, and the header is
                          pinned so it stays reachable at any scroll position.
                          stopPropagation is required — the whole th is the
                          sort control, so without it every Add Show click
                          would also re-sort the grid. */}
                      {planFilter && !planIsViewOnly && (
                        <button className="icon-link"
                          onClick={e => { e.stopPropagation(); openAddShowModal(); }}
                          title="Add a show to this plan">
                          <PlusCircleIcon />Add Show
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="num" onClick={() => sortBy('quarter')}>Period</th>
                  {/* Demo column — only in All Plans, where every row may have
                      a different primary demo. Per-plan view omits this column
                      because the demo is implied by the selected plan. */}
                  {!planFilter && <th className="num">Demo</th>}
                  <th className="num">Day/Time</th>
                  <th className="num" onClick={() => sortBy('unitLength')}>Len</th>
                  <th className="num" onClick={() => sortBy('totalUnits')}>#</th>
                  <th className="num">Equiv</th>
                  <th className="num">RTG HH</th>
                  <th className="num"><GridHdr label="HH (000s)" /></th>
                  <th className="num"><GridHdr label="Total HH (000s)" /></th>
                  <th className="num">CPM HH</th>
                  {/* Demo-specific columns: per-plan view shows the plan's
                      demo (e.g. "RTG A35+"); All Plans uses generic labels
                      because the per-row Demo cell already disambiguates. */}
                  <th className="num" id="rtgDemoColHeader"><GridHdr label={planFilter ? `RTG ${demoName}` : 'RTG'} /></th>
                  <th className="num" id="vpvhColHeader"><GridHdr label={planFilter ? `VPVH ${demoName}` : 'VPVH'} /></th>
                  <th className="num" id="actualColHeader"><GridHdr label={planFilter ? `${demoName}\n(000s)` : 'Avg\n(000s)'} /></th>
                  <th className="num" id="totalDemoColHeader"><GridHdr label={planFilter ? `Total (000s) ${demoName}` : 'Total (000s)'} /></th>
                  <th className="num">Original CPM</th>
                  <th className="num" id="cpmDemoColHeader"><GridHdr label={planFilter ? `CPM\n${demoName}` : 'CPM'} /></th>
                  {secondaryDemos.map(d => (
                    <React.Fragment key={d}>
                      <th className="num muted"><GridHdr label={`${d} (000s)`} /></th>
                      <th className="num muted"><GridHdr label={`CPM ${d}`} /></th>
                    </React.Fragment>
                  ))}
                  <th className="num" onClick={() => sortBy('newRate')}>Rate</th>
                  <th className="num" onClick={() => sortBy('totalDollars')}>Total $</th>
                </tr>
              </thead>
              <tbody>
                {/* Chunked rendering — the full row array was built
                    once at the top of the component (allTableRows).
                    Slice to the current visibleRowCount; the
                    grow+reset effects upstairs stream the remaining
                    rows in across the next few frames. */}
                {allTableRows.slice(0, visibleRowCount)}
              </tbody>
            </table>
            {/* ── HISTORICAL DIFF FOOTER ───────────────────────────
                Shown only when viewing a historical version and the
                latest has rows that don't exist in this snapshot.
                Lists the affected programs so the user can see at a
                glance what was added between then and now. */}
            {historicalDiff && historicalDiff.addedInLatest.length > 0 && (
              <div style={{
                marginTop: 8,
                padding: '10px 14px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 6,
                fontSize: 12,
                color: '#92400e',
              }}>
                <span style={{ fontWeight: 700 }}>
                  {historicalDiff.addedInLatest.length} {historicalDiff.addedInLatest.length === 1 ? 'row' : 'rows'} added since this version:
                </span>{' '}
                <span style={{ color: '#a16207' }}>
                  {(() => {
                    const programs = Array.from(new Set(historicalDiff.addedInLatest.map(r => r.program)));
                    const shown = programs.slice(0, 6).join(', ');
                    const extra = programs.length > 6 ? ` + ${programs.length - 6} more` : '';
                    return shown + extra;
                  })()}
                </span>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* TOAST */}
      <div className={`toast${toast ? ' show' : ''}`}>{toast}</div>

      {/* ADD SHOW MODAL */}
      {showAddShowModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) { setShowAddShowModal(false); setAddShowSearch(''); setAddShowSelected(new Set());} }}>
          <div className="modal-box" style={{ width: 460 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>Add Show to Plan</h2>
              <button onClick={() => { setShowAddShowModal(false); setAddShowSearch(''); setAddShowSelected(new Set());}} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
            </div>
            {/* Quarter target chips — toggle which quarters the picked
                program lands in. Click a program tile below to commit;
                one row is created per checked quarter, all sharing the
                same seeded defaults. */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Add to {addShowQuarterKeys.size === 1 ? 'quarter' : `${addShowQuarterKeys.size} quarters`}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {/* Chip picker uses visibleQuarters so a CY plan offers
                    1Q-4Q'27, Scatter all 5, etc — instead of the version's
                    fixed BY set. */}
                {visibleQuarters.map(t => {
                  const checked = addShowQuarterKeys.has(t.key);
                  // On a pushed plan only the late quarter is editable — every
                  // other chip would write into a locked quarter of an order
                  // already sent to Salesforce. Disable rather than hide, so
                  // the constraint is visible rather than mysterious.
                  const locked = isLateQuarterOnlyEditing
                    && t.key !== planLateQuarter?.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;
                        setAddShowQuarterKeys(prev => {
                          const next = new Set(prev);
                          if (next.has(t.key)) {
                            // Don't allow clearing the last chip — picking a
                            // program with zero quarters is a no-op anyway.
                            if (next.size <= 1) return prev;
                            next.delete(t.key);
                          } else {
                            next.add(t.key);
                          }
                          return next;
                        });
                      }}
                      style={{
                        padding: '5px 11px',
                        fontSize: 11.5, fontWeight: 600,
                        border: `1px solid ${checked ? colors.primary : colors.border}`,
                        background: locked
                          ? colors.bgPanel
                          : (checked ? colors.primaryLight : '#fff'),
                        color: locked
                          ? colors.textDisabled
                          : (checked ? colors.primary : colors.textMuted),
                        borderRadius: 999,
                        cursor: locked ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                        opacity: locked ? 0.6 : 1,
                      }}
                      title={
                        locked
                          ? `${t.label} is locked — this plan is an Order in Salesforce. Only ${planLateQuarter?.label} can still be added to.`
                          : (checked ? `Remove ${t.label} from target quarters` : `Also add to ${t.label}`)
                      }
                    >
                      {checked && '✓ '}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Plan-level default LEN hint. When the plan has no
                defaultUnitLength set, every row this modal seeds
                lands with LEN blank — which blocks save until the
                user hand-fills LEN on each row. Surface that before
                the user commits so they can either set a default via
                Edit Details first (one click) or at least know they
                need to fill LEN per row afterward. Hidden when the
                plan already has a default (no problem to warn about)
                or when the plan can't resolve a meta yet. */}
            {planFilter && !resolveDefaultUnitLength(planFilter) && (
              <div style={{
                marginBottom: 12,
                padding: '8px 10px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 6,
                color: '#92400e',
                fontSize: 11.5,
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
              }}>
                <span style={{ fontSize: 12, lineHeight: 1, marginTop: 1 }}>💡</span>
                <div>
                  These rows will have blank LEN — fill LEN on each after adding, or set a plan default first (Edit Details → Default Unit Length).
                </div>
              </div>
            )}
            <input
              autoFocus
              value={addShowSearch}
              onChange={e => setAddShowSearch(e.target.value)}
              placeholder="Search shows…"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #dde1e9', borderRadius: 6, fontSize: 13, marginBottom: 10, outline: 'none', fontFamily: "'Inter', Arial, sans-serif" }}
            />
            {/* Only offer programs that exist in the plan's bound
                estimate — otherwise adding a show would produce a row
                with all-dashes for RTG/HH/CPM/demo (000s) since the
                lookup misses. Mirrors the same match logic used by
                calcEstimateFields:
                  1) case-insensitive canonical via activeVersion.programNameIndex
                  2) manual alias via ESTIMATE_NAME_MAP
                If neither resolves, the program is unavailable.
                Legacy plans with no bound estimate → empty list +
                "No estimate bound" notice, so users can't add anything
                until someone binds an estimate. */}
            {(() => {
              const nameIndex = activeVersion?.programNameIndex ?? null;
              // No estimate binding → hard-block adds with a notice.
              if (!nameIndex || Object.keys(nameIndex).length === 0) {
                return (
                  <div style={{
                    padding: '16px 14px',
                    fontSize: 12,
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 6,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="#d97706" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round"
                         style={{ flexShrink: 0, marginTop: 1 }}>
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>No estimate bound to this plan.</div>
                      <div style={{ color: '#a16207', lineHeight: 1.4 }}>
                        Shows can&apos;t be added until an estimate version is attached. Bind an estimate first — new rows would otherwise render with no impressions or ratings data.
                      </div>
                    </div>
                  </div>
                );
              }
              // Match a program against the estimate's programNameIndex.
              // Same fallback chain as calcEstimateFields.
              const nameInEstimate = (p: string): boolean => {
                const normalized = normalizeProgramName(p);
                if (nameIndex[normalized]) return true;
                const aliased = ESTIMATE_NAME_MAP[p];
                if (aliased) return true;
                return false;
              };
              // Tighter check: does the estimate actually have a data
              // row for this program under the plan's primary demo at
              // any of the target quarters? A program can be present
              // in the nameIndex (e.g. "25 Words or Less") without
              // having a row for the specific (quarter, day, demo)
              // tuple the user is trying to add — which is exactly
              // how blank-cell rows used to slip in.
              //
              // Demo is resolved with the same precedence addShow()
              // uses: first deal in the current quarter → plan meta
              // → empty string (matches the estimate's blank-demo
              // rows if any). Target quarters come from the modal's
              // chip picker (addShowQuarterKeys). Any single match
              // qualifies — if the show has data for 4Q'26 but not
              // 1Q'27, we still let the user pick it (the 1Q'27 row
              // would land blank, but the 4Q'26 row would work). If
              // the user later needs stricter behavior we can flip
              // this to every() instead of some().
              const targetQuarters = visibleQuarters.filter(t => addShowQuarterKeys.has(t.key));
              const manualMetaForFilter = getPlanMeta(planFilter);
              const demoForFilter = filteredDeals[0]?.demo || manualMetaForFilter?.demo || '';
              const demoRTG = demoToRTG(demoForFilter);
              const hasEstimateRowForTargets = (p: string): boolean => {
                if (targetQuarters.length === 0) return true; // no targets yet → don't over-filter
                const day = DAY_TIME[p] || '';
                for (const t of targetQuarters) {
                  if (getEstimate(p, day, fmtPeriod(t.quarter, t.year), demoRTG)) return true;
                }
                return false;
              };
              const programInEstimate = (p: string): boolean => nameInEstimate(p) && hasEstimateRowForTargets(p);
              const eligible = ALL_PROGRAMS.filter(programInEstimate);
              const filtered = eligible.filter(p =>
                (PROGRAM_DISPLAY[p] || p).toLowerCase().includes(addShowSearch.toLowerCase()),
              );
              return (
                <>
                  {/* Multi-select list. Clicking a row toggles it in
                      the addShowSelected set; the visible checkbox on
                      the left mirrors the state. The Add button below
                      flushes every picked show × every checked quarter
                      in one commit. Programs already in the plan get a
                      subtle "in plan" badge — they're still selectable
                      (users sometimes want to intentionally duplicate a
                      show across a quarter it wasn't previously in). */}
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                      <div style={{
                        padding: '14px 12px',
                        fontSize: 12,
                        color: colors.textMuted,
                        fontStyle: 'italic',
                        textAlign: 'center',
                      }}>
                        {addShowSearch
                          ? `No shows in the current estimate match "${addShowSearch}"`
                          : 'No shows available in the current estimate.'}
                      </div>
                    ) : filtered.map(p => {
                      const inPlan = filteredDeals.some(d => d.program === p);
                      const selected = addShowSelected.has(p);
                      return (
                        <div key={p}
                          onClick={() => {
                            setAddShowSelected(prev => {
                              const next = new Set(prev);
                              if (next.has(p)) next.delete(p);
                              else next.add(p);
                              return next;
                            });
                          }}
                          style={{
                            padding: '10px 12px', fontSize: 13, cursor: 'pointer',
                            borderRadius: 6, display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', color: colors.text,
                            background: selected ? colors.primaryLight : undefined,
                          }}
                          onMouseEnter={e => { if (!selected) e.currentTarget.style.background = colors.primaryLight; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = ''; }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {/* Checkbox — read-only visual; the row's
                                onClick handles the toggle so clicking
                                anywhere on the row works. */}
                            <input
                              type="checkbox"
                              checked={selected}
                              readOnly
                              tabIndex={-1}
                              style={{ margin: 0, cursor: 'pointer', pointerEvents: 'none' }}
                            />
                            <span>{PROGRAM_DISPLAY[p] || p}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, color: colors.textDisabled }}>{DAY_TIME[p] || ''}</span>
                            {inPlan && <span style={{ fontSize: 10, background: '#f0f4ff', color: colors.primary, padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>in plan</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Commit strip — disabled when nothing is picked or
                      no quarters are checked. Count summary tells the
                      user exactly what's about to happen. */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 12, marginTop: 12, borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>
                      {addShowSelected.size === 0
                        ? 'Select one or more shows to add'
                        : `${addShowSelected.size} show${addShowSelected.size === 1 ? '' : 's'} × ${addShowQuarterKeys.size} quarter${addShowQuarterKeys.size === 1 ? '' : 's'} = ${addShowSelected.size * addShowQuarterKeys.size} row${addShowSelected.size * addShowQuarterKeys.size === 1 ? '' : 's'}`}
                    </div>
                    <button
                      disabled={addShowSelected.size === 0 || addShowQuarterKeys.size === 0}
                      onClick={() => commitAddShow()}
                      style={{
                        padding: '6px 14px', fontSize: 13, fontWeight: 600,
                        background: (addShowSelected.size === 0 || addShowQuarterKeys.size === 0) ? colors.textDisabled : colors.primary,
                        color: '#fff', border: 'none', borderRadius: 6,
                        cursor: (addShowSelected.size === 0 || addShowQuarterKeys.size === 0) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Add {addShowSelected.size > 0 ? `(${addShowSelected.size})` : ''}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* NEW PLAN MODAL */}
      {showNewPlanModal && (
        <div
          className="modal-overlay"
          onClick={e => {
            // Block dismiss-via-backdrop-click while a save is in
            // flight so the user can't orphan the POST by accident.
            if (isCreatingPlan) return;
            if (e.target === e.currentTarget) closeNewPlanModal();
          }}
        >
          <div className="modal-box" style={{ width: 560, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Create New 26/27 Plan</h2>
              <button
                onClick={closeNewPlanModal}
                disabled={isCreatingPlan}
                style={{
                  background: 'none', border: 'none', fontSize: 20,
                  cursor: isCreatingPlan ? 'not-allowed' : 'pointer',
                  color: colors.textMuted,
                  opacity: isCreatingPlan ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Form-content wrapper — pointer-events + opacity flip
                on isCreatingPlan locks every input/lookup/dropdown/
                button below while the POST is in flight, so the user
                can't change fields mid-save. Buttons + resolve overlay
                sit outside this wrapper so they stay reachable. */}
            <div
              style={{
                pointerEvents: isCreatingPlan ? 'none' : 'auto',
                opacity: isCreatingPlan ? 0.55 : 1,
                transition: 'opacity 120ms ease',
              }}
            >
            {/* NEW-PLAN SOURCE PICKER — optional. Two source flavors:
                  • 25/26 (roll-forward): year+1 shift, unit-length remap
                  • 26/27 (clone): verbatim same-season copy
                Which flavor drives every downstream behavior — helper
                text, chip color, whether the Unit Length picker shows,
                and the save flow itself. */}
            <div style={{ marginBottom: 18, padding: '12px 14px', background: colors.bgPanel, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                Start from existing plan? <span style={{ textTransform: 'none', fontWeight: 500, color: colors.textDisabled }}>(optional)</span>
              </label>
              {/* Helper heading text — reads different depending on which
                  flavor the picked source is, so the user knows the
                  create action's semantics up front. */}
              {npRollForwardSource && npRollForwardYear && (
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 6, fontStyle: 'italic' }}>
                  {npRollForwardYear === '25/26'
                    ? 'Rolling forward — deals shift to 26/27 with a year adjustment'
                    : 'Cloning — deals copy verbatim into a new 26/27 plan'}
                </div>
              )}
              {npRollForwardSource ? (
                (() => {
                  // Year chip + confirmation-box color match the Load-a-plan
                  // picker's palette: 25/26 = amber (older-season), 26/27 =
                  // blue (current-season). Keeps the two pickers visually
                  // consistent across the app.
                  const is2526 = npRollForwardYear === '25/26';
                  const chipBg = is2526 ? '#fef3c7' : '#dbeafe';
                  const chipFg = is2526 ? '#92400e' : '#1e40af';
                  const boxBg  = is2526 ? '#fffbeb' : '#eff6ff';
                  const boxBorder = is2526 ? '#fde68a' : '#bfdbfe';
                  const nameFg = is2526 ? '#78350f' : '#1e3a8a';
                  const xFg    = is2526 ? '#92400e' : '#1e40af';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: boxBg, border: `1px solid ${boxBorder}`, borderRadius: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, background: chipBg, color: chipFg, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.3 }}>{npRollForwardYear}</span>
                      <span style={{ fontSize: 13, color: nameFg, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{npRollForwardSource}</span>
                      <button onClick={() => pickRollForwardSource(null)} title="Clear and start blank"
                        style={{ background: 'none', border: 'none', color: xFg, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}>✕</button>
                    </div>
                  );
                })()
              ) : (
                <div ref={npRollForwardRef} style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={npRollForwardSearch}
                    onChange={e => { setNpRollForwardSearch(e.target.value); setNpRollForwardOpen(true); }}
                    onFocus={() => setNpRollForwardOpen(true)}
                    placeholder={showCloneSources ? 'Search plans…' : 'Search 25/26 plans…'}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #dde1e9', borderRadius: 6, fontSize: 13 }}
                  />
                  {npRollForwardOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #dde1e9', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxHeight: 220, overflowY: 'auto', zIndex: 20 }}>
                      {filteredRollForwardSources.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: colors.textDisabled, fontStyle: 'italic' }}>
                          {rollForwardSources.length === 0
                            ? 'No plans available.'
                            : 'No matches — try fewer keywords.'}
                        </div>
                      ) : filteredRollForwardSources.slice(0, 50).map(opt => {
                        // Row chip matches the confirmation-box chip palette
                        // — 25/26 amber, 26/27 blue — so the user sees the
                        // same signal both before and after selection.
                        const is2526 = opt.year === '25/26';
                        const chipBg = is2526 ? '#fef3c7' : '#dbeafe';
                        const chipFg = is2526 ? '#92400e' : '#1e40af';
                        return (
                          <div key={`${opt.year}-${opt.name}`} onClick={() => pickRollForwardSource(opt)}
                            style={{ padding: '8px 12px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = '')}>
                            <span style={{ fontSize: 9.5, fontWeight: 700, background: chipBg, color: chipFg, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.3, flexShrink: 0 }}>{opt.year}</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.name}</span>
                          </div>
                        );
                      })}
                      {filteredRollForwardSources.length > 50 && (
                        <div style={{ padding: '6px 12px', fontSize: 11, color: colors.textDisabled, textAlign: 'center', fontStyle: 'italic' }}>
                          Showing first 50 — refine your search to see more.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* UNIT LENGTH FOR PLAN — copy transform. Sits under the
                  source picker because it only affects rolled-forward
                  rows; a from-scratch plan (no source) has nothing to
                  transform. Visible-but-disabled until a source is
                  picked so the user sees the option exists before
                  committing to a source. Default is the most-common
                  length in the picked source; "As is" at the bottom
                  bypasses conversion entirely.
                  Hidden entirely for 26/27 clones — same-season copies
                  are always verbatim (no length remap makes sense). */}
              {/* Unit Length for Plan lives INSIDE the source panel
                  ONLY when a 25/26 source is picked — that's when the
                  picker's transform job is meaningful (it remaps
                  source rows to the target length as they roll into
                  the new plan). It also stores the plan-level default
                  as a side-effect.
                  For from-scratch plans (no source) the picker moves
                  down into the main field grid, alongside Marketplace
                  — it's just a plan-level setting there, not a
                  transform. See the mirrored render block further
                  down.
                  For 26/27 verbatim clones the equivalent picker
                  lives under Plan Nickname in the main grid, pre-
                  populated from the source plan's own default (see
                  pickRollForwardSource). Same field, same purpose,
                  just no "As is" option because there's no length
                  transform on same-season clones. */}
              {npRollForwardYear === '25/26' && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                  Unit Length for Plan
                </label>
                <select
                  value={npUnitLength}
                  onChange={e => setNpUnitLength(e.target.value)}
                  title={
                    npRollForwardSource
                      ? 'Also converts rolled-forward rows to this unit length. Non-target rows fold into the target-length row (units>1) for the same show/quarter/demo. Stored as the plan default for new rows added later.'
                      : 'Default unit length for this plan — new rows added via + Add Show / + Add Row seed their length from this value.'
                  }
                  style={{
                    padding: '6px 10px', fontSize: 13,
                    border: '1px solid #dde1e9', borderRadius: 6,
                    background: '#fff', cursor: 'pointer',
                    color: colors.text, minWidth: 140,
                  }}
                >
                  <option value="">— (no default)</option>
                  <option value="15">:15</option>
                  <option value="30">:30</option>
                  <option value="45">:45</option>
                  <option value="60">:60</option>
                  {npRollForwardSource && (
                    <option value="as-is">As is (no conversion)</option>
                  )}
                </select>
              </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Plan Nickname — free-text label that writes to
                  `SALES_PLAN__C.NAME`. First field in the grid so the
                  user names their plan before the SF-lookup work.
                  Not the uniqueness key — multiple plans can share a
                  nickname. Uniqueness lives on the Plan Name row at
                  the bottom (auto-generated from lookups). */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={formLabelStyle}>Plan Nickname *</label>
                <input
                  type="text"
                  value={npDealname}
                  onChange={e => setNpDealname(e.target.value)}
                  placeholder="e.g. HYGIENE 4Q'26"
                  style={{
                    ...formInputStyle,
                    ...(npCloneNicknameCollision ? {
                      background: '#fef2f2',
                      border: `1.5px solid ${colors.danger}`,
                    } : {}),
                  }}
                />
                {npCloneNicknameCollision && (
                  <div style={{ marginTop: 6, fontSize: 11.5, color: '#b91c1c', lineHeight: 1.4 }}>
                    <strong>Nickname matches the source plan.</strong>{' '}
                    Change it (e.g. add {' '}
                    <code style={{ background: '#fef2f2', padding: '0 4px', borderRadius: 3, fontFamily: 'DM Mono, monospace' }}>
                      {' – v2'}
                    </code>{' '}
                    or{' '}
                    <code style={{ background: '#fef2f2', padding: '0 4px', borderRadius: 3, fontFamily: 'DM Mono, monospace' }}>
                      {' – Alt'}
                    </code>
                    ) so this clone doesn&apos;t collide with{' '}
                    <strong>{npRollForwardSource}</strong>.
                  </div>
                )}
              </div>
              {/* From-scratch OR 26/27-clone Default Unit Length —
                  mirrors the placement of the source-panel picker.
                  From-scratch: user picks a default for future rows.
                  26/27 clone: pre-populated from the source plan's
                  own default (see pickRollForwardSource). User can
                  override; the picker exists so cloning a plan with
                  no default doesn't silently produce another
                  no-default plan.
                  Hidden only for the 25/26 clone case, whose source-
                  panel picker above serves both the transform and the
                  new-plan default in one field. */}
              {npRollForwardYear !== '25/26' && (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={formLabelStyle}>Default Unit Length</label>
                  <select
                    value={npUnitLength}
                    onChange={e => setNpUnitLength(e.target.value)}
                    title="Applied to new rows added via + Add Show / + Add Row. Editable later in Plan → Details."
                    style={{
                      ...formInputStyle,
                      padding: '8px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">— (no default)</option>
                    <option value="15">:15</option>
                    <option value="30">:30</option>
                    <option value="45">:45</option>
                    <option value="60">:60</option>
                  </select>
                </div>
              )}
              {/* Agency & Buyer — combined picker. First lookup field
                  in the modal because sales-plan workflows start from
                  the agency relationship (who's buying, from whom).
                  Backend searches Contact.Name OR Account.Name and
                  tokenizes the query so "starcom amy" finds "Starcom —
                  Amy Huang". Each dropdown row renders <b>{Agency}</b>
                  — {Buyer} so the "which General Market" ambiguity is
                  gone. Picking sets four state pieces atomically
                  (contactId + buyer name + accountId + account name)
                  so the plan-name formula + save payload stay coherent.
                  Spans both grid columns since there's no separate
                  Agency field anymore. */}
              <div style={{ gridColumn: '1 / -1' }}>
                <LookupField<ContactLookupItem>
                  label="Agency & Buyer"
                  required
                  placeholder="Type an agency or buyer name…"
                  value={npContactId}
                  displayName={npBuyer}
                  searchFn={searchContacts}
                  showTbdOption
                  // TBD covers BOTH Agency and Buyer (they share a
                  // picker). Picking TBD nulls both SF ids and sets
                  // both display names to "TBD" — downstream push
                  // gate treats either being TBD as a block.
                  isTbdPicked={npBuyer === 'TBD' && !npContactId && npAgency === 'TBD' && !npAccountId}
                  onSelectTbd={() => {
                    setNpContactId(null);
                    setNpBuyer('TBD');
                    setNpAccountId(null);
                    setNpAgency('TBD');
                  }}
                  renderItem={item => (
                    <span>
                      <strong>{item.accountName ?? '(No agency)'}</strong>
                      <span style={{ color: '#4b5563' }}> — {item.name}</span>
                    </span>
                  )}
                  formatSelectedValue={buyerName => {
                    if (npAgency) return `${npAgency} — ${buyerName}`;
                    return buyerName;
                  }}
                  onChange={(id, name, extra) => {
                    setNpContactId(id);
                    setNpBuyer(name);
                    setNpAccountId(extra.accountId ?? null);
                    setNpAgency(extra.accountName ?? '');
                  }}
                  onClear={() => {
                    setNpContactId(null);
                    setNpBuyer('');
                    setNpAccountId(null);
                    setNpAgency('');
                  }}
                />
              </div>
              {/* Advertiser — SF lookup. Picking clears any picked Brand
                  (since Brand is scoped to Advertiser). */}
              <div style={{ gridColumn: '1/-1' }}>
                <LookupField
                  label="Advertiser"
                  required
                  placeholder="Type to search advertisers…"
                  value={npAdvertiserId}
                  displayName={npAdvertiser}
                  searchFn={searchAdvertisers}
                  showTbdOption
                  isTbdPicked={npAdvertiser === 'TBD' && !npAdvertiserId}
                  onSelectTbd={() => {
                    setNpAdvertiserId(null);
                    setNpAdvertiser('TBD');
                    // TBD advertiser also clears Brand — brand was
                    // scoped to whatever advertiser was picked before.
                    setNpBrandId(null);
                    setNpBrand('');
                  }}
                  onChange={(id, name) => {
                    setNpAdvertiserId(id);
                    setNpAdvertiser(name);
                    // Reset Brand — different advertiser means different brand list.
                    setNpBrandId(null);
                    setNpBrand('');
                  }}
                  onClear={() => {
                    setNpAdvertiserId(null);
                    setNpAdvertiser('');
                    setNpBrandId(null);
                    setNpBrand('');
                  }}
                />
              </div>
              {/* Brand — bidirectional with Advertiser. If an
                  Advertiser is picked, search is scoped to its brands.
                  Otherwise search spans all 21k. Picking a Brand
                  always pulls in the parent Advertiser too (or swaps
                  it if it differs from the current pick). Required so
                  the Sales Plan Name formula always has a Brand part. */}
              <div>
                <LookupField<BrandLookupItem>
                  label="Brand"
                  required
                  placeholder={npAdvertiserId ? 'Type to search brands…' : 'Type to search brands (any advertiser)…'}
                  value={npBrandId}
                  displayName={npBrand}
                  searchFn={(q) => searchBrands(q, npAdvertiserId)}
                  showTbdOption
                  isTbdPicked={npBrand === 'TBD' && !npBrandId}
                  onSelectTbd={() => {
                    setNpBrandId(null);
                    setNpBrand('TBD');
                  }}
                  onChange={(id, name, extra) => {
                    setNpBrandId(id);
                    setNpBrand(name);
                    // Auto-fill / swap the Advertiser when the picked
                    // brand belongs to a different one. Empty current
                    // advertiser → fills it; mismatched current → swaps.
                    // Matching current → no-op.
                    if (extra.advertiserId && extra.advertiserId !== npAdvertiserId) {
                      setNpAdvertiserId(extra.advertiserId);
                      setNpAdvertiser(extra.advertiserName ?? '');
                    }
                  }}
                  onClear={() => { setNpBrandId(null); setNpBrand(''); }}
                  emptyResultsMessage={(q) => {
                    // When Advertiser is picked and the scoped brand
                    // search returns zero rows, flag the dead-end in red
                    // so the user knows to either clear the Advertiser
                    // (fall back to all-brands search) or add the brand
                    // in Salesforce first. Only fires with a search
                    // string — an empty focused state stays generic.
                    if (!q || !npAdvertiserId) return null;
                    return {
                      text: `${npAdvertiser || 'This advertiser'} has no brands matching "${q}" in Salesforce. Clear the Advertiser to search all brands, or add the brand in SF first.`,
                      error: true,
                    };
                  }}
                />
              </div>
              {/* Account Exec — plain text, defaults to Tiffany DeDeo. */}
              <div>
                <label style={formLabelStyle}>Account Exec</label>
                <input type="text" value={npAe} onChange={e => setNpAe(e.target.value)} style={formInputStyle} />
              </div>
              <div>
                <label style={formLabelStyle}>Primary Demo *</label>
                {/* Typeahead behavior — same UX as LookupField:
                    • Click into a filled field → text auto-selects (native)
                      so typing immediately replaces
                    • Dropdown opens on focus showing the FULL list —
                      when the input still matches the picked value,
                      we bypass the filter so users can arrow-key to
                      a neighbor without hunting
                    • × clear button for one-click wipe
                    • Current pick highlighted + scrolled into view */}
                {(() => {
                  // Filter bypass: when the input shows the picked
                  // value (unchanged since last pick), don't filter —
                  // show the full list so the user can change to a
                  // neighboring option. First keystroke different from
                  // the picked value flips this off.
                  const isFieldShowingPickedValue = npDemoInput === npDemo && npDemo !== '';
                  const filterText = isFieldShowingPickedValue ? '' : npDemoInput;
                  // primaryDemoOptions = SF picklist when loaded, else
                  // SYSTEM_DEMOS shortlist fallback (see boot effect).
                  const filtered = primaryDemoOptions.filter(d =>
                    d.toLowerCase().includes(filterText.toLowerCase()),
                  );
                  // Ref so we can blur the input after a pick — otherwise
                  // focus visually lingers and the user has to click
                  // elsewhere to dismiss the "active" outline.
                  const demoInputRef = npDemoInputRef;
                  return (
                    <div style={{ position: 'relative' }}>
                      <input
                        ref={demoInputRef}
                        type="text" value={npDemoInput} autoComplete="off" placeholder="Type to search…"
                        onChange={e => { setNpDemoInput(e.target.value); setNpShowDemoDropdown(true); }}
                        onFocus={e => {
                          setNpShowDemoDropdown(true);
                          const el = e.currentTarget;
                          requestAnimationFrame(() => { try { el.select(); } catch { /* ignore */ } });
                        }}
                        style={{ ...formInputStyle, paddingRight: npDemo ? 28 : (formInputStyle as any).padding }} />
                      {npDemo && (
                        <button
                          type="button"
                          aria-label="Clear selection"
                          onMouseDown={e => {
                            // mousedown so blur doesn't close the dropdown
                            // before the click registers on some browsers.
                            e.preventDefault();
                            setNpDemo('');
                            setNpDemoInput('');
                            setNpShowDemoDropdown(true);
                            // Refocus the input so the user can start
                            // typing immediately.
                            requestAnimationFrame(() => demoInputRef.current?.focus());
                          }}
                          style={{
                            position: 'absolute', right: 6, top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent', border: 'none',
                            color: '#6b7280', cursor: 'pointer',
                            fontSize: 14, padding: '2px 6px',
                          }}
                        >×</button>
                      )}
                      {npShowDemoDropdown && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 160, overflowY: 'auto' }}>
                          {filtered.map(d => {
                            const isCurrent = d === npDemo;
                            return (
                              <div
                                key={d}
                                ref={el => {
                                  // Scroll the current pick into view on
                                  // open so the user sees it immediately
                                  // instead of at the top of the list.
                                  if (isCurrent && el && npShowDemoDropdown) {
                                    requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));
                                  }
                                }}
                                onMouseDown={e => {
                                  e.preventDefault();
                                  setNpDemoInput(d);
                                  setNpDemo(d);
                                  setNpShowDemoDropdown(false);
                                  // Blur the input so focus doesn't
                                  // linger on the field visually.
                                  demoInputRef.current?.blur();
                                }}
                                style={{
                                  padding: '8px 10px', cursor: 'pointer', fontSize: 13,
                                  background: isCurrent ? colors.primaryLight : undefined,
                                  fontWeight: isCurrent ? 600 : 400,
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = colors.primaryLight)}
                                onMouseLeave={e => (e.currentTarget.style.background = isCurrent ? colors.primaryLight : '')}
                              >
                                {d}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label style={formLabelStyle}>Marketplace *</label>
                {/* Marketplace picklist — mirrors SF SALES_PLAN__C.Upfront_Scatter__c.
                    "Upfront" was split into BY (broadcast year) and CY (calendar year)
                    variants in SF; old plans saved as plain "Upfront" will show blank
                    here until re-picked + saved.
                    Custom SimpleSelect (not native <select>) so the dropdown menu
                    styling matches Advertiser / Brand / Buyer / Primary Demo. */}
                <SimpleSelect
                  value={npMarketplace}
                  onChange={setNpMarketplace}
                  options={['BY Upfront', 'CY Upfront', 'Scatter']}
                  requiredMissing={!npMarketplace}
                />
              </div>
              {/* Rate Card — which estimate file this plan pulls from.
                  Upfront only: a rate card is a variant OF an upfront, and
                  there is no WPP scatter, so the field disappears entirely
                  when Marketplace is Scatter (npRateCard is forced back to
                  'general' by the effect above, so a stale WPP pick can't
                  survive the switch).

                  Only cards that actually have a 26/27 estimate are offered.
                  That single rule removes the whole class of "plan bound to
                  a rate card with nothing behind it" problems — including
                  the null-estimateVersionId path in submitNewPlan.

                  Defaults to General and is never inferred from Agency or
                  anything else. The user picks it. */}
              {npRateCardApplies && npRateCardOptions.length > 1 && (
                <div>
                  <label style={formLabelStyle}>Rate Card</label>
                  <SimpleSelect
                    value={formatRateCard(npRateCard) ?? 'General'}
                    onChange={label => {
                      const picked = npRateCardOptions.find(
                        c => (formatRateCard(c) ?? 'General') === label,
                      );
                      if (picked) setNpRateCard(picked);
                    }}
                    options={npRateCardOptions.map(c => formatRateCard(c) ?? 'General')}
                  />
                </div>
              )}
              <div>
                <label style={formLabelStyle}>Deal Year</label>
                <input type="text" value="26/27" readOnly
                  title="Only 26/27 plans are saved to Snowflake today."
                  style={{ ...formInputStyle, border: '1px solid #e5e7eb', background: '#f3f4f6', color: colors.textMuted, cursor: 'default' }} />
              </div>
              {/* Plan Name (read-only) — live preview of the SF
                  `Sales_Plan_Name__c` workflow formula. Sits at the
                  bottom of the grid as a "summary line" — the user
                  fills in Nickname / Agency & Buyer / Advertiser /
                  Brand / Demo above, then sees the resulting Sales
                  Plan Name assemble here right before the Create
                  button. "…" placeholders mark missing ingredients.
                  Acts as the uniqueness key against existing 26/27
                  plans; Create is gated on `npNameCollision`. */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ ...formLabelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Plan Name
                  <span style={{ fontSize: 10, fontWeight: 500, color: colors.textDisabled, textTransform: 'none', letterSpacing: 0 }}>
                    · auto-generated · read-only
                  </span>
                </label>
                <div
                  title="Auto-generated from Agency-Brand-Advertiser-Demo-Year. Populated by Salesforce workflow on save."
                  style={{
                    ...formInputStyle,
                    // Darker grey background so this field reads clearly
                    // as read-only vs. the white editable inputs above.
                    background: npNameCollision ? '#fef2f2' : '#eef2f6',
                    border: npNameCollision ? `1.5px solid ${colors.danger}` : formInputStyle.border,
                    color: npComputedNameComplete ? colors.text : colors.textMuted,
                    fontFamily: "'Inter', Arial, sans-serif",
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'not-allowed',
                  }}
                >
                  {npComputedSalesPlanName}
                </div>
                {npNameCollision && npCollidingPlanNickname && (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: '#b91c1c' }}>
                    A plan with this Sales Plan Name already exists for 26/27:{' '}
                    <a
                      href="#open-existing-plan"
                      onClick={e => {
                        e.preventDefault();
                        const nickname = npCollidingPlanNickname;
                        closeNewPlanModal();
                        switchPlan(nickname);
                      }}
                      style={{ color: colors.primary, textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {npCollidingPlanNickname}
                    </a>
                    . Change Advertiser, Brand, Buyer, or Demo to differentiate.
                  </div>
                )}
                {!npComputedNameComplete && (() => {
                  const missing = [
                    !npAdvertiser && 'Advertiser',
                    !npBrand && 'Brand',
                    !npBuyer && 'Buyer',
                    !npDemo && 'Demo',
                  ].filter(Boolean) as string[];
                  if (missing.length === 0) return null;
                  const list = missing.length === 1
                    ? missing[0]
                    : missing.length === 2
                      ? `${missing[0]} and ${missing[1]}`
                      : `${missing.slice(0, -1).join(', ')}, and ${missing[missing.length - 1]}`;
                  return (
                    <div style={{ marginTop: 4, fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
                      Pick {list} to complete the name.
                    </div>
                  );
                })()}
              </div>
            </div>
            </div>{/* end form-content wrapper */}
            {npError && <div style={{ color: colors.danger, fontSize: 12, marginTop: 12 }}>{npError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={closeNewPlanModal}
                disabled={isCreatingPlan}
                style={{
                  padding: '9px 18px',
                  border: '1px solid #dde1e9',
                  borderRadius: 6,
                  background: '#fff',
                  fontSize: 13,
                  cursor: isCreatingPlan ? 'not-allowed' : 'pointer',
                  color: colors.textSecondary,
                  opacity: isCreatingPlan ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              {/* Create is gated on ALL three: no collision, not mid-save,
                  and every required field filled. The disabled state
                  and tooltip surface the exact reason so the user can
                  see the block before clicking. */}
              <button
                onClick={submitNewPlan}
                disabled={npNameCollision || npCloneNicknameCollision || isCreatingPlan || npHasMissingRequired}
                title={
                  isCreatingPlan
                    ? 'Saving to Snowflake…'
                    : npNameCollision
                      ? 'Plan name collides with an existing plan — change the name to continue.'
                      : npCloneNicknameCollision
                        ? `Rename the plan — nickname matches the source ("${npRollForwardSource}") and would collide.`
                        : npHasMissingRequired
                          ? `Fill in required field${npMissingRequired.length > 1 ? 's' : ''}: ${npMissingRequired.join(', ')}`
                          : 'Create this 26/27 plan'
                }
                style={{
                  padding: '9px 18px',
                  background: (npNameCollision || npCloneNicknameCollision || isCreatingPlan || npHasMissingRequired) ? colors.textDisabled : colors.primary,
                  color: '#fff', border: 'none', borderRadius: 6,
                  fontSize: 13, fontWeight: 600,
                  cursor: (npNameCollision || npCloneNicknameCollision || isCreatingPlan || npHasMissingRequired) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                {isCreatingPlan && <span className="vh-spin" style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: '#fff' }} />}
                {isCreatingPlan ? 'Creating…' : 'Create Plan'}
              </button>
            </div>

            {/* RESOLVE OVERLAY — covers the whole modal box while the
                pickRollForwardSource → resolveSalesPlan round-trip is in
                flight, so the user sees that the lookup fields are about
                to auto-populate rather than mistaking the delay for a
                broken modal. Positioned absolute inside modal-box (which
                is position: relative above) so it doesn't spill out. */}
            {isResolvingSalesPlan && (
              <div
                style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(255,255,255,0.85)',
                  borderRadius: 12,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  gap: 12, zIndex: 10,
                }}
              >
                <span
                  className="vh-spin"
                  style={{ width: 24, height: 24, borderWidth: 3 }}
                />
                <span style={{ fontSize: 13, color: colors.textSecondary, fontWeight: 500 }}>
                  Loading plan details…
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 20% GUARD MODAL — fires for single edits that move CPM or
          demo (000) by more than ±20%. Promise-based: `showGuardModal`
          returns a Promise that resolves true on Confirm, false on
          Cancel (or overlay click). The edit handler awaits the
          result and applies / reverts accordingly. */}
      {guardModal && (() => {
        const { fieldLabel, oldValue, newValue, formatValue, resolve } = guardModal;
        const pct = (newValue - oldValue) / oldValue;
        const pctText = `${pct >= 0 ? '+' : ''}${(pct * 100).toFixed(1)}%`;
        const close = (confirmed: boolean) => {
          resolve(confirmed);
          setGuardModal(null);
        };
        return (
          <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) close(false); }}>
            <div className="modal-box" style={{ width: 420 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>⚠</span>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
                  Big change — are you sure?
                </h2>
              </div>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 18px 0' }}>
                <strong>{fieldLabel}</strong> is going from{' '}
                <strong>{formatValue(oldValue)}</strong> to{' '}
                <strong>{formatValue(newValue)}</strong>
                {' '}— a <strong>{pctText}</strong> change. That&apos;s bigger than the 20%
                guardrail. Are you sure?
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => close(false)}
                  style={{ padding: '8px 16px', border: '1px solid #dde1e9', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: colors.textSecondary }}
                >Cancel</button>
                <button
                  onClick={() => close(true)}
                  style={{ padding: '8px 16px', background: colors.danger, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Yes, apply</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MARKETPLACE-CHANGE WARNING MODAL — fires from submitEditPlan
          when the new Marketplace would hide quarters that contain
          data on this plan. Shows the per-quarter breakdown so the
          user sees the impact concretely before committing. Data is
          preserved either way — switching back recovers it. */}
      {marketplaceWarn && (() => {
        const { oldMarketplace, newMarketplace, hidden, resolve } = marketplaceWarn;
        const close = (confirmed: boolean) => {
          resolve(confirmed);
          setMarketplaceWarn(null);
        };
        const totalRows = hidden.reduce((s, h) => s + h.dealCount + h.addedRowCount, 0);
        // z-index override — the marketplace warning is a NESTED modal
        // fired from inside the Edit Plan Details modal's save flow.
        // Both modals share `.modal-overlay` (z-index: 1000), so without
        // an explicit boost the later-rendered Edit modal covers the
        // warning and the user sees nothing happen when they click Save
        // Changes. Bump the overlay + a matching backdrop-boost so the
        // warning always sits on top of whatever spawned it.
        return (
          <div
            className="modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={e => { if (e.target === e.currentTarget) close(false); }}
          >
            <div className="modal-box" style={{ width: 480 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>⚠</span>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
                  Change Marketplace from {oldMarketplace} to {newMarketplace}?
                </h2>
              </div>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 12px 0' }}>
                The new Marketplace doesn&apos;t include all quarters from the previous one.
                {' '}<strong>{totalRows} {totalRows === 1 ? 'row' : 'rows'}</strong> in the
                following quarter{hidden.length === 1 ? '' : 's'} will be hidden:
              </p>
              <ul style={{ fontSize: 13, color: colors.text, lineHeight: 1.7, margin: '0 0 14px 18px', padding: 0 }}>
                {hidden.map(h => {
                  const parts: string[] = [];
                  if (h.dealCount > 0)     parts.push(`${h.dealCount} ${h.dealCount === 1 ? 'saved deal' : 'saved deals'}`);
                  if (h.addedRowCount > 0) parts.push(`${h.addedRowCount} unsaved ${h.addedRowCount === 1 ? 'row' : 'rows'}`);
                  return (
                    <li key={h.key}>
                      <strong>{h.label}</strong>: {parts.join(', ')}
                    </li>
                  );
                })}
              </ul>
              <p style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.5, margin: '0 0 18px 0', fontStyle: 'italic' }}>
                Data isn&apos;t deleted — switching back to a Marketplace that includes
                these quarters will restore the rows.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => close(false)}
                  style={{ padding: '8px 16px', border: '1px solid #dde1e9', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: colors.textSecondary }}
                >Cancel</button>
                <button
                  onClick={() => close(true)}
                  style={{ padding: '8px 16px', background: colors.warn, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Yes, change Marketplace</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DELETE ROW CONFIRMATION — mirrors the plan-delete modal but
          scoped to a single saved deal row inside the current plan.
          Promise-based so requestDeleteSavedRow can await the user's
          answer without hoisting UI state into every caller. */}
      {/* POST-ORDER EXPORT MODAL — prompts the user to save a named
          snapshot version alongside the export file. Only shows when
          the plan is already in Order status; pre-order exports skip
          this and download directly. */}
      {postOrderExportModal && (() => {
        const { resolve } = postOrderExportModal;
        const close = (choice: { save: true; name: string } | { save: false } | null) => {
          resolve(choice);
          setPostOrderExportModal(null);
        };
        return (
          <PostOrderExportModal onClose={close} isRevisingOrder={isRevisingOrder} />
        );
      })()}

      {deleteRowConfirm && (() => {
        const { program, quarter, year, totalUnits, resolve } = deleteRowConfirm;
        const close = (confirmed: boolean) => {
          resolve(confirmed);
          setDeleteRowConfirm(null);
        };
        return (
          <div
            className="modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={e => { if (e.target === e.currentTarget) close(false); }}
          >
            <div className="modal-box" style={{ width: 460 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>🗑</span>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
                  Delete this row?
                </h2>
              </div>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 8px 0' }}>
                <strong style={{ color: colors.text }}>{program}</strong>
                {' · '}{fmtPeriod(quarter, year)}
                {' · '}{totalUnits} unit{totalUnits === 1 ? '' : 's'}
              </p>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 12px 0' }}>
                Takes effect on your next Save. If you reload without
                saving, the row returns.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  onClick={() => close(false)}
                  style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: colors.textSecondary }}
                >Cancel</button>
                <button
                  onClick={() => close(true)}
                  style={{ padding: '8px 16px', background: colors.danger, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Delete Row</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DELETE PLAN CONFIRMATION — in-app modal replacing the native
          window.confirm() so the dialog matches app chrome and is
          automation-friendly. Same Promise-based pattern as the
          marketplace warn above. */}
      {deleteConfirm && (() => {
        const { planName, dealCount, resolve } = deleteConfirm;
        const close = (confirmed: boolean) => {
          resolve(confirmed);
          setDeleteConfirm(null);
        };
        return (
          <div
            className="modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={e => { if (e.target === e.currentTarget) close(false); }}
          >
            <div className="modal-box" style={{ width: 460 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>🗑</span>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
                  Delete plan?
                </h2>
              </div>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 8px 0' }}>
                <strong style={{ color: colors.text }}>{planName}</strong>
              </p>
              <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 12px 0' }}>
                {dealCount > 0
                  ? <>This will remove {dealCount} deal{dealCount === 1 ? '' : 's'} from the plan.</>
                  : <>This plan has no deals.</>}
                {' '}This cannot be undone from the app.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  onClick={() => close(false)}
                  style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: colors.textSecondary }}
                >Cancel</button>
                <button
                  onClick={() => close(true)}
                  style={{ padding: '8px 16px', background: colors.danger, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Delete Plan</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* EDIT PLAN DETAILS MODAL — mirrors the Create Plan modal's
          field set but pre-fills from the active plan and operates
          on the existing plan (writes a new PLAN_VERSION with fresh
          meta, queues nickname rename). Roll-forward picker is omitted
          (edits on existing plans). */}
      {showEditPlanModal && (
        <div className="modal-overlay" style={{ alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' }}
             onClick={e => { if (e.target === e.currentTarget) closeEditPlanModal(); }}>
          <div className="modal-box" style={{ width: 'min(92vw, 720px)', marginBottom: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Edit Plan Details</h2>
              <button onClick={closeEditPlanModal} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Plan Nickname + Default Unit Length — first row.
                  Nickname takes ~2/3 of the row, Default Unit Length
                  takes the remaining ~1/3 so users can set both plan-
                  identity fields together at the top of the modal.
                  Uniqueness lives on Plan Name below. */}
              <div style={{ gridColumn: '1/-1', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
                <div>
                  <label style={formLabelStyle}>Plan Nickname *</label>
                  <input
                    type="text"
                    value={editNickname}
                    onChange={e => setEditNickname(e.target.value)}
                    placeholder="e.g. HYGIENE 4Q'26"
                    style={formInputStyle}
                  />
                </div>
                <div>
                  <label style={formLabelStyle}>Default Unit Length</label>
                  <select
                    value={editDefaultUnitLength}
                    onChange={e => setEditDefaultUnitLength(e.target.value)}
                    title="Applied to new rows added via + Add Show / + Add Row."
                    style={{
                      ...formInputStyle,
                      padding: '8px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">— (no default)</option>
                    <option value="15">:15</option>
                    <option value="30">:30</option>
                    <option value="45">:45</option>
                    <option value="60">:60</option>
                  </select>
                </div>
              </div>
              {/* Agency & Buyer — combined picker (same treatment as
                  New Plan modal; see full comment there). */}
              <div style={{ gridColumn: '1 / -1' }}>
                <LookupField<ContactLookupItem>
                  label="Agency & Buyer"
                  required
                  placeholder="Type an agency or buyer name…"
                  value={editContactId}
                  displayName={editBuyer}
                  searchFn={searchContacts}
                  showTbdOption
                  isTbdPicked={editBuyer === 'TBD' && !editContactId && editAgency === 'TBD' && !editAccountId}
                  onSelectTbd={() => {
                    setEditContactId(null);
                    setEditBuyer('TBD');
                    setEditAccountId(null);
                    setEditAgency('TBD');
                  }}
                  renderItem={item => (
                    <span>
                      <strong>{item.accountName ?? '(No agency)'}</strong>
                      <span style={{ color: '#4b5563' }}> — {item.name}</span>
                    </span>
                  )}
                  formatSelectedValue={buyerName => {
                    if (editAgency) return `${editAgency} — ${buyerName}`;
                    return buyerName;
                  }}
                  onChange={(id, name, extra) => {
                    setEditContactId(id);
                    setEditBuyer(name);
                    setEditAccountId(extra.accountId ?? null);
                    setEditAgency(extra.accountName ?? '');
                  }}
                  onClear={() => {
                    setEditContactId(null);
                    setEditBuyer('');
                    setEditAccountId(null);
                    setEditAgency('');
                  }}
                />
              </div>
              {/* Advertiser — full row */}
              <div style={{ gridColumn: '1/-1' }}>
                <LookupField
                  label="Advertiser"
                  required
                  placeholder="Type to search advertisers…"
                  value={editAdvertiserId}
                  displayName={editAdvertiser}
                  searchFn={searchAdvertisers}
                  showTbdOption
                  isTbdPicked={editAdvertiser === 'TBD' && !editAdvertiserId}
                  onSelectTbd={() => {
                    setEditAdvertiserId(null);
                    setEditAdvertiser('TBD');
                    setEditBrandId(null);
                    setEditBrand('');
                  }}
                  onChange={(id, name) => {
                    setEditAdvertiserId(id);
                    setEditAdvertiser(name);
                    // Brand is scoped to Advertiser — clear it.
                    setEditBrandId(null);
                    setEditBrand('');
                  }}
                  onClear={() => {
                    setEditAdvertiserId(null);
                    setEditAdvertiser('');
                    setEditBrandId(null);
                    setEditBrand('');
                  }}
                />
              </div>
              {/* Brand — bidirectional with Advertiser. Picking a
                  brand also auto-fills (or swaps) the Advertiser to
                  the brand's parent. See the matching block in the
                  Create Plan modal for the full rationale. */}
              <div>
                <LookupField<BrandLookupItem>
                  label="Brand"
                  required
                  placeholder={editAdvertiserId ? 'Type to search brands…' : 'Type to search brands (any advertiser)…'}
                  value={editBrandId}
                  displayName={editBrand}
                  searchFn={(q) => searchBrands(q, editAdvertiserId)}
                  showTbdOption
                  isTbdPicked={editBrand === 'TBD' && !editBrandId}
                  onSelectTbd={() => {
                    setEditBrandId(null);
                    setEditBrand('TBD');
                  }}
                  onChange={(id, name, extra) => {
                    setEditBrandId(id);
                    setEditBrand(name);
                    if (extra.advertiserId && extra.advertiserId !== editAdvertiserId) {
                      setEditAdvertiserId(extra.advertiserId);
                      setEditAdvertiser(extra.advertiserName ?? '');
                    }
                  }}
                  onClear={() => { setEditBrandId(null); setEditBrand(''); }}
                />
              </div>
              {/* Account Exec — free text */}
              <div>
                <label style={formLabelStyle}>Account Exec</label>
                <input type="text" value={editAe} onChange={e => setEditAe(e.target.value)} style={formInputStyle} />
              </div>
              {/* Primary Demo.
                  This used to filter the option list by whatever was in the
                  input — and the input is pre-filled with the plan's current
                  demo, so opening it showed exactly one entry (the value you
                  already had) and you had to clear the field by hand to see
                  any alternative. Now it matches the Create Plan picker: the
                  list stays unfiltered until you type something different,
                  there's a × to clear, focus selects the text so typing
                  replaces it, and the current pick is highlighted and
                  scrolled into view. */}
              <div>
                <label style={formLabelStyle}>Primary Demo *</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={editDemoInput} autoComplete="off" placeholder="Type to search…"
                    onChange={e => { setEditDemoInput(e.target.value); setEditShowDemoDropdown(true); }}
                    onFocus={e => { setEditShowDemoDropdown(true); e.target.select(); }}
                    onBlur={() => setEditShowDemoDropdown(false)}
                    style={{ ...formInputStyle, paddingRight: 28 }} />
                  {editDemoInput && (
                    <button type="button"
                      title="Clear"
                      onMouseDown={e => {
                        // onMouseDown, not onClick — the input's onBlur closes
                        // the dropdown and would unmount this before a click
                        // could land.
                        e.preventDefault();
                        setEditDemoInput('');
                        setEditDemo('');
                        setEditShowDemoDropdown(true);
                      }}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}
                    >×</button>
                  )}
                  {editShowDemoDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 160, overflowY: 'auto' }}>
                      {/* Unfiltered while the text still equals the committed
                          pick; narrows only once the user types. */}
                      {(editDemoInput === editDemo || !editDemoInput.trim()
                        ? primaryDemoOptions
                        : primaryDemoOptions.filter(d => d.toLowerCase().includes(editDemoInput.trim().toLowerCase()))
                      ).map(d => (
                        <div key={d}
                          ref={el => {
                            if (d === editDemo && el) {
                              requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));
                            }
                          }}
                          onMouseDown={e => { e.preventDefault(); setEditDemoInput(d); setEditDemo(d); setEditShowDemoDropdown(false); }}
                          style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, background: d === editDemo ? colors.primaryLight : undefined }}
                          onMouseEnter={e => (e.currentTarget.style.background = colors.primaryLight)}
                          onMouseLeave={e => (e.currentTarget.style.background = d === editDemo ? colors.primaryLight : '')}>
                          {d}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Marketplace — same custom SimpleSelect as New Plan modal. */}
              <div>
                <label style={formLabelStyle}>Marketplace *</label>
                <SimpleSelect
                  value={editMarketplace}
                  onChange={setEditMarketplace}
                  options={['BY Upfront', 'CY Upfront', 'Scatter']}
                  requiredMissing={!editMarketplace}
                />
              </div>
              {/* Equivalized */}
              <div>
                <label style={formLabelStyle}>Equivalized</label>
                <SimpleSelect
                  value={editEquiv}
                  onChange={setEditEquiv}
                  options={['Yes', 'No']}
                />
              </div>
              {/* Default Unit Length used to live here — now rides on
                  the first row next to Plan Nickname so users see it as
                  a plan-identity field, not an afterthought after
                  Equivalized. */}
              {/* Deal Year — locked */}
              <div>
                <label style={formLabelStyle}>Deal Year</label>
                <input type="text" value="26/27" readOnly
                  title="Only 26/27 plans are saved to Snowflake today."
                  style={{ ...formInputStyle, border: '1px solid #e5e7eb', background: '#f3f4f6', color: colors.textMuted, cursor: 'default' }} />
              </div>
              {/* Plan Name (auto-generated, read-only) — sits at the
                  bottom of the grid as a "summary line" mirroring the
                  New Plan modal. Uniqueness check + collision hint
                  still live here. */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ ...formLabelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Plan Name
                  <span style={{ fontSize: 10, fontWeight: 500, color: colors.textDisabled, textTransform: 'none', letterSpacing: 0 }}>
                    · auto-generated · read-only
                  </span>
                </label>
                <div
                  title="Auto-generated from Agency-Brand-Advertiser-Demo-Year. Populated by Salesforce workflow on save."
                  style={{
                    ...formInputStyle,
                    background: editNameCollision ? '#fef2f2' : '#eef2f6',
                    border: editNameCollision ? `1.5px solid ${colors.danger}` : formInputStyle.border,
                    color: editComputedNameComplete ? colors.text : colors.textMuted,
                    fontFamily: "'Inter', Arial, sans-serif",
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'not-allowed',
                  }}
                >
                  {editComputedSalesPlanName}
                </div>
                {editNameCollision && editCollidingPlanNickname && (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: '#b91c1c' }}>
                    A plan with this Sales Plan Name already exists for 26/27:{' '}
                    <a
                      href="#open-existing-plan-edit"
                      onClick={e => {
                        e.preventDefault();
                        const nickname = editCollidingPlanNickname;
                        closeEditPlanModal();
                        switchPlan(nickname);
                      }}
                      style={{ color: colors.primary, textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {editCollidingPlanNickname}
                    </a>
                    . Change Advertiser, Brand, Buyer, or Demo to differentiate.
                  </div>
                )}
                {!editComputedNameComplete && (
                  <div style={{ marginTop: 4, fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
                    Fill Advertiser, Brand, Buyer, and Demo to complete the name.
                  </div>
                )}
              </div>
            </div>

            {editError && <div style={{ color: colors.danger, fontSize: 12, marginTop: 14 }}>{editError}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22 }}>
              <div style={{ fontSize: 11.5, color: colors.textMuted, fontStyle: 'italic' }}>
                Changes save locally — click <strong style={{ color: colors.textSecondary }}>Save</strong> in the toolbar to commit to Snowflake.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={closeEditPlanModal}
                  style={{ padding: '9px 18px', border: `1px solid ${colors.border}`, borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: colors.textSecondary }}>
                  Cancel
                </button>
                {(() => {
                  // Dirty-check: compare current edit* state to the
                  // plan's persisted metadata. Save Changes stays grey
                  // when no field has moved from what's on disk so users
                  // don't waste a version bump on a no-op save.
                  const currentMeta = planFilter ? getPlanMeta(planFilter) : null;
                  const editHasChanges = !!currentMeta && (
                    editAdvertiserId !== (currentMeta.advertiserId ?? null)
                    || editBrandId    !== (currentMeta.brandId    ?? null)
                    || editContactId  !== (currentMeta.contactId  ?? null)
                    || editNickname.trim() !== (currentMeta.dealName ?? planFilter ?? '')
                    || editDemo       !== (currentMeta.demo       ?? '')
                    || editMarketplace!== (currentMeta.marketplace ?? '')
                    || editEquiv      !== (currentMeta.equiv      ?? '')
                    || editAe         !== (currentMeta.ae         ?? '')
                    // Default Unit Length treats '' and null as the
                    // same "no default" state — normalise both sides
                    // so switching between them doesn't false-trigger
                    // dirty, but actually picking :15/:30/:45/:60 does.
                    || (editDefaultUnitLength || '') !== (currentMeta.defaultUnitLength ?? '')
                  );
                  const btnDisabled = editNameCollision || !editHasChanges;
                  const btnTitle = editNameCollision
                    ? 'Sales Plan Name collides with an existing plan — change one of the inputs.'
                    : !editHasChanges
                      ? 'No changes to save — edit a field to enable Save Changes.'
                      : 'Save changes locally; click Save in the toolbar to commit to Snowflake.';
                  return (
                    <button
                      onClick={submitEditPlan}
                      disabled={btnDisabled}
                      title={btnTitle}
                      style={{
                        padding: '9px 18px',
                        background: btnDisabled ? colors.textDisabled : colors.primary,
                        color: '#fff', border: 'none', borderRadius: 6,
                        fontSize: 13, fontWeight: 600,
                        cursor: btnDisabled ? 'not-allowed' : 'pointer',
                      }}>
                      Save Changes
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PUSH-TO-SALESFORCE CONFIRMATION MODAL —
          Opened when a not-yet-pushed plan's status pill is switched to
          "Order". Confirms the terminal, one-way push. On success the
          backend flips status to Order + stamps pushed_to_sf_at, which
          locks the plan (planIsViewOnly = true via isPushed). On
          failure the modal stays open with a discriminated error
          surface so the user can act on the specific code returned. */}
      {/* Revert confirmation modal — small confirmation dialog before
          the destructive "wipe unsaved changes" action. Same modal
          styling as Delete Plan for consistency. */}
      {revertConfirmOpen && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setRevertConfirmOpen(false); }}>
          <div className="modal-box" style={{ width: 440 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span aria-hidden="true" style={{ fontSize: 20 }}>↺</span>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>Revert unsaved changes?</h2>
            </div>
            <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 18px' }}>
              Are you sure? This will undo any changes since the last time you saved.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                className="btn"
                onClick={() => setRevertConfirmOpen(false)}
                style={{ minWidth: 90 }}
              >Cancel</button>
              <button
                className="btn warn"
                onClick={() => {
                  revertToSaved();
                  setRevertConfirmOpen(false);
                }}
                style={{ minWidth: 90 }}
              >Revert</button>
            </div>
          </div>
        </div>
      )}

      {/* LEAVING A DIRTY PLAN. Unsaved edits used to survive a plan switch
          invisibly — nothing on screen said the plan you walked away from had
          work in it. Forcing the choice here means unsaved work is always
          either saved or knowingly dropped.

          No "Save and switch" button on purpose. Saving is not neutral: it
          writes a version, and on a pushed Order or mid-revision it runs into
          the late-quarter guard and the revision lifecycle — and it can now
          come back 409 if someone else saved first. A switch-plans dialog is
          the wrong place to discover any of that. The body text points at
          Save instead, which costs one click and keeps this honest. */}
      {pendingPlanSwitch !== null && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setPendingPlanSwitch(null); }}>
          <div className="modal-box" style={{ width: 440 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span aria-hidden="true" style={{ fontSize: 20 }}>⚠</span>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
                Unsaved changes on “{planFilter}”
              </h2>
            </div>
            <p style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 18px' }}>
              Switching plans will discard them. Stay and hit <strong>Save</strong> first
              if you want to keep them.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                className="btn"
                autoFocus
                onClick={() => setPendingPlanSwitch(null)}
                style={{ minWidth: 90 }}
              >Stay</button>
              <button
                className="btn warn"
                onClick={confirmPlanSwitchDiscardingChanges}
                style={{ minWidth: 90 }}
              >Discard and switch</button>
            </div>
          </div>
        </div>
      )}

      {pushToSfModalOpen && (() => {
        const saved = planFilter ? getSavedPlanByName(planFilter) : null;
        const meta = planFilter ? getPlanMeta(planFilter) : null;
        // Same season-narrowing rationale as the SF Structure preview:
        // consolidated-nickname plans (rolled 26/27 + 25/26 SF source
        // sharing a name) would double-count without the visibleQuarters
        // filter. The backend push endpoint keys off plan_id directly so
        // it always pushes only the 26/27 plan's rows — but the totals
        // shown in this confirmation modal need the same narrowing so
        // "Total dollars" matches what the user sees in Plan Details.
        //
        // Include unsaved AddedRows in the count: push flow auto-saves
        // before pushing (see handleConfirmPushToSf), so AddedRows will
        // land in SF too. Modal previously showed 0 for a fresh plan
        // whose only content was AddedRows, misleading the user into
        // thinking they had nothing to push.
        const pushVisibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
        const planDealsForPush = planFilter
          ? allDeals.filter(d =>
              d.salesPlan === planFilter
              && d.totalUnits > 0
              && pushVisibleKeys.has(`${d.quarter}|${d.year}`)
            )
          : [];
        const planAddedRowsForPush = planFilter
          ? addedRows.filter(r =>
              r.salesPlan === planFilter
              && r.program
              && r.unitLength
              && (r.totalUnits ?? 0) > 0
              && pushVisibleKeys.has(`${r.quarter}|${r.year}`)
            )
          : [];
        const totalDollars =
          planDealsForPush.reduce((acc, d) => acc + (d.totalDollars || 0), 0)
          + planAddedRowsForPush.reduce((acc, r) => acc + (r.totalDollars || 0), 0);
        // Nothing-to-push guard: prevents the Confirm button from firing
        // when the plan has no billable rows in the current season. Fresh
        // plans, plans with only 0-unit rows, and plans whose only content
        // was cleared all hit this. Message on the button explains why.
        const pushScheduleCount = planDealsForPush.length + planAddedRowsForPush.length;
        const hasNothingToPush = pushScheduleCount === 0;
        // Quarter range: first + last of visibleQuarters (labels like "4Q'26")
        const firstQ = visibleQuarters[0]?.label ?? '';
        const lastQ = visibleQuarters[visibleQuarters.length - 1]?.label ?? '';
        const quarterRange = firstQ && lastQ
          ? (firstQ === lastQ ? firstQ : `${firstQ} – ${lastQ}`)
          : '—';
        const nickname = saved?.name ?? planFilter ?? '';
        const advertiser = meta?.advertiser ?? '';
        const brand = meta?.brand ?? '';

        const closeModal = () => {
          if (pushToSfInFlight) return;
          setPushToSfModalOpen(false);
          setPushToSfError(null);
        };

        return (
          <div
            className="modal-overlay"
            onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
          >
            <div className="modal-box" style={{ width: 540, maxHeight: '90vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.text, margin: 0 }}>
                  Push Plan to Salesforce
                </h2>
                <button
                  onClick={closeModal}
                  disabled={pushToSfInFlight}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 20,
                    cursor: pushToSfInFlight ? 'not-allowed' : 'pointer',
                    color: colors.textMuted,
                    opacity: pushToSfInFlight ? 0.4 : 1,
                  }}
                  title={pushToSfInFlight ? 'Push in progress — cannot cancel' : 'Close'}
                >
                  ✕
                </button>
              </div>

              {/* Plan summary card */}
              <div style={{
                background: colors.bgPanel,
                border: `1px solid ${colors.borderInk}`,
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 14,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
                  {nickname}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>
                  {advertiser}{brand ? ` • ${brand}` : ''}
                </div>
                {(() => {
                  // Compute (Deal, Schedule) split for display. Same
                  // grouping key as the backend push (push-to-sf.ts): rows
                  // sharing Program+Length+Quarter+Year collapse to one
                  // Deal__c, and each row inside a group becomes one
                  // Schedules__c child. Users pushing 11 rows might get 7
                  // Deals in SF (if some P+L+Q+Y tuples repeat), so we
                  // surface both counts here to prevent surprise.
                  // AddedRows (in-session, unsaved) also count — they'll
                  // land in SF via the auto-save-on-push step.
                  const grouped = new Set<string>();
                  for (const d of planDealsForPush) {
                    grouped.add(`${d.program}|${d.unitLength}|${d.quarter}|${d.year}`);
                  }
                  for (const r of planAddedRowsForPush) {
                    grouped.add(`${r.program}|${r.unitLength}|${r.quarter}|${r.year}`);
                  }
                  const dealCount = grouped.size;
                  const scheduleCount = planDealsForPush.length + planAddedRowsForPush.length;
                  return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Deals
                    </div>
                    <div style={{ color: colors.text, fontFamily: "'Inter', Arial, sans-serif" }}>
                      {dealCount}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Schedules
                    </div>
                    <div style={{ color: colors.text, fontFamily: "'Inter', Arial, sans-serif" }}>
                      {scheduleCount}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Total $
                    </div>
                    <div style={{ color: colors.text, fontFamily: "'Inter', Arial, sans-serif" }}>
                      ${totalDollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Quarter Range
                    </div>
                    <div style={{ color: colors.text, fontFamily: "'Inter', Arial, sans-serif" }}>
                      {quarterRange}
                    </div>
                  </div>
                </div>
                );
                })()}
              </div>

              {/* Warning banner */}
              <div style={{
                background: colors.warnBg,
                border: '1px solid #fcd34d',
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 14,
                fontSize: 12.5,
                color: '#92400e',
                lineHeight: 1.45,
              }}>
                Once pushed, this plan will be <strong>locked</strong>. Status becomes Order and you won&apos;t be able to push again.
              </div>

              {/* Error area — discriminated by code */}
              {pushToSfError && (
                <div style={{
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  padding: '10px 12px',
                  marginBottom: 14,
                  fontSize: 12.5,
                  color: '#991b1b',
                  lineHeight: 1.45,
                }}>
                  {pushToSfError.code === 'MISSING_PROGRAM_IDS' ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Missing Salesforce Program IDs</div>
                      <div>
                        Missing Salesforce Program IDs for:{' '}
                        <strong>{(pushToSfError.affectedPrograms ?? []).join(', ') || '(unknown)'}</strong>.
                        Delete these rows and re-add them, or re-pick the program in the row&apos;s program picker.
                      </div>
                    </>
                  ) : pushToSfError.code === 'MISSING_FIELDS' ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Missing Required Fields</div>
                      <div>{pushToSfError.message}</div>
                      <div style={{ marginTop: 6 }}>
                        Edit Plan Details modal to fill in missing fields, then Save, then try again.
                      </div>
                    </>
                  ) : pushToSfError.code === 'ALREADY_PUSHED' ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Already Pushed</div>
                      <div>{pushToSfError.message}</div>
                    </>
                  ) : pushToSfError.code === 'SF_REJECTED' ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Salesforce Rejected the Push</div>
                      <div>{pushToSfError.message}</div>
                      {/*
                        Pattern-matched hint block — parses common SF
                        error signatures and surfaces actionable guidance
                        above the raw JSON. Uncommon errors just render
                        the JSON blob unchanged.
                      */}
                      {(() => {
                        const sfDetailStr = JSON.stringify(pushToSfError.sfDetail ?? '').toLowerCase();
                        let hint: React.ReactNode = null;
                        if (sfDetailStr.includes('json_parser_error') && sfDetailStr.includes('deserialize')) {
                          hint = (
                            <>
                              <strong>Likely a field type mismatch.</strong>{' '}
                              MPB sent a value in the wrong type (e.g. a string where SF expected a number).
                              Check the raw error below — the phrase after &quot;value&quot; identifies the
                              specific bad value. Common culprit: quarter format (should be 1-4, not &quot;Q1&quot;).
                            </>
                          );
                        } else if (sfDetailStr.includes('invalid_type') || sfDetailStr.includes('not a valid child relationship')) {
                          hint = (
                            <>
                              <strong>Wrong child relationship name.</strong>{' '}
                              The Composite Tree expects a specific child relationship on Sales_Plan__c
                              or Deal__c that doesn&apos;t match what SF has. Check Setup → Object Manager →
                              Deal__c → Sales_Plan__c field → &quot;Child Relationship Name&quot;, and same
                              for Schedules__c → Deal_Parent__c. Update the two constants at the top of
                              <code style={{ margin: '0 4px', padding: '1px 4px', background: '#fee', borderRadius: 3 }}>
                                push-to-sf.ts
                              </code>
                              and restart.
                            </>
                          );
                        } else if (sfDetailStr.includes('required_field_missing')) {
                          hint = (
                            <>
                              <strong>Required SF field missing from the push payload.</strong>{' '}
                              Check the field name after &quot;required&quot; in the error below. Either
                              add it to the mapping in push-to-sf.ts, or set a default value on the
                              SF field so it&apos;s not required.
                            </>
                          );
                        } else if (sfDetailStr.includes('invalid_field_for_insert_update') || sfDetailStr.includes('unable_to_lock_row')) {
                          hint = (
                            <>
                              <strong>Can&apos;t set this field.</strong>{' '}
                              The field is a formula, rollup, or system-managed. Remove it from the
                              mapping in push-to-sf.ts.
                            </>
                          );
                        } else if (sfDetailStr.includes('field_custom_validation_exception') || sfDetailStr.includes('field_integrity_exception')) {
                          hint = (
                            <>
                              <strong>SF validation rule blocked the record.</strong>{' '}
                              A custom validation rule or picklist constraint rejected the value.
                              Check the raw error for the field name + constraint.
                            </>
                          );
                        } else if (sfDetailStr.includes('malformed_id')) {
                          hint = (
                            <>
                              <strong>A lookup ID is malformed.</strong>{' '}
                              One of the SF FK values (Advertiser, Brand, Contact, Program) isn&apos;t a
                              valid 15/18-char SF ID. Try re-picking the field in Edit Plan Details.
                            </>
                          );
                        }
                        return hint ? (
                          <div style={{
                            marginTop: 8,
                            padding: '8px 10px',
                            background: '#fef3c7',
                            border: '1px solid #fcd34d',
                            borderRadius: 4,
                            color: '#78350f',
                            fontSize: 12.5,
                            lineHeight: 1.45,
                          }}>
                            {hint}
                          </div>
                        ) : null;
                      })()}
                      {/*
                        Friendly translation of the Composite Tree
                        referenceId. Push-to-sf.ts creates references:
                          SP       → Sales Plan
                          D_${i}   → Deal at 0-indexed position i
                          S_${i}_${j} → Deal i's Schedule j
                        The raw JSON below shows something like "S_2_1"
                        which is opaque to users. Parse it and render a
                        1-indexed human label above the JSON so users
                        can trace WHICH of their deals hit the error.
                      */}
                      {(() => {
                        type SfError = { referenceId?: string };
                        const sfResults = (pushToSfError.sfDetail as any)?.results;
                        const rejected: string[] = Array.isArray(sfResults)
                          ? sfResults
                              .filter((r: SfError) => r?.referenceId)
                              .map((r: SfError) => {
                                const ref = r.referenceId ?? '';
                                if (ref === 'SP') return 'Sales Plan';
                                const dMatch = /^D_(\d+)$/.exec(ref);
                                if (dMatch) return `Deal ${Number(dMatch[1]) + 1}`;
                                const sMatch = /^S_(\d+)_(\d+)$/.exec(ref);
                                if (sMatch) return `Deal ${Number(sMatch[1]) + 1}'s Schedule ${Number(sMatch[2]) + 1}`;
                                return ref;
                              })
                          : [];
                        // Dedupe (a single SF error can list multiple failed
                        // references) so the header stays concise.
                        const unique = Array.from(new Set(rejected));
                        if (unique.length === 0) return null;
                        return (
                          <div style={{
                            marginTop: 8,
                            padding: '6px 10px',
                            background: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: 4,
                            color: '#7f1d1d',
                            fontSize: 12,
                            fontWeight: 600,
                          }}>
                            SF rejected {unique.length === 1 ? 'record' : `${unique.length} records`}: {unique.join(', ')}
                          </div>
                        );
                      })()}
                      <div style={{ marginTop: 8, fontSize: 11, color: '#7f1d1d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Raw Salesforce Response
                      </div>
                      <pre style={{
                        marginTop: 4,
                        padding: '8px 10px',
                        background: '#fff',
                        border: '1px solid #fecaca',
                        borderRadius: 4,
                        fontFamily: "'DM Mono', ui-monospace, SFMono-Regular, monospace",
                        fontSize: 11,
                        color: '#7f1d1d',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}>
{JSON.stringify(pushToSfError.sfDetail, null, 2)}
                      </pre>
                      {/* Request body toggle — collapsed by default,
                          rendered as a <details> so we don't clutter
                          the modal for simple errors. */}
                      {pushToSfError.requestBody != null && (
                        <details style={{ marginTop: 8, fontSize: 11 }}>
                          <summary style={{ cursor: 'pointer', color: '#7f1d1d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Request payload MPB sent to Salesforce
                          </summary>
                          <pre style={{
                            marginTop: 4,
                            padding: '8px 10px',
                            background: '#fff',
                            border: '1px solid #fecaca',
                            borderRadius: 4,
                            fontFamily: "'DM Mono', ui-monospace, SFMono-Regular, monospace",
                            fontSize: 11,
                            color: '#7f1d1d',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 300,
                            overflow: 'auto',
                          }}>
{JSON.stringify(pushToSfError.requestBody, null, 2)}
                          </pre>
                        </details>
                      )}
                    </>
                  ) : (
                    <div>{pushToSfError.message}</div>
                  )}
                  {/*
                    Guidance below every rejected push: an immediate
                    re-click would fail identically, so tell the user
                    what to do (address the SF issue) before another
                    Order/Push attempt. Complements the "Close and Fix
                    Issues" button that replaces "Confirm and Push"
                    while an error is on screen.
                  */}
                  <div style={{ marginTop: 10, fontSize: 12, color: '#7f1d1d', fontStyle: 'italic' }}>
                    To retry, close this dialog, address the Salesforce error above, then Order → Push again.
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
                <button
                  onClick={closeModal}
                  disabled={pushToSfInFlight}
                  style={{
                    padding: '9px 18px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    background: '#fff',
                    fontSize: 13,
                    cursor: pushToSfInFlight ? 'not-allowed' : 'pointer',
                    color: colors.textSecondary,
                    opacity: pushToSfInFlight ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                {/*
                  Post-error state: once SF rejects the push, disable
                  Confirm and Push and flip its label to "Close and Fix
                  Issues". Immediate re-click with unchanged data would
                  produce the same error, so we route the user out of the
                  modal to fix the underlying SF issue (e.g. Advertiser
                  Finance Hold, dedupe an old Schedule) before retrying.
                  The Cancel button below already handles the "close" —
                  we relabel to reinforce it's the intended action.
                */}
                {pushToSfError ? (
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '9px 18px',
                      background: colors.textDisabled,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    title="Close this dialog, address the Salesforce error, then Order → Push again."
                  >
                    Close and Fix Issues
                  </button>
                ) : (
                  <button
                    className="btn success"
                    onClick={() => { void handleConfirmPushToSf(); }}
                    disabled={pushToSfInFlight || hasNothingToPush}
                    style={{
                      padding: '9px 18px',
                      background: (pushToSfInFlight || hasNothingToPush) ? colors.textDisabled : colors.success,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: (pushToSfInFlight || hasNothingToPush) ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    title={
                      hasNothingToPush
                        ? 'Add at least one row with units > 0 before pushing to Salesforce.'
                        : (pushToSfInFlight ? 'Push in progress…' : 'Push this plan to Salesforce.')
                    }
                  >
                    {pushToSfInFlight && <span className="vh-spin" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.4)', borderTopWidth: 2 }} />}
                    {pushToSfInFlight
                      ? 'Pushing to Salesforce…'
                      : (hasNothingToPush ? 'Nothing to Push' : 'Confirm and Push')}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* SF STRUCTURE MODAL */}
      {showSFModal && (() => {
        const meta = getPlanMeta(planFilter);
        // Season-narrow the deal list. Without this, when a rolled 26/27
        // plan shares its nickname with its 25/26 SF source (consolidation),
        // `allDeals.filter(d => d.salesPlan === planFilter)` returns BOTH
        // the source's 25/26 deals AND the rolled 26/27 deals — 2× the
        // count the user actually sees in Plan Details. Pushing that to
        // SF would create duplicate Deal + Schedule records in the
        // Composite Tree payload. Narrow to the plan's currently-visible
        // quarters (per its marketplace) so the preview matches the plan.
        const visibleKeys = new Set(visibleQuarters.map(t => `${t.quarter}|${t.year}`));
        const planDeals = allDeals.filter(d =>
          d.salesPlan === planFilter
          && d.totalUnits > 0
          && visibleKeys.has(`${d.quarter}|${d.year}`)
        );
        // Include unsaved AddedRows in the preview — the push flow
        // auto-saves before pushing, so AddedRows also land in SF as
        // Deal/Schedule records. Without this, a fresh plan whose only
        // rows are AddedRows shows "No deals with units yet" even though
        // the row IS visible in the table and the Card DEALS count says 1.
        // Normalize each AddedRow into the fields the preview render
        // reads: same shape as a Deal for grouping and Schedule display.
        const planAddedRowsAsDeals: Deal[] = planFilter
          ? addedRows
              .filter(r =>
                r.salesPlan === planFilter
                && r.program
                && r.unitLength
                && (r.totalUnits ?? 0) > 0
                && visibleKeys.has(`${r.quarter}|${r.year}`)
              )
              .map(r => ({
                // Cast: only the fields the preview reads matter. The
                // rest are defaulted so Deal-typed code doesn't blow up.
                dealKey: r.key,
                salesPlan: r.salesPlan,
                program: r.program,
                quarter: r.quarter,
                year: r.year,
                unitLength: r.unitLength,
                cpm: r.cpm,
                totalUnits: r.totalUnits,
                totalDollars: r.totalDollars ?? 0,
                newRate: r.newRate ?? null,
                baseRate: r.baseRate ?? null,
                guaranteed: r.guaranteed ?? 0,
                demo: r.demo ?? meta?.demo ?? '',
                originalCpm: r.originalCpm ?? null,
              } as unknown as Deal))
          : [];
        const previewRows: Deal[] = [...planDeals, ...planAddedRowsAsDeals];
        const SFField = ({ label, value }: { label: string; value: string }) =>
          value ? (
            <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 160 }}>{label}</span>
              <span style={{ fontSize: 13, color: colors.text }}>{value}</span>
            </div>
          ) : null;
        return (
          <div className="modal-overlay" style={{ alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' }} onClick={e => { if (e.target === e.currentTarget) setShowSFModal(false); }}>
            <div className="modal-box" style={{ width: 680, maxHeight: '85vh', overflowY: 'auto', marginBottom: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>⬡ Salesforce Structure Preview</h2>
                <button onClick={() => setShowSFModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
              </div>
              {meta && (
                <div style={{ background: colors.primaryLight, border: '1px solid #bfdbfe', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.primary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>📋 Sales Plan</div>
                  <SFField label="Sales Plan Name" value={meta.planName} />
                  <SFField label="Advertiser" value={meta.advertiser} />
                  <SFField label="Brand" value={meta.brand} />
                  <SFField label="Season" value={meta.year} />
                  <SFField label="Status" value={meta.status} />
                  <SFField label="Primary Demographic" value={meta.demo} />
                  <SFField label="Upfront/Scatter" value={meta.marketplace} />
                  <SFField label="Measurement Type" value="Nielsen + Panel" />
                  <SFField label="Buyer (Contact)" value={meta.buyer} />
                  <SFField label="Agency" value={meta.agency} />
                  <SFField label="Account Exec" value={meta.ae} />
                  <SFField label="Equivalized" value={meta.equiv} />
                  <SFField label="Calendar Type" value="STANDARD BROADCAST" />
                </div>
              )}
              {!previewRows.length ? (
                <div style={{ textAlign: 'center', padding: 24, color: colors.textMuted, fontSize: 13 }}>No deals with units yet.</div>
              ) : (() => {
                // Group PLAN_DEAL rows by (Program, Length, Quarter, Year)
                // to mirror the backend push logic (push-to-sf.ts line 331).
                // Each group → 1 Deal__c parent; each PLAN_DEAL row inside
                // it → 1 Schedules__c child. Preview must match this shape
                // so the counts are trustworthy (e.g. "7 Deals · 11
                // Schedules") and users can see the parent/child structure
                // before pushing. previewRows includes both saved
                // PLAN_DEAL rows and unsaved AddedRows (auto-saved on push).
                type Group = { key: string; program: string; unitLength: string | number; quarter: string; year: number; rows: Deal[] };
                const grouped = new Map<string, Group>();
                for (const d of previewRows) {
                  const k = `${d.program}|${d.unitLength}|${d.quarter}|${d.year}`;
                  let g = grouped.get(k);
                  if (!g) {
                    g = { key: k, program: d.program, unitLength: d.unitLength, quarter: d.quarter, year: d.year, rows: [] };
                    grouped.set(k, g);
                  }
                  g.rows.push(d);
                }
                const groups = Array.from(grouped.values());
                const dealCount = groups.length;
                const scheduleCount = previewRows.length;
                return (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      {dealCount} Deal{dealCount !== 1 ? 's' : ''} · {scheduleCount} Schedule{scheduleCount !== 1 ? 's' : ''}
                    </div>
                    <div style={{ fontSize: 11.5, color: colors.textMuted, marginBottom: 12, fontStyle: 'italic' }}>
                      MPB rows are grouped by Program + Length + Quarter + Year into Deal__c parents; each row in a group becomes one Schedules__c child under that Deal.
                    </div>
                    {groups.map((g, i) => (
                      <div key={g.key} style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
                        <div style={{ background: colors.bgPanel, padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: colors.success, textTransform: 'uppercase' }}>Deal {i + 1}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginLeft: 10 }}>
                            {g.program} — {g.unitLength}s · {fmtPeriod(g.quarter, g.year)}
                          </span>
                          <span style={{ fontSize: 11, color: colors.textMuted, marginLeft: 10 }}>
                            ({g.rows.length} schedule{g.rows.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                        {/*
                          Deal__c fields the backend push writes
                          (push-to-sf.ts line 375-396):
                            Program__c, Length__c, Brands__c, Contact__c,
                            Quarter__c, Year__c, RecordTypeId
                          Preview surfaces the same set so users can eyeball
                          what SF will receive at the Deal grain.
                        */}
                        <div style={{ padding: '10px 14px' }}>
                          <SFField label="Program" value={g.program} />
                          <SFField label="Length" value={g.unitLength + 's'} />
                          <SFField label="Quarter" value={String(g.quarter)} />
                          <SFField label="Year" value={String(g.year)} />
                          {meta && <SFField label="Brand (from Plan)" value={meta.brand} />}
                          {meta && <SFField label="Contact (from Plan)" value={meta.buyer} />}
                        </div>
                        {g.rows.map((d, j) => {
                          const est = calcEstimateFields(d);
                          // Push preview: honor overrideDemo000 so the
                          // Guaranteed Impressions the user sees matches
                          // what the backend actually sends to SF (which
                          // reads the effective demo, not the raw
                          // estimate).
                          const effDemo = d.overrideDemo000 != null ? d.overrideDemo000 : est.demo000;
                          const guarImpressions = effDemo != null ? Math.round(effDemo * d.totalUnits).toLocaleString() : '—';
                          // Broadcast first-Monday of the quarter — same
                          // formula as the backend's BROADCAST_QUARTER_FIRST_WEEK
                          // table in push-to-sf.ts. Kept as an inline map
                          // (small, only two years) so the preview doesn't
                          // need a network round-trip. If push-to-sf.ts's
                          // table extends past 2027 you'll want to sync
                          // this too, else the preview shows "—" while
                          // the actual push still succeeds.
                          const bwMap: Record<number, Record<number, string>> = {
                            2026: { 1: '2025-12-29', 2: '2026-03-30', 3: '2026-06-29', 4: '2026-09-28' },
                            2027: { 1: '2026-12-28', 2: '2027-03-29', 3: '2027-06-28', 4: '2027-09-27' },
                          };
                          const qNum = Number(String(d.quarter).replace(/^Q/i, ''));
                          const week = bwMap[d.year]?.[qNum] ?? '—';
                          const rate = d.newRate ?? d.baseRate ?? null;
                          return (
                            <div key={`${g.key}-${j}`} style={{ background: '#f0fdf4', borderTop: '1px solid #e2e8f0', padding: '10px 14px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: colors.success, marginBottom: 8 }}>
                                ↳ Schedule {j + 1} of {g.rows.length}
                              </div>
                              {/*
                                Schedules__c fields the backend push writes
                                (push-to-sf.ts line 397-417):
                                  Week__c, Units_Available__c, CPM__c, Rate__c,
                                  Guaranteed_000_Primary_Demo__c, RecordTypeId
                                Quarter/Year deliberately excluded — they're
                                formula fields on SF derived from Week__c.
                                Status not shown because we don't push it
                                (SF admin defaults it after insert).
                              */}
                              <SFField label="Week (first Monday)" value={week} />
                              <SFField label="Units Available" value={d.totalUnits.toFixed(0)} />
                              <SFField label="CPM" value={d.cpm ? '$' + d.cpm.toFixed(2) : '—'} />
                              <SFField label="Rate (per spot)" value={rate != null ? '$' + Math.round(rate).toLocaleString() : '—'} />
                              <SFField label="Guaranteed Impressions (000s) — Primary Demo" value={guarImpressions} />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Historical version load error toast — shown briefly if the
          picker's fetch failed. The main grid stays on whatever it
          was displaying before. */}
      {historicalVersionErr && !historicalVersionLoading && (
        <div style={{
          position: 'fixed', top: 80, right: 24, zIndex: 100,
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', padding: '10px 16px', borderRadius: 8,
          fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxWidth: 320,
        }}>
          Couldn&apos;t load version: {historicalVersionErr}
        </div>
      )}

      {/* HISTORICAL-VERSION LOADING OVERLAY — full-viewport dim while
          openHistoricalVersion / returnToLatest fetches + rebuilds
          allDeals. Without this, users get a brief flash where the
          picker updates but the grid values lag by 100-300ms, which
          reads as "did the click even register?" */}
      {/* ── Export All → format chooser ────────────────────────────────
          Opens only after handleExportGate() has passed, so cancelling
          here cannot leave a stranded post-order version behind. */}
      {exportFormatPrompt && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 210,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setExportFormatPrompt(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Choose export format"
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 10, padding: '22px 24px',
              width: 460, maxWidth: '92vw',
              boxShadow: '0 18px 48px rgba(15,23,42,0.28)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              Export All Quarters
            </div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 16 }}>
              Choose a format for <strong>{planFilter}</strong>.
            </div>

            {/* `display:block` + `whiteSpace:normal` override `.btn`, which is a
                TOOLBAR button: inline-flex + align-items:center + white-space:
                nowrap. Those turn this button's title and description divs into
                two flex items side by side on one unwrappable line, so the
                description ran past the button's right edge instead of sitting
                under the title. These two are card-shaped buttons, so they need
                normal block flow. */}
            <button
              className="btn"
              style={{
                width: '100%', textAlign: 'left', marginBottom: 10, padding: '12px 14px',
                display: 'block', whiteSpace: 'normal',
              }}
              onClick={() => { setExportFormatPrompt(false); exportAllQuartersToExcel(); }}
            >
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>Excel workbook (.xlsx)</div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                Summary sheet plus one sheet per quarter. Editable.
              </div>
            </button>

            <button
              className="btn success"
              style={{
                width: '100%', textAlign: 'left', padding: '12px 14px',
                display: 'block', whiteSpace: 'normal',
              }}
              onClick={() => { setExportFormatPrompt(false); exportAllQuartersToPdf(); }}
            >
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>PDF proposal (.pdf)</div>
              <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                Landscape, client-ready. Quarter summary + program detail with costs.
              </div>
            </button>

            <div style={{ textAlign: 'right', marginTop: 14 }}>
              <button className="btn" onClick={() => setExportFormatPrompt(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {historicalVersionLoading && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(15, 23, 42, 0.35)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 14, backdropFilter: 'blur(2px)',
            // Non-blocking to clicks would let the user close the
            // picker mid-load and end up in a partial state; keep
            // this as a full overlay for that brief window.
          }}
          aria-live="polite"
          aria-busy="true"
        >
          <span
            className="vh-spin"
            style={{
              width: 32, height: 32, borderWidth: 3,
              borderColor: 'rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
            }}
          />
          <div style={{ fontSize: 14, color: '#fff', fontWeight: 500, letterSpacing: '0.01em' }}>
            Loading version…
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Pretty-printed date for the version-history popup ("Mar 5, 2026").
 * Falls back to the raw string for unparseable inputs so the popup
 * never collapses on bad data. Shared between the popup and the
 * snapshot viewer's modal header.
 */
function formatVersionDateExternal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The five chip labels the version picker displays. One chip per row.
 * The old "Original Order" / "Order Update N" distinction is gone —
 * "Superseded" already tells the user this row was an order that's
 * been replaced, and the vN identifies which one.
 */
type VersionChipKind = 'initial' | 'live-order' | 'superseded' | 'named' | 'exported';

function chipLabel(kind: VersionChipKind): string {
  switch (kind) {
    case 'initial':     return 'Initial';
    case 'live-order':  return '● Live Order';
    case 'superseded':  return 'Superseded';
    // "Client Sent" reads more meaningfully than "Named" in ad-sales
    // context — these milestones are the shareable snapshots the
    // team sent to the client (either via + Name this version or
    // Name Version & Export). Both flows produce the same
    // client-facing artifact; the timeline icon (tag vs upload
    // arrow) is what differentiates their origin.
    case 'named':       return 'Client Sent';
    case 'exported':    return 'Client Sent';
  }
}

/**
 * Inline SVG icon set for the timeline dots. Each returns a small SVG
 * sized for the 20×20 circular badge that sits on the timeline spine.
 * Kept inline (not imported from a library) so the popup has zero
 * external icon-font dependencies.
 */
const IconCheck  = () => (<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const IconStar   = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>);
const IconTag    = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41L13.42 20.58a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>);
const IconUpload = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>);
const IconMinus  = () => (<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IconSparkle = () => (<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z"/></svg>);
// Pencil — used for lifecycles that opened implicitly (v2, v3, ...)
// via the user starting to edit again after a closure. Signals "you
// are drafting the next version" without carrying any contractual
// or ceremonial weight (unlike the blue sparkle "initial" icon).
const IconPencil  = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>);
// Flag — section header icon for Milestones. Uppercase strokes so it
// reads at 14px alongside a bold uppercase label.
const IconFlag    = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>);
// Clock — section header icon for Version History.
const IconClock   = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
// Info circle — tooltip target beside section header labels.
const IconInfo    = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>);
const IconChevron = ({ dir }: { dir: 'right' | 'down' }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: dir === 'down' ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * Icon + circle style for each chip kind. Colors are matched to the
 * pill palette so the timeline and the chip on the row read as one
 * visual unit.
 */
function timelineIcon(kind: VersionChipKind): { icon: React.ReactNode; bg: string; color: string; border: string; filled: boolean } {
  switch (kind) {
    case 'live-order':  return { icon: <IconCheck />,   bg: '#10b981', color: '#fff',    border: '#10b981', filled: true };
    case 'superseded':  return { icon: <IconMinus />,   bg: '#fff',    color: '#9ca3af', border: '#d1d5db', filled: false };
    case 'named':       return { icon: <IconTag />,     bg: '#f97316', color: '#fff',    border: '#f97316', filled: true };
    // Same orange as `named` so both read as one concept in the
    // picker; keep the upload arrow icon so the origin (a Name
    // Version & Export click) stays identifiable at a glance.
    case 'exported':    return { icon: <IconUpload />,  bg: '#f97316', color: '#fff',    border: '#f97316', filled: true };
    case 'initial':     return { icon: <IconSparkle />, bg: '#2563eb', color: '#fff',    border: '#2563eb', filled: true };
  }
}

/**
 * Compact identifier — what the user sees for this row.
 *
 * Lifecycle model:
 *   • Working lifecycles keep vN identity.
 *   • Named / exported closures identify by their user-supplied name
 *     (no vN prefix). Their identity IS the name — "Upfront V1", not
 *     "v2". The picker layout still makes clear they're the closure
 *     of the lifecycle above them.
 *   • Order closures identify by "v{N} · Live Order" — orders don't
 *     have a user-supplied name so we compose one from the parent
 *     lifecycle's vN plus a fixed system label.
 *   • Initial (opens v1) reads as "v1".
 *   • Working revisions read as "v{N}.{revisionNumber}".
 *
 * Raw versionNo stays monotonic under the hood — this is display-only.
 */
function compactVersionLabel(v: PlanVersionMeta): string {
  const idx = v.milestoneIndex ?? v.versionNo;
  // Named / exported: identity is the name itself. Fallback to
  // vN if name is somehow missing (defensive — shouldn't happen).
  if (v.keyType === 'named' || v.keyType === 'export') {
    return v.name?.trim() ? v.name : `v${idx}`;
  }
  // Order closure — distinguish the ORIGINAL order push from a
  // post-order REVISION so users can tell them apart in the picker.
  // Only the newest order/order_update is Live (via kindFor's
  // liveOrderVersionNo check); older ones flip to Superseded chip.
  if (v.keyType === 'order') {
    return `Live Order V${idx}`;
  }
  if (v.keyType === 'order_update') {
    return `Order Revision V${idx}`;
  }
  // Initial: opens v1 (or vN more generally if we ever backfill
  // initial-tagged rows on migrated plans).
  if (v.keyType === 'initial') return `v${idx}`;
  // Working revision.
  if (v.revisionNumber > 0) return `v${idx}.${v.revisionNumber}`;
  return `v${idx}`;
}

/**
 * Split a closure row's identity into two visual pieces:
 *   • prefix  — "v3", "v4.1", etc. Rendered as a small gray chip.
 *   • name    — the user-supplied name (from Name & Export) if set,
 *               otherwise a fallback describing what kind of closure
 *               this is ("Live Order", "Order Revision", "Client
 *               Sent"). Rendered as the primary label.
 *
 * Two callers use this: the popup rows (top-level closures, lifecycle-
 * branch closures, "You are viewing" header) and the plan-chrome
 * header chip. Keeping the split here means all four render sites
 * scan the same shape.
 *
 * `compactVersionLabel` above still exists — it produces a single
 * combined string used in tooltips, historical-viewing labels, and
 * anywhere the caller can't render multiple elements.
 */
function versionPrefix(v: PlanVersionMeta): string {
  const idx = v.milestoneIndex ?? v.versionNo;
  if (v.revisionNumber > 0) return `v${idx}.${v.revisionNumber}`;
  return `v${idx}`;
}
function versionPrimaryName(v: PlanVersionMeta): string {
  if (v.name && v.name.trim()) return v.name.trim();
  // No user-supplied name — fall back to a description of what this
  // row is. Kept in sync with the badge labels so the reading is
  // consistent: an unnamed order_update reads "Order Revision" with
  // a matching REVISED pill; an unnamed order reads "Live Order"
  // with an ORIGINAL pill.
  switch (v.keyType) {
    case 'order':        return 'Live Order';
    case 'order_update': return 'Order Revision';
    case 'named':
    case 'export':       return 'Client Sent';
    case 'initial':      return 'Initial version';
    default:             return versionPrefix(v);
  }
}
/**
 * Inline style for the small gray version-prefix chip that sits before
 * every closure's primary name. Muted enough to read as metadata, not
 * to compete with the identity/status badges beside it.
 */
const versionPrefixChipStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
  background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  letterSpacing: '0.02em', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center',
};

/**
 * Order-family rows carry TWO badges: an identity (Original vs Revised)
 * and a status (Current vs Superseded). The identity is fixed for the
 * life of the row (Original for keyType=='order', Revised for
 * 'order_update'); the status flips whenever a newer order-family row
 * lands. Together they let the user answer both "what kind of push
 * was this" and "is this what's live right now" at a glance.
 *
 * Returns null for non-order rows so the caller can fall back to the
 * single-chip path used for Named / Exported / Initial.
 */
type OrderIdentity = 'original' | 'revised';
type OrderStatus = 'current' | 'superseded';
function orderBadges(
  v: PlanVersionMeta,
  liveOrderVersionNo: number | null,
): { identity: OrderIdentity; status: OrderStatus } | null {
  if (v.keyType !== 'order' && v.keyType !== 'order_update') return null;
  const identity: OrderIdentity = v.keyType === 'order' ? 'original' : 'revised';
  const status: OrderStatus =
    v.versionNo === liveOrderVersionNo ? 'current' : 'superseded';
  return { identity, status };
}

/**
 * Style + label for the four order-badge chips. Colors are picked so
 * identity + status read as two distinct dimensions: identity uses
 * blue/violet, status uses emerald/gray. That keeps a Revised+Current
 * row from feeling monochromatic while still making Current the
 * dominant signal (bright emerald).
 */
function orderIdentityPill(
  identity: OrderIdentity,
): { style: React.CSSProperties; label: string } {
  const base: React.CSSProperties = {
    fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
  if (identity === 'original') {
    return {
      // Blue — this is the "founding" push, matches the initial-lifecycle
      // color language so the shared meaning of "root artifact" transfers.
      style: { ...base, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #93c5fd' },
      label: '★ Original',
    };
  }
  return {
    // Violet — distinct from every other pill in the picker (blue Initial,
    // orange Client Sent, emerald Current, gray Superseded) so revisions
    // read as their own concept.
    style: { ...base, color: '#6d28d9', background: '#ede9fe', border: '1px solid #c4b5fd' },
    label: '↻ Revised',
  };
}
function orderStatusPill(
  status: OrderStatus,
): { style: React.CSSProperties; label: string } {
  const base: React.CSSProperties = {
    fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
  if (status === 'current') {
    return {
      style: { ...base, color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7' },
      label: '● Current',
    };
  }
  return {
    style: { ...base, color: '#6b7280', background: '#e5e7eb', border: '1px solid #d1d5db' },
    label: 'Superseded',
  };
}

/** Small pill inline style per chip kind. */
function pillStyle(kind: VersionChipKind): React.CSSProperties {
  const base: React.CSSProperties = { fontSize: 9.5, fontWeight: 500, padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
  switch (kind) {
    case 'live-order':  return { ...base, color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7' };
    case 'superseded':  return { ...base, color: '#6b7280', background: '#e5e7eb', border: '1px solid #d1d5db' };
    case 'named':       return { ...base, color: '#9a3412', background: '#ffedd5', border: '1px solid #fdba74' };
    // Exports share the Named pill styling — same concept, just a
    // different origin (Name Version & Export vs + Name this version).
    case 'exported':    return { ...base, color: '#9a3412', background: '#ffedd5', border: '1px solid #fdba74' };
    case 'initial':     return { ...base, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #93c5fd' };
  }
}

/** The prominent italic display name lives in the same color family as the chip. */
function nameColor(kind: VersionChipKind): string {
  switch (kind) {
    case 'named':      return '#c2410c';   // orange-700 — matches tag icon
    // Exported names share the orange treatment — one visual concept.
    case 'exported':   return '#c2410c';
    default:           return '#111827';
  }
}

/**
 * Popup body for the Snowflake plan version-history chip.
 *
 * Renders the two-tier taxonomy per Eric's 1B mockup:
 *   1. "You are viewing" — the current working copy at the top.
 *   2. "Milestones" — every key version (is_key=TRUE), each collapsible
 *      to reveal the revisions saved under it.
 *
 * Anchored by the parent via `position: relative` on the wrapper — this
 * component only owns the content. Click-outside / Esc / plan-switch
 * dismissal stay in the parent because they touch many other state
 * paths.
 *
 * `currentVersionNo` is the chip's displayed version (i.e. the plan's
 * latest); the matching row is highlighted.
 */
interface VersionHistoryPopupProps {
  versions: PlanVersionMeta[] | null;
  loading: boolean;
  err: string | null;
  currentVersionNo: number;
  onPickVersion: (versionNo: number) => void;
  /** Prompts the user for a name and calls the /versions/:no/name endpoint. */
  onNameVersion: (versionNo: number) => void;
}
const VersionHistoryPopup: React.FC<VersionHistoryPopupProps> = ({
  versions, loading, err, currentVersionNo, onPickVersion, onNameVersion,
}) => {
  // Milestones show their revisions inline by default (matching Kiyana's
  // latest mockup). The user can still collapse via the chevron, but
  // the default state is "everything expanded" so the hierarchy is
  // visible at a glance. Track collapse (opt-out) instead of expand
  // (opt-in) so the initial render doesn't need the versions array.
  const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (versionNo: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(versionNo)) next.delete(versionNo);
      else next.add(versionNo);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="version-popup" role="dialog" aria-label="Version history">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px 0', gap: 8, color: '#6b7280', fontSize: 12 }}>
          <span className="vh-spin" /> Loading…
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="version-popup" role="dialog" aria-label="Version history">
        <div style={{ padding: '12px', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12 }}>{err}</div>
      </div>
    );
  }
  if (!versions || versions.length === 0) {
    return (
      <div className="version-popup" role="dialog" aria-label="Version history">
        <div style={{ padding: '12px', color: '#6b7280', fontSize: 12, fontStyle: 'italic' }}>No saved versions yet.</div>
      </div>
    );
  }

  // Server returns versions DESC (newest first). Under the lifecycle
  // model we group all rows by milestone_index — every row in the
  // same lifecycle shares one. For each group we distinguish:
  //   • closure   — the row that CLOSED this lifecycle (named /
  //                 export / order / order_update). At most one.
  //   • initial   — the row that OPENED v1. Only present for
  //                 milestoneIndex=1 (subsequent lifecycles open
  //                 implicitly with a regular save).
  //   • revisions — working revisions between opener and closure.
  //   • header    — the row to render as the lifecycle's identity
  //                 in the picker. Prefer initial (for v1), else
  //                 fall back to the earliest revision (synthetic
  //                 header for lifecycles that opened implicitly).
  //
  // Groups are then sorted DESC so the newest lifecycle sits at the
  // top of the picker (matching the "newest first" pattern users
  // expect from version history).
  const currentMeta = versions.find(v => v.versionNo === currentVersionNo) ?? null;

  type LifecycleGroup = {
    index: number;
    closure: PlanVersionMeta | null;   // named / export / order / order_update
    opener: PlanVersionMeta;           // the row rendered as "v{N}" — initial for v1,
                                       // or the first regular save of the lifecycle for v2+
    revisions: PlanVersionMeta[];      // DESC — revisionNumber > 0 only
  };
  const groupsByIndex = new Map<number, {
    index: number;
    closure: PlanVersionMeta | null;
    opener: PlanVersionMeta | null;
    revisions: PlanVersionMeta[];
  }>();
  for (const v of versions) {
    const idx = v.milestoneIndex;
    let g = groupsByIndex.get(idx);
    if (!g) {
      g = { index: idx, closure: null, opener: null, revisions: [] };
      groupsByIndex.set(idx, g);
    }
    // Under the lifecycle model, a row's role is:
    //   • CLOSURE  — is_key=TRUE with a non-initial key_type
    //   • OPENER   — the row with revisionNumber=0 that ISN'T a
    //     closure (either the initial for v1 or the first save
    //     after the previous closure for v2+)
    //   • REVISION — everything else (revisionNumber ≥ 1)
    const isClosure = v.isKey && v.keyType !== 'initial';
    if (isClosure) {
      g.closure = v;
    } else if (v.revisionNumber === 0) {
      g.opener = v;
    } else {
      g.revisions.push(v);
    }
  }
  // Turn into a sorted array. Every group MUST have an opener under
  // the new model — save.ts always writes v1 as key_type='initial'
  // (revisionNumber=0) and each subsequent lifecycle's first save
  // gets revisionNumber=0 via list-versions.ts's walk. If somehow
  // missing (corrupt / mid-migration data), fall back to the closure
  // as opener so the picker still renders.
  const groups: LifecycleGroup[] = Array.from(groupsByIndex.values())
    .map(g => ({
      index: g.index,
      closure: g.closure,
      opener: (g.opener ?? g.closure) as PlanVersionMeta,
      revisions: g.revisions,
    }))
    .sort((a, b) => b.index - a.index);

  // Order closures across the whole plan: only the newest order-typed
  // row is LIVE — every older one is SUPERSEDED. This spans lifecycles
  // because a plan can (rarely) have multiple order pushes over time.
  const allClosures = versions.filter(v => v.isKey && v.keyType !== 'initial');
  const orderClosures = allClosures.filter(
    m => m.keyType === 'order' || m.keyType === 'order_update',
  );
  const liveOrderVersionNo = orderClosures[0]?.versionNo ?? null;

  /** Resolve a milestone to the ONE chip kind it should display. */
  const kindFor = (m: PlanVersionMeta): VersionChipKind => {
    if (m.keyType === 'order' || m.keyType === 'order_update') {
      return m.versionNo === liveOrderVersionNo ? 'live-order' : 'superseded';
    }
    if (m.keyType === 'named')  return 'named';
    if (m.keyType === 'export') return 'exported';
    return 'initial';
  };

  /**
   * Renders a closure row (named / exported / ordered) — the row
   * that ended a lifecycle. Sits ABOVE the lifecycle it closed and
   * carries the lifecycle's outward identity (the name the user
   * sent to a client, or the SF order label). No collapsible
   * revisions — revisions belong to the lifecycle header below.
   */
  const renderClosureRow = (c: PlanVersionMeta): React.ReactNode => {
    const isCurrent = c.versionNo === currentVersionNo;
    const kind = kindFor(c);
    const t = timelineIcon(kind);
    const isNamed = c.keyType === 'named' || c.keyType === 'export';
    const prefix = versionPrefix(c);
    const primaryName = versionPrimaryName(c);
    return (
      // Subtle bottom-divider only — the parent Milestones card
      // already carries the outer border, so we just need to hint
      // separation between stacked closures.
      <div key={`closure-${c.versionNo}`} style={{ borderBottom: '1px solid #ffedd5' }}>
        <div
          className={`version-popup-row${isCurrent ? ' current' : ''}`}
          onClick={() => onPickVersion(c.versionNo)}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px 9px 12px', position: 'relative' }}
          title="Open this version"
        >
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: t.filled ? t.bg : '#fff',
            border: `2px solid ${t.border}`,
            color: t.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: 1, position: 'relative', zIndex: 1,
            fontSize: 12,
          }}>
            <span style={{ transform: 'scale(1.1)', display: 'inline-flex' }}>{t.icon}</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={versionPrefixChipStyle}>{prefix}</span>
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: '#111827',
                wordBreak: 'break-word',
              }}>
                {primaryName}
              </span>
              {(() => {
                // Order-family rows wear two pills side-by-side:
                //   identity (Original / Revised) + status (Current /
                //   Superseded). Non-order closures (Named / Exported)
                //   keep the single-chip treatment via chipLabel(kind).
                const badges = orderBadges(c, liveOrderVersionNo);
                if (badges) {
                  const idPill = orderIdentityPill(badges.identity);
                  const stPill = orderStatusPill(badges.status);
                  return (
                    <>
                      <span style={idPill.style}>{idPill.label}</span>
                      <span style={stPill.style}>{stPill.label}</span>
                    </>
                  );
                }
                return <span style={pillStyle(kind)}>{chipLabel(kind)}</span>;
              })()}
              {isCurrent && (
                <span style={{
                  color: '#fff', background: '#2563eb', border: '1px solid #2563eb',
                  fontSize: 9, fontWeight: 500, padding: '1px 6px',
                  borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>Viewing</span>
              )}
            </div>
            <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2 }}>
              {formatVersionDateExternal(c.savedAt)}
              {c.savedBy ? ` · ${c.savedBy.split('@')[0]}` : ''}
              {c.dealCount > 0 && ` · ${c.dealCount} deal${c.dealCount === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders a lifecycle header row + its working revisions branch.
   * The header identifies the working phase (v1, v2, ...) and is
   * distinct from the closure row above it (if any). It's always
   * clickable → picks the header row's version (initial for v1;
   * the earliest revision for lifecycles that opened implicitly).
   *
   * If the lifecycle has zero revisions (e.g., just an initial and
   * then a closure, no working saves in between), the header still
   * renders alone as a picker entry — the closure row above it
   * carries the "content", but the header preserves the v{N}
   * identity so the user can click back to the working state.
   */
  const renderLifecycleHeader = (g: LifecycleGroup): React.ReactNode => {
    const h = g.opener;
    const isCurrent = h.versionNo === currentVersionNo;
    const revs = g.revisions;                    // DESC by versionNo, revisionNumber > 0
    const isExpanded = !collapsed.has(h.versionNo);
    // For lifecycles opened implicitly (v2, v3, ...) we don't have
    // a blue-sparkle "initial" icon. Use a yellow pencil to signal
    // "you're drafting the next version" — a lighter, editorial
    // feel that reads as an in-progress working revision rather
    // than a formal opener event. Amber-500 matches the "revision
    // in flight" pill palette elsewhere.
    const isImplicitOpener = h.keyType !== 'initial';
    const t = isImplicitOpener
      ? { icon: <IconPencil />,
          bg: '#f59e0b', color: '#fff', border: '#f59e0b', filled: true }
      : timelineIcon('initial');
    return (
      // No outer border — each lifecycle now sits in its own bordered
      // card at the parent level. Inner separators aren't needed.
      <div key={`lifecycle-${g.index}`}>
        <div
          className={`version-popup-row${isCurrent ? ' current' : ''}`}
          onClick={() => onPickVersion(h.versionNo)}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 10px 9px 12px', position: 'relative' }}
          title={`Open v${g.index}`}
        >
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            background: t.filled ? t.bg : '#fff',
            border: `2px solid ${t.border}`,
            color: t.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: 1, position: 'relative', zIndex: 1,
            fontSize: 11,
          }}>
            <span style={{ display: 'inline-flex' }}>{t.icon}</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{`v${g.index}`}</span>
              {isCurrent && (
                <span style={{
                  color: '#fff', background: '#2563eb', border: '1px solid #2563eb',
                  fontSize: 9, fontWeight: 500, padding: '1px 6px',
                  borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>Current</span>
              )}
            </div>
            <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2 }}>
              {formatVersionDateExternal(h.savedAt)}
              {h.savedBy ? ` · ${h.savedBy.split('@')[0]}` : ''}
            </div>
          </div>
          {(revs.length > 0 || g.closure) && (
            <button
              onClick={e => { e.stopPropagation(); toggleExpanded(h.versionNo); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#9ca3af', padding: 4, alignSelf: 'center',
              }}
              aria-label={isExpanded ? 'Collapse revisions' : 'Expand revisions'}
              title={isExpanded ? 'Hide history' : 'Show history'}
            >
              <IconChevron dir={isExpanded ? 'down' : 'right'} />
            </button>
          )}
        </div>
        {isExpanded && (revs.length > 0 || g.closure) && (() => {
          // Branch items: closure first (chronologically newest — the
          // event that ENDED this lifecycle), then working revisions
          // DESC. Rendering the closure inside the branch means the
          // user can see the full arc of v1 in one place — opener at
          // the top, working saves in the middle, closure at the
          // top of the branch (since it's the newest). It still
          // appears in the top MILESTONES section for prominence.
          type BranchItem =
            | { kind: 'closure'; row: PlanVersionMeta }
            | { kind: 'revision'; row: PlanVersionMeta };
          const branchItems: BranchItem[] = [
            ...(g.closure ? [{ kind: 'closure' as const, row: g.closure }] : []),
            ...revs.map(r => ({ kind: 'revision' as const, row: r })),
          ];
          const ROW = 32;
          return (
          <div style={{ position: 'relative', paddingBottom: 4 }}>
            <div style={{
              position: 'absolute',
              left: 21,
              top: -6,
              width: 2,
              height: (branchItems.length - 1) * ROW + ROW / 2 + 6,
              background: '#d1d5db',
              pointerEvents: 'none',
              zIndex: 2,
            }} />
            {branchItems.map((item, idx) => {
              const r = item.row;
              const isCurr = r.versionNo === currentVersionNo;
              const isLast = idx === branchItems.length - 1;
              const isClosure = item.kind === 'closure';
              const closureKind = isClosure ? kindFor(r) : null;
              const closureTicon = isClosure && closureKind ? timelineIcon(closureKind) : null;
              return (
                <div
                  key={r.versionNo}
                  onClick={() => onPickVersion(r.versionNo)}
                  className={`version-popup-row${isCurr ? ' current' : ''}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 10px 5px 52px', cursor: 'pointer', position: 'relative',
                    minHeight: ROW,
                  }}
                  title={isClosure ? 'Open this milestone' : 'Open this revision'}
                >
                  {isLast ? (
                    <div style={{
                      position: 'absolute',
                      left: 21,
                      top: ROW / 2 - 1,
                      width: 13,
                      height: 8,
                      borderLeft: '2px solid #d1d5db',
                      borderBottom: '2px solid #d1d5db',
                      borderBottomLeftRadius: 8,
                      pointerEvents: 'none',
                      zIndex: 2,
                      marginTop: -6,
                    }} />
                  ) : (
                    <div style={{
                      position: 'absolute',
                      left: 22,
                      top: 'calc(50% - 1px)',
                      width: 12,
                      height: 2,
                      background: '#d1d5db',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }} />
                  )}
                  {isClosure && closureTicon ? (
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: closureTicon.filled ? closureTicon.bg : '#fff',
                      border: `2px solid ${closureTicon.border}`,
                      color: closureTicon.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, position: 'absolute', left: 32,
                      top: 'calc(50% - 8px)', zIndex: 3,
                      fontSize: 9,
                    }}>
                      <span style={{ transform: 'scale(0.85)', display: 'inline-flex' }}>{closureTicon.icon}</span>
                    </div>
                  ) : (
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: isCurr ? '#2563eb' : '#9ca3af',
                      flexShrink: 0, position: 'absolute', left: 34,
                      top: 'calc(50% - 5px)', zIndex: 3,
                    }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {isClosure ? (
                        <>
                          <span style={versionPrefixChipStyle}>{versionPrefix(r)}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                            {versionPrimaryName(r)}
                          </span>
                          {(() => {
                            // Order-family closures inside a lifecycle
                            // branch get the same identity+status pair
                            // as the top-level closure rows. Non-order
                            // closures fall back to the single chip.
                            const badges = orderBadges(r, liveOrderVersionNo);
                            if (badges) {
                              const idPill = orderIdentityPill(badges.identity);
                              const stPill = orderStatusPill(badges.status);
                              return (
                                <>
                                  <span style={idPill.style}>{idPill.label}</span>
                                  <span style={stPill.style}>{stPill.label}</span>
                                </>
                              );
                            }
                            return closureKind ? (
                              <span style={pillStyle(closureKind)}>{chipLabel(closureKind)}</span>
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#111827' }}>
                            Revision {r.revisionNumber}
                          </span>
                          <span style={{ fontSize: 10, color: '#9ca3af' }}>
                            {compactVersionLabel(r)}
                          </span>
                        </>
                      )}
                      {isCurr && (
                        <span style={{
                          color: '#fff', background: '#2563eb', border: '1px solid #2563eb',
                          fontSize: 9, fontWeight: 500, padding: '1px 6px',
                          borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>Viewing</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                      {formatVersionDateExternal(r.savedAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="version-popup" role="dialog" aria-label="Version history">
      {/* ── "You are viewing" ──
          Under the lifecycle model, closures identify by their name
          (or "v{N} · Live Order" for orders). Working revisions still
          identify by "v{N}[.rev]". The heading + chip reflect that:
            • Named / exported closure → big label is the QUOTED name,
              chip reads NAMED (orange). No separate italic line —
              the name IS the identifier.
            • Order closure → big label reads "v{N} · Live Order",
              chip reads LIVE ORDER (green).
            • Working revision (or initial) → big label is v{N} or
              v{N}.{rev}, chip is REVISION for non-initial revisions,
              nothing for the initial (implicit). */}
      {currentMeta && (() => {
        const kind = kindFor(currentMeta);
        const isNamedClosure = currentMeta.keyType === 'named' || currentMeta.keyType === 'export';
        const isRevision = !currentMeta.isKey && currentMeta.keyType !== 'initial';
        const compact = compactVersionLabel(currentMeta);
        // Version + primary name split. Header shows "[v5] Client
        // Revision A" instead of "Order Revision V5" so the row's
        // identity mirrors the closure rows below.
        const currentPrefix = versionPrefix(currentMeta);
        const currentPrimaryName = versionPrimaryName(currentMeta);
        // Rename button target — always uses the compact label so the
        // button reads "Rename Upfront V1" / "Name v1.3" / etc.
        const renameTarget = compact;
        return (
        <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#2563eb', marginBottom: 3 }}>
                You are viewing
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ ...versionPrefixChipStyle, fontSize: 11, padding: '2px 8px' }}>
                  {currentPrefix}
                </span>
                <span
                  style={{
                    fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15,
                    color: '#111827',
                    wordBreak: 'break-word',
                  }}
                >
                  {currentPrimaryName}
                </span>
                {isRevision ? (
                  <span style={{
                    color: '#1d4ed8', background: '#fff', border: '1px solid #93c5fd',
                    fontSize: 9, fontWeight: 500, padding: '1px 7px',
                    borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>Revision</span>
                ) : (() => {
                  // "You are viewing" header: order-family closures get
                  // the double-pill treatment (identity + status) so
                  // the top-level identifier reads the same way as the
                  // rows below it. Non-order closures fall back to the
                  // single-chip label; initial rows render no pill.
                  const badges = orderBadges(currentMeta, liveOrderVersionNo);
                  if (badges) {
                    const idPill = orderIdentityPill(badges.identity);
                    const stPill = orderStatusPill(badges.status);
                    return (
                      <>
                        <span style={idPill.style}>{idPill.label}</span>
                        <span style={stPill.style}>{stPill.label}</span>
                      </>
                    );
                  }
                  return kind !== 'initial' ? (
                    <span style={pillStyle(kind)}>{chipLabel(kind)}</span>
                  ) : null;
                })()}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                {formatVersionDateExternal(currentMeta.savedAt)}
                {currentMeta.savedBy ? ` · ${currentMeta.savedBy.split('@')[0]}` : ''}
                {' · '}{currentMeta.dealCount} deal{currentMeta.dealCount === 1 ? '' : 's'}
              </div>
            </div>
            <button
              onClick={() => onNameVersion(currentMeta.versionNo)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 11, fontWeight: 500, color: '#1e40af',
                background: '#fff', border: '1px solid #bfdbfe', borderRadius: 999,
                padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              title="Give this version a memorable label"
            >
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              {currentMeta.name ? `Rename ${renameTarget}` : `Name ${renameTarget}`}
            </button>
          </div>
        </div>
        );
      })()}

      {/* ── MILESTONES section ────────────────────────────────────────────
          Just the closures — named / exported / ordered — inside an
          orange-tinted card. No embedded revisions here (those live
          in Version History below). Hidden when a plan has no
          closures so a fresh v1 doesn't show an empty section. */}
      {(() => {
        const allClosures = versions
          .filter(v => v.isKey && v.keyType !== 'initial')
          .sort((a, b) => b.versionNo - a.versionNo);
        if (allClosures.length === 0) return null;
        return (
          <div style={{
            margin: '10px 10px 6px',
            border: '1px solid #fed7aa',
            borderRadius: 8,
            background: '#fffbf5',
            overflow: 'hidden',
          }}>
            {/* Header — flag icon, uppercase label, count pill, info */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 10px 6px',
              borderBottom: '1px solid #fed7aa',
              background: '#fff7ed',
            }}>
              <span style={{ color: '#f97316', display: 'inline-flex', transform: 'scale(0.85)' }}><IconFlag /></span>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                color: '#c2410c', textTransform: 'uppercase',
              }}>
                Milestones
              </span>
              <span style={{
                fontSize: 9.5, fontWeight: 500,
                color: '#9a3412', background: '#ffedd5',
                border: '1px solid #fdba74', borderRadius: 999,
                padding: '1px 7px',
              }}>
                {allClosures.length} milestone{allClosures.length === 1 ? '' : 's'}
              </span>
              <div style={{ flex: 1 }} />
              <span
                style={{ color: '#c2410c', opacity: 0.7, display: 'inline-flex', cursor: 'help', transform: 'scale(0.85)' }}
                title="Shareable / contractual snapshots — named exports, orders, order updates."
              >
                <IconInfo />
              </span>
            </div>
            <div className="vp-timeline">
              {allClosures.map(renderClosureRow)}
            </div>
          </div>
        );
      })()}

      {/* ── VERSION HISTORY section ───────────────────────────────────────
          Blue-tinted card wrapping every working lifecycle. Each
          lifecycle gets its own inner sub-card so v1's revisions
          are visually enclosed under v1's header and don't bleed
          into v2's group. */}
      <div style={{
        margin: '4px 10px 10px',
        border: '1px solid #dbeafe',
        borderRadius: 8,
        background: '#f8fafc',
        overflow: 'hidden',
      }}>
        {/* Header — clock icon, uppercase label, count pill, info */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 10px 6px',
          borderBottom: '1px solid #dbeafe',
          background: '#eff6ff',
        }}>
          <span style={{ color: '#2563eb', display: 'inline-flex', transform: 'scale(0.85)' }}><IconClock /></span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            color: '#1d4ed8', textTransform: 'uppercase',
          }}>
            Version History
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 500,
            color: '#1d4ed8', background: '#dbeafe',
            border: '1px solid #93c5fd', borderRadius: 999,
            padding: '1px 7px',
          }}>
            {groups.length} version group{groups.length === 1 ? '' : 's'}
          </span>
          <div style={{ flex: 1 }} />
          <span
            style={{ color: '#1d4ed8', opacity: 0.7, display: 'inline-flex', cursor: 'help', transform: 'scale(0.85)' }}
            title="Working lifecycles — every save from a v{N} opener through its revisions."
          >
            <IconInfo />
          </span>
        </div>
        {groups.length === 0 ? (
          <div style={{ padding: '14px 14px', fontSize: 11, color: '#6b7280', fontStyle: 'italic', textAlign: 'center' }}>
            No versions yet. Save the plan to create v1.
          </div>
        ) : (
          <div style={{ padding: '6px' }}>
            {groups.map(g => (
              <div
                key={`lc-${g.index}`}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: 6,
                  background: '#fff', marginBottom: 6, overflow: 'hidden',
                }}
              >
                {renderLifecycleHeader(g)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer counts — under the lifecycle model, the top-line metric
          is the working-lifecycle count (v1, v2, ...). Total saved
          states includes revisions + closures + initials. */}
      <div style={{ padding: '10px 12px', fontSize: 11, color: '#9ca3af', textAlign: 'center', borderTop: '1px solid #e5e7eb' }}>
        {versions.length} saved state{versions.length === 1 ? '' : 's'} total ·
        {' '}{groups.length} version{groups.length === 1 ? '' : 's'}
      </div>
    </div>
  );
};

/**
 * Export-as-key dialog. Shown when the user hits Export on a plan
 * whose latest saved version isn't already a key milestone. Three
 * exits:
 *   • "Save & Export" — auto-saves any pending edits, snapshots the
 *     result as key_type='export' via /versions/export, then
 *     downloads the file.
 *   • "Just Export" — downloads without saving a version.
 *   • Cancel (X, backdrop, Esc) — aborts entirely.
 *
 * Re-exporting a version that's ALREADY a key skips this modal
 * entirely (see handleExportGate) — no duplicate snapshot needed.
 *
 * Kept simple and self-contained so it can be lifted into a shared
 * modal component later without dragging MPB-specific state along.
 */
interface PostOrderExportModalProps {
  onClose: (choice: { save: true; name: string } | { save: false } | null) => void;
  /**
   * True when the user is actively revising an order — the "save"
   * choice will finalize the revision as the new Live Order
   * (order_update key_type) rather than a plain export snapshot.
   * Drives the copy + primary button label so the consequence is
   * spelled out in-context.
   */
  isRevisingOrder?: boolean;
}
const PostOrderExportModal: React.FC<PostOrderExportModalProps> = ({ onClose, isRevisingOrder }) => {
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => { inputRef.current?.focus(); }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= 200;
  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 1100 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(null); }}
    >
      <div className="modal-box" style={{ width: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 18 }}>{isRevisingOrder ? '📝' : '⬇'}</span>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            {isRevisingOrder ? 'Finalize order revision?' : 'Save as new version?'}
          </h2>
        </div>
        <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, margin: '0 0 12px 0' }}>
          {isRevisingOrder ? (
            <>
              Naming and exporting will finalize this revision as the
              new <strong>Live Order</strong>. The previous Live Order
              will be marked Superseded in version history.
              This does <strong>not</strong> re-push to Salesforce —
              only the initial order push does that.
            </>
          ) : (
            <>
              Optionally save this state as a named version — it becomes a
              milestone in the version history so you (or a teammate) can
              find and reopen exactly what you shared. The export file
              downloads either way.
            </>
          )}
        </p>
        <label htmlFor="export-version-name" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {isRevisingOrder ? 'Revision name' : 'Version name'}
        </label>
        <input
          ref={inputRef}
          id="export-version-name"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && canSave) onClose({ save: true, name: trimmed }); }}
          placeholder={isRevisingOrder ? 'e.g. Client Options V1' : 'e.g. Client review draft'}
          maxLength={200}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 12, fontFamily: 'inherit', outline: 'none' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={() => onClose(null)}
            style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#4b5563' }}
          >Cancel</button>
          <button
            onClick={() => onClose({ save: false })}
            style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
            title="Download the file without saving a version"
          >Just Export</button>
          <button
            onClick={() => onClose({ save: true, name: trimmed })}
            disabled={!canSave}
            style={{
              padding: '8px 16px',
              background: canSave ? (isRevisingOrder ? '#7c3aed' : '#2563eb') : (isRevisingOrder ? '#c4b5fd' : '#93c5fd'),
              color: '#fff', border: 'none', borderRadius: 6,
              fontSize: 13, fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
            title={canSave
              ? (isRevisingOrder
                ? 'Finalize this revision as the new Live Order and export the file'
                : 'Save this snapshot in version history, then export the file')
              : 'Enter a name to enable'}
          >{isRevisingOrder ? 'Finalize Revision & Export' : 'Name Version & Export'}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Custom picklist that visually matches LookupField / the Primary Demo
 * picker (rounded panel, hoverable rows, current-pick highlight,
 * scroll-into-view). Replaces native `<select>` for Marketplace,
 * Equivalized, and any other short static-options picklist in the
 * plan modals so all form controls read as one visual family.
 *
 * Why not just style the native `<select>`: browsers hardcode the
 * OPEN dropdown menu to OS chrome — you can strip the closed-state
 * chevron but the menu that appears on click stays native. To get
 * a consistent panel-style dropdown we render the popover ourselves.
 */
interface SimpleSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  requiredMissing?: boolean;   // red border when empty + required
  style?: React.CSSProperties;
}
const SimpleSelect: React.FC<SimpleSelectProps> = ({
  value, onChange, options, placeholder = '— Select —', requiredMissing, style,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const currentRowRef = React.useRef<HTMLDivElement | null>(null);

  // Click-outside dismissal.
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Esc closes.
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Scroll current pick into view when dropdown opens.
  React.useEffect(() => {
    if (!open || !value) return;
    const raf = requestAnimationFrame(() => {
      currentRowRef.current?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, value]);

  const baseStyle: React.CSSProperties = {
    width: '100%', padding: '8px 32px 8px 10px',
    border: `1px solid ${requiredMissing ? '#fca5a5' : '#dde1e9'}`,
    borderRadius: 6, fontSize: 13, lineHeight: 1.4,
    color: value ? '#111827' : '#9ca3af',
    background:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\") no-repeat right 10px center, #fff",
    cursor: 'pointer',
    textAlign: 'left',
    ...style,
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={baseStyle}
      >
        {value || placeholder}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #dde1e9', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 20, maxHeight: 200, overflowY: 'auto',
        }}>
          {options.map(opt => {
            const isCurrent = opt === value;
            return (
              <div
                key={opt}
                ref={el => { if (isCurrent) currentRowRef.current = el; }}
                onMouseDown={e => {
                  e.preventDefault();
                  onChange(opt);
                  setOpen(false);
                }}
                style={{
                  padding: '8px 10px', cursor: 'pointer', fontSize: 13,
                  borderTop: '1px solid #f1f5f9',
                  background: isCurrent ? '#eff4ff' : undefined,
                  fontWeight: isCurrent ? 600 : 400,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#eff4ff')}
                onMouseLeave={e => (e.currentTarget.style.background = isCurrent ? '#eff4ff' : '')}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Branded loading screen that renders while estimate-versions + source
 * deals hydrate. Replaces the previous one-line "Loading…" text with a
 * centered card that shows:
 *
 *   • MVM badge + "Media Plan Builder" title
 *   • Large animated percentage display (fake-but-believable ticker —
 *     climbs to 95% over ~2.5s, then holds; real loading completion
 *     dismounts this component so the user never sees 100% hang)
 *   • Slim progress bar mirroring the same percentage
 *   • Four step indicators with REAL hydration status:
 *       1. Estimates       (estimatesHydrated)
 *       2. Source deals    (dataLoaded)
 *       3. Saved plans     (plansHydrated)
 *       4. Plan bindings   (bindingsHydrated)
 *     Pending → empty grey circle. In-flight → blue spinning ring.
 *     Done → green check.
 *
 * Sits BELOW the parent app's 64px fixed Nosey toolbar (StyledHeader
 * in App.tsx) — uses min-height: calc(100vh - 64px) so the toolbar
 * stays visible at the top while the card centers in the remaining
 * area. Background matches MPB's main view (#f0f2f5) so the transition
 * to the loaded view is seamless.
 */
const MediaPlanBuilderLoadingScreen: React.FC<{
  estimatesHydrated: boolean;
  dataLoaded: boolean;
  plansHydrated: boolean;
  bindingsHydrated: boolean;
  sourceDealsError: string | null;
}> = ({ estimatesHydrated, dataLoaded, plansHydrated, bindingsHydrated, sourceDealsError }) => {
  // Faked progress percentage. Ticks up smoothly from 0 toward 95 over
  // about 2.5 seconds; never reaches 100 — the component unmounts when
  // the real loading gate (estimatesHydrated && dataLoaded) clears, so
  // there's no awkward "100%, waiting…" pause.
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (progress >= 95) return;
    // Slow down as we approach 95 so the bar doesn't slam to the cap
    // and sit there; feels organic.
    const remaining = 95 - progress;
    const stepDelay = remaining > 50 ? 25 : remaining > 20 ? 50 : 120;
    const id = window.setTimeout(() => setProgress(p => Math.min(95, p + 1)), stepDelay);
    return () => window.clearTimeout(id);
  }, [progress]);

  // Map each pipeline stage to its real status, in the order we want
  // to display. Order matches the practical sequence the user expects
  // ("data loaded" before "plans saved" before "bindings").
  type StageStatus = 'done' | 'loading' | 'pending';
  const stages: Array<{ label: string; status: StageStatus }> = [
    {
      label: 'Estimates',
      status: estimatesHydrated ? 'done' : 'loading',
    },
    {
      label: 'Source deals',
      // Source deals can fail entirely (network / Snowflake). Reflect
      // that as a pending state with the error spelled out below.
      status: sourceDealsError ? 'pending' : (dataLoaded ? 'done' : 'loading'),
    },
    {
      label: 'Saved plans',
      // Plans hydrate in parallel; if estimates+deals finish first,
      // plans might still be in flight. Show its real status.
      status: plansHydrated ? 'done' : 'loading',
    },
    {
      label: 'Plan bindings',
      status: bindingsHydrated ? 'done' : 'loading',
    },
  ];

  // SVG icons for each stage status. All sized 14px.
  const renderStatusIcon = (status: StageStatus): React.ReactNode => {
    if (status === 'done') {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
          <circle cx="7" cy="7" r="7" fill="#059669" />
          <path d="M3.8 7.2L6 9.4l4.2-4.6" stroke="#fff" strokeWidth="1.7"
                fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (status === 'loading') {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden
             style={{ animation: 'mpb-spin 0.9s linear infinite' }}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="#dbeafe" strokeWidth="1.6" />
          <path d="M7 1.5 A 5.5 5.5 0 0 1 12.5 7" fill="none"
                stroke="#2563eb" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    }
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
        <circle cx="7" cy="7" r="5.5" fill="none" stroke="#cbd5e1" strokeWidth="1.4" />
      </svg>
    );
  };

  return (
    <div style={{
      // Leave the parent app's fixed Nosey toolbar (StyledHeader in
      // App.tsx) visible at the top — matches the rest of the app's chrome.
      minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT}px)`,
      background: '#f0f2f5',
      fontFamily: "'Inter', Arial, sans-serif",
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <style>{`
        @keyframes mpb-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes mpb-pulse {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1; }
        }
      `}</style>
      <div style={{
        width: 420,
        maxWidth: '100%',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        boxShadow: '0 10px 32px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)',
        padding: '32px 32px 28px',
      }}>
        {/* MVM badge + product name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{
            fontFamily: "'Inter', Arial, sans-serif",
            fontSize: 12, fontWeight: 700,
            color: colors.primary,
            letterSpacing: '0.6px',
            background: colors.primaryLight,
            padding: '5px 11px',
            borderRadius: 6,
          }}>MVM</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.textDark }}>
            Media Plan Builder
          </span>
        </div>
        <div style={{
          fontSize: 12, color: '#64748b',
          marginBottom: 26,
        }}>
          {sourceDealsError
            ? 'Couldn’t reach Snowflake — see details below.'
            : 'Pulling the latest plans, estimates, and inventory.'}
        </div>

        {/* Percentage + progress bar */}
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          marginBottom: 8,
        }}>
          <span style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 36, fontWeight: 500, color: colors.textDark,
            letterSpacing: '-0.02em',
            // Subtle breathing pulse on the number so it feels alive
            // even when the ticker holds at 95.
            animation: progress >= 95 ? 'mpb-pulse 1.4s ease-in-out infinite' : undefined,
          }}>
            {progress}<span style={{ fontSize: 22, color: '#94a3b8', marginLeft: 2 }}>%</span>
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Loading
          </span>
        </div>
        <div style={{
          height: 6,
          background: colors.borderInk,
          borderRadius: 999,
          overflow: 'hidden',
          marginBottom: 24,
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
            borderRadius: 999,
            transition: 'width 120ms ease-out',
          }} />
        </div>

        {/* Step list — real hydration status per row. */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10,
          paddingTop: 4,
          borderTop: '1px solid #f1f5f9',
        }}>
          <div style={{ height: 4 }} />
          {stages.map(s => (
            <div key={s.label} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 12.5,
              color: s.status === 'done' ? colors.textDark : '#64748b',
            }}>
              {renderStatusIcon(s.status)}
              <span style={{ fontWeight: s.status === 'done' ? 600 : 500 }}>{s.label}</span>
              <span style={{
                marginLeft: 'auto',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: s.status === 'done' ? colors.success
                     : s.status === 'loading' ? colors.primary
                     : '#94a3b8',
              }}>
                {s.status === 'done' ? 'Done'
                 : s.status === 'loading' ? 'Loading…'
                 : 'Pending'}
              </span>
            </div>
          ))}
        </div>

        {/* Error ribbon for the only failure path the legacy code
            surfaced — source-deals network/Snowflake failure. Other
            hydrate failures degrade silently today; could be added
            later if needed. */}
        {sourceDealsError && (
          <div style={{
            marginTop: 18,
            padding: '10px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            fontSize: 11.5, color: '#991b1b',
            lineHeight: 1.5,
          }}>
            <strong>Source deals failed to load.</strong>{' '}
            {sourceDealsError}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPlanBuilder;
